import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { generateSharingUrl } from '../../lib/utils';
import { getCandidateDisplayName } from '../../utils/candidateName';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import { QRCodeCanvas } from 'qrcode.react';
import {
  AcademicCapIcon,
  ArrowLeftIcon,
  BoltIcon,
  ArrowTopRightOnSquareIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  DocumentArrowUpIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  BellIcon,
  FlagIcon,
  FunnelIcon,
  InformationCircleIcon,
  LinkIcon,
  MicrophoneIcon,
  PhoneIcon,
  PlusCircleIcon,
  ViewColumnsIcon,
  StarIcon,
  TrashIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
  XCircleIcon,
  XMarkIcon,
  ShieldExclamationIcon,
  CalendarDaysIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import { MapPinIcon, CurrencyRupeeIcon, BriefcaseIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { CheckIcon, StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Listbox, Transition } from '@headlessui/react';
import AssessmentCreateFlowModal from '../assessments/AssessmentCreateFlowModal';
import AnimatedModal from '../../components/ui/AnimatedModal';
import AvailabilitySetupModal from '../../components/scheduling/AvailabilitySetupModal';
import CandidateInsightModal from '../../components/results/CandidateInsightModal';
import SendEmailToCandidatesModal from '../../components/results/SendEmailToCandidatesModal';
import ResendInviteModal from '../../components/screenings/ResendInviteModal';
import WhatsAppInviteModal from '../../components/screenings/WhatsAppInviteModal';
import QualifyingAgentInviteModal from '../../components/screenings/QualifyingAgentInviteModal';
import WhatsAppInterviewReplayModal from '../../components/screenings/WhatsAppInterviewReplayModal';
import QualifyingAgentReplayModal from '../../components/screenings/QualifyingAgentReplayModal';
import QualifyingAnswersCard from '../../components/screenings/QualifyingAnswersCard';
import QualifyingAgentDetailsModal from '../../components/screenings/QualifyingAgentDetailsModal';
import QualifyingAgentBoundary from '../../components/screenings/QualifyingAgentBoundary';
import ShareCandidateReviewModal from '../../components/modals/ShareCandidateReviewModal';
import DpaRequiredModal from '../../components/documents/DpaRequiredModal';
import CollectDocumentsModal from '../../components/documents/CollectDocumentsModal';
import DocumentViewerModal from '../../components/documents/DocumentViewerModal';
import CandidateAssessmentScoreContent from '../../components/results/CandidateAssessmentScoreContent';
import ResumeScoreConfig from '../../components/screenings/ResumeScoreConfig';
import DocumentCollectionConfig from '../../components/screenings/DocumentCollectionConfig';
import CandidateProctoringDetailsContent from '../../components/results/CandidateProctoringDetailsContent';
import AICandidateSearchBar from '../../components/results/AICandidateSearchBar';
import CandidatesFiltersModal from './CandidatesFiltersModal';
import AutomationsModal from '../../components/screenings/AutomationsModal';
import DecisionEmailModal from '../../components/screenings/DecisionEmailModal';
import BKDecisionEmailModal from '../../components/screenings/BKDecisionEmailModal';
import MoveCandidateJobPostModal from '../../components/screenings/MoveCandidateJobPostModal';
import CandidateColumnsModal, { buildCandidateColumnChoices } from './CandidateColumnsModal';
import ColumnSaveScopeModal from './ColumnSaveScopeModal';
import { ScoreFilterDropdown } from './ScoreFilterDropdown';
import { candidateMatchesStatusFilter, isStatusFilterActive } from './candidateStatusFilter';
// Analysis & Transcript body of the interview replay modal. Extracted from this
// file so the public hiring-manager review page renders the identical UI — the
// two surfaces previously had separate, drifted implementations.
import InterviewAnalysisTranscript from './InterviewAnalysisTranscript';
import InterviewAssessmentCards from '../../components/screenings/InterviewAssessmentCards';
import {
  buildCandidateComparator,
  getOverallScoreNumber as getSharedOverallScoreNumber,
} from './candidateSort';
import { buildMonthOptions, filterByMonth, loadMonth, saveMonth } from './monthFilter';
import MetaFilterDropdown from './MetaFilterDropdown';
import { useAISearch } from '../../hooks/useAISearch';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';
import { useAuth } from '../../contexts/AuthContext';
import { INVITE_MODAL_SIZING, INVITE_MODAL_COLORS } from '../../constants/inviteFlowModalTheme';
import {
  getCombinedScreeningProctoringBadge,
  getRiskBadgeFromScore,
  getTotalRiskPointsFromDetails,
} from '../../components/results/proctoringUtils';
import {
  inviteFlowModalStyles,
  PROCTORING_EYE_GAZE_CRITERIA,
  resolveCandidateDisplayName,
  inBucket,
  EXPERIENCE_BUCKETS,
  AGE_BUCKETS,
  CTC_BUCKETS,
  MAX_REJECT_REASON_LENGTH,
  buildRejectPayload,
} from './screeningConstants';
import RecordingPlaybackButton from './RecordingPlaybackButton';
import CandidatesTable, { isTranscriptsColumnAvailable, getTranscriptsAvailableLabel } from './CandidatesTable';
import {
  IncompleteDownloadError,
  buildGoColorsPayrollEntry,
  missingSummary,
  recordPayrollDownload,
  safeFileTitle,
  saveGoColorsPayrollZipParts,
} from './goColorsPayrollExport';
import SalaryDetailsModal from '../../components/screenings/SalaryDetailsModal';
import { buildScreeningAssessmentModuleMeta, normalizeAssessmentModulesFromScreening } from './assessmentModuleColumns';
import { personalityExportColumns } from '../../components/results/personalityProfile';
import InsightsTab from './InsightsTab';
import useQualifyingSummaryRefresh from '../../components/screenings/useQualifyingSummaryRefresh';
import { qualifyingCanStillChange } from '../../components/screenings/qualifyingAgentStatus';
import { BK_REJECT_REASONS } from '../../lib/bkRejectReasons';
import { filterStoresByFormat, getStoreCodeRule } from '../../utils/storeFormat';
const AIInterviewCreate = React.lazy(() => import('../ai/AIInterviewCreate'));
const TABS = ['candidates', 'shortlist', 'rejected', 'insights', 'details'];

/**
 * Fallback order for Selected tab when org has NO candidates layout configured at all.
 * Decision first, Notes last.
 */
const PLATFORM_SHORTLIST_COLUMN_ORDER = [
  'decision',
  'overallScore', 'resumeScore', 'assessmentScore', 'interviewScore',
  'shortlistedDate', 'salaryDetails', 'completedAt', 'appliedDate', 'phone',
  'proctoring', 'duplicates', 'documents', 'status',
  'companyName', 'currentCtc', 'noticePeriod', 'designation', 'experience',
  'experienceYears', 'latestEducation', 'location', 'industry', 'skills', 'age',
  'email', 'social', 'screening',
  'storeId', 'storeName', 'storeRegion', 'armName', 'armEmail',
  'cityCode', 'cityCity', 'cityState', 'cityRegion',
  'candidateDetails',
  'notes', // always last
];

/**
 * Fallback order for Rejected tab when org has NO candidates layout configured at all.
 * Decision first, Notes last.
 */
const PLATFORM_REJECTED_COLUMN_ORDER = [
  'decision',
  'overallScore', 'resumeScore', 'assessmentScore', 'interviewScore',
  'completedAt', 'appliedDate', 'phone',
  'proctoring', 'duplicates', 'documents', 'status',
  'companyName', 'currentCtc', 'noticePeriod', 'designation', 'experience',
  'experienceYears', 'latestEducation', 'location', 'industry', 'skills', 'age',
  'email', 'social', 'screening',
  'storeId', 'storeName', 'storeRegion', 'armName', 'armEmail',
  'cityCode', 'cityCity', 'cityState', 'cityRegion',
  'candidateDetails',
  'notes', // always last
];

/**
 * Builds the smart default column order for the Selected (shortlist) tab.
 *
 * Priority:
 * 1. If the org has a candidates tab column order configured, inherit it
 *    (this preserves custom fields like Division/HQ in their configured position).
 *    Then: prepend Decision, append Notes.
 * 2. If no candidates layout exists, use PLATFORM_SHORTLIST_COLUMN_ORDER.
 *
 * @param {Object|null} userUi - user.ui from AuthContext
 * @returns {string[]} column ID array
 */
function buildShortlistDefaultOrder(userUi) {
  const candidatesOrder = userUi?.defaultCandidateColumnOrder;
  if (candidatesOrder?.length > 0) {
    // Remove decision/notes/shortlistedDate/salaryDetails from the candidates base order
    // (we'll re-insert them at specific positions)
    const base = candidatesOrder.filter(
      (id) => id !== 'decision' && id !== 'notes' && id !== 'shortlistedDate' && id !== 'salaryDetails'
    );
    // Insert shortlistedDate and salaryDetails after the last score column
    const scoreKeys = ['overallScore', 'resumeScore', 'assessmentScore', 'interviewScore'];
    const lastScoreIdx = Math.max(...scoreKeys.map((k) => base.indexOf(k)));
    const insertAt = lastScoreIdx >= 0 ? lastScoreIdx + 1 : 0;
    const withDate = [...base];
    withDate.splice(insertAt, 0, 'shortlistedDate', 'salaryDetails');
    // Decision first, notes last
    return ['decision', ...withDate, 'notes'];
  }
  return PLATFORM_SHORTLIST_COLUMN_ORDER;
}

/**
 * Builds the smart default column order for the Rejected tab.
 * Same logic as buildShortlistDefaultOrder but without shortlistedDate.
 *
 * @param {Object|null} userUi - user.ui from AuthContext
 * @returns {string[]}
 */
function buildRejectedDefaultOrder(userUi) {
  const candidatesOrder = userUi?.defaultCandidateColumnOrder;
  if (candidatesOrder?.length > 0) {
    const base = candidatesOrder.filter(
      (id) => id !== 'decision' && id !== 'notes'
    );
    return ['decision', ...base, 'notes'];
  }
  return PLATFORM_REJECTED_COLUMN_ORDER;
}

const PHONE_REGEX = /^[+]?[0-9]{7,15}$/;

function normalizePhoneForInvite(value) {
  if (!value) return '';
  return String(value).replace(/[^\d+]/g, '');
}

function buildChannelRecipient(row) {
  const name =
    row.name ||
    [row.firstName, row.lastName]
      .map((part) => (part || '').trim())
      .filter(Boolean)
      .join(' ') ||
    row.email ||
    '';
  const mobile = normalizePhoneForInvite(row.mobile || row.phone || '');
  return { name, mobile };
}

function isValidInvitePhone(value) {
  return PHONE_REGEX.test(value);
}

/** "6 questions asked by call and WhatsApp." — what the call asks: a role brief switched off is not. */
function qualifyingQuestionsSummary(screening) {
  const all = screening?.qualifyingAgentQuestions || [];
  if (!all.length) {
    return 'Using the built-in default questions.';
  }
  const briefOff = screening.qualifyingAgentRoleBrief === false;
  const asked = all.filter((q) => !(briefOff && q.kind === 'willingness')).length;
  const mandatory = all.filter((q) => q.mandatory === true).length;
  const by = { call: 'by call', whatsapp: 'on WhatsApp' }[screening.qualifyingAgentMode] || 'by call and WhatsApp';
  return `${asked} question${asked === 1 ? '' : 's'} asked ${by}${mandatory ? `, ${mandatory} mandatory` : ''}.`;
}

const ScreeningDetailPage = () => {
  const { user } = useAuth();
  const isGoColors = user?.orgSlug === 'go-colors---go-fashion-india-ltd-sdf61l';
  const isBurgerKing = user?.orgSlug === 'b-pia1dr';
  const weightsConfig = user?.scoreWeights || null;
  const isSuperAdmin = user?.email?.toLowerCase() === 'superadmin@testguru.io';
  const isSamtaGroup = String(user?.orgId) === '6a3b9faeb52a49d66cc5f63e';
  const { jobId, screeningId } = useParams();
  // Go Colors: after a candidate is moved to Selected/Rejected, open the HRBP decision-email preview.
  // Go Colors: Regional Head salary details workflow
  const armSalaryDetailsEnabled =
    user?.features?.arm_salary_details === true ||
    user?.features?.store_decision_emails === true ||
    user?.features?.store_list === true;
  const [salaryRequestsMap, setSalaryRequestsMap] = useState({});
  // Go Colors: filled salary details can be corrected from the Salary & Onboarding modal.
  const [salaryDetailsEditable, setSalaryDetailsEditable] = useState(false);
  const [selectedCandidateForSalaryModal, setSelectedCandidateForSalaryModal] = useState(null);
  const [selectedSalaryDataForModal, setSelectedSalaryDataForModal] = useState(null);
  const [isSalaryModalOpen, setIsSalaryModalOpen] = useState(false);
  // When enabled, recruiters must type a reason before rejecting. The reason is saved as a
  // candidate note (attributed to them) and the Notes column defaults to visible on Rejected.
  const rejectionReasonRequired = user?.features?.rejection_reason_required === true;
  const [decisionEmailModal, setDecisionEmailModal] = useState(null); // { candidateId, decision, candidateName } | null
  const [decisionEmailQueue, setDecisionEmailQueue] = useState([]);
  // Burger King: after a candidate is moved to Selected, open the HRBP selection-email
  // preview. Kept entirely separate from the Go Colors flow above — different feature flag,
  // different endpoint, and Selected only (BK sends nothing on reject or drop).
  const bkDecisionEmailsEnabled = user?.features?.bk_decision_emails === true;
  const [bkDecisionEmailModal, setBkDecisionEmailModal] = useState(null); // { candidateId, candidateName, onSent } | null
  const [bkDecisionEmailQueue, setBkDecisionEmailQueue] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const initialScreeningFromState = location.state?.screening || null;
  const [screening, setScreening] = useState(initialScreeningFromState);

  // EBO and LFS jobs both go through this flow; the server reads the job's store format and
  // says what to do (LFS: Selected previews the ARM salary/DOJ request, Rejected/Dropped come
  // back `noEmail` and move straight away). Don't branch on storeFormat here.
  const decisionEmailsEnabled = user?.features?.store_decision_emails === true;
  const [screeningLoading, setScreeningLoading] = useState(!initialScreeningFromState);
  const [screeningError, setScreeningError] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [insightsReevaluateLoading, setInsightsReevaluateLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('candidates');
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [showWhatsAppInviteModal, setShowWhatsAppInviteModal] = useState(false);
  const [showQualifyingAgentInviteModal, setShowQualifyingAgentInviteModal] = useState(false);
  const [showQualifyingAgentDetailsModal, setShowQualifyingAgentDetailsModal] = useState(false);
  const [whatsAppReplayCandidate, setWhatsAppReplayCandidate] = useState(null);
  const [qualifyingReplayCandidate, setQualifyingReplayCandidate] = useState(null);
  // The quick answers card the Status chip opens on a qualifying-only screening.
  const [qualifyingAnswersCandidate, setQualifyingAnswersCandidate] = useState(null);
  const [inviteStep, setInviteStep] = useState(1);
  const [inviteMode, setInviteMode] = useState(null);
  const [inviteChannels, setInviteChannels] = useState({ email: true, whatsapp: false });
  const [singleInviteRows, setSingleInviteRows] = useState([
    { id: 1, firstName: '', lastName: '', email: '', mobile: '+91', storeId: '' },
  ]);
  const [singleInviteSubmitting, setSingleInviteSubmitting] = useState(false);
  const [linkToggleUpdating, setLinkToggleUpdating] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkDragOver, setBulkDragOver] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [journeyAutomationEnabled, setJourneyAutomationEnabled] = useState(false);
  const [journeyAutomationResumeMinScore, setJourneyAutomationResumeMinScore] = useState(60);
  const [journeyAutomationAssessmentMinScore, setJourneyAutomationAssessmentMinScore] = useState(60);
  const [moveToOnboarding, setMoveToOnboarding] = useState(false);

  useEffect(() => {
    if (screening) {
      setJourneyAutomationEnabled(!!screening.journeyAutomation?.enabled);
      setJourneyAutomationResumeMinScore(screening.journeyAutomation?.resumeMinScore ?? 60);
      setJourneyAutomationAssessmentMinScore(screening.journeyAutomation?.assessmentMinScore ?? 60);
    }
  }, [screening]);

  const sendChannelInvitesForChannel = useCallback(
    async (channel, rows, template, followUp) => {
      if (!screeningId || !rows?.length) return null;
      const response = await axios.post(`/api/screenings/${screeningId}/channel-invite`, {
        channel,
        candidates: rows,
        template,
        followUp,
      });
      return response.data?.summary || null;
    },
    [screeningId]
  );

  const maybeSendChannelInvites = useCallback(
    async (rows, channels) => {
      if (!channels?.whatsapp) return;
      const recipients = rows
        .map((row) => buildChannelRecipient(row))
        .filter((recipient) => isValidInvitePhone(recipient.mobile));
      if (recipients.length === 0) {
        toast.error('Provide a valid mobile number to send WhatsApp invites');
        return;
      }

      for (const channel of ['whatsapp']) {
        if (!channels[channel]) continue;
        const channelLabel = 'WhatsApp';
        try {
          const summary = await sendChannelInvitesForChannel(channel, recipients);
          if (summary?.sent) {
            toast.success(
              `${summary.sent} ${channelLabel} invite${summary.sent === 1 ? '' : 's'} queued`
            );
          }
          if (summary?.failed) {
            toast.error(
              `${summary.failed} ${channelLabel} invite${summary.failed === 1 ? '' : 's'} failed`
            );
          }
          if (!summary?.sent && !summary?.failed) {
            toast.error(`No ${channelLabel} invites were sent`);
          }
        } catch (error) {
          toast.error(error.response?.data?.message || `Failed to send ${channelLabel} invites`);
        }
      }
    },
    [sendChannelInvitesForChannel]
  );
  const [selectedCandidateForResumeBreakdown, setSelectedCandidateForResumeBreakdown] =
    useState(null);
  const [candidateInsightDetailsById, setCandidateInsightDetailsById] = useState({});
  const [candidateInsightDetailsLoading, setCandidateInsightDetailsLoading] = useState(false);
  const [candidateInsightModalInitialTab, setCandidateInsightModalInitialTab] = useState('score');
  const [candidateInsightModalCandidateDetailsOnly, setCandidateInsightModalCandidateDetailsOnly] =
    useState(false);
  const [showCreateAssessmentModal, setShowCreateAssessmentModal] = useState(false);
  const [showCreateInterviewModal, setShowCreateInterviewModal] = useState(false);
  const [openInterviewModalAfterAssessmentClose, setOpenInterviewModalAfterAssessmentClose] =
    useState(false);
  const [showInterviewDetailsModal, setShowInterviewDetailsModal] = useState(false);
  const [interviewDetails, setInterviewDetails] = useState(null);
  const [interviewDetailsLoading, setInterviewDetailsLoading] = useState(false);
  const [interviewLinkCopied, setInterviewLinkCopied] = useState(false);
  const [showAssessmentScoreModal, setShowAssessmentScoreModal] = useState(false);
  const [selectedAssessmentCandidate, setSelectedAssessmentCandidate] = useState(null);
  const [assessmentCandidateDetails, setAssessmentCandidateDetails] = useState(null);
  const [assessmentProctoringDetails, setAssessmentProctoringDetails] = useState(null);
  const [assessmentModalActiveTab, setAssessmentModalActiveTab] = useState('score'); // 'score' | 'proctoring'
  const [assessmentModalInitialModuleIdx, setAssessmentModalInitialModuleIdx] = useState(0);
  const [showInterviewReplayModal, setShowInterviewReplayModal] = useState(false);
  const [selectedInterviewSession, setSelectedInterviewSession] = useState(null);
  const [interviewSessionLoading, setInterviewSessionLoading] = useState(false);
  const [interviewSessionLoadError, setInterviewSessionLoadError] = useState('');
  const [interviewSessionMeta, setInterviewSessionMeta] = useState({
    candidateName: '',
    interviewTitle: '',
  });
  const [interviewModalActiveTab, setInterviewModalActiveTab] = useState('analysis');
  const [interviewInnerTab, setInterviewInnerTab] = useState('questions');
  const [interviewRecordingUrl, setInterviewRecordingUrl] = useState(null);
  const [interviewRecordingLoading, setInterviewRecordingLoading] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [analysisExpanded, setAnalysisExpanded] = useState(false);
  const [interviewProctoringData, setInterviewProctoringData] = useState(null);
  const [interviewProctoringLoading, setInterviewProctoringLoading] = useState(false);
  const [interviewAiReprocessLoading, setInterviewAiReprocessLoading] = useState(false);
  const [bulkScreeningAiReprocessLoading, setBulkScreeningAiReprocessLoading] = useState(false);
  const [showHiddenAiReprocessButtons, setShowHiddenAiReprocessButtons] = useState(false);
  const [showScoringCompareMode, setShowScoringCompareMode] = useState(false);
  const [scoringCompareTab, setScoringCompareTab] = useState('new');
  const [dualScoringData, setDualScoringData] = useState(null);
  const [dualScoringLoading, setDualScoringLoading] = useState(false);
  const [interviewTitleTapCount, setInterviewTitleTapCount] = useState(0);
  // Transcript translation UI state — Original ↔ English toggle for
  // non-English interviews. See InterviewSessionModal.js for the
  // canonical implementation; kept in sync with this inline modal.
  const [interviewDisplayLang, setInterviewDisplayLang] = useState('hi');
  const [interviewTranslating, setInterviewTranslating] = useState(false);
  const [interviewLocalTranscriptEnglish, setInterviewLocalTranscriptEnglish] = useState(null);
  const [interviewLocalTranscriptEnglishStatus, setInterviewLocalTranscriptEnglishStatus] = useState(null);
  const [notificationReadState, setNotificationReadState] = useState(null); // { lastReadAt }
  const [shareReviewActivities, setShareReviewActivities] = useState([]);
  const [expandedProctoringCategories, setExpandedProctoringCategories] = useState({});
  const [expandedProctoringGroups, setExpandedProctoringGroups] = useState({
    environment: true,
    audio: true,
    video: true,
  });
  const [selectedProctoringCategory, setSelectedProctoringCategory] = useState(null);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [autoOpenDone, setAutoOpenDone] = useState(false);
  const [showAssessmentDetailsModal, setShowAssessmentDetailsModal] = useState(false);
  const [assessmentDetails, setAssessmentDetails] = useState(null);
  const [assessmentDetailsLoading, setAssessmentDetailsLoading] = useState(false);
  const [assessmentSharingLink, setAssessmentSharingLink] = useState('');
  const [assessmentLinkCopied, setAssessmentLinkCopied] = useState(false);
  const [maxCandidatesInput, setMaxCandidatesInput] = useState('');
  const [hasSyncedMaxCandidates, setHasSyncedMaxCandidates] = useState(false);
  const [expandedAssessmentTests, setExpandedAssessmentTests] = useState(new Set());
  const [expandedAssessmentLibraryQuestions, setExpandedAssessmentLibraryQuestions] = useState(new Set());
  const [expandedAssessmentCustomQuestions, setExpandedAssessmentCustomQuestions] = useState(new Set());
  const [expandedInterviewQuestions, setExpandedInterviewQuestions] = useState(new Set());

  const [showResumeScoringDetailsModal, setShowResumeScoringDetailsModal] = useState(false);
  const [showDocumentCollectionDetailsModal, setShowDocumentCollectionDetailsModal] = useState(false);
  const [documentCollectionConfigUpdating, setDocumentCollectionConfigUpdating] = useState(false);

  const [openScoreFilterId, setOpenScoreFilterId] = useState(null);
  const [openMetaFilterId, setOpenMetaFilterId] = useState(null);
  const [insightsModalCard, setInsightsModalCard] = useState(null);
  const [insightsModalQuestionsSubTab, setInsightsModalQuestionsSubTab] = useState('assessment');
  const [insightsModalAssessmentQuestions, setInsightsModalAssessmentQuestions] = useState([]);
  const [insightsModalInterviewQuestions, setInsightsModalInterviewQuestions] = useState([]);
  const [insightsModalQuestionsLoading, setInsightsModalQuestionsLoading] = useState({
    assessment: false,
    interview: false,
  });
  const [insightsQuestionsCount, setInsightsQuestionsCount] = useState(null);
  const scoreBreakdownRef = useRef(null);
  const interviewVideoRef = useRef(null);
  const interviewAnalysisSession = showScoringCompareMode
    ? scoringCompareTab === 'old'
      ? dualScoringData?.old || selectedInterviewSession
      : dualScoringData?.new || selectedInterviewSession
    : selectedInterviewSession;

  const handleUpdateDocumentCollectionConfig = async (newDocs) => {
    if (!screeningId) return;
    try {
      setDocumentCollectionConfigUpdating(true);
      const res = await axios.put(`/api/screenings/${screeningId}`, {
        requestedDocuments: newDocs,
      });
      if (res.data?.screening) {
        setScreening(prev => ({ ...prev, ...res.data.screening }));
      }
      toast.success('Saved', { style: { fontSize: '13px', padding: '6px 12px' } });
    } catch (e) {
      toast.error('Failed to save', { style: { fontSize: '13px', padding: '6px 12px' } });
    } finally {
      setDocumentCollectionConfigUpdating(false);
    }
  };

  useEffect(() => {
    if (showDetailedAnalysis && scoreBreakdownRef.current) {
      scoreBreakdownRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [showDetailedAnalysis]);
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && String(e.key).toLowerCase() === 's') {
        e.preventDefault();
        setShowHiddenAiReprocessButtons((prev) => !prev);
      }
      if (e.ctrlKey && e.shiftKey && (e.code === 'KeyY' || String(e.key).toLowerCase() === 'y')) {
        e.preventDefault();
        e.stopPropagation();
        setShowScoringCompareMode((prev) => {
          const next = !prev;
          if (!next) {
            setDualScoringData(null);
            setScoringCompareTab('new');
          }
          toast(next ? 'Scoring compare mode enabled' : 'Scoring compare mode disabled');
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  useEffect(() => {
    if (interviewTitleTapCount < 5) return;
    setInterviewTitleTapCount(0);
    setShowScoringCompareMode((prev) => {
      const next = !prev;
      if (!next) {
        setDualScoringData(null);
        setScoringCompareTab('new');
      }
      toast(next ? 'Scoring compare mode enabled' : 'Scoring compare mode disabled');
      return next;
    });
  }, [interviewTitleTapCount]);
  // Shortlist Next Steps (mirrors Job Results)
  const [selectedShortlistCandidateIds, setSelectedShortlistCandidateIds] = useState([]);
  const [showShortlistNextStepsModal, setShowShortlistNextStepsModal] = useState(false);
  const [shortlistNextStepsChoice, setShortlistNextStepsChoice] = useState(null);
  const [shortlistNextStepsStage, setShortlistNextStepsStage] = useState('choice');
  const [showScheduleInterviewModal, setShowScheduleInterviewModal] = useState(false);
  const [pendingSchedulingPayload, setPendingSchedulingPayload] = useState(null);
  const [showScheduleEmailModal, setShowScheduleEmailModal] = useState(false);
  const [showCollectDocumentsModal, setShowCollectDocumentsModal] = useState(false);
  const [showDpaRequiredModal, setShowDpaRequiredModal] = useState(false);
  const [viewDocumentsCandidate, setViewDocumentsCandidate] = useState(null);
  const [documentStatusMap, setDocumentStatusMap] = useState(null);
  const [documentStatusRefreshKey, setDocumentStatusRefreshKey] = useState(0);
  const [reportFormatChoice, setReportFormatChoice] = useState(null);
  const [moveDestinationScreenings, setMoveDestinationScreenings] = useState([]);
  const [moveDestinationLoading, setMoveDestinationLoading] = useState(false);
  const [moveDestinationScreeningId, setMoveDestinationScreeningId] = useState('');
  const [movingShortlistCandidates, setMovingShortlistCandidates] = useState(false);
  const [emailModalScreeningId, setEmailModalScreeningId] = useState(null);
  const [emailModalContext, setEmailModalContext] = useState(undefined); // e.g. 'invite'
  const [stores, setStores] = useState([]);

  useEffect(() => {
    if (user?.features?.store_list === true) {
      axios.get('/api/stores')
        .then((res) => {
          if (res.data?.success && Array.isArray(res.data?.data)) {
            setStores(res.data.data);
          }
        })
        .catch((err) => {
          console.error('Failed to load stores for invite dropdown:', err);
        });
    }
  }, [user?.features?.store_list]);

  const fetchSalaryRequestsBulk = useCallback(async () => {
    if (!screeningId || !armSalaryDetailsEnabled) return;
    try {
      const res = await axios.get(`/api/arm-salary/${screeningId}/bulk-status`);
      if (res.data?.success && Array.isArray(res.data?.data)) {
        const map = {};
        res.data.data.forEach((item) => {
          if (item.candidateId) {
            map[String(item.candidateId)] = item;
          }
        });
        setSalaryRequestsMap(map);
        setSalaryDetailsEditable(res.data.editable === true);
      }
    } catch (err) {
      console.error('Failed to fetch salary requests bulk:', err);
    }
  }, [screeningId, armSalaryDetailsEnabled]);

  useEffect(() => {
    fetchSalaryRequestsBulk();
  }, [fetchSalaryRequestsBulk]);
  // The ARM fills DOJ / salary outside the app, so re-read on opening Selected or Onboarded:
  // a candidate filled since page load then moves to Onboarded without a reload.
  useEffect(() => {
    if (activeTab === 'shortlist' || activeTab === 'onboarded') fetchSalaryRequestsBulk();
  }, [activeTab, fetchSalaryRequestsBulk]);
  const [showSendEmailToCandidateModal, setShowSendEmailToCandidateModal] = useState(false);
  const [showSendEmailToHiringManagerModal, setShowSendEmailToHiringManagerModal] = useState(false);
  // The hiring-manager review link can now be raised from either the Candidates tab or the
  // Selected tab, so which candidates it covers - and where "back" goes - has to be recorded
  // when the modal opens rather than re-derived from the shortlist selection.
  //
  // Only the IDS are held. The candidate objects are memoised off the live `candidates` list
  // below, because ShareCandidateReviewModal renders the hiring manager's decisions from these
  // objects and its Refresh button works by re-fetching candidates - hand it a frozen snapshot
  // and Refresh fetches but never updates the panel. The memo also gives the array a stable
  // identity between fetches, so the modal's effect mints the link once per open instead of on
  // every parent render.
  const [hiringManagerShareCandidateIds, setHiringManagerShareCandidateIds] = useState([]);
  const [hiringManagerShareOrigin, setHiringManagerShareOrigin] = useState('shortlist');
  const [pendingMoveToScreening, setPendingMoveToScreening] = useState(null); // { destinationId, sourceScreeningId, candidateIds }
  const skipAbandonMoveConfirmOnCloseRef = useRef(false);
  const [showAbandonMoveConfirmModal, setShowAbandonMoveConfirmModal] = useState(false);
  const [showMissingEmailForMoveModal, setShowMissingEmailForMoveModal] = useState(false);
  const [missingEmailCandidatesForMove, setMissingEmailCandidatesForMove] = useState([]);
  const [showSkippedNoEmailModal, setShowSkippedNoEmailModal] = useState(false);
  const [skippedNoEmailItems, setSkippedNoEmailItems] = useState([]);
  const [eligibleMoveCandidateIds, setEligibleMoveCandidateIds] = useState([]);
  const [bulkUploadEmailCandidates, setBulkUploadEmailCandidates] = useState([]);
  const [pendingChannelInvites, setPendingChannelInvites] = useState(null);
  const [showChooseEmailModal, setShowChooseEmailModal] = useState(false);
  const [emailDrafts, setEmailDrafts] = useState([]);
  const [emailDraftsLoading, setEmailDraftsLoading] = useState(false);
  const [emailDraftForSend, setEmailDraftForSend] = useState(null);
  const [showViewDetailsModal, setShowViewDetailsModal] = useState(false);
  // Bulk shortlist selection from Candidates tab
  const [selectedCandidatesForShortlist, setSelectedCandidatesForShortlist] = useState([]);
  const [selectedRejectedCandidateIds, setSelectedRejectedCandidateIds] = useState([]);
  const [selectedDroppedCandidateIds, setSelectedDroppedCandidateIds] = useState([]);
  const [shortlistDecisions, setShortlistDecisions] = useState({}); // { [candidateId]: shortlistDecision value | null }
  const [shortlistNotes, setShortlistNotes] = useState({}); // { [candidateId]: string }
  const [bulkShortlisting, setBulkShortlisting] = useState(false);
  const [removingFromShortlist, setRemovingFromShortlist] = useState(false);
  const [showShortlistConfirmModal, setShowShortlistConfirmModal] = useState(false);
  const [candidateToShortlist, setCandidateToShortlist] = useState(null); // single candidate for row-action shortlist
  const [bulkRejecting, setBulkRejecting] = useState(false);
  const [restoringFromRejected, setRestoringFromRejected] = useState(false);
  const [showRejectConfirmModal, setShowRejectConfirmModal] = useState(false);
  const [candidateToReject, setCandidateToReject] = useState(null); // single candidate for row-action reject
  const [rejectReason, setRejectReason] = useState(''); // shared reason typed in the reject modal
  const [rejectReasonError, setRejectReasonError] = useState('');
  const [bkRejectDropdownReason, setBkRejectDropdownReason] = useState('');
  const [bkRejectRemarks, setBkRejectRemarks] = useState('');
  const [rejectedNameQuery, setRejectedNameQuery] = useState('');
  const [resendingInvite, setResendingInvite] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [deleteCandidateLoading, setDeleteCandidateLoading] = useState(false);
  const [showBulkDeleteConfirmModal, setShowBulkDeleteConfirmModal] = useState(false);
  const [bulkDeletingCandidates, setBulkDeletingCandidates] = useState(false);
  const [downloadingAllResumes, setDownloadingAllResumes] = useState(false);
  const [showResendConfirmModal, setShowResendConfirmModal] = useState(false);
  const [candidateToResend, setCandidateToResend] = useState(null); // single candidate for row-action resend
  const [showCallConfirmModal, setShowCallConfirmModal] = useState(false);
  const [candidateToCall, setCandidateToCall] = useState(null);
  const [showMoveJobPostModal, setShowMoveJobPostModal] = useState(false);
  const [candidateToMoveJobPost, setCandidateToMoveJobPost] = useState(null);
  const [callingCandidate, setCallingCandidate] = useState(false);
  const [candidateToRecall, setCandidateToRecall] = useState(null);
  const [recallingCandidate, setRecallingCandidate] = useState(false);
  const [resendSections, setResendSections] = useState([]); // which sections to re-send

  const [openActionsCandidate, setOpenActionsCandidate] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [openBulkActionsMenu, setOpenBulkActionsMenu] = useState(false);
  const [bulkActionsMenuPosition, setBulkActionsMenuPosition] = useState(null);
  const [shortlistNameQuery, setShortlistNameQuery] = useState('');
  const [droppedNameQuery, setDroppedNameQuery] = useState('');
  const [shortlistMonth, setShortlistMonth] = useState(() => loadMonth(screeningId, 'shortlist'));
  const [rejectedMonth, setRejectedMonth] = useState(() => loadMonth(screeningId, 'rejected'));
  useEffect(() => {
    setShortlistMonth(loadMonth(screeningId, 'shortlist'));
    setRejectedMonth(loadMonth(screeningId, 'rejected'));
  }, [screeningId]);
  // Clearing the selection stops bulk actions hitting rows the new month hides.
  const changeShortlistMonth = (v) => {
    setShortlistMonth(v);
    saveMonth(screeningId, 'shortlist', v);
    setSelectedShortlistCandidateIds([]);
  };
  const changeRejectedMonth = (v) => {
    setRejectedMonth(v);
    saveMonth(screeningId, 'rejected', v);
    setSelectedRejectedCandidateIds([]);
  };
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showAutomationsModal, setShowAutomationsModal] = useState(false);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false);
  const [tableSortColumn, setTableSortColumn] = useState('overallScore');
  const [tableSortDirection, setTableSortDirection] = useState('desc');
  const [columnsModalContext, setColumnsModalContext] = useState('candidates');

  // ── Persist column visibility & order to localStorage per screening ──
  const colStorageKey = screeningId ? `tc:cols:${screeningId}` : null;

  const loadColPrefs = (tab) => {
    let orgDef = { visibility: {}, order: [] };
    if (tab === 'candidates' && user?.ui) {
      orgDef = {
        visibility: user.ui.defaultCandidateColumnVisibility || {},
        order: user.ui.defaultCandidateColumnOrder || [],
      };
    } else if (tab === 'shortlist') {
      orgDef = {
        visibility: user?.ui?.defaultShortlistColumnVisibility || {},
        order: user?.ui?.defaultShortlistColumnOrder?.length > 0
          ? user.ui.defaultShortlistColumnOrder
          : buildShortlistDefaultOrder(user?.ui),
      };
    } else if (tab === 'rejected') {
      orgDef = {
        visibility: user?.ui?.defaultRejectedColumnVisibility || {},
        order: user?.ui?.defaultRejectedColumnOrder?.length > 0
          ? user.ui.defaultRejectedColumnOrder
          : buildRejectedDefaultOrder(user?.ui),
      };
    }

    if (!colStorageKey) {
      return orgDef;
    }

    try {
      const raw = localStorage.getItem(`${colStorageKey}:${tab}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          visibility: { ...orgDef.visibility, ...(parsed.visibility || {}) },
          order: Array.isArray(parsed.order) && parsed.order.length > 0 ? parsed.order : orgDef.order,
        };
      }
    } catch { /* ignore */ }

    return orgDef;
  };

  const [candidateColumnVisibility, setCandidateColumnVisibility] = useState(() => loadColPrefs('candidates').visibility);
  const [candidateColumnOrder, setCandidateColumnOrder] = useState(() => loadColPrefs('candidates').order);
  const [shortlistColumnVisibility, setShortlistColumnVisibility] = useState(() => loadColPrefs('shortlist').visibility);
  const [shortlistColumnOrder, setShortlistColumnOrder] = useState(() => loadColPrefs('shortlist').order);
  const [rejectedColumnVisibility, setRejectedColumnVisibility] = useState(() => loadColPrefs('rejected').visibility);
  const [rejectedColumnOrder, setRejectedColumnOrder] = useState(() => loadColPrefs('rejected').order);

  // ── Server-persisted column layouts (shared per-screening + personal per-user) ──
  // Shape: { shared: { [tab]: {order, visibility, updatedAt} }, personal: { [tab]: {...} } }
  // Resolution (below): between a shared and a personal layout, the most-recently-edited
  // one wins (ties → shared); then org default → platform default.
  const [serverColLayouts, setServerColLayouts] = useState({ shared: {}, personal: {} });
  const [scopeModalOpen, setScopeModalOpen] = useState(false);
  const [pendingColumnSave, setPendingColumnSave] = useState(null); // { tab, order, visibility }
  const [savingColumnScope, setSavingColumnScope] = useState(false);

  // Fetch the current user's personal + this screening's shared layouts once known.
  useEffect(() => {
    if (!screeningId) return undefined;
    let cancelled = false;
    axios
      .get(`/api/screenings/${screeningId}/column-layout`)
      .then((res) => {
        if (cancelled) return;
        setServerColLayouts({
          shared: res.data?.shared || {},
          personal: res.data?.personal || {},
        });
      })
      .catch(() => {
        /* keep org/platform defaults on failure */
      });
    return () => {
      cancelled = true;
    };
  }, [screeningId]);

  // Org/platform default layout for a tab (no server override present).
  const orgDefaultForTab = useCallback(
    (tab) => {
      if (tab === 'shortlist') {
        return {
          visibility: user?.ui?.defaultShortlistColumnVisibility || {},
          order:
            user?.ui?.defaultShortlistColumnOrder?.length > 0
              ? user.ui.defaultShortlistColumnOrder
              : buildShortlistDefaultOrder(user?.ui),
          sortColumn: user?.ui?.defaultShortlistSortColumn || 'overallScore',
          sortDirection: user?.ui?.defaultShortlistSortDirection || 'desc',
        };
      }
      if (tab === 'rejected') {
        return {
          visibility: user?.ui?.defaultRejectedColumnVisibility || {},
          order:
            user?.ui?.defaultRejectedColumnOrder?.length > 0
              ? user.ui.defaultRejectedColumnOrder
              : buildRejectedDefaultOrder(user?.ui),
          sortColumn: user?.ui?.defaultRejectedSortColumn || 'overallScore',
          sortDirection: user?.ui?.defaultRejectedSortDirection || 'desc',
        };
      }
      return {
        visibility: user?.ui?.defaultCandidateColumnVisibility || {},
        order: user?.ui?.defaultCandidateColumnOrder || [],
        sortColumn: user?.ui?.defaultCandidateSortColumn || 'overallScore',
        sortDirection: user?.ui?.defaultCandidateSortDirection || 'desc',
      };
    },
    [user]
  );

  // Resolve the effective layout for a tab. Between a shared and a personal layout the
  // most-recently-edited one wins (ties → shared, so a freshly-set "for everyone" layout
  // overrides older personal ones); otherwise fall back to org / platform default.
  const resolveTabLayout = useCallback(
    (tab) => {
      const shared = serverColLayouts.shared?.[tab];
      const personal = serverColLayouts.personal?.[tab];
      const sharedValid = shared && Array.isArray(shared.order) && shared.order.length > 0;
      const personalValid = personal && Array.isArray(personal.order) && personal.order.length > 0;

      let picked = null;
      if (sharedValid && personalValid) {
        const sharedAt = new Date(shared.updatedAt || 0).getTime();
        const personalAt = new Date(personal.updatedAt || 0).getTime();
        picked = personalAt > sharedAt ? personal : shared;
      } else if (sharedValid) {
        picked = shared;
      } else if (personalValid) {
        picked = personal;
      }

      const orgDef = orgDefaultForTab(tab);

      if (picked) {
        return {
          visibility: { ...orgDef.visibility, ...(picked.visibility || {}) },
          order: (picked.order && picked.order.length > 0) ? picked.order : orgDef.order,
          sortColumn: picked.sortColumn,
          sortDirection: picked.sortDirection
        };
      }
      return orgDef;
    },
    [serverColLayouts, orgDefaultForTab]
  );

  // Keep the per-tab table state in sync with the resolved layouts. Runs on initial
  // server load, after every save/reset (which update serverColLayouts), and on activeTab change.
  useEffect(() => {
    const c = resolveTabLayout('candidates');
    setCandidateColumnVisibility(c.visibility);
    setCandidateColumnOrder(c.order);

    const s = resolveTabLayout('shortlist');
    setShortlistColumnVisibility(s.visibility);
    setShortlistColumnOrder(s.order);

    const r = resolveTabLayout('rejected');
    setRejectedColumnVisibility(r.visibility);
    setRejectedColumnOrder(r.order);

    // Sync the sort column and direction to the layout of the currently active tab
    const activeLayoutTab =
      activeTab === 'insights' ? 'candidates' : activeTab === 'onboarded' ? 'shortlist' : activeTab;
    const currentLayout = resolveTabLayout(activeLayoutTab);
    if (currentLayout.sortColumn && currentLayout.sortDirection) {
      setTableSortColumn(currentLayout.sortColumn);
      setTableSortDirection(currentLayout.sortDirection);
    }
  }, [resolveTabLayout, activeTab]);

  // Lock body scroll while any modal in this page is open. Prevents background
  // page from scrolling when the user scrolls inside a modal.
  useBodyScrollLock(
    isInviteOpen ||
    isFiltersModalOpen ||
    showCreateAssessmentModal ||
    showCreateInterviewModal ||
    showShortlistNextStepsModal ||
    showBulkDeleteConfirmModal ||
    Boolean(candidateToDelete) ||
    Boolean(candidateToRecall) ||
    showShortlistConfirmModal ||
    showRejectConfirmModal ||
    showResendConfirmModal ||
    showAbandonMoveConfirmModal ||
    showMissingEmailForMoveModal ||
    showInterviewDetailsModal ||
    showAssessmentScoreModal ||
    showInterviewReplayModal ||
    showAssessmentDetailsModal ||
    showResumeScoringDetailsModal ||
    showScheduleInterviewModal ||
    showScheduleEmailModal ||
    showCollectDocumentsModal ||
    showDpaRequiredModal ||
    showSendEmailToCandidateModal ||
    showSendEmailToHiringManagerModal ||
    showChooseEmailModal ||
    showViewDetailsModal ||
    showNotificationsModal ||
    isColumnsModalOpen,
  );

  // Candidate search (screening-scoped; results mapped onto this screening's loaded rows)
  const aiSearch = useAISearch(screeningId);
  const {
    query: aiSearchQuery,
    setQuery: setAiSearchQuery,
    results: aiSearchResults,
    isActive: isAISearchActive,
    isSearching: isAISearching,
    handleSearch: handleAISearch,
    clear: clearAISearch,
  } = aiSearch;
  const fetchCandidates = useCallback(async ({ forceRefresh = false, quiet = false } = {}) => {
    if (!screeningId) return;
    try {
      if (!quiet) setCandidatesLoading(true);
      const url = forceRefresh
        ? `/api/screenings/${screeningId}/candidates/refresh?lite=1`
        : `/api/screenings/${screeningId}/candidates?lite=1`;

      // Retry once on server errors (500+) to handle cold-start / transient timeouts
      let candidatesRes;
      try {
        candidatesRes = await axios.get(url);
      } catch (firstErr) {
        if (firstErr?.response?.status >= 500) {
          await new Promise((r) => setTimeout(r, 1500));
          candidatesRes = await axios.get(url);
        } else {
          throw firstErr;
        }
      }

      const enhancedCandidates = candidatesRes.data?.candidates || [];
      setCandidates(enhancedCandidates);
      setShortlistDecisions(
        Object.fromEntries(
          enhancedCandidates
            .map((c) => [String(c.id || c._id), c.shortlistDecision || null])
            .filter(([, decision]) => !!decision)
        )
      );
      setShortlistNotes(
        Object.fromEntries(
          enhancedCandidates
            .map((c) => [String(c.id || c._id), c.shortlistNote || null])
            .filter(([, note]) => !!note)
        )
      );
      return enhancedCandidates;
    } catch (e) {
      return candidates;
    } finally {
      if (!quiet) setCandidatesLoading(false);
    }
  }, [screeningId, screening]);

  // The Qualifying Agent columns are re-read on demand only (Refresh button, or closing a
  // session's card/modal), never on a timer, so the table does not keep Railway and Mongo busy.
  const { refresh: refreshQualifyingSummary, refreshing: qualifyingSummaryRefreshing } =
    useQualifyingSummaryRefresh({
      screeningId,
      enabled: screening?.includeQualifyingAgent === true,
      candidates,
      setCandidates,
      refetchCandidates: fetchCandidates,
    });

  const INTERVIEW_INVITE_SUBJECT = 'Interview Invitation — {{jobTitle}} at {{Company}}';
  const SHORTLIST_EMAIL_SUBJECT =
    'Congratulations — You are shortlisted for {{jobTitle}} at {{company name}}';
  const SHORTLIST_EMAIL_TEMPLATES = {
    Default: `<p>Dear {{First Name}},</p>
<p>Thank you for your interest in the {{jobTitle}} position at {{company name}}.</p>
<p>We are pleased to let you know that your profile has been <strong>shortlisted</strong> for the next stage of our hiring process.</p>
<p>Our team will share the next steps with you shortly. If you have any questions, feel free to reply to this email.</p>
<p>Best regards,<br/>TalentCrux</p>`,
  };

  const VIRTUAL_INTERVIEW_EMAIL_TEMPLATES = {
    'Interview Invite': `<p>Dear {{First Name}},</p>
<p>We are pleased to inform you that you have been shortlisted for the <strong>{{jobTitle}}</strong> position at <strong>{{Company}}</strong>.</p>
<p>We would like to invite you to a virtual interview. Please use the link below to select a time slot that works best for you:</p>
<p style="text-align:center;margin:20px 0;">
  <a href="{{bookingLink}}" style="display:inline-block;background:#007BFF;color:#fff;font-size:15px;font-weight:600;padding:12px 32px;border-radius:8px;text-decoration:none;">Choose Your Interview Slot</a>
</p>
<p><strong>Interview Details:</strong></p>
<ul>
  <li><strong>Mode:</strong> Virtual (video call link will be shared upon confirmation)</li>
  <li><strong>Duration:</strong> Approximately {{slotDuration}} minutes</li>
</ul>
<p>Please ensure you have a stable internet connection and a quiet environment for the interview. We recommend joining 5 minutes early to test your setup.</p>
<p>If you have any questions or need to reschedule, please reply to this email.</p>
<p>Best regards,<br/>{{Company}}</p>`,

    'Brief Invite': `<p>Hi {{First Name}},</p>
<p>Great news — you've been shortlisted for the <strong>{{jobTitle}}</strong> role at <strong>{{Company}}</strong>!</p>
<p>Please pick a convenient time for your virtual interview:</p>
<p style="text-align:center;margin:20px 0;">
  <a href="{{bookingLink}}" style="display:inline-block;background:#007BFF;color:#fff;font-size:15px;font-weight:600;padding:12px 32px;border-radius:8px;text-decoration:none;">Book Interview Slot</a>
</p>
<p>A video call link will be shared once your slot is confirmed.</p>
<p>Best regards,<br/>{{Company}}</p>`,
  };

  const IN_PERSON_INTERVIEW_EMAIL_TEMPLATES = {
    'Interview Invite': `<p>Dear {{First Name}},</p>
<p>We are pleased to inform you that you have been shortlisted for the <strong>{{jobTitle}}</strong> position at <strong>{{Company}}</strong>.</p>
<p>We would like to invite you to an in-person interview at our office. Please use the link below to select a time slot that works best for you:</p>
<p style="text-align:center;margin:20px 0;">
  <a href="{{bookingLink}}" style="display:inline-block;background:#007BFF;color:#fff;font-size:15px;font-weight:600;padding:12px 32px;border-radius:8px;text-decoration:none;">Choose Your Interview Slot</a>
</p>
<p><strong>Interview Details:</strong></p>
<ul>
  <li><strong>Mode:</strong> In-person</li>
  <li><strong>Location:</strong> {{interviewAddress}}</li>
  <li><strong>Duration:</strong> Approximately {{slotDuration}} minutes</li>
</ul>
<p><strong>Please remember to:</strong></p>
<ul>
  <li>Carry a valid photo ID and an updated copy of your resume</li>
  <li>Arrive 10–15 minutes before your scheduled time</li>
  <li>Ask for the HR team at the reception desk</li>
</ul>
<p>If you have any questions or need to reschedule, please reply to this email.</p>
<p>Best regards,<br/>{{Company}}</p>`,

    'Brief Invite': `<p>Hi {{First Name}},</p>
<p>Great news — you've been shortlisted for the <strong>{{jobTitle}}</strong> role at <strong>{{Company}}</strong>!</p>
<p>Please pick a convenient time for your in-person interview:</p>
<p style="text-align:center;margin:20px 0;">
  <a href="{{bookingLink}}" style="display:inline-block;background:#007BFF;color:#fff;font-size:15px;font-weight:600;padding:12px 32px;border-radius:8px;text-decoration:none;">Book Interview Slot</a>
</p>
<p><strong>Interview Location:</strong> {{interviewAddress}}</p>
<p>Please bring a valid photo ID and arrive 10–15 minutes early.</p>
<p>Best regards,<br/>{{Company}}</p>`,
  };

  const handleScheduleConfigureEmail = (schedulingPayload) => {
    setPendingSchedulingPayload(schedulingPayload);
    setShowScheduleInterviewModal(false);
    setShowScheduleEmailModal(true);
  };

  const handleScheduleEmailSend = async (emailData) => {
    if (!pendingSchedulingPayload) {
      toast.error('Scheduling data missing. Please go back and set up availability again.');
      return;
    }

    const payload = {
      ...pendingSchedulingPayload,
      customEmail: {
        subject: emailData?.subject,
        bodyHtml: emailData?.html,
        cc: emailData?.cc,
        bcc: emailData?.bcc,
        includeSignature: emailData?.includeSignature,
      },
    };

    await axios.post('/api/scheduling/create', payload);
  };
  /** Bypass Redis and rebuild the candidate list so Insights charts (scores, gender, education, tenure, parameters) match the DB. */
  const refreshScreeningInsights = useCallback(async () => {
    if (!screeningId) return;
    setInsightsReevaluateLoading(true);
    try {
      const res = await axios.get(`/api/screenings/${screeningId}/candidates/refresh?lite=1`);
      setCandidates(res.data?.candidates || []);
      toast.success('Insights refreshed from the latest candidate data');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to refresh insights');
    } finally {
      setInsightsReevaluateLoading(false);
    }
  }, [screeningId]);
  // Load destination screenings for the "move to another screening" flow.
  useEffect(() => {
    if (!showShortlistNextStepsModal) return;
    if (shortlistNextStepsStage !== 'move_destination') return;
    if (!jobId) return;
    let cancelled = false;
    setMoveDestinationLoading(true);
    setMoveDestinationScreenings([]);
    axios
      .get(`/api/screenings/job/${jobId}/summary`)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.screenings || [];
        const filtered = list.filter((s) => String(s?._id) !== String(screeningId));
        setMoveDestinationScreenings(filtered);
        if (filtered.length > 0) {
          setMoveDestinationScreeningId((prev) => prev || String(filtered[0]._id));
        }
      })
      .catch(() => {
        if (cancelled) return;
        toast.error('Failed to load destination screenings');
      })
      .finally(() => {
        if (cancelled) return;
        setMoveDestinationLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showShortlistNextStepsModal, shortlistNextStepsStage, jobId, screeningId]);
  const openCandidateResumeBreakdown = useCallback(async (candidate, initialTab = 'score') => {
    if (!candidate?._id || candidate.isAllZero) return;
    setCandidateInsightModalInitialTab(initialTab === 'candidate' ? 'candidate' : 'score');
    setCandidateInsightModalCandidateDetailsOnly(initialTab === 'candidate');
    const canonicalDisplayName = resolveCandidateDisplayName(candidate) || '';
    setSelectedCandidateForResumeBreakdown(
      candidate ? { ...candidate, canonicalDisplayName } : null
    );
    if (candidateInsightDetailsById[candidate._id]) return;
    try {
      setCandidateInsightDetailsLoading(true);
      const res = await axios.get(`/api/screenings/${screeningId}/candidates/${candidate._id}`);
      const detailedCandidate = res.data?.candidate || null;
      if (detailedCandidate) {
        const detailedDisplayName = canonicalDisplayName || resolveCandidateDisplayName(detailedCandidate);
        setCandidateInsightDetailsById((prev) => ({
          ...prev,
          [candidate._id]: {
            ...detailedCandidate,
            canonicalDisplayName: detailedDisplayName,
          },
        }));
      }
    } catch (e) {
      toast.error('Failed to load candidate details');
    } finally {
      setCandidateInsightDetailsLoading(false);
    }
  }, [candidateInsightDetailsById, screeningId]);
  useEffect(() => {
    let isMounted = true;
    const hasStateScreening =
      initialScreeningFromState &&
      (String(initialScreeningFromState._id || initialScreeningFromState.id || '') ===
        String(screeningId || ''));
    const fetchScreening = async ({ background = false } = {}) => {
      try {
        if (!background) setScreeningLoading(true);
        const res = await axios.get(`/api/screenings/${screeningId}`);
        if (isMounted) {
          setScreening((prev) => {
            const next = {
              ...(prev || {}),
              ...(res.data.screening || {}),
            };
            return next;
          });
          setScreeningError('');
        }
      } catch (e) {
        if (isMounted) {
          if (e.response?.status === 404) {
            navigate(jobId ? `/dashboard/job-posts/jobs/${jobId}/screenings` : '/dashboard/job-posts', { replace: true });
            return;
          }
          setScreeningError(e.response?.data?.message || 'Failed to load screening');
          if (!background) {
            setScreening(null);
          }
        }
      } finally {
        if (isMounted && !background) setScreeningLoading(false);
      }
    };
    if (!screeningId) return;
    // Fast-path: use data passed from previous page to avoid blocking title/UI.
    if (hasStateScreening) {
      setScreeningError('');
      setScreeningLoading(false);
      // Still hydrate full screening in background to get fields like assessmentId/interviewId.
      fetchScreening({ background: true });
      return () => {
        isMounted = false;
      };
    }
    fetchScreening();
    return () => {
      isMounted = false;
    };
  }, [screeningId, navigate, jobId, initialScreeningFromState]);
  // Update job's lastAccessedAt when screening page is opened
  useEffect(() => {
    if (!jobId) return;
    axios
      .patch(`/api/job-posts/jobs/${jobId}`, {
        lastAccessedAt: new Date().toISOString(),
      })
      .catch(() => { });
  }, [jobId]);
  useEffect(() => {
    if (screeningId && !screeningLoading) fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screeningId, screeningLoading]);
  // Disabled 15s auto-polling to prevent server load (recruiter can click Refresh button when needed)
  useEffect(() => {
    if (!openActionsCandidate) return;
    const close = (e) => {
      if (
        e.target.closest('[data-candidate-actions-menu]') ||
        e.target.closest('[data-candidate-actions-trigger]')
      )
        return;
      setOpenActionsCandidate(null);
      setActionMenuPosition(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openActionsCandidate]);
  useEffect(() => {
    if (!openBulkActionsMenu) return;
    const close = (e) => {
      if (
        e.target.closest('[data-bulk-actions-menu]') ||
        e.target.closest('[data-bulk-actions-trigger]')
      )
        return;
      setOpenBulkActionsMenu(false);
      setBulkActionsMenuPosition(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openBulkActionsMenu]);
  useEffect(() => {
    if (selectedCandidatesForShortlist.length === 0) {
      setOpenBulkActionsMenu(false);
      setBulkActionsMenuPosition(null);
    }
  }, [selectedCandidatesForShortlist.length]);
  useEffect(() => {
    if (!showChooseEmailModal || !screeningId) return;
    let cancelled = false;
    setEmailDraftsLoading(true);
    axios
      .get(`/api/screenings/${screeningId}/email-drafts`)
      .then((res) => {
        const list = res.data.drafts || [];
        if (!cancelled) setEmailDrafts(list.filter((d) => d.context !== 'invite'));
      })
      .catch(() => {
        if (!cancelled) setEmailDrafts([]);
      })
      .finally(() => {
        if (!cancelled) setEmailDraftsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showChooseEmailModal, screeningId]);
  // Fetch assessment and AI questions when Questions card is opened
  useEffect(() => {
    if (!insightsModalCard || insightsModalCard !== 'questions' || !screening) return;
    const assessmentId = screening.assessmentId?._id || screening.assessmentId;
    const interviewId = screening.interviewId?._id || screening.interviewId;
    if (assessmentId) {
      setInsightsModalQuestionsLoading((p) => ({ ...p, assessment: true }));
      axios
        .get(`/api/assessments/${assessmentId}`)
        .then((res) => {
          const content = res.data?.assessment?.content;
          const custom = content?.customQuestions || [];
          const list = Array.isArray(custom)
            ? custom.map((q, i) => ({
              num: i + 1,
              title: q.title || q.content || 'Question',
              content: q.content || '',
            }))
            : [];
          setInsightsModalAssessmentQuestions(list);
        })
        .catch(() => setInsightsModalAssessmentQuestions([]))
        .finally(() => setInsightsModalQuestionsLoading((p) => ({ ...p, assessment: false })));
    } else {
      setInsightsModalAssessmentQuestions([]);
    }
    if (interviewId) {
      setInsightsModalQuestionsLoading((p) => ({ ...p, interview: true }));
      axios
        .get(`/api/ai-interviews/${interviewId}`)
        .then((res) => {
          const questions = res.data?.interview?.questions || [];
          const list = Array.isArray(questions)
            ? questions.map((q, i) => ({
              num: i + 1,
              prompt: typeof q === 'string' ? q : q.prompt || '',
              type: typeof q === 'object' ? q.type : undefined,
            }))
            : [];
          setInsightsModalInterviewQuestions(list);
        })
        .catch(() => setInsightsModalInterviewQuestions([]))
        .finally(() => setInsightsModalQuestionsLoading((p) => ({ ...p, interview: false })));
    } else {
      setInsightsModalInterviewQuestions([]);
    }
  }, [insightsModalCard, screening]);
  // Fetch total question counts for the Questions card when on Insights tab
  useEffect(() => {
    if (activeTab !== 'insights' || !screening) {
      return;
    }
    const assessmentId = screening.assessmentId?._id || screening.assessmentId;
    const interviewId = screening.interviewId?._id || screening.interviewId;
    if (!assessmentId && !interviewId) {
      setInsightsQuestionsCount({ assessment: 0, interview: 0 });
      return;
    }
    let cancelled = false;
    const counts = { assessment: 0, interview: 0 };
    const done = () => {
      if (!cancelled) setInsightsQuestionsCount({ ...counts });
    };
    let pending = 0;
    if (assessmentId) {
      pending++;
      axios
        .get(`/api/assessments/${assessmentId}`)
        .then((res) => {
          const custom = res.data?.assessment?.content?.customQuestions || [];
          counts.assessment = Array.isArray(custom) ? custom.length : 0;
        })
        .catch(() => { })
        .finally(() => {
          pending--;
          if (pending === 0) done();
        });
    }
    if (interviewId) {
      pending++;
      axios
        .get(`/api/ai-interviews/${interviewId}`)
        .then((res) => {
          const questions = res.data?.interview?.questions || [];
          counts.interview = Array.isArray(questions) ? questions.length : 0;
        })
        .catch(() => { })
        .finally(() => {
          pending--;
          if (pending === 0) done();
        });
    }
    if (pending === 0) done();
    return () => {
      cancelled = true;
    };
  }, [activeTab, screening]);
  // When arriving from Create screening with "Set up assessment" or "Set up interview" checked,
  // open the corresponding modal(s) once screening has loaded. If both checked, open assessment
  // first then interview after assessment closes. As a fallback, also auto-open based on the
  // screening's own includeAssessment/includeInterview flags when no location.state is present.
  useEffect(() => {
    if (!screening || screeningLoading || autoOpenDone) return;
    const state = location.state || {};
    let wantAssessment = state.openAssessmentModal;
    let wantInterview = state.openInterviewModal;
    // Fallback: if we don't have explicit state (e.g. user refreshed or state was lost),
    // infer intent from screening flags for newly created screenings that don't yet have
    // attached assessment/interview IDs.
    if (wantAssessment == null && wantInterview == null) {
      const shouldHaveAssessment = screening.includeAssessment && !screening.assessmentId;
      const shouldHaveInterview = screening.includeInterview && !screening.interviewId;
      wantAssessment = shouldHaveAssessment;
      wantInterview = shouldHaveInterview;
    }
    if (!wantAssessment && !wantInterview) return;
    setAutoOpenDone(true);
    navigate(location.pathname, { replace: true, state: {} });
    if (wantAssessment && wantInterview) {
      setShowCreateAssessmentModal(true);
      setOpenInterviewModalAfterAssessmentClose(true);
    } else if (wantAssessment) {
      setShowCreateAssessmentModal(true);
    } else {
      setShowCreateInterviewModal(true);
    }
  }, [screening, screeningLoading, location.state, location.pathname, navigate]);
  // helper to open interview details modal and load data
  const openInterviewDetails = useCallback(async (interviewId) => {
    if (!interviewId) return;
    try {
      setInterviewDetailsLoading(true);
      setShowInterviewDetailsModal(true);
      const res = await axios.get(`/api/ai-interviews/${interviewId}`);
      setInterviewDetails(res.data.interview || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load interview details');
      setShowInterviewDetailsModal(false);
    } finally {
      setInterviewDetailsLoading(false);
    }
  }, []);
  // helper to open assessment details modal and load data
  const openAssessmentDetails = useCallback(async (assessmentId) => {
    if (!assessmentId) return;
    try {
      setAssessmentDetailsLoading(true);
      setShowAssessmentDetailsModal(true);
      const response = await axios.get(`/api/assessments/${assessmentId}`);
      const assessment = response.data.assessment;
      setAssessmentDetails(assessment);
      if (assessment.sharingLink) {
        const finalSharingLink = generateSharingUrl(`/assessment/${assessment.sharingLink}`);
        setAssessmentSharingLink(finalSharingLink);
      }
      if (assessment.content?.tests) {
        const testIds = new Set(
          assessment.content.tests.map((t) => t.test?._id || t.test?.id).filter(Boolean)
        );
        setExpandedAssessmentTests(testIds);
      }
    } catch (error) {
      console.error('Error fetching assessment details:', error);
      toast.error('Failed to load assessment details');
      setShowAssessmentDetailsModal(false);
    } finally {
      setAssessmentDetailsLoading(false);
    }
  }, []);
  // If the caller requested to open the created interview/assessment directly (post-create),
  // handle that intent here by opening the relevant details modal(s). Clear location.state
  // afterwards so refresh/back won't re-trigger it.
  useEffect(() => {
    if (!screening || screeningLoading) return;
    const state = location.state || {};
    const wantOpenInterview = state.openInterviewDetails;
    const wantOpenAssessment = state.openAssessmentDetails;
    if (!wantOpenInterview && !wantOpenAssessment) return;
    // Clear the navigation state so this intent only runs once
    navigate(location.pathname, { replace: true, state: {} });
    if (wantOpenInterview) {
      const interviewId =
        state.interviewId ||
        (screening.interviewId && (screening.interviewId._id || screening.interviewId));
      if (interviewId) {
        // openInterviewDetails sets modal state and fetches details
        openInterviewDetails(interviewId);
      }
    }
    if (wantOpenAssessment) {
      const assessmentId =
        state.assessmentId ||
        (screening.assessmentId && (screening.assessmentId._id || screening.assessmentId));
      if (assessmentId) {
        openAssessmentDetails(assessmentId);
      }
    }
  }, [
    screening,
    screeningLoading,
    location.state,
    location.pathname,
    navigate,
    openInterviewDetails,
    openAssessmentDetails,
  ]);
  const refetchScreening = useCallback(async () => {
    if (!screeningId) return;
    try {
      const res = await axios.get(`/api/screenings/${screeningId}`);
      setScreening(res.data.screening);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to refresh screening');
    }
  }, [screeningId]);
  const closeInterviewDetails = useCallback(() => {
    setShowInterviewDetailsModal(false);
    setExpandedInterviewQuestions(new Set());
    setInterviewDetails(null);
    setInterviewLinkCopied(false);
  }, []);
  const copyInterviewShareLink = useCallback(() => {
    if (!interviewDetails?.sharingLink) return;
    const url = `${window.location.origin}/ai-interview/${interviewDetails.sharingLink}`;
    navigator.clipboard.writeText(url).then(() => {
      setInterviewLinkCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setInterviewLinkCopied(false), 2000);
    });
  }, [interviewDetails?.sharingLink]);
  const handleInlineViewAssessmentScore = useCallback(
    async (candidate, options = {}) => {
      if (!candidate) return;
      const moduleIndex =
        typeof options.moduleIndex === 'number' && options.moduleIndex >= 0
          ? options.moduleIndex
          : 0;
      try {
        setSelectedAssessmentCandidate(candidate);
        setAssessmentCandidateDetails(null);
        setAssessmentModalActiveTab('score');
        setAssessmentModalInitialModuleIdx(moduleIndex);
        setShowAssessmentScoreModal(true);
        // A personality (IPIP-50) result has no score by design — it is attempted, not missing.
        if (
          !candidate.personalityResult &&
          candidate.assessmentScore !== '' &&
          isNaN(Number(candidate.assessmentScore))
        ) {
          setAssessmentCandidateDetails({ notAttempted: true });
          return;
        }
        const assessmentId =
          (screening && screening.assessmentId
            ? screening.assessmentId._id
            : screening.assessmentId) ||
          candidate.assessmentId ||
          candidate.assessment_id;
        if (!assessmentId || !candidate.email) {
          setAssessmentCandidateDetails({
            error: true,
            message: 'Missing assessment or candidate email.',
          });
          return;
        }
        const resp = await axios.get(
          `/api/assessments/${assessmentId}/results/${encodeURIComponent(candidate.email)}`
        );
        setAssessmentCandidateDetails(resp.data);
      } catch (e) {
        const is404 = e.response?.status === 404;
        if (!is404) toast.error('Failed to load candidate details');
        setAssessmentCandidateDetails(
          is404
            ? { notAttempted: true }
            : { error: true, message: 'Failed to load' }
        );
      }
    },
    [screening]
  );
  const handleInlineViewAssessmentProctoring = useCallback(
    async (candidate) => {
      if (!candidate) return;
      try {
        setSelectedAssessmentCandidate(candidate);
        setAssessmentProctoringDetails(null);
        setAssessmentModalActiveTab('proctoring');
        setShowAssessmentScoreModal(true);
        if (
          !candidate.personalityResult &&
          candidate.assessmentScore !== '' &&
          isNaN(Number(candidate.assessmentScore))
        ) {
          setAssessmentProctoringDetails({ notAttempted: true });
          return;
        }
        const assessmentId =
          (screening && screening.assessmentId
            ? screening.assessmentId._id
            : screening?.assessmentId) ||
          candidate.assessmentId ||
          candidate.assessment_id;
        if (!assessmentId || !candidate.email) {
          setAssessmentProctoringDetails({
            error: true,
            message: 'Missing assessment or candidate email.',
          });
          return;
        }
        const assessmentIdStr = assessmentId.toString();

        // Helper: fetch full session detail (includes videoProctoringResult + categories).
        const fetchFullSession = async (s) => {
          if (!s?.sessionId) return s;
          try {
            const resp = await axios.get(`/api/proctoring/session/${s.sessionId}`);
            return resp.data?.session || s;
          } catch (_) {
            return s;
          }
        };

        // Check if this is a multi-module assessment by fetching scoreDetails.
        let scoreDetails = assessmentCandidateDetails;
        if (!scoreDetails || scoreDetails.notAttempted || scoreDetails.error) {
          try {
            const resp = await axios.get(
              `/api/assessments/${assessmentIdStr}/results/${encodeURIComponent(candidate.email)}`
            );
            scoreDetails = resp.data || null;
          } catch (_) {
            scoreDetails = null;
          }
        }

        const isMultiModule = scoreDetails?.assessment?.isMultiModule === true && !scoreDetails?.assessment?.isRubricBased;
        const moduleScores = scoreDetails?.candidate?.moduleScores || [];

        // --- Multi-module: load one session per module and pass as array ---
        if (isMultiModule && moduleScores.length > 0) {
          // Strategy 1: use proctoringSessionId stored on each moduleScore.
          const hasLinked = moduleScores.some((ms) => !!ms.proctoringSessionId);
          if (hasLinked) {
            const moduleSessions = await Promise.all(
              moduleScores.map(async (ms) => {
                if (!ms.proctoringSessionId) return null;
                try {
                  const resp = await axios.get(`/api/proctoring/session/${ms.proctoringSessionId}`);
                  const sess = resp.data?.session || null;
                  if (sess && ms.moduleTitle && !sess.moduleTitle) sess.moduleTitle = ms.moduleTitle;
                  return sess;
                } catch (_) {
                  return null;
                }
              })
            );
            const valid = moduleSessions.filter(Boolean);
            if (valid.length > 0) {
              setAssessmentProctoringDetails(valid);
              return;
            }
          }

          // Strategy 2: search all sessions, group by moduleIndex.
          const sessionsResp = await axios.get('/api/proctoring/sessions');
          const sessions = sessionsResp.data?.sessions || [];
          const relevant = sessions.filter(
            (s) =>
              (s.candidateEmail || '').toLowerCase() === candidate.email.toLowerCase() &&
              (s.assessmentId?._id?.toString?.() === assessmentIdStr ||
                s.assessmentId?.toString?.() === assessmentIdStr)
          );
          const withModuleIndex = relevant.filter((s) => s.moduleIndex != null);
          if (withModuleIndex.length > 0) {
            const sorted = withModuleIndex.slice().sort((a, b) => (a.moduleIndex ?? 0) - (b.moduleIndex ?? 0));
            const moduleSessions = await Promise.all(sorted.map((s) => fetchFullSession(s)));
            setAssessmentProctoringDetails(moduleSessions.filter(Boolean));
            return;
          }

          // Strategy 3: multiple sessions without moduleIndex — sort by time.
          if (relevant.length > 1) {
            const sorted = relevant.slice().sort(
              (a, b) => new Date(a.startedAt || a.createdAt) - new Date(b.startedAt || b.createdAt)
            );
            const moduleSessions = await Promise.all(
              sorted.map(async (s, i) => {
                const full = await fetchFullSession(s);
                if (full && !full.moduleTitle && moduleScores[i]?.moduleTitle) {
                  full.moduleTitle = moduleScores[i].moduleTitle;
                }
                return full;
              })
            );
            const valid = moduleSessions.filter(Boolean);
            if (valid.length > 1) {
              setAssessmentProctoringDetails(valid);
              return;
            }
          }
        }

        // --- Single-module (or fallback): pick one best session ---
        if (candidate.proctoringSessionId) {
          try {
            const exact = await axios.get('/api/proctoring/sessions', {
              params: { sessionId: candidate.proctoringSessionId },
            });
            const one = (exact.data?.sessions || []).find(
              (s) =>
                (s._id || s.sessionId)?.toString() === candidate.proctoringSessionId.toString()
            );
            if (one) {
              setAssessmentProctoringDetails(await fetchFullSession(one));
              return;
            }
          } catch (_) {
            // fall through
          }
        }

        const sessionsResp = await axios.get('/api/proctoring/sessions');
        const sessions = sessionsResp.data?.sessions || [];
        const relevant = sessions.filter(
          (s) =>
            (s.candidateEmail || '').toLowerCase() === candidate.email.toLowerCase() &&
            (s.assessmentId?._id?.toString?.() === assessmentIdStr ||
              s.assessmentId?.toString?.() === assessmentIdStr)
        );
        let session = null;
        const withNumericRisk = relevant.filter(
          (s) => typeof getTotalRiskPointsFromDetails(s) === 'number'
        );
        if (withNumericRisk.length > 0) {
          session = withNumericRisk.reduce((max, s) => {
            const current = getTotalRiskPointsFromDetails(s);
            const maxScore = getTotalRiskPointsFromDetails(max);
            return current > maxScore ? s : max;
          }, withNumericRisk[0]);
        }
        if (!session) {
          session =
            relevant.find((s) => s.status === 'completed') ||
            relevant.find((s) => (s.violations?.totalCount || 0) > 0) ||
            relevant.find(
              (s) => (Array.isArray(s.events) ? s.events.length : s.events || 0) > 0
            ) ||
            relevant[0];
        }
        if (session) {
          setAssessmentProctoringDetails(await fetchFullSession(session));
        } else {
          setAssessmentProctoringDetails({ notAttempted: true });
        }
      } catch (e) {
        toast.error('Failed to load proctoring details');
        setAssessmentProctoringDetails({ error: true, message: 'Failed to load' });
      }
    },
    [screening, assessmentCandidateDetails]
  );
  const closeAssessmentScoreModal = useCallback(() => {
    setShowAssessmentScoreModal(false);
    setSelectedAssessmentCandidate(null);
    setAssessmentCandidateDetails(null);
    setAssessmentProctoringDetails(null);
    setAssessmentModalActiveTab('score');
    setAssessmentModalInitialModuleIdx(0);
  }, []);
  const closeInterviewReplayModal = useCallback(() => {
    // Stop any playing video/audio before closing
    if (interviewVideoRef.current) {
      interviewVideoRef.current.pause();
      interviewVideoRef.current.src = '';
    }
    setShowInterviewReplayModal(false);
    setSelectedInterviewSession(null);
    setInterviewSessionLoading(false);
    setInterviewSessionLoadError('');
    setInterviewSessionMeta({ candidateName: '', interviewTitle: '', candidateId: null });
    setInterviewModalActiveTab('analysis');
    setInterviewRecordingUrl(null);
    setExpandedQuestions(new Set());
    setInterviewProctoringData(null);
    setExpandedProctoringCategories({});
    setExpandedProctoringGroups({
      environment: true,
      audio: true,
      video: true,
    });
    setShowDetailedAnalysis(false);
    setShowScoringCompareMode(false);
    setScoringCompareTab('new');
    setDualScoringData(null);
    setDualScoringLoading(false);
    setInterviewTitleTapCount(0);
    setInterviewDisplayLang('hi');
    setInterviewTranslating(false);
    setInterviewLocalTranscriptEnglish(null);
    setInterviewLocalTranscriptEnglishStatus(null);
  }, []);

  // Reset per-session translation state when the modal opens for a different
  // session. New session may already have a cached translation or none at all.
  useEffect(() => {
    setInterviewDisplayLang('hi');
    setInterviewTranslating(false);
    setInterviewLocalTranscriptEnglish(null);
    setInterviewLocalTranscriptEnglishStatus(null);
  }, [selectedInterviewSession?.id, selectedInterviewSession?._id]);

  // Derived translation-UI flags + text lookup helpers for the inline
  // Interview Replay & Analysis modal below. `pickTurnText` is used by both
  // the per-question view and the full-transcript view to swap Original ↔ English.
  const interviewLanguageRaw =
    selectedInterviewSession?.audioConfig?.language;
  const interviewLanguage = (() => {
    if (typeof interviewLanguageRaw !== 'string') return 'en';
    const v = interviewLanguageRaw.trim().toLowerCase();
    if (v === 'hindi') return 'hi';
    if (v === 'hinglish') return 'hi-en';
    if (v === 'english') return 'en';
    return v || 'en';
  })();
  const isNonEnglishInterview = interviewLanguage !== 'en';
  const effectiveInterviewTranscriptEnglish = interviewLocalTranscriptEnglish
    || (Array.isArray(selectedInterviewSession?.transcriptEnglish)
      ? selectedInterviewSession.transcriptEnglish
      : null);
  const effectiveInterviewTranslationStatus =
    interviewLocalTranscriptEnglishStatus
    || selectedInterviewSession?.transcriptEnglishStatus
    || null;
  const hasStoredInterviewTranslation =
    effectiveInterviewTranslationStatus === 'ready'
    && effectiveInterviewTranscriptEnglish
    && effectiveInterviewTranscriptEnglish.length > 0;
  const showInterviewLanguageToggle = isNonEnglishInterview && hasStoredInterviewTranslation;
  const showInterviewTranslateButton = isNonEnglishInterview && !hasStoredInterviewTranslation && !interviewTranslating;
  const showInterviewTranslatingSpinner = isNonEnglishInterview && interviewTranslating;
  const showInterviewEnglish = showInterviewLanguageToggle && interviewDisplayLang === 'en';
  const normalizeInterviewText = useCallback((s) =>
    (s || '')
      .replace(/[.,/#!$%^&*;:{}=\-_`~()?]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase(),
    []);
  const englishByOriginalInterview = useMemo(() => {
    const m = new Map();
    if (!effectiveInterviewTranscriptEnglish) return m;
    for (const t of effectiveInterviewTranscriptEnglish) {
      const orig = t?.metadata?.originalText;
      const eng = t?.text;
      if (typeof orig === 'string' && typeof eng === 'string') {
        const key = normalizeInterviewText(orig);
        if (key) m.set(key, eng);
      }
    }
    return m;
  }, [effectiveInterviewTranscriptEnglish, normalizeInterviewText]);
  const pickInterviewTurnText = useCallback((turn) => {
    const raw = typeof turn?.text === 'string'
      ? turn.text
      : (turn?.text?.text || turn?.content || turn?.message || '');
    if (!showInterviewEnglish || !hasStoredInterviewTranslation) return raw;
    return englishByOriginalInterview.get(normalizeInterviewText(raw)) || raw;
  }, [showInterviewEnglish, hasStoredInterviewTranslation, englishByOriginalInterview, normalizeInterviewText]);

  const handleInterviewTranslateClick = useCallback(async () => {
    const sessionId = selectedInterviewSession?.id || selectedInterviewSession?._id;
    if (!sessionId || interviewTranslating) return;
    setInterviewTranslating(true);
    try {
      const res = await axios.post(`/api/ai-interviews/session/${sessionId}/translate-transcript`);
      const data = res.data || {};
      if (data.transcriptEnglishStatus === 'ready' && Array.isArray(data.transcriptEnglish) && data.transcriptEnglish.length > 0) {
        setInterviewLocalTranscriptEnglish(data.transcriptEnglish);
        setInterviewLocalTranscriptEnglishStatus('ready');
        setInterviewDisplayLang('en'); // Auto-switch to English on success.
      }
      // Any other outcome (missing/failed): spinner disappears, button reappears silently.
    } catch (err) {
      // Silent failure per product spec.
    } finally {
      setInterviewTranslating(false);
    }
  }, [selectedInterviewSession?.id, selectedInterviewSession?._id, interviewTranslating]);

  const triggerDualInterviewRescore = useCallback(async () => {
    const sessionId = selectedInterviewSession?.id || selectedInterviewSession?._id;
    if (!sessionId) return;
    setDualScoringLoading(true);
    try {
      const res = await axios.post(`/api/ai-interviews/session/${sessionId}/rescore-dual`);
      setDualScoringData({
        old: res.data?.old || null,
        new: res.data?.new || null,
      });
      toast.success('Old and new scoring updated');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to rescore');
    } finally {
      setDualScoringLoading(false);
    }
  }, [selectedInterviewSession?.id, selectedInterviewSession?._id]);
  const closeAssessmentDetails = useCallback(() => {
    setShowAssessmentDetailsModal(false);
    setAssessmentDetails(null);
    setAssessmentSharingLink('');
    setExpandedAssessmentTests(new Set());
  }, []);

  const closeResumeScoringDetails = useCallback(() => {
    setShowResumeScoringDetailsModal(false);
  }, []);



  const backToScreeningDetails = useCallback(() => {
    setShowAssessmentDetailsModal(false);
    setShowInterviewDetailsModal(false);

    setShowResumeScoringDetailsModal(false);

    setAssessmentDetails(null);
    setInterviewDetails(null);
    setExpandedAssessmentTests(new Set());
    setExpandedInterviewQuestions(new Set());
    setShowViewDetailsModal(true);
  }, []);
  const copyAssessmentLink = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(assessmentSharingLink);
        setAssessmentLinkCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setAssessmentLinkCopied(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = assessmentSharingLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setAssessmentLinkCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setAssessmentLinkCopied(false), 2000);
      }
    } catch (error) {
      console.error('Error copying link:', error);
      toast.error('Failed to copy link');
    }
  }, [assessmentSharingLink]);
  const toggleAssessmentTestExpansion = useCallback((testId) => {
    setExpandedAssessmentTests((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(testId)) newSet.delete(testId);
      else newSet.add(testId);
      return newSet;
    });
  }, []);
  const resolveInterviewSessionIdForCandidate = useCallback(async (candidate) => {
    const directSessionIdRaw =
      candidate?.interviewSessionId || candidate?.interviewSession || candidate?.interviewSession?._id;
    const directSessionId =
      typeof directSessionIdRaw === 'string'
        ? directSessionIdRaw
        : directSessionIdRaw?._id || directSessionIdRaw?.id || null;
    if (directSessionId) return directSessionId;
    const interviewObj = screening?.interviewId;
    const interviewId =
      typeof interviewObj === 'string' ? interviewObj : interviewObj?._id || interviewObj?.id;
    const email = (candidate?.email || '').trim().toLowerCase();
    if (!interviewId || !email) return null;
    try {
      const res = await axios.get(`/api/ai-interviews/${interviewId}/sessions`);
      const sessions = Array.isArray(res.data?.sessions) ? res.data.sessions : [];
      const matched = sessions.find(
        (s) => (s?.candidateEmail || '').trim().toLowerCase() === email
      );
      return matched?._id || matched?.id || null;
    } catch (err) {
      return null;
    }
  }, [screening?.interviewId]);

  const updateCandidateViewStateInList = useCallback((candidateId, { isViewed, viewedAt }) => {
    const idStr = String(candidateId);
    setCandidates((prev) =>
      prev.map((c) => {
        const cid = String(c.id || c._id);
        if (cid !== idStr) return c;
        return { ...c, isViewed: !!isViewed, viewedAt: viewedAt || null };
      })
    );
  }, []);

  const markCandidateViewed = useCallback(
    async (candidateId) => {
      if (!screeningId || !candidateId) return;
      try {
        const res = await axios.post(
          `/api/screenings/${screeningId}/candidates/${candidateId}/mark-viewed`
        );
        const viewedAt = res.data?.viewedAt || new Date().toISOString();
        updateCandidateViewStateInList(candidateId, { isViewed: true, viewedAt });
      } catch (err) {
        console.warn('[candidate-view-state] mark-viewed failed', err);
      }
    },
    [screeningId, updateCandidateViewStateInList]
  );

  const markCandidateUnviewed = useCallback(
    async (candidateId) => {
      if (!screeningId || !candidateId) return;
      try {
        await axios.post(
          `/api/screenings/${screeningId}/candidates/${candidateId}/mark-unviewed`
        );
        updateCandidateViewStateInList(candidateId, { isViewed: false, viewedAt: null });
      } catch (err) {
        console.warn('[candidate-view-state] mark-unviewed failed', err);
      }
    },
    [screeningId, updateCandidateViewStateInList]
  );

  const openInterviewSession = useCallback(async (sessionId, initialTab = 'analysis', viewOptions = {}) => {
    const { markViewedCandidateId } = viewOptions;
    if (!sessionId) return false;
    setShowInterviewReplayModal(true);
    setShowScoringCompareMode(false);
    setScoringCompareTab('new');
    setDualScoringData(null);
    setDualScoringLoading(false);
    setInterviewTitleTapCount(0);
    setInterviewSessionLoading(true);
    setInterviewSessionLoadError('');
    setInterviewModalActiveTab(initialTab);
    setExpandedQuestions(new Set());
    setInterviewProctoringData(null);
    setExpandedProctoringCategories({});
    setExpandedProctoringGroups({
      environment: true,
      audio: true,
      video: true,
    });

    // Helper to fetch replay data with one automatic retry on server errors (500+).
    // This handles cold-start / transient-timeout scenarios where the first request
    // fails but the second succeeds once the backend has warmed up.
    const fetchReplayWithRetry = async () => {
      try {
        return await axios.get(`/api/ai-interviews/session/${sessionId}/replay`);
      } catch (firstErr) {
        const status = firstErr?.response?.status;
        if (status && status >= 500) {
          // Server error — retry once after a short delay
          await new Promise((r) => setTimeout(r, 1500));
          return await axios.get(`/api/ai-interviews/session/${sessionId}/replay`);
        }
        throw firstErr;
      }
    };

    try {
      const res = await fetchReplayWithRetry();
      const session = res.data?.session;
      if (!session) {
        setInterviewSessionLoadError('We could not load interview details. Please try again.');
        return false;
      }
      setSelectedInterviewSession(session);
      setInterviewSessionLoadError('');
      setInterviewSessionMeta(prev => ({
        ...prev,
        candidateName: session.candidateName || prev.candidateName || '',
        interviewTitle: screening?.interviewId?.title || prev.interviewTitle || '',
      }));
      if (markViewedCandidateId) {
        void markCandidateViewed(markViewedCandidateId);
      }
      // Fetch proctoring data in background
      setInterviewProctoringLoading(true);
      axios.get(`/api/ai-interviews/session/${sessionId}/proctoring`)
        .then((procRes) => {
          setInterviewProctoringData(procRes.data);
        })
        .catch(() => {
          // Silently ignore proctoring fetch errors
        })
        .finally(() => {
          setInterviewProctoringLoading(false);
        });
      return true;
    } catch (e) {
      setInterviewSessionLoadError(
        e.response?.data?.message || 'Failed to load interview session. Please retry.'
      );
      return false;
    } finally {
      setInterviewSessionLoading(false);
    }
  }, [screening?.interviewId?.title, markCandidateViewed]);
  const openInterviewSessionForCandidate = useCallback(
    async (candidate, initialTab = 'analysis') => {
      const candidateId = candidate?.id || candidate?._id;
      setShowInterviewReplayModal(true);
      setInterviewSessionLoading(true);
      setInterviewSessionLoadError('');
      setInterviewSessionMeta({
        candidateName: resolveCandidateDisplayName(candidate) || candidate?.candidateName || 'Candidate',
        interviewTitle: screening?.interviewId?.title || '',
        candidateId: candidateId || null,
      });
      const directSessionId = await resolveInterviewSessionIdForCandidate(candidate);
      if (directSessionId) {
        await openInterviewSession(directSessionId, initialTab, {
          markViewedCandidateId: candidateId,
        });
        return;
      }
      setInterviewSessionLoadError('Interview session is not available for this candidate yet.');
      setInterviewSessionLoading(false);
    },
    [openInterviewSession, resolveInterviewSessionIdForCandidate, screening?.interviewId?.title]
  );
  // WhatsApp Agent screenings have no InterviewSession to resolve — they get their own
  // small replay modal (WhatsAppInterviewReplayModal) that fetches directly by candidateId.
  const openWhatsAppSessionForCandidate = useCallback((candidate) => {
    setWhatsAppReplayCandidate(candidate);
  }, []);
  // Qualifying Agent sessions likewise have no InterviewSession, and no score either —
  // their replay modal fetches the call/WhatsApp session directly by candidateId.
  const openQualifyingSessionForCandidate = useCallback((candidate) => {
    setQualifyingReplayCandidate(candidate);
  }, []);
  const openQualifyingAnswersForCandidate = useCallback((candidate) => {
    setQualifyingAnswersCandidate(candidate);
  }, []);
  // Closing a session's card or modal re-reads the table's summary once if that session could
  // still have moved on. The view just fetched fresh data, so the row should not lag behind it.
  const closeQualifyingSessionView = (openCandidate, setOpenCandidate) => () => {
    setOpenCandidate(null);
    if (qualifyingCanStillChange(openCandidate?.qualifying)) void refreshQualifyingSummary();
  };
  // When replay modal shows a completed session with no score/summary (deferred scoring),
  // poll replay endpoint so UI updates once backend finishes scoring.
  useEffect(() => {
    const session = selectedInterviewSession;
    const sessionId = session?.id || session?._id;
    if (!sessionId || session?.status !== 'completed') return;
    const score = session.scores?.overall ?? session.score;
    const hasSummary = !!(
      session.analytics?.summary ||
      session.analysisSummary ||
      session.analytics?.detailedAnalysis?.analysisSummary ||
      session.analytics?.analysisSummary
    );
    if ((score != null && score > 0) || hasSummary) return;
    const POLL_INTERVAL_MS = 3000;
    const POLL_DURATION_MS = 30000;
    const deadline = Date.now() + POLL_DURATION_MS;
    const t = setInterval(async () => {
      if (Date.now() > deadline) {
        clearInterval(t);
        return;
      }
      try {
        const res = await axios.get(`/api/ai-interviews/session/${sessionId}/replay`);
        const updated = res.data?.session;
        if (!updated) return;
        const newScore = updated.scores?.overall ?? updated.score;
        const newHasSummary = !!(
          updated.analytics?.summary ||
          updated.analysisSummary ||
          updated.analytics?.detailedAnalysis?.analysisSummary ||
          updated.analytics?.analysisSummary
        );
        if ((newScore != null && newScore > 0) || newHasSummary) {
          setSelectedInterviewSession(updated);
          clearInterval(t);
        }
      } catch {
        // ignore
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [selectedInterviewSession?.id, selectedInterviewSession?._id, selectedInterviewSession?.status, selectedInterviewSession?.scores?.overall, selectedInterviewSession?.analytics?.summary]);
  const screeningMeta = useMemo(() => {
    const multiModuleTableScoresEnabled = Boolean(user?.features?.multi_module_table_scores);
    const moduleMeta = buildScreeningAssessmentModuleMeta(screening, multiModuleTableScoresEnabled);
    if (!screening) {
      return {
        id: screeningId,
        name: '',
        jobTitle: '',
        storeFormat: 'EBO',
        hasResumeScoring: true,
        includeAssessment: true,
        includeInterview: true,
        hasAssessment: false,
        hasInterview: false,
        applyLinkActive: true,
        applyLinkExpiresAt: null,
        maxCandidatesAllowed: null,
        candidateCount: 0,
        multiModuleTableScoresEnabled,
        showAssessmentModuleColumns: false,
        assessmentModules: [],
        includeDocumentCollection: false,
        requestedDocuments: [],
        includeWhatsAppAgent: false,
        includeQualifyingAgent: false,
        includeApplicationForm: false,
      };
    }
    return {
      id: screening._id,
      name: screening.name,
      jobTitle: screening.jobId?.title || screening.job?.title || screening.name,
      storeFormat: screening.jobId?.storeFormat || screening.job?.storeFormat || 'EBO',
      hasResumeScoring: screening.hasResumeScoring !== false,
      includeAssessment: screening.includeAssessment !== false,
      // WhatsApp Agent writes its score into the same `interviewScore` field the AI Interview
      // column reads (see Candidate.whatsAppInterviewScore fallback in
      // buildScreeningCandidatesList, screenings.route.js) — treat it as "has an interview"
      // for column-visibility purposes so candidatesTableLayout.js shows the score column.
      // `hasInterview`/`interviewId` below stay false, so AI-Interview-specific UI (replay,
      // reprocess) correctly stays hidden.
      includeInterview: screening.includeInterview !== false || screening.includeWhatsAppAgent === true,
      // A "Moved Candidates" pool runs no stages of its own, so its stage flags are all
      // false. Its candidates still carry scores from the screening they came from, so
      // anything that suppresses a score column on `flag === false` must exempt the pool.
      isMovedCandidatesPool: screening.isMovedCandidatesPool === true,
      hasAssessment: !!screening.assessmentId,
      hasInterview: !!screening.interviewId,
      assessmentId: screening.assessmentId,
      interviewId: screening.interviewId,
      applyLinkActive: screening.applyLinkActive !== false,
      applyLinkExpiresAt: screening.applyLinkExpiresAt || null,
      maxCandidatesAllowed: screening.maxCandidatesAllowed || null,
      candidateCount: screening.candidateCount || 0,
      multiModuleTableScoresEnabled,
      includeDocumentCollection: screening.includeDocumentCollection === true,
      requestedDocuments: screening.requestedDocuments || [],
      includeWhatsAppAgent: screening.includeWhatsAppAgent === true,
      includeQualifyingAgent: screening.includeQualifyingAgent === true,
      // The Qualifying Agent is the screening's only stage: the Status column then shows where
      // the candidate is in it (Contacting, In progress, Completed, Not interested…).
      qualifyingOnly:
        screening.includeQualifyingAgent === true &&
        !screening.assessmentId &&
        !screening.interviewId &&
        screening.includeWhatsAppAgent !== true,
      includeApplicationForm: screening.includeApplicationForm === true,
      ...moduleMeta,
    };
  }, [screening, screeningId, user?.features?.multi_module_table_scores]);

  // Stores a candidate of this job may be linked to: an LFS job only offers LFS stores,
  // an EBO job only EBO. The full list still backs store lookups for display.
  const jobStores = useMemo(
    () => filterStoresByFormat(stores, screeningMeta?.storeFormat),
    [stores, screeningMeta?.storeFormat]
  );

  useEffect(() => {
    if (screeningMeta?.id && !hasSyncedMaxCandidates) {
      setMaxCandidatesInput(screeningMeta.maxCandidatesAllowed || '');
      setHasSyncedMaxCandidates(true);
    }
  }, [screeningMeta, hasSyncedMaxCandidates]);

  useEffect(() => {
    if (!screeningId || !screeningMeta?.hasInterview) {
      setInterviewQuestions([]);
      return;
    }
    let cancelled = false;
    axios
      .get(`/api/screenings/${screeningId}/interview-questions`)
      .then((res) => {
        if (cancelled) return;
        const qs = Array.isArray(res?.data?.questions) ? res.data.questions : [];
        setInterviewQuestions(qs);
      })
      .catch(() => {
        if (!cancelled) setInterviewQuestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [screeningId, screeningMeta?.hasInterview]);

  const candidateColumns = useMemo(
    () =>
      buildCandidateColumnChoices(screeningMeta, {
        showStatus: true,
        showShortlistedDate: false,
        showRejectedDate: false,
        isShortlistView: false,
        hasArmSalaryDetails: armSalaryDetailsEnabled,
        showDocumentsColumn: screeningMeta?.includeDocumentCollection === true || Object.keys(documentStatusMap || {}).length > 0,
        user,
        orgDefaultColumnOrder: user?.ui?.defaultCandidateColumnOrder,
      }),
    [screeningMeta, documentStatusMap, user, armSalaryDetailsEnabled]
  );

  const shortlistColumns = useMemo(
    () =>
      buildCandidateColumnChoices(screeningMeta, {
        showStatus: true,
        showShortlistedDate: true,
        showRejectedDate: false,
        isShortlistView: true,
        hasArmSalaryDetails: armSalaryDetailsEnabled,
        showDocumentsColumn: screeningMeta?.includeDocumentCollection === true || Object.keys(documentStatusMap || {}).length > 0,
        user,
        orgDefaultColumnOrder: user?.ui?.defaultShortlistColumnOrder,
      }),
    [documentStatusMap, screeningMeta, user, armSalaryDetailsEnabled]
  );

  const rejectedColumns = useMemo(
    () =>
      buildCandidateColumnChoices(screeningMeta, {
        showStatus: true,
        showShortlistedDate: false,
        showRejectedDate: true,
        isShortlistView: true,
        isRejectedView: true,
        hasArmSalaryDetails: armSalaryDetailsEnabled,
        // Notes carries the mandatory rejection reason, so it is only on by default
        // for orgs with that feature enabled.
        notesDefaultVisible: rejectionReasonRequired,
        showDocumentsColumn: screeningMeta?.includeDocumentCollection === true || Object.keys(documentStatusMap || {}).length > 0,
        user,
        orgDefaultColumnOrder: user?.ui?.defaultRejectedColumnOrder,
      }),
    [documentStatusMap, screeningMeta, user, rejectionReasonRequired, armSalaryDetailsEnabled]
  );

  const columnsModalColumns =
    columnsModalContext === 'shortlist'
      ? shortlistColumns
      : columnsModalContext === 'rejected'
        ? rejectedColumns
        : candidateColumns;
  const columnsModalValue =
    columnsModalContext === 'shortlist'
      ? shortlistColumnVisibility
      : columnsModalContext === 'rejected'
        ? rejectedColumnVisibility
        : candidateColumnVisibility;
  const columnsModalOrder =
    columnsModalContext === 'shortlist'
      ? shortlistColumnOrder
      : columnsModalContext === 'rejected'
        ? rejectedColumnOrder
        : candidateColumnOrder;
  // Reset ("Reset to org defaults") clears this user's PERSONAL layout for the tab
  // on the server, then falls back to the shared/org/platform layout.
  const performColumnReset = useCallback(
    async (tab) => {
      // Clear any legacy per-browser cache so it can't resurface.
      if (colStorageKey) localStorage.removeItem(`${colStorageKey}:${tab}`);
      try {
        await axios.delete(`/api/screenings/${screeningId}/column-layout`, {
          data: { tab, scope: 'all' },
        });
      } catch {
        toast.error('Failed to reset columns');
      }
      // Drop the personal and shared layers; the sync effect re-resolves the visible layout.
      setServerColLayouts((prev) => {
        const personal = { ...prev.personal };
        delete personal[tab];
        const shared = { ...prev.shared };
        delete shared[tab];
        return { shared, personal };
      });
    },
    [screeningId, colStorageKey]
  );

  // Called by the columns modal's "Apply". A null payload means "Reset to org defaults".
  // Otherwise we ask (via the scope modal) whether to save for just this user or for
  // everyone on the screening, then persist server-side.
  const handleApplyColumns = useCallback(
    (nextVisibility, nextOrder, nextSortCol, nextSortDir) => {
      const tab = columnsModalContext; // 'candidates' | 'shortlist' | 'rejected'

      if (nextSortCol && nextSortDir) {
        setTableSortColumn(nextSortCol);
        setTableSortDirection(nextSortDir);
      }

      const isReset = nextVisibility === null && nextOrder === null;
      if (isReset) {
        performColumnReset(tab);
        return;
      }
      setPendingColumnSave({
        tab,
        visibility: nextVisibility || {},
        order: Array.isArray(nextOrder) ? nextOrder : [],
        sortColumn: nextSortCol,
        sortDirection: nextSortDir,
      });
      setScopeModalOpen(true);
    },
    [columnsModalContext, performColumnReset]
  );

  // Persist the pending column change once the user picks a scope in the scope modal.
  const commitColumnSave = useCallback(
    async (scope) => {
      if (!pendingColumnSave) return;
      const { tab, order, visibility, sortColumn, sortDirection } = pendingColumnSave;
      if (!Array.isArray(order) || order.length === 0) {
        setScopeModalOpen(false);
        setPendingColumnSave(null);
        return;
      }
      setSavingColumnScope(true);
      try {
        const res = await axios.put(`/api/screenings/${screeningId}/column-layout`, {
          tab,
          scope,
          order,
          visibility,
          sortColumn,
          sortDirection,
        });
        // Stamp with the server's updatedAt (fallback: now) so this freshly-saved layer
        // is the most-recently-edited and therefore wins in resolveTabLayout.
        const savedAt = res?.data?.layout?.updatedAt || new Date().toISOString();
        // Update the matching server layer so the sync effect re-resolves the view.
        setServerColLayouts((prev) => {
          const key = scope === 'shared' ? 'shared' : 'personal';
          return {
            ...prev,
            [key]: { ...prev[key], [tab]: { order, visibility, sortColumn, sortDirection, updatedAt: savedAt } },
          };
        });
        toast.success(
          scope === 'shared'
            ? 'Column order saved for everyone on this screening'
            : 'Column order saved for you'
        );
        setScopeModalOpen(false);
        setPendingColumnSave(null);
      } catch {
        toast.error('Failed to save column order');
      } finally {
        setSavingColumnScope(false);
      }
    },
    [pendingColumnSave, screeningId]
  );

  const invitedCount = candidates.filter((c) => !c.rejected).length;
  const candidatesTabCount = candidates.filter((c) => !c.rejected && !c.shortlisted && !c.dropped).length;
  const duplicateFeatureEnabled = useMemo(() => {
    return Boolean(user?.features?.duplicate_candidate_alerts);
  }, [user?.features?.duplicate_candidate_alerts]);
  const recommendedChipEnabled = useMemo(() => {
    return Boolean(user?.features?.recommended_chip);
  }, [user?.features?.recommended_chip]);
  const recommendedChipThreshold = useMemo(() => {
    return user?.limits?.recommended_chip_threshold ?? 85;
  }, [user?.limits?.recommended_chip_threshold]);
  const completedCount = candidates.filter((c) => c.status === 'Completed').length;
  const shortlistedCount = candidates.filter((c) => c.shortlisted).length;
  // Go Colors: a selected candidate whose DOJ + gross salary the ARM has filled is "onboarded"
  // and moves from the Selected tab to the Onboarded tab. Derived, not stored — un-selecting the
  // candidate takes them out of Onboarded too. The Insights funnel still counts them as selected.
  const isOnboardedCandidate = useCallback(
    (c) => isGoColors && !!c?.shortlisted && salaryRequestsMap?.[String(c.id || c._id)]?.status === 'completed',
    [isGoColors, salaryRequestsMap]
  );
  const onboardedCount = candidates.filter(isOnboardedCandidate).length;
  const selectedTabCount = shortlistedCount - onboardedCount;
  const rejectedCount = candidates.filter((c) => c.rejected).length;
  const droppedCount = candidates.filter((c) => c.dropped).length;
  const duplicateCandidates = useMemo(() => {
    if (!duplicateFeatureEnabled) return [];
    return candidates.filter((c) => c?.duplicateSummary?.count > 0);
  }, [duplicateFeatureEnabled, candidates]);
  const [duplicateModalCandidate, setDuplicateModalCandidate] = useState(null);
  const [notificationSessionReadIds, setNotificationSessionReadIds] = useState(() => new Set());
  const getAssessmentPctForCandidate = useCallback((c) => {
    if (!c) return null;
    if (c.assessmentScore == null) return null;
    const total = c.assessmentTotalPoints;
    if (total && total > 0) return Math.round((c.assessmentScore / total) * 100);
    // Some older records store assessmentScore already as a percent-like number.
    return c.assessmentScore;
  }, []);
  const candidateNotifications = useMemo(() => {
    if (!Array.isArray(candidates) || candidates.length === 0) return [];
    const completed = candidates
      .filter((candidate) => (candidate.status || 'Invited').toLowerCase() === 'completed')
      .map((candidate) => {
        const displayName =
          resolveCandidateDisplayName(candidate) || candidate.email || 'Unnamed candidate';
        const candidateIdentifier = candidate.email || displayName;
        const assessmentPct = getAssessmentPctForCandidate(candidate);
        return {
          id: candidate._id || candidate.id,
          candidateName: displayName,
          candidateIdentifier,
          type: 'completed',
          message: 'Screening completed successfully.',
          completedAt: candidate.completedAt || null,
          resumeScore: candidate.resumeScore ?? null,
          interviewScore: candidate.interviewScore ?? null,
          assessmentScorePct: assessmentPct,
        };
      });

    const documentNotifs = Object.entries(documentStatusMap || {})
      .filter(([_, d]) => d?.status === 'submitted' || d?.status === 'partial' || d?.status === 'deleted')
      .map(([candidateId, d]) => {
        const candidate = candidates.find((c) => String(c._id || c.id) === String(candidateId));
        const name =
          resolveCandidateDisplayName(candidate) || candidate?.email || 'Unnamed candidate';
        const uploaded = d?.uploaded ?? 0;
        const total = d?.total ?? 0;
        const isDeleted = d?.status === 'deleted';
        return {
          id: isDeleted ? `doc-del-${candidateId}` : `doc-${candidateId}`,
          candidateName: name,
          candidateIdentifier: candidate?.email || name,
          type: isDeleted ? 'document_deleted' : 'document_upload',
          message: isDeleted
            ? `${name} deleted the submitted documents`
            : d?.status === 'submitted'
              ? `${name} submitted all ${total} documents`
              : `${name} submitted ${uploaded} of ${total} documents`,
          completedAt: isDeleted ? d?.deletedAt || d?.updatedAt || null : d?.submittedAt || d?.updatedAt || null,
        };
      });

    // Prefer per-action entries from the share-link activity log so each
    // decision/note is attributed to the named reviewer ("Priya made a decision:
    // Selected for Rahul K."). Fall back to the candidate-derived summary for
    // candidates that have no activity entries yet (legacy share-link decisions
    // recorded before this feature shipped).
    const candidatesWithActivity = new Set(
      (shareReviewActivities || [])
        .map((a) => String(a.candidateId || ''))
        .filter(Boolean)
    );

    const decisionStatusLabel = (s) =>
      s === 'selected'
        ? 'Selected'
        : s === 'rejected'
          ? 'Rejected'
          : s === 'pending'
            ? 'Marked as Pending'
            : s === 'offered'
              ? 'Offered'
              : s || 'Updated';

    const shareActivityNotifs = (shareReviewActivities || [])
      .filter((a) => a.actorType !== 'recruiter')
      .map((a) => {
        const reviewer = a.reviewerName || 'A reviewer';
        const target = a.candidateName || 'a candidate';
        let message;
        let type = 'hiring_manager_decision';
        if (a.action === 'view') {
          message = `${reviewer} viewed the review link`;
          type = 'share_link_view';
        } else if (a.action === 'decision' && a.decision) {
          message = `${reviewer} made a decision: ${decisionStatusLabel(a.decision)} for ${target}`;
        } else if (a.action === 'decision_cleared') {
          message = `${reviewer} cleared the decision for ${target}`;
        } else if (a.action === 'note') {
          message = `${reviewer} added a note on ${target}`;
        } else if (a.action === 'note_cleared') {
          message = `${reviewer} removed the note on ${target}`;
        } else {
          message = `${reviewer} updated ${target}`;
        }
        return {
          id: `share-act-${a.id}`,
          candidateName: a.action === 'view' ? 'Review link' : target,
          candidateIdentifier: a.action === 'view' ? a.shareCode || 'Review link' : target,
          type,
          message,
          completedAt: a.at || null,
          decisionStatus: a.decision || null,
          decisionNotes: a.note || '',
        };
      });

    const legacyHmDecisionNotifs = candidates
      .filter(
        (c) =>
          c.shortlisted &&
          c.decisionStatus &&
          c.decisionAt &&
          !candidatesWithActivity.has(String(c._id || c.id))
      )
      .map((c) => {
        const name = resolveCandidateDisplayName(c) || c.email || 'Unnamed candidate';
        return {
          id: `hm-decision-${c._id || c.id}`,
          candidateName: name,
          candidateIdentifier: c.email || name,
          type: 'hiring_manager_decision',
          message: `Hiring Manager reviewed and marked ${name} as ${decisionStatusLabel(c.decisionStatus)}`,
          completedAt: c.decisionAt || null,
          decisionStatus: c.decisionStatus,
          decisionNotes: c.decisionNotes || '',
        };
      });

    return [
      ...completed,
      ...documentNotifs,
      ...shareActivityNotifs,
      ...legacyHmDecisionNotifs,
    ].sort((a, b) => {
      const at = a.completedAt ? new Date(a.completedAt).getTime() : 0;
      const bt = b.completedAt ? new Date(b.completedAt).getTime() : 0;
      if (bt !== at) return bt - at;
      return String(a.candidateName || '').localeCompare(String(b.candidateName || ''));
    });
  }, [candidates, getAssessmentPctForCandidate, documentStatusMap, shareReviewActivities]);
  // Derived: which notifications are read vs unread based on persisted lastReadAt
  const fetchNotificationReadState = useCallback(async () => {
    if (!screeningId) return;
    try {
      const res = await axios.get(`/api/screenings/${screeningId}/notifications/read-state`);
      setNotificationReadState(res.data?.readState || null);
    } catch (e) {
      setNotificationReadState(null);
    }
  }, [screeningId]);

  // Keep badge count correct on initial page load / when navigating back to this page.
  useEffect(() => {
    fetchNotificationReadState();
  }, [fetchNotificationReadState]);

  // Fetch share-link review activity (decisions/notes made by hiring managers
  // via public review links) so the notifications panel can attribute each
  // action to the named reviewer. Refresh on a light interval so recruiters
  // see new activity without manual reload.
  const fetchShareReviewActivity = useCallback(async () => {
    if (!screeningId) return;
    try {
      const res = await axios.get(
        `/api/screenings/${screeningId}/share-review-activity?limit=100`
      );
      setShareReviewActivities(
        Array.isArray(res.data?.activities) ? res.data.activities : []
      );
    } catch (e) {
      // Soft-fail: panel still renders other notifications.
    }
  }, [screeningId]);

  useEffect(() => {
    fetchShareReviewActivity();
    if (!screeningId) return undefined;
    const interval = setInterval(fetchShareReviewActivity, 60_000);
    return () => clearInterval(interval);
  }, [fetchShareReviewActivity, screeningId]);
  const enrichedCandidateNotifications = useMemo(() => {
    if (!candidateNotifications || candidateNotifications.length === 0) return [];
    const lastReadAt =
      notificationReadState && notificationReadState.lastReadAt
        ? new Date(notificationReadState.lastReadAt).getTime()
        : null;
    const readIdSet = new Set(
      Array.isArray(notificationReadState?.readNotificationIds)
        ? notificationReadState.readNotificationIds
        : []
    );
    return candidateNotifications.map((n) => {
      const completedTs = n.completedAt ? new Date(n.completedAt).getTime() : null;
      const isReadByTime = lastReadAt != null && completedTs != null && completedTs <= lastReadAt;
      const isReadById = n.id != null && readIdSet.has(String(n.id));
      const isRead = isReadById || isReadByTime;
      return { ...n, isRead };
    });
  }, [candidateNotifications, notificationReadState]);
  const unreadNotificationsCount = useMemo(() => {
    return enrichedCandidateNotifications.filter((n) => !n.isRead).length;
  }, [enrichedCandidateNotifications]);
  // Keep both unread and read items visible in the panel so recruiters retain a
  // lightweight notification history after opening or marking items as read.
  const visibleCandidateNotifications = useMemo(() => {
    return enrichedCandidateNotifications;
  }, [enrichedCandidateNotifications]);
  const notificationsBadgeCount = unreadNotificationsCount;
  const [notificationsTab, setNotificationsTab] = useState('focused');
  const insightsStats = useMemo(() => {
    const getAssessmentPct = (c) => {
      if (c.assessmentScore == null) return null;
      const total = c.assessmentTotalPoints;
      if (total && total > 0) return Math.round((c.assessmentScore / total) * 100);
      return c.assessmentScore;
    };
    const getOverall = (c) => {
      const scores = [c.resumeScore, c.interviewScore, getAssessmentPct(c)].filter(
        (s) => s != null
      );
      if (scores.length === 0) return null;
      return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    };
    const withResume = candidates.filter((c) => c.resumeScore != null);
    const withAssessment = candidates.filter((c) => c.assessmentScore != null);
    const withInterview = candidates.filter(
      (c) => c.interviewScore != null || c.interviewSessionId
    );
    const avgResume =
      withResume.length > 0
        ? Math.round(withResume.reduce((a, c) => a + (c.resumeScore ?? 0), 0) / withResume.length)
        : null;
    const assessmentPcts = withAssessment.map(getAssessmentPct).filter((v) => v != null);
    const avgAssessment =
      assessmentPcts.length > 0
        ? Math.round(assessmentPcts.reduce((a, b) => a + b, 0) / assessmentPcts.length)
        : null;
    const interviewScores = withInterview.map((c) => c.interviewScore).filter((v) => v != null);
    const avgInterview =
      interviewScores.length > 0
        ? Math.round(interviewScores.reduce((a, b) => a + b, 0) / interviewScores.length)
        : null;
    const overallScores = candidates.map(getOverall).filter((v) => v != null);
    const avgOverall =
      overallScores.length > 0
        ? Math.round(overallScores.reduce((a, b) => a + b, 0) / overallScores.length)
        : null;
    const buckets = [
      { range: '0-20', label: '0-20%', count: 0, fill: '#ef4444' },
      { range: '21-40', label: '21-40%', count: 0, fill: '#f97316' },
      { range: '41-60', label: '41-60%', count: 0, fill: '#eab308' },
      { range: '61-80', label: '61-80%', count: 0, fill: '#22c55e' },
      { range: '81-100', label: '81-100%', count: 0, fill: '#007BFF' },
    ];
    overallScores.forEach((s) => {
      if (s <= 20) buckets[0].count++;
      else if (s <= 40) buckets[1].count++;
      else if (s <= 60) buckets[2].count++;
      else if (s <= 80) buckets[3].count++;
      else buckets[4].count++;
    });
    const appliedCount = candidates.length;
    const funnelData = [
      { stage: 'Applied', count: appliedCount, fill: '#007BFF', conversion: 100 },
    ];
    let prevCount = appliedCount;
    if (screeningMeta?.includeAssessment) {
      const count = withAssessment.length;
      funnelData.push({
        stage: 'Completed Assessment',
        count,
        fill: '#0ea5e9',
        conversion: prevCount > 0 ? Math.round((count / prevCount) * 100) : 0,
      });
      prevCount = count;
    }
    if (screeningMeta?.includeInterview) {
      const count = withInterview.length;
      funnelData.push({
        stage: 'Completed Interview',
        count,
        fill: '#8b5cf6',
        conversion: prevCount > 0 ? Math.round((count / prevCount) * 100) : 0,
      });
      prevCount = count;
    }
    funnelData.push({
      stage: 'Selected',
      count: shortlistedCount,
      fill: '#10b981',
      conversion: prevCount > 0 ? Math.round((shortlistedCount / prevCount) * 100) : 0,
    });
    const inProgressCount = candidates.filter((c) => c.status === 'In Progress').length;
    const statusPie = [
      { name: 'Completed', value: completedCount, fill: '#10b981' },
      { name: 'In Progress', value: inProgressCount, fill: '#f97316' },
      {
        name: 'Invited (pending)',
        value: invitedCount - completedCount - inProgressCount,
        fill: '#94a3b8',
      },
    ].filter((d) => d.value > 0);
    // Resume score breakdown aggregates (5-parameter system)
    const breakdownKeys = [
      { key: 'keyResponsibilitiesMatch', label: 'Key Responsibilities', max: 45, fill: '#3b82f6' },
      { key: 'experienceAndLevel', label: 'Experience & Level', max: 25, fill: '#8b5cf6' },
      { key: 'impactAchievements', label: 'Impact & Achievements', max: 15, fill: '#10b981' },
      { key: 'careerTrajectory', label: 'Career Trajectory', max: 5, fill: '#ec4899' },
      { key: 'educationCredentials', label: 'Education & Credentials', max: 10, fill: '#14b8a6' },
    ];
    const withBreakdown = candidates.filter(
      (c) => c.scoreBreakdown && typeof c.scoreBreakdown === 'object'
    );
    const resumeBreakdownData = breakdownKeys.map(({ key, label, max, fill }) => {
      const vals = withBreakdown
        .map((c) => c.scoreBreakdown[key])
        .filter((v) => v != null && typeof v === 'number');
      const avg =
        vals.length > 0
          ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
          : null;
      const norm10 = avg != null && max > 0 ? Math.round((avg / max) * 10 * 10) / 10 : null;
      return { label, avg, max, fill, count: vals.length, norm10 };
    });
    // Resume score distribution buckets
    const resumeBuckets = [
      { range: '0-20', label: '0-20%', count: 0, fill: '#ef4444' },
      { range: '21-40', label: '21-40%', count: 0, fill: '#f97316' },
      { range: '41-60', label: '41-60%', count: 0, fill: '#eab308' },
      { range: '61-80', label: '61-80%', count: 0, fill: '#22c55e' },
      { range: '81-100', label: '81-100%', count: 0, fill: '#007BFF' },
    ];
    withResume.forEach((c) => {
      const s = c.resumeScore ?? 0;
      if (s <= 20) resumeBuckets[0].count++;
      else if (s <= 40) resumeBuckets[1].count++;
      else if (s <= 60) resumeBuckets[2].count++;
      else if (s <= 80) resumeBuckets[3].count++;
      else resumeBuckets[4].count++;
    });
    // Per-parameter distribution: score bucket (0-1, 1-2, ... 9-10) -> count of candidates (for 5-line chart)
    const bucketLabels = ['0-1', '1-2', '2-3', '3-4', '4-5', '5-6', '6-7', '7-8', '8-9', '9-10'];
    const parameterDistributionBuckets = bucketLabels.map((bucket) => ({
      bucket,
      ...Object.fromEntries(breakdownKeys.map((k) => [k.key, 0])),
    }));
    withBreakdown.forEach((c) => {
      breakdownKeys.forEach(({ key, max }) => {
        const raw = c.scoreBreakdown[key];
        if (raw == null || typeof raw !== 'number' || max <= 0) return;
        const norm10 = (raw / max) * 10;
        const bucketIdx = Math.min(9, Math.floor(norm10));
        parameterDistributionBuckets[bucketIdx][key]++;
      });
    });
    // V2: criteria match-level distribution
    const v2Candidates = candidates.filter(
      (c) => c.scoringVersion && c.scoringVersion.startsWith('v2') && Array.isArray(c.criteriaResults) && c.criteriaResults.length > 0
    );
    const hasV2Data = v2Candidates.length > 0;
    const criteriaMatchDistribution = [];
    if (hasV2Data) {
      const criteriaMap = new Map();
      v2Candidates.forEach((c) => {
        (c.criteriaResults || []).forEach((cr) => {
          const key = cr.criterionText || cr.criterionId;
          if (!key) return;
          if (!criteriaMap.has(key)) {
            criteriaMap.set(key, { text: key, strong: 0, partial: 0, weak: 0, not_found: 0, total: 0 });
          }
          const entry = criteriaMap.get(key);
          const match = cr.match || 'not_found';
          if (entry[match] !== undefined) entry[match]++;
          entry.total++;
        });
      });
      criteriaMap.forEach((val) => criteriaMatchDistribution.push(val));
    }
    const dealBreakerCount = candidates.filter((c) => c.hasDealBreakerConcerns).length;
    return {
      withResumeCount: withResume.length,
      withAssessmentCount: withAssessment.length,
      withInterviewCount: withInterview.length,
      avgResume,
      avgAssessment,
      avgInterview,
      avgOverall,
      funnelData,
      scoreBuckets: buckets,
      statusPie,
      resumeBreakdownData,
      resumeBuckets,
      parameterDistributionBuckets,
      breakdownKeys,
      hasV2Data,
      criteriaMatchDistribution,
      v2CandidateCount: v2Candidates.length,
      dealBreakerCount,
    };
  }, [candidates, shortlistedCount, screeningMeta?.includeAssessment, screeningMeta?.includeInterview]);
  const candidatePoolInsights = useMemo(() => {
    const parseExpYears = (c) => {
      if (
        c.experienceYears != null &&
        typeof c.experienceYears === 'number' &&
        !Number.isNaN(c.experienceYears)
      )
        return c.experienceYears;
      const str = c.experience;
      if (!str || typeof str !== 'string') return null;
      const s = str.trim();
      const rangeMatch = s.match(/(\d+(?:\.\d+)?)\s*[-–to]+\s*(\d+(?:\.\d+)?)/i);
      if (rangeMatch) {
        const a = parseFloat(rangeMatch[1], 10);
        const b = parseFloat(rangeMatch[2], 10);
        if (!Number.isNaN(a) && !Number.isNaN(b)) return (a + b) / 2;
      }
      const singleMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:years?|yoe)?/i);
      return singleMatch ? parseFloat(singleMatch[1], 10) : null;
    };
    const total = candidates.length;
    const expYearsArr = candidates.map(parseExpYears).filter((n) => n != null && n >= 0);
    const sortedExp = [...expYearsArr].sort((a, b) => a - b);
    const percentile = (arr, p) => {
      if (arr.length === 0) return null;
      const i = (p / 100) * (arr.length - 1);
      const lo = Math.floor(i);
      const hi = Math.ceil(i);
      return lo === hi ? arr[lo] : arr[lo] + (i - lo) * (arr[hi] - arr[lo]);
    };
    const experienceStats =
      sortedExp.length > 0
        ? {
          avg: Math.round((sortedExp.reduce((a, b) => a + b, 0) / sortedExp.length) * 10) / 10,
          p25: Math.round(percentile(sortedExp, 25) * 10) / 10,
          median: Math.round(percentile(sortedExp, 50) * 10) / 10,
          p75: Math.round(percentile(sortedExp, 75) * 10) / 10,
          count: sortedExp.length,
        }
        : null;
    const parseEmploymentPeriodToYears = (period) => {
      if (!period || typeof period !== 'object') return null;
      const startStr = period.startDate || period.start;
      const endStr = period.endDate || period.end || '';
      if (!startStr) return null;
      /** Decimal calendar year (e.g. Jan 2024 → 2024.0) so durations subtract cleanly */
      const nowYearFrac = () => {
        const d = new Date();
        return d.getFullYear() + d.getMonth() / 12;
      };
      const parseDate = (str) => {
        if (!str || typeof str !== 'string') return null;
        const s = str.trim().toLowerCase();
        if (s === 'present' || s === 'current' || s === 'now') return nowYearFrac();
        const mmYY = s.match(/^(\d{1,2})[/-](\d{4})$/);
        if (mmYY) return parseInt(mmYY[2], 10) + (parseInt(mmYY[1], 10) - 1) / 12;
        const yyyy = s.match(/^(\d{4})$/);
        if (yyyy) return parseInt(yyyy[1], 10);
        return null;
      };
      const start = parseDate(startStr);
      const endRaw = String(endStr).trim();
      const end =
        !endRaw
          ? nowYearFrac()
          : parseDate(endStr) ?? (endRaw.toLowerCase() === 'present' ? nowYearFrac() : null);
      if (start == null || end == null || end < start) return null;
      const years = Math.round((end - start) * 10) / 10;
      return years <= 0 ? null : years;
    };
    const tenureYearsArr = candidates
      .map((c) => {
        const workExp =
          Array.isArray(c.workExperience) && c.workExperience.length > 0
            ? c.workExperience
            : Array.isArray(c.structuredResumeText?.workExperience)
              ? c.structuredResumeText.workExperience
              : [];
        if (workExp.length > 0) {
          const tenures = workExp
            .map((we) => parseEmploymentPeriodToYears(we?.employmentPeriod))
            .filter((n) => n != null && n >= 0);
          if (tenures.length > 0) {
            const avg = tenures.reduce((a, b) => a + b, 0) / tenures.length;
            return Math.round(avg * 10) / 10;
          }
        }
        const exp = parseExpYears(c);
        const companies = Array.isArray(c.previousCompanies) ? c.previousCompanies : [];
        let numCompanies = companies.filter((x) => x != null && String(x).trim()).length;
        if (numCompanies < 1 && workExp.length > 0) {
          const withName = workExp.filter((we) => we?.companyName && String(we.companyName).trim()).length;
          numCompanies = withName > 0 ? withName : workExp.length;
        }
        if (numCompanies < 1 && c.company && String(c.company).trim()) {
          numCompanies = 1;
        }
        if (exp == null || exp < 0 || numCompanies < 1) return null;
        return Math.round((exp / numCompanies) * 10) / 10;
      })
      .filter((n) => n != null && n >= 0);
    const sortedTenure = [...tenureYearsArr].sort((a, b) => a - b);
    const tenureStats =
      sortedTenure.length > 0
        ? {
          avg:
            Math.round((sortedTenure.reduce((a, b) => a + b, 0) / sortedTenure.length) * 10) / 10,
          p25: Math.round(percentile(sortedTenure, 25) * 10) / 10,
          median: Math.round(percentile(sortedTenure, 50) * 10) / 10,
          p75: Math.round(percentile(sortedTenure, 75) * 10) / 10,
          count: sortedTenure.length,
        }
        : null;
    const expBucketLabels = ['0-2', '2-5', '5-10', '10-15', '15-20', '20+'];
    const expBucketRanges = [
      [0, 2],
      [2, 5],
      [5, 10],
      [10, 15],
      [15, 20],
      [20, 999],
    ];
    const experienceDistribution = expBucketRanges.map(([lo, hi], i) => {
      const count = expYearsArr.filter((y) => y >= lo && (hi === 999 ? true : y < hi)).length;
      return {
        years: expBucketLabels[i],
        count,
        fill: ['#007BFF', '#0d9488', '#7c3aed', '#ea580c', '#dc2626', '#4f46e5'][i] || '#007BFF',
      };
    });
    const bucketEducationLevel = (ed) => {
      // Fallback rule: when education is unknown/unclear, bucket into Bachelors
      // so insights do not surface a noisy "Other" category.
      if (!ed || typeof ed !== 'string') return 'Bachelors';
      const s = ed.toLowerCase().trim();
      if (/associate|diploma|certificate\b/i.test(s)) return 'Associates';
      if (/phd|ph\.d|doctorate|d\.?phil/i.test(s)) return 'Doctorates';
      if (/master|m\.?tech|m\.?s\b|msc|mba|m\.?a\b|m\.?sc\b/i.test(s)) return 'Masters';
      if (/bachelor|b\.?tech|b\.?e\b|bsc|ba\b|b\.?a\b|b\.?e\.|b\.?sc\b/i.test(s))
        return 'Bachelors';
      return 'Bachelors';
    };
    const EDUCATION_LEVELS = ['Associates', 'Bachelors', 'Masters', 'Doctorates'];
    const educationLevelCounts = {
      Associates: 0,
      Bachelors: 0,
      Masters: 0,
      Doctorates: 0,
    };
    candidates.forEach((c) => {
      const level =
        c.maxEducationLevel && EDUCATION_LEVELS.includes(c.maxEducationLevel)
          ? c.maxEducationLevel
          : bucketEducationLevel(c.education);
      if (educationLevelCounts[level] !== undefined) educationLevelCounts[level]++;
    });
    const educationLevelFunnel = [
      { level: 'Associates', count: educationLevelCounts.Associates, fill: '#22c55e' },
      { level: 'Bachelors', count: educationLevelCounts.Bachelors, fill: '#f97316' },
      { level: 'Masters', count: educationLevelCounts.Masters, fill: '#0ea5e9' },
      { level: 'Doctorates', count: educationLevelCounts.Doctorates, fill: '#ec4899' },
    ];
    const skillCounts = {};
    candidates.forEach((c) => {
      const skills = Array.isArray(c.skills) ? c.skills : [];
      skills.forEach((sk) => {
        const s = String(sk).trim();
        if (s) skillCounts[s] = (skillCounts[s] || 0) + 1;
      });
    });
    const topSkills = Object.entries(skillCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15)
      .map(([name, count]) => ({
        name,
        count,
        pct: total > 0 ? Math.round((count / total) * 100) : 0,
        fill: '#007BFF',
      }));
    const genderCounts = {
      male: 0,
      female: 0,
      non_binary: 0,
      other: 0,
      prefer_not_to_say: 0,
      unspecified: 0,
    };
    candidates.forEach((c) => {
      const g = (c.gender || '').toLowerCase().trim();
      // Worst-case fallback rule requested: map non-standard/other gender values to male.
      if (g === 'male' || g === 'm') {
        genderCounts.male++;
      } else if (g === 'female' || g === 'f') {
        genderCounts.female++;
      } else if (!g || g === 'other' || g === 'non_binary' || g === 'prefer_not_to_say') {
        genderCounts.male++;
      } else if (genderCounts[g] !== undefined) {
        genderCounts[g]++;
      } else {
        genderCounts.male++;
      }
    });
    const themeColors = {
      male: '#007BFF',
      female: '#ec4899',
      non_binary: '#8b5cf6',
      other: '#64748b',
      prefer_not_to_say: '#94a3b8',
      unspecified: '#cbd5e1',
    };
    const genderPie = Object.entries(genderCounts)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({
        name: name === 'unspecified' ? 'Not specified' : name.replace(/_/g, ' '),
        value,
        fill: themeColors[name] || '#94a3b8',
      }));
    const withLocation = candidates.filter((c) => c.location && String(c.location).trim());
    const locationCounts = {};
    withLocation.forEach((c) => {
      const loc = String(c.location).trim();
      locationCounts[loc] = (locationCounts[loc] || 0) + 1;
    });
    const topLocations = Object.entries(locationCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, count]) => ({
        name,
        count,
        pct: total > 0 ? Math.round((count / total) * 100) : 0,
        fill: '#007BFF',
      }));
    const industryCounts = {};
    candidates.forEach((c) => {
      const ind = Array.isArray(c.industries) ? c.industries : [];
      ind.forEach((name) => {
        const s = String(name).trim();
        if (s) industryCounts[s] = (industryCounts[s] || 0) + 1;
      });
    });
    const topIndustries = Object.entries(industryCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({
        name,
        count,
        pct: total > 0 ? Math.round((count / total) * 100) : 0,
        fill: '#0d9488',
      }));
    const withCtc = candidates.filter(
      (c) => c.currentCtcLacs != null && typeof c.currentCtcLacs === 'number'
    );
    const ctcLacs = withCtc.map((c) => c.currentCtcLacs);
    const ctcStats =
      ctcLacs.length > 0
        ? {
          min: Math.min(...ctcLacs),
          max: Math.max(...ctcLacs),
          avg: Math.round((ctcLacs.reduce((a, b) => a + b, 0) / ctcLacs.length) * 10) / 10,
          count: ctcLacs.length,
        }
        : null;
    const withExperience = candidates.filter((c) => c.experience && String(c.experience).trim());
    const withEducation = candidates.filter((c) => c.education && String(c.education).trim());
    const withNoticePeriod = candidates.filter(
      (c) => c.noticePeriod && String(c.noticePeriod).trim()
    );
    const withCurrentRole = candidates.filter((c) => c.currentRole && String(c.currentRole).trim());
    const withCompany = candidates.filter((c) => c.company && String(c.company).trim());
    const educationCounts = {};
    withEducation.forEach((c) => {
      const ed = String(c.education).trim().slice(0, 40);
      educationCounts[ed] = (educationCounts[ed] || 0) + 1;
    });
    const topEducation = Object.entries(educationCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, count]) => ({
        name: name.length >= 40 ? `${name}…` : name,
        count,
        pct: total > 0 ? Math.round((count / total) * 100) : 0,
        fill: '#0d9488',
      }));
    const noticeCounts = {};
    withNoticePeriod.forEach((c) => {
      const np = String(c.noticePeriod).trim();
      noticeCounts[np] = (noticeCounts[np] || 0) + 1;
    });
    const topNoticePeriod = Object.entries(noticeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, count]) => ({
        name,
        count,
        pct: total > 0 ? Math.round((count / total) * 100) : 0,
        fill: '#7c3aed',
      }));
    const roleCounts = {};
    withCurrentRole.forEach((c) => {
      const r = String(c.currentRole).trim().slice(0, 30);
      roleCounts[r] = (roleCounts[r] || 0) + 1;
    });
    const topRoles = Object.entries(roleCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, count]) => ({
        name: name.length >= 30 ? `${name}…` : name,
        count,
        pct: total > 0 ? Math.round((count / total) * 100) : 0,
        fill: '#ea580c',
      }));
    const profileCompletenessData = [
      { label: 'Experience', value: withExperience.length, fill: '#8b5cf6' },
      { label: 'Education', value: withEducation.length, fill: '#0d9488' },
      { label: 'Current role', value: withCurrentRole.length, fill: '#ea580c' },
      { label: 'Company', value: withCompany.length, fill: '#007BFF' },
      { label: 'Notice period', value: withNoticePeriod.length, fill: '#7c3aed' },
    ].filter((d) => d.value > 0);
    return {
      total,
      experienceStats,
      tenureStats,
      experienceDistribution,
      educationLevelFunnel,
      topSkills,
      topIndustries,
      genderPie,
      topLocations,
      ctcStats,
      topEducation,
      topNoticePeriod,
      topRoles,
      profileCompletenessData,
      withExperienceCount: withExperience.length,
      withEducationCount: withEducation.length,
      withNoticePeriodCount: withNoticePeriod.length,
      withCurrentRoleCount: withCurrentRole.length,
      withCompanyCount: withCompany.length,
    };
  }, [candidates]);
  const [scoreFilters, setScoreFilters] = useState({
    overall: { mode: null, value: null, min: null, max: null },
    resume: { mode: null, value: null, min: null, max: null },
    assessmentMin: null,
    interview: { mode: null, value: null, min: null, max: null },
    interviewPerQuestion: [], // [{ index: number, min: number }]
    status: null, // 'Completed' | 'In Progress' | 'Invited' | null
  });
  const [metaFilters, setMetaFilters] = useState({
    proctoring: null, // 'Compliant' | 'Flagged' | 'High Risk' | 'No data' | null
    experience: null, // bucket id | null
    age: { mode: null, value: null, min: null, max: null }, // numeric filter
    currentCtc: null, // bucket id | null
    noticePeriod: null, // string | null
    completedAtFrom: null, // YYYY-MM-DD | null
    completedAtTo: null, // YYYY-MM-DD | null
    education: [], // string[] (maxEducationLevel values)
    skills: [], // string[]
    location: [], // string[]
    company: [], // string[]
    currentRole: [], // string[]
    gender: [], // string[]
  });
  const filtersButtonRef = useRef(null);
  const [filtersModalContext, setFiltersModalContext] = useState('candidates'); // 'candidates' | 'shortlist'
  const activeFiltersCount = useMemo(() => {
    const isNumericFilterActive = (f) => {
      if (!f || !f.mode) return false;
      if (f.mode === 'between') return f.min != null && f.max != null;
      return f.value != null;
    };
    let n = 0;
    if (isStatusFilterActive(scoreFilters.status)) n += 1;
    if (isNumericFilterActive(scoreFilters.overall)) n += 1;
    if (isNumericFilterActive(scoreFilters.resume)) n += 1;
    if (scoreFilters.assessmentMin != null) n += 1;
    if (isNumericFilterActive(scoreFilters.interview)) n += 1;
    if (
      Array.isArray(scoreFilters.interviewPerQuestion) &&
      scoreFilters.interviewPerQuestion.some(
        (q) => q && typeof q.index === 'number' && q.min != null && Number.isFinite(Number(q.min))
      )
    ) {
      n += 1;
    }
    if (metaFilters.proctoring) n += 1;
    if (metaFilters.experience) n += 1;
    if (isNumericFilterActive(metaFilters.age)) n += 1;
    if (metaFilters.currentCtc) n += 1;
    if (metaFilters.noticePeriod) n += 1;
    if (metaFilters.completedAtFrom || metaFilters.completedAtTo) n += 1;
    if (metaFilters.education?.length > 0) n += 1;
    if (metaFilters.skills?.length > 0) n += 1;
    if (metaFilters.location?.length > 0) n += 1;
    if (metaFilters.company?.length > 0) n += 1;
    if (metaFilters.currentRole?.length > 0) n += 1;
    if (metaFilters.gender?.length > 0) n += 1;
    return n;
  }, [scoreFilters, metaFilters]);
  const getAssessmentPctNumber = useCallback((c) => {
    if (c.assessmentScore == null) return null;
    const score = c.assessmentScore;
    const totalPoints = c.assessmentTotalPoints;
    if (totalPoints && totalPoints > 0) {
      return Math.round((score / totalPoints) * 100);
    }
    return score;
  }, []);
  // Implementation moved to ./candidateSort so the public hiring-manager review
  // page ranks candidates identically. Behaviour here is unchanged.
  const getOverallScoreNumber = useCallback(
    (c) => getSharedOverallScoreNumber(c, screeningMeta, weightsConfig),
    [screeningMeta, weightsConfig]
  );
  const candidatesToShow = useMemo(() => {
    // Always exclude rejected, shortlisted, and dropped candidates from the main Candidates tab
    const filteredCandidates = candidates.filter((c) => !c.rejected && !c.shortlisted && !c.dropped);
    if (!isAISearchActive || aiSearchResults === null) return filteredCandidates;

    // Search results are a lean projection; render the loaded table row for each hit so
    // status/exclusion flags and columns come from the same source as the unfiltered view.
    const candidateById = new Map(
      filteredCandidates.map((candidate) => [String(candidate.id || candidate._id), candidate])
    );
    return aiSearchResults.flatMap((searchCandidate) => {
      const fullCandidate = candidateById.get(String(searchCandidate.id || searchCandidate._id));
      if (!fullCandidate) return [];
      return [
        {
          ...fullCandidate,
          aiSearchMatchReason: searchCandidate.aiSearchMatchReason,
          aiSearchRelevanceScore: searchCandidate.aiSearchRelevanceScore,
        },
      ];
    });
  }, [isAISearchActive, aiSearchResults, candidates]);
  const canBulkShortlist = selectedCandidatesForShortlist.length > 0 && !bulkShortlisting;
  const canBulkReject = selectedCandidatesForShortlist.length > 0 && !bulkRejecting;
  const canShortlistNextSteps = selectedShortlistCandidateIds.length > 0;
  const canBulkRemoveFromShortlist =
    selectedShortlistCandidateIds.length > 0 && !removingFromShortlist;
  const canBulkRestoreFromRejected =
    selectedRejectedCandidateIds.length > 0 && !restoringFromRejected;

  const queueDecisionEmailPreviews = (items) => {
    if (!decisionEmailsEnabled) return;
    const normalized = (Array.isArray(items) ? items : [items]).filter(
      (item) => item?.candidateId && ['selected', 'rejected', 'dropped'].includes(item.decision)
    );
    if (!normalized.length) return;
    setDecisionEmailModal(normalized[0]);
    setDecisionEmailQueue(normalized.slice(1));
  };

  const closeDecisionEmailPreview = () => {
    setDecisionEmailQueue((prev) => {
      const [next, ...rest] = prev;
      setDecisionEmailModal(next || null);
      return rest;
    });
  };

  // Go Colors: check whether the decision email can be sent for a candidate BEFORE moving
  // them to the new round. A blocked preview (e.g. no candidate email, or an LFS store with no
  // ARM) means we must NOT move the candidate — the recruiter has to fix it first. `noEmail`
  // means this decision sends nothing (LFS Rejected/Dropped): move without a preview. Only an
  // explicit block stops the move; transient/preview errors let the action proceed as before.
  const checkDecisionSendable = async (candidateId, decision) => {
    try {
      const res = await axios.get(
        `/api/store-decision-emails/${screeningId}/candidates/${candidateId}/decision-email/preview`,
        { params: { decision } }
      );
      return {
        blocked: !!res.data?.blocked,
        noEmail: res.data?.noEmail === true,
        reason: res.data?.reason || '',
      };
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.reason) {
        return { blocked: true, noEmail: false, reason: err.response.data.reason };
      }
      return { blocked: false, noEmail: false, reason: '' };
    }
  };

  // Partition candidate ids into: `sendable` (preview modal first, move on send), `direct`
  // (move now, no email — feature off, or a decision that sends nothing) and `blocked`.
  const partitionByDecisionSendable = async (ids, decision) => {
    if (!decisionEmailsEnabled) return { sendable: [], direct: ids, blocked: [] };
    const checks = await Promise.all(
      ids.map(async (candidateId) => ({
        candidateId,
        ...(await checkDecisionSendable(candidateId, decision)),
      }))
    );
    return {
      sendable: checks.filter((r) => !r.blocked && !r.noEmail).map((r) => r.candidateId),
      direct: checks.filter((r) => !r.blocked && r.noEmail).map((r) => r.candidateId),
      blocked: checks.filter((r) => r.blocked),
    };
  };

  // ── Burger King selection emails ──────────────────────────────────────────────────────
  // Mirrors the three Go Colors helpers above against the BK endpoint. Duplicated rather
  // than parameterised on purpose: these are two clients' outbound email flows, and a change
  // to one must not be able to alter the other's recipients.

  const queueBkDecisionEmailPreviews = (items) => {
    if (!bkDecisionEmailsEnabled) return;
    const normalized = (Array.isArray(items) ? items : [items]).filter((item) => item?.candidateId);
    if (!normalized.length) return;
    setBkDecisionEmailModal(normalized[0]);
    setBkDecisionEmailQueue(normalized.slice(1));
  };

  const closeBkDecisionEmailPreview = () => {
    setBkDecisionEmailQueue((prev) => {
      const [next, ...rest] = prev;
      setBkDecisionEmailModal(next || null);
      return rest;
    });
  };

  // Check the selection email can be sent BEFORE moving the candidate. Only an explicit
  // block (422) stops the move — a missing HRBP is a warning inside the modal, not a block,
  // so a candidate is never stranded because their region is unmapped.
  const checkBkDecisionSendable = async (candidateId) => {
    try {
      const res = await axios.get(
        `/api/bk-decision-emails/${screeningId}/candidates/${candidateId}/decision-email/preview`,
        { params: { decision: 'selected' } }
      );
      return { blocked: !!res.data?.blocked, reason: res.data?.reason || '' };
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.reason) {
        return { blocked: true, reason: err.response.data.reason };
      }
      return { blocked: false, reason: '' };
    }
  };

  const partitionByBkDecisionSendable = async (ids) => {
    if (!bkDecisionEmailsEnabled) return { sendable: ids, blocked: [] };
    const checks = await Promise.all(
      ids.map(async (candidateId) => ({
        candidateId,
        ...(await checkBkDecisionSendable(candidateId)),
      }))
    );
    return {
      sendable: checks.filter((r) => !r.blocked).map((r) => r.candidateId),
      blocked: checks.filter((r) => r.blocked),
    };
  };

  /** Read the configured onboarding doc types from localStorage (set by the
   *  "Configure Onboarding Documents" modal in JobScreeningsPage).
   *  Returns an array of doc objects, or null if not configured. */
  const getOnboardingDocTypes = () => {
    if (!jobId) return null;
    try {
      const raw = localStorage.getItem(`onboarding_doc_config_${jobId}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch {
      return null;
    }
  };

  const toggleShortlist = async (candidateId) => {
    const c = candidates.find((x) => (x.id || x._id) === candidateId);
    if (!c) return;
    const nextShortlisted = !c.shortlisted;
    // Go Colors: gate on sendability, then defer the status change until the email is sent.
    const selectCheck =
      nextShortlisted && decisionEmailsEnabled
        ? await checkDecisionSendable(candidateId, 'selected')
        : null;
    if (selectCheck?.blocked) {
      toast.error(selectCheck.reason || "This email can't be sent yet — fix the store first.");
      return;
    }
    if (selectCheck && !selectCheck.noEmail) {
      queueDecisionEmailPreviews({
        candidateId,
        decision: 'selected',
        candidateName: c.name || '',
        onSent: async () => {
          const onboardingDocTypes = moveToOnboarding ? getOnboardingDocTypes() : undefined;
          await axios.patch(`/api/screenings/${screeningId}/candidates/${candidateId}`, {
            shortlisted: true,
            ...(moveToOnboarding && { onboardingQuarter: 'Q1' }),
            ...(onboardingDocTypes && { onboardingDocTypes }),
          });
          setCandidates((prev) =>
            prev.map((x) =>
              (x.id || x._id) === candidateId ? { ...x, shortlisted: true } : x
            )
          );
        },
      });
      return;
    }
    // Burger King: same shape, its own endpoint and its own modal. Selected only.
    if (nextShortlisted && bkDecisionEmailsEnabled) {
      const { blocked, reason } = await checkBkDecisionSendable(candidateId);
      if (blocked) {
        toast.error(reason || "This email can't be sent yet.");
        return;
      }
      queueBkDecisionEmailPreviews({
        candidateId,
        candidateName: c.name || '',
        onSent: async () => {
          const onboardingDocTypes = moveToOnboarding ? getOnboardingDocTypes() : undefined;
          await axios.patch(`/api/screenings/${screeningId}/candidates/${candidateId}`, {
            shortlisted: true,
            ...(moveToOnboarding && { onboardingQuarter: 'Q1' }),
            ...(onboardingDocTypes && { onboardingDocTypes }),
          });
          setCandidates((prev) =>
            prev.map((x) => ((x.id || x._id) === candidateId ? { ...x, shortlisted: true } : x))
          );
        },
      });
      return;
    }
    try {
      const onboardingDocTypes = moveToOnboarding ? getOnboardingDocTypes() : undefined;
      await axios.patch(`/api/screenings/${screeningId}/candidates/${candidateId}`, {
        shortlisted: nextShortlisted,
        ...(moveToOnboarding && { onboardingQuarter: 'Q1' }),
        ...(onboardingDocTypes && { onboardingDocTypes }),
      });
      setCandidates((prev) =>
        prev.map((x) =>
          (x.id || x._id) === candidateId ? { ...x, shortlisted: nextShortlisted } : x
        )
      );
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update selection');
    }
  };
  const bulkShortlistCandidates = async () => {
    if (!selectedCandidatesForShortlist.length) return;
    setBulkShortlisting(true);
    try {
      // Burger King: only move candidates whose selection email can be prepared, then defer
      // each status change until their email is actually sent. Returns early so the Go Colors
      // path below is never entered — the two flows never mix.
      if (bkDecisionEmailsEnabled) {
        const { sendable, blocked } = await partitionByBkDecisionSendable(
          selectedCandidatesForShortlist
        );
        if (!sendable.length) {
          toast.error(
            blocked[0]?.reason || 'No candidates could be moved — their selection email cannot be sent.'
          );
          return;
        }
        if (blocked.length) {
          toast.error(`${blocked.length} skipped — selection email could not be prepared.`);
        }
        queueBkDecisionEmailPreviews(
          sendable.map((candidateId) => {
            const c = candidates.find((x) => (x.id || x._id) === candidateId);
            return {
              candidateId,
              candidateName: c?.name || '',
              onSent: async () => {
                const onboardingDocTypes = moveToOnboarding ? getOnboardingDocTypes() : undefined;
                await axios.patch(`/api/screenings/${screeningId}/candidates/${candidateId}`, {
                  shortlisted: true,
                  ...(moveToOnboarding && { onboardingQuarter: 'Q1' }),
                  ...(onboardingDocTypes && { onboardingDocTypes }),
                });
                setCandidates((prev) =>
                  prev.map((x) =>
                    (x.id || x._id) === candidateId ? { ...x, shortlisted: true } : x
                  )
                );
              },
            };
          })
        );
        setSelectedCandidatesForShortlist([]);
        return;
      }

      // Go Colors: only move candidates whose decision email can be sent; skip blocked ones.
      const { sendable: ids, direct, blocked } = await partitionByDecisionSendable(
        selectedCandidatesForShortlist,
        'selected'
      );
      if (!ids.length && !direct.length) {
        toast.error(
          blocked[0]?.reason || 'No candidates could be moved — fix their stores first.'
        );
        return;
      }
      if (blocked.length) {
        toast.error(`${blocked.length} skipped — ${blocked[0].reason || 'decision email could not be prepared.'}`);
      }
      if (direct.length) {
        const onboardingDocTypes = moveToOnboarding ? getOnboardingDocTypes() : undefined;
        await Promise.all(
          direct.map((candidateId) =>
            axios.patch(`/api/screenings/${screeningId}/candidates/${candidateId}`, {
              shortlisted: true,
              ...(moveToOnboarding && { onboardingQuarter: 'Q1' }),
              ...(onboardingDocTypes && { onboardingDocTypes }),
            })
          )
        );
        setCandidates((prev) =>
          prev.map((x) =>
            direct.includes(x.id || x._id) ? { ...x, shortlisted: true } : x
          )
        );
        toast.success(`Moved ${direct.length} candidate${direct.length === 1 ? '' : 's'} to selected`);
      }
      // Go Colors: defer each candidate's status change until their decision email is sent.
      if (ids.length) {
        queueDecisionEmailPreviews(
          ids.map((candidateId) => {
            const c = candidates.find((x) => (x.id || x._id) === candidateId);
            return {
              candidateId,
              decision: 'selected',
              candidateName: c?.name || '',
              onSent: async () => {
                const onboardingDocTypes = moveToOnboarding ? getOnboardingDocTypes() : undefined;
                await axios.patch(`/api/screenings/${screeningId}/candidates/${candidateId}`, {
                  shortlisted: true,
                  ...(moveToOnboarding && { onboardingQuarter: 'Q1' }),
                  ...(onboardingDocTypes && { onboardingDocTypes }),
                });
                setCandidates((prev) =>
                  prev.map((x) =>
                    (x.id || x._id) === candidateId ? { ...x, shortlisted: true } : x
                  )
                );
              },
            };
          })
        );
      }
      setSelectedCandidatesForShortlist([]);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update selection');
    } finally {
      setBulkShortlisting(false);
    }
  };

  const handleMarkDropped = async (candidateId, candidateName) => {
    try {
      const dropCheck = decisionEmailsEnabled
        ? await checkDecisionSendable(candidateId, 'dropped')
        : null;
      if (dropCheck?.blocked) {
        toast.error(dropCheck.reason || "This email can't be sent yet — fix the store first.");
        return;
      }
      if (dropCheck && !dropCheck.noEmail) {
        queueDecisionEmailPreviews([{
          candidateId,
          decision: 'dropped',
          candidateName,
          onSent: async () => {
            await axios.patch(
              `/api/screenings/${screeningId}/candidates/${candidateId}`,
              { dropped: true }
            );
            setCandidates((prev) => prev.map((x) => (x._id === candidateId || x.id === candidateId ? { ...x, dropped: true, shortlisted: false, rejected: false } : x)));
          }
        }]);
        return;
      }
      await axios.patch(
        `/api/screenings/${screeningId}/candidates/${candidateId}`,
        { dropped: true }
      );
      setCandidates((prev) => prev.map((x) => (x._id === candidateId || x.id === candidateId ? { ...x, dropped: true, shortlisted: false, rejected: false } : x)));
      toast.success('Candidate marked as dropped');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to mark candidate as dropped');
    }
  };

  // ── Move to another job post (Go Colors) ─────────────────────────────────────────────
  // Opening the modal only stages the candidate; the move itself is confirmed inside it,
  // because the eligible targets and any duplicate clash are resolved server-side first.
  const handleOpenMoveJobPost = (candidate) => {
    setCandidateToMoveJobPost(candidate);
    setShowMoveJobPostModal(true);
  };

  // A moved candidate leaves this screening, so drop the row rather than patching it.
  const handleCandidateMoved = ({ candidateId, targetJobTitle }) => {
    setCandidates((prev) =>
      prev.filter((x) => x._id !== candidateId && x.id !== candidateId)
    );
    setCandidateToMoveJobPost(null);
    toast.success(`Candidate moved to "${targetJobTitle}"`);
  };

  const handleUndoMoveJobPost = async (candidate) => {
    const candidateId = candidate?._id || candidate?.id;
    if (!candidateId) return;
    try {
      const res = await axios.post(
        `/api/screenings/${screeningId}/candidates/${candidateId}/job-post-move/undo`
      );
      setCandidates((prev) =>
        prev.filter((x) => x._id !== candidateId && x.id !== candidateId)
      );
      toast.success(res.data?.message || 'Candidate moved back');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to move this candidate back');
    }
  };

  const handleBulkMarkDropped = async () => {
    if (!selectedCandidatesForShortlist.length) return;
    setBulkShortlisting(true);
    try {
      const { sendable, direct: ids, blocked } = await partitionByDecisionSendable(
        selectedCandidatesForShortlist,
        'dropped'
      );
      if (!sendable.length && !ids.length) {
        toast.error(blocked[0]?.reason || 'No candidates could be marked as dropped.');
        return;
      }
      if (blocked.length) {
        toast.error(`${blocked.length} skipped — ${blocked[0].reason || 'decision email could not be prepared.'}`);
      }
      if (sendable.length) {
        queueDecisionEmailPreviews(
          sendable.map((candidateId) => {
            const c = candidates.find((x) => (x.id || x._id) === candidateId);
            return {
              candidateId,
              decision: 'dropped',
              candidateName: c?.firstName || c?.name || '',
              onSent: async () => {
                await axios.patch(
                  `/api/screenings/${screeningId}/candidates/${candidateId}`,
                  { dropped: true }
                );
                setCandidates((prev) => prev.map((x) => ((x.id || x._id) === candidateId ? { ...x, dropped: true, shortlisted: false, rejected: false } : x)));
              }
            };
          })
        );
      }
      if (ids.length) {
        await Promise.all(
          ids.map((candidateId) =>
            axios.patch(`/api/screenings/${screeningId}/candidates/${candidateId}`, { dropped: true })
          )
        );
        setCandidates((prev) => prev.map((x) => (ids.includes(x.id || x._id) ? { ...x, dropped: true, shortlisted: false, rejected: false } : x)));
        toast.success(`Marked ${ids.length} candidate${ids.length === 1 ? '' : 's'} as dropped`);
      }
      setSelectedCandidatesForShortlist([]);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to mark candidates as dropped');
    } finally {
      setBulkShortlisting(false);
    }
  };

  const bulkRemoveFromShortlist = async () => {
    if (!selectedShortlistCandidateIds.length) return;
    setRemovingFromShortlist(true);
    const ids = selectedShortlistCandidateIds;
    try {
      await Promise.all(
        ids.map((candidateId) =>
          axios.patch(`/api/screenings/${screeningId}/candidates/${candidateId}`, {
            shortlisted: false,
          })
        )
      );
      setCandidates((prev) =>
        prev.map((x) => (ids.includes(x.id || x._id) ? { ...x, shortlisted: false } : x))
      );
      toast.success(`Removed ${ids.length} candidate${ids.length === 1 ? '' : 's'} from selected`);
      setSelectedShortlistCandidateIds([]);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update selection');
    } finally {
      setRemovingFromShortlist(false);
    }
  };
  const patchCandidateShortlistField = async (candidateId, fields) => {
    try {
      const res = await axios.patch(`/api/screenings/${screeningId}/candidates/${candidateId}`, fields);
      const updatedCandidate = res.data?.candidate || {};
      setCandidates((prev) =>
        prev.map((x) =>
          (x.id || x._id) === candidateId
            ? { ...x, ...fields, storeDetails: updatedCandidate.storeDetails }
            : x
        )
      );
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
      return false;
    }
  };
  const handleRemoveFromShortlistRow = async (candidate) => {
    const candidateId = candidate?.id || candidate?._id;
    if (!candidateId) return;
    try {
      await toggleShortlist(candidateId);
      setSelectedShortlistCandidateIds((prev) =>
        prev.filter((id) => id !== candidateId)
      );
      toast.success('Candidate removed from selected');
    } catch (e) {
      // toggleShortlist already shows an error toast on failure
    }
  };
  // Mirror the note the server appends on a reject so the Notes cell flips to "View/Edit"
  // without a refetch. Only for candidates moved right away — a deferred decision-email move
  // writes its note once the email is sent.
  const mirrorRejectNote = (ids, reason) => {
    if (!reason) return;
    setShortlistNotes((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[String(id)] = `Rejected: ${reason}`;
      });
      return next;
    });
  };
  // Returns true when the candidate's rejection state was updated (or handed off to the
  // decision-email flow), false when it failed — the reject modal stays open on false.
  const toggleReject = async (candidateId, reason = '') => {
    const c = candidates.find((x) => (x.id || x._id) === candidateId);
    if (!c) return false;
    const nextRejected = !c.rejected;
    // Only sent when actually rejecting — restoring a candidate never carries a reason.
    const trimmedReason = nextRejected ? String(reason || '').trim() : '';
    // Go Colors: gate on sendability, then defer the status change until the email is sent.
    const rejectCheck =
      nextRejected && decisionEmailsEnabled
        ? await checkDecisionSendable(candidateId, 'rejected')
        : null;
    if (rejectCheck?.blocked) {
      toast.error(rejectCheck.reason || "This email can't be sent yet — fix the store first.");
      return false;
    }
    if (rejectCheck && !rejectCheck.noEmail) {
      queueDecisionEmailPreviews({
        candidateId,
        decision: 'rejected',
        candidateName: c.name || '',
        onSent: async () => {
          await axios.patch(
            `/api/screenings/${screeningId}/candidates/${candidateId}`,
            buildRejectPayload(true, trimmedReason)
          );
          setCandidates((prev) =>
            prev.map((x) =>
              (x.id || x._id) === candidateId
                ? { ...x, rejected: true, shortlisted: false }
                : x
            )
          );
        },
      });
      return true;
    }
    try {
      await axios.patch(
        `/api/screenings/${screeningId}/candidates/${candidateId}`,
        buildRejectPayload(nextRejected, trimmedReason)
      );
      setCandidates((prev) =>
        prev.map((x) =>
          (x.id || x._id) === candidateId
            ? { ...x, rejected: nextRejected, ...(nextRejected ? { shortlisted: false } : {}) }
            : x
        )
      );
      mirrorRejectNote([candidateId], trimmedReason);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update rejection');
      return false;
    }
  };
  // Returns true when the bulk rejection went through, false when it failed — the reject
  // modal stays open on false so the recruiter doesn't lose the reason they typed.
  const bulkRejectCandidates = async (reason = '') => {
    if (!selectedCandidatesForShortlist.length) return false;
    // One shared reason is applied to every candidate in the batch.
    const trimmedReason = String(reason || '').trim();
    setBulkRejecting(true);
    try {
      // Go Colors: only move candidates whose decision email can be sent; skip blocked ones.
      const { sendable: ids, direct, blocked } = await partitionByDecisionSendable(
        selectedCandidatesForShortlist,
        'rejected'
      );
      if (!ids.length && !direct.length) {
        toast.error(
          blocked[0]?.reason || 'No candidates could be moved — fix their stores first.'
        );
        return false;
      }
      if (blocked.length) {
        toast.error(`${blocked.length} skipped — ${blocked[0].reason || 'decision email could not be prepared.'}`);
      }
      if (direct.length) {
        await Promise.all(
          direct.map((candidateId) =>
            axios.patch(
              `/api/screenings/${screeningId}/candidates/${candidateId}`,
              buildRejectPayload(true, trimmedReason)
            )
          )
        );
        setCandidates((prev) =>
          prev.map((x) =>
            direct.includes(x.id || x._id)
              ? { ...x, rejected: true, shortlisted: false }
              : x
          )
        );
        mirrorRejectNote(direct, trimmedReason);
        toast.success(`Moved ${direct.length} candidate${direct.length === 1 ? '' : 's'} to rejected`);
      }
      // Go Colors: defer each candidate's status change until their decision email is sent.
      if (ids.length) {
        queueDecisionEmailPreviews(
          ids.map((candidateId) => {
            const c = candidates.find((x) => (x.id || x._id) === candidateId);
            return {
              candidateId,
              decision: 'rejected',
              candidateName: c?.name || '',
              onSent: async () => {
                await axios.patch(
                  `/api/screenings/${screeningId}/candidates/${candidateId}`,
                  buildRejectPayload(true, trimmedReason)
                );
                setCandidates((prev) =>
                  prev.map((x) =>
                    (x.id || x._id) === candidateId
                      ? { ...x, rejected: true, shortlisted: false }
                      : x
                  )
                );
              },
            };
          })
        );
      }
      setSelectedCandidatesForShortlist([]);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to reject candidates');
      return false;
    } finally {
      setBulkRejecting(false);
    }
  };
  // Closing always clears the typed reason so the modal never reopens pre-filled.
  const closeRejectModal = () => {
    setShowRejectConfirmModal(false);
    setCandidateToReject(null);
    setRejectReason('');
    setRejectReasonError('');
    setBkRejectDropdownReason('');
    setBkRejectRemarks('');
  };
  const confirmRejectFromModal = async () => {
    let trimmedReason = '';
    if (isBurgerKing) {
      const trimmedRemarks = bkRejectRemarks.trim();
      if (rejectionReasonRequired && (!bkRejectDropdownReason || !trimmedRemarks)) {
        setRejectReasonError('Please select a reason and enter remarks.');
        return;
      }
      trimmedReason = `${bkRejectDropdownReason} - ${trimmedRemarks}`;
      if (trimmedReason.length > MAX_REJECT_REASON_LENGTH) {
        setRejectReasonError(`Reason too long (max ${MAX_REJECT_REASON_LENGTH} characters).`);
        return;
      }
    } else {
      trimmedReason = rejectReason.trim();
      if (rejectionReasonRequired && !trimmedReason) {
        setRejectReasonError('Please enter a reason for rejecting.');
        return;
      }
    }
    setRejectReasonError('');
    if (candidateToReject) {
      const cid = candidateToReject.id || candidateToReject._id;
      const ok = await toggleReject(cid, trimmedReason);
      if (ok === false) return;
      toast.success('Candidate rejected');
    } else {
      const ok = await bulkRejectCandidates(trimmedReason);
      if (ok === false) return;
    }
    closeRejectModal();
  };
  const handleCallCandidateConfirm = async () => {
    if (!candidateToCall) return;
    const candidateId = candidateToCall.id || candidateToCall._id;
    setCallingCandidate(true);
    const toastId = toast.loading(`Initiating call to ${candidateToCall.name || 'candidate'}...`);
    try {
      await axios.post(`/api/ai-interviews/candidate/${candidateId}/call`);
      toast.success('Call placed successfully!', { id: toastId });
      setShowCallConfirmModal(false);
      setCandidateToCall(null);
      void fetchCandidates({ quiet: true });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to place call', { id: toastId });
    } finally {
      setCallingCandidate(false);
    }
  };
  const handleRecallQualifyingConfirm = async () => {
    if (!candidateToRecall) return;
    const candidateId = candidateToRecall.id || candidateToRecall._id;
    setRecallingCandidate(true);
    try {
      const { data } = await axios.post(`/api/screenings/${screeningId}/qualifying-agent-recall/${candidateId}`);
      toast.success(`${data.message}${data.maxAttempts ? ` (up to ${data.maxAttempts} calls)` : ''}`);
      setCandidateToRecall(null);
      void fetchCandidates({ forceRefresh: true, quiet: true });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Recall failed');
    } finally {
      setRecallingCandidate(false);
    }
  };
  const bulkRestoreFromRejected = async () => {

    if (!selectedRejectedCandidateIds.length) return;
    setRestoringFromRejected(true);
    const ids = selectedRejectedCandidateIds;
    try {
      await Promise.all(
        ids.map((candidateId) =>
          axios.patch(`/api/screenings/${screeningId}/candidates/${candidateId}`, {
            rejected: false,
          })
        )
      );
      setCandidates((prev) =>
        prev.map((x) => (ids.includes(x.id || x._id) ? { ...x, rejected: false } : x))
      );
      toast.success(`Restored ${ids.length} candidate${ids.length === 1 ? '' : 's'}`);
      setSelectedRejectedCandidateIds([]);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to restore candidates');
    } finally {
      setRestoringFromRejected(false);
    }
  };
  const handleRestoreFromRejectedRow = async (candidate) => {
    const candidateId = candidate?.id || candidate?._id;
    if (!candidateId) return;
    // Restoring never needs a rejection reason — toggleReject reports failure via toast.
    const ok = await toggleReject(candidateId);
    if (ok === false) return;
    setSelectedRejectedCandidateIds((prev) => prev.filter((id) => id !== candidateId));
    toast.success('Candidate restored');
  };

  const handleRestoreFromDroppedRow = async (candidate) => {
    const candidateId = candidate?.id || candidate?._id;
    if (!candidateId) return;
    try {
      await axios.patch(`/api/screenings/${screeningId}/candidates/${candidateId}`, { dropped: false });
      setCandidates((prev) => prev.map((x) => (x._id === candidateId || x.id === candidateId ? { ...x, dropped: false } : x)));
      setSelectedDroppedCandidateIds((prev) => prev.filter((id) => id !== candidateId));
      toast.success('Candidate restored from dropped status');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to restore candidate');
    }
  };
  const handleDownloadSelectedResumes = useCallback(async () => {
    if (!screeningId || selectedCandidatesForShortlist.length === 0) return;
    setDownloadingAllResumes(true);
    const toastId = toast.loading(
      `Preparing ${selectedCandidatesForShortlist.length} resume${selectedCandidatesForShortlist.length === 1 ? '' : 's'} for download… This may take a moment.`
    );
    try {
      const idsParam = selectedCandidatesForShortlist.join(',');
      const res = await axios.get(
        `/api/screenings/${screeningId}/download-resumes?candidateIds=${encodeURIComponent(idsParam)}`,
        { responseType: 'blob' }
      );
      const blob = res.data;
      const contentType = res.headers['content-type'] || '';
      if (!blob || blob.size === 0) {
        toast.error('No resumes available to download', { id: toastId });
        return;
      }
      if (contentType.includes('application/json')) {
        const text = await blob.text();
        let msg = 'Download failed';
        try {
          const json = JSON.parse(text);
          msg = json.message || msg;
        } catch (_) { }
        toast.error(msg, { id: toastId });
        return;
      }
      const disposition = res.headers['content-disposition'];
      let filename = 'resumes.zip';
      if (disposition) {
        const match = /filename="?([^";\n]+)"?/.exec(disposition);
        if (match) filename = match[1].trim();
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(
        `${selectedCandidatesForShortlist.length} resume${selectedCandidatesForShortlist.length === 1 ? '' : 's'} downloaded successfully!`,
        { id: toastId }
      );
    } catch (err) {
      let message = err.response?.data?.message ?? err.message ?? 'Failed to download resumes';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          message = json.message || message;
        } catch (_) { }
      }
      toast.error(message, { id: toastId });
    } finally {
      setDownloadingAllResumes(false);
    }
  }, [screeningId, selectedCandidatesForShortlist]);
  const handlePhoneChange = (rowId, rawValue) => {
    const pureDigits = rawValue.replace(/[^\d]/g, '');
    let suffix = '';
    if (pureDigits.startsWith('91')) {
      suffix = pureDigits.slice(2, 12);
    } else {
      suffix = pureDigits.slice(0, 10);
    }
    setSingleInviteRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, mobile: '+91' + suffix } : r))
    );
  };
  const handleSingleInvite = async (e) => {
    e.preventDefault();
    const isWhatsAppOnlySelected = !inviteChannels.email && inviteChannels.whatsapp;
    const rowsToSubmit = singleInviteRows
      .map((row) => {
        const cleanEmail = row.email.trim();
        const cleanMobile = row.mobile === '+91' ? '' : (row.mobile || '').trim();
        let finalEmail = cleanEmail;
        if (!cleanEmail && isWhatsAppOnlySelected && cleanMobile) {
          finalEmail = `${cleanMobile.replace(/[^\d]/g, '')}@no-email.com`;
        }
        return {
          ...row,
          email: finalEmail,
          firstName: row.firstName.trim(),
          lastName: row.lastName.trim(),
          mobile: cleanMobile,
          id: `temp-${Math.random().toString(36).substring(2, 9)}`,
        };
      })
      .filter((row) => row.email);
    if (!rowsToSubmit.length) return;

    // Defer channel invites until the email modal "Send" is clicked
    if (inviteChannels.whatsapp) {
      setPendingChannelInvites({ rows: rowsToSubmit, channels: { ...inviteChannels } });
    }
    setSingleInviteSubmitting(true);
    try {
      const createdCandidates = rowsToSubmit.map((r) => ({
        id: r.id,
        _id: r.id,
        name: [r.firstName, r.lastName].filter(Boolean).join(' ').trim() || r.email.split('@')[0],
        email: r.email,
        firstName: r.firstName,
        lastName: r.lastName,
        mobile: r.mobile,
        storeId: r.storeId || undefined,
      }));

      setSingleInviteRows([{ id: 1, firstName: '', lastName: '', email: '', mobile: '+91', storeId: '' }]);
      setIsInviteOpen(false);
      setInviteStep(1);
      setInviteMode(null);
      setInviteChannels({ email: true, whatsapp: false });

      // Open the email modal immediately passing provisional candidates
      setBulkUploadEmailCandidates(createdCandidates);
      setEmailModalContext('invite');
      setShowSendEmailToCandidateModal(true);

      // Resolve any saved invite email draft
      setEmailDraftForSend(null);
      try {
        const draftRes = await axios.get(`/api/screenings/${screeningId}/email-drafts`, {
          params: { context: 'invite' },
        });
        if (draftRes.data?.draft) setEmailDraftForSend(draftRes.data.draft);
      } catch (_) { }
    } catch (err) {
      toast.error('Failed to prepare candidates');
    } finally {
      setSingleInviteSubmitting(false);
    }
  };
  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    const isWhatsAppOnlySelected = !inviteChannels.email && inviteChannels.whatsapp;
    try {
      const formData = new FormData();
      formData.append('candidateList', bulkFile);
      const res = await axios.post(`/api/screenings/${screeningId}/upload-candidates?isWhatsAppOnly=${isWhatsAppOnlySelected}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { candidatesCreated, duplicatesSkipped, skippedNoEmail, createdCandidates, isResumeOnlyScreening, errors } =
        res.data;
      const parts = [`${candidatesCreated} added`];
      if (duplicatesSkipped) parts.push(`${duplicatesSkipped} duplicates skipped`);
      if (skippedNoEmail) parts.push(`${skippedNoEmail} skipped (no/invalid email)`);
      toast.success(`Uploaded: ${parts.join(', ')}`);
      if (skippedNoEmail && Array.isArray(errors) && errors.length && !isResumeOnlyScreening) {
        const items = errors.map((e) => {
          const s = String(e || '');
          const idx = s.indexOf(': ');
          return idx > 0
            ? { label: s.slice(0, idx).trim(), reason: s.slice(idx + 2).trim() }
            : { label: s.trim(), reason: 'no valid email' };
        });
        setSkippedNoEmailItems(items);
        setShowSkippedNoEmailModal(true);
      }
      setBulkFile(null);
      setIsInviteOpen(false);
      setInviteStep(1);
      setInviteMode(null);
      if (candidatesCreated > 0) {
        void fetchCandidates({ forceRefresh: true, quiet: true });
      }
      if (Array.isArray(createdCandidates) && createdCandidates.length) {
        // Defer channel invites until the email modal "Send" is clicked
        if (inviteChannels.whatsapp) {
          setPendingChannelInvites({ rows: createdCandidates, channels: { ...inviteChannels } });
        }
      }
      const archiveName = bulkFile?.name?.toLowerCase() || '';
      const isArchiveUpload = archiveName.endsWith('.zip') || archiveName.endsWith('.rar');
      if (isResumeOnlyScreening && isArchiveUpload) {
        if (candidatesCreated > 0) {
          toast.success('Resumes are being scored. Results will appear shortly.');
        }
      } else if (Array.isArray(createdCandidates) && createdCandidates.length) {
        setEmailDraftForSend(null);
        try {
          const draftRes = await axios.get(`/api/screenings/${screeningId}/email-drafts`, {
            params: { context: 'invite' },
          });
          if (draftRes.data?.draft) setEmailDraftForSend(draftRes.data.draft);
        } catch (_) { }
        setBulkUploadEmailCandidates(createdCandidates);
        setShowSendEmailToCandidateModal(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };
  const downloadBulkTemplate = (format) => {
    const isWhatsAppOnlySelected = !inviteChannels.email && inviteChannels.whatsapp;
    let headers = ['First name', 'Last name', 'Email'];
    let sampleRows = [
      ['Gladys', 'Gorilla', 'g.gorilla@emailprovider.com'],
      ['Taylor', 'Patel', 'taylor-patel@emailprovider.com'],
    ];

    if (isWhatsAppOnlySelected) {
      headers = ['First name', 'Last name', 'Email (Optional)', 'Mobile'];
      sampleRows = [
        ['Gladys', 'Gorilla', '', '9876543210'],
        ['Taylor', 'Patel', 'taylor-patel@emailprovider.com', '9876543211'],
      ];
    } else if (inviteChannels.whatsapp) {
      headers = ['First name', 'Last name', 'Email', 'Mobile'];
      sampleRows = [
        ['Gladys', 'Gorilla', 'g.gorilla@emailprovider.com', '9876543210'],
        ['Taylor', 'Patel', 'taylor-patel@emailprovider.com', '9876543211'],
      ];
    }

    if (user?.features?.store_list === true) {
      // Sample codes in this job's scheme (1101 for EBO, LF-PT-PV01 for LFS), real ones when loaded.
      const sampleStoreCodes = jobStores.length
        ? jobStores.slice(0, sampleRows.length).map((store) => store.storeId)
        : [getStoreCodeRule(screeningMeta?.storeFormat).example];
      headers.push('Store Code');
      sampleRows.forEach((row, i) => row.push(sampleStoreCodes[i % sampleStoreCodes.length]));
    }

    if (format === 'csv') {
      const csvContent = [headers.join(','), ...sampleRows.map((r) => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'candidates_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
      XLSX.writeFile(wb, 'candidates_template.xlsx');
    }
  };
  const shortlistCandidates = candidates.filter((c) => c.shortlisted);
  const hiringManagerShareCandidates = useMemo(() => {
    if (hiringManagerShareCandidateIds.length === 0) return [];
    const wanted = new Set(hiringManagerShareCandidateIds.map((id) => String(id)));
    return candidates.filter((c) => wanted.has(String(c.id || c._id)));
  }, [candidates, hiringManagerShareCandidateIds]);
  const rejectedCandidates = candidates.filter((c) => c.rejected);
  const droppedCandidates = candidates.filter((c) => c.dropped);
  useEffect(() => {
    if (!screeningId) return;
    let cancelled = false;
    const fetchDocumentStatus = async () => {
      try {
        const res = await axios.get(`/api/documents/screening/${screeningId}`);
        if (!cancelled) {
          setDocumentStatusMap(res.data || {});
        }
      } catch (_) {
        if (!cancelled) {
          setDocumentStatusMap({});
        }
      }
    };
    fetchDocumentStatus();
    return () => {
      cancelled = true;
    };
  }, [screeningId, shortlistCandidates.length, candidates.map(c => c.status).join(','), documentStatusRefreshKey]);
  const parseExperienceYearsForFilter = useCallback((c) => {
    if (c?.experienceYears != null && typeof c.experienceYears === 'number' && !Number.isNaN(c.experienceYears)) {
      return c.experienceYears;
    }
    const str = c?.experience;
    if (!str || typeof str !== 'string') return null;
    const s = str.trim();
    const rangeMatch = s.match(/(\d+(?:\.\d+)?)\s*[-–to]+\s*(\d+(?:\.\d+)?)/i);
    if (rangeMatch) {
      const a = parseFloat(rangeMatch[1], 10);
      const b = parseFloat(rangeMatch[2], 10);
      if (!Number.isNaN(a) && !Number.isNaN(b)) return (a + b) / 2;
    }
    const singleMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:years?|yoe)?/i);
    const n = singleMatch ? parseFloat(singleMatch[1], 10) : null;
    return n != null && !Number.isNaN(n) ? n : null;
  }, []);
  const parseAgeYearsForFilter = useCallback((c) => {
    const raw = c?.ageYears ?? c?.age;
    if (raw == null) return null;
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw).trim());
    return n != null && !Number.isNaN(n) ? n : null;
  }, []);
  const parseCurrentCtcLacsForFilter = useCallback((c) => {
    if (c?.currentCtcLacs != null && typeof c.currentCtcLacs === 'number' && !Number.isNaN(c.currentCtcLacs)) {
      return c.currentCtcLacs;
    }
    const raw = c?.currentCtc;
    if (!raw) return null;
    const s = String(raw).toLowerCase();
    const numMatch = s.match(/(\d+(?:\.\d+)?)/);
    if (!numMatch) return null;
    const n = parseFloat(numMatch[1]);
    return !Number.isNaN(n) ? n : null;
  }, []);
  const filterRowsWith = useCallback(
    (rows, scoreF, metaF) => {
      const { overall, resume, assessmentMin, interview, interviewPerQuestion, status } = scoreF || {};
      const {
        proctoring, experience, age, currentCtc, noticePeriod, completedAtFrom, completedAtTo,
        education: educationFilter, skills: skillsFilter, location: locationFilter,
        company: companyFilter, currentRole: currentRoleFilter, gender: genderFilter,
      } = metaF || {};
      const activePerQuestion = Array.isArray(interviewPerQuestion)
        ? interviewPerQuestion.filter(
          (q) =>
            q && typeof q.index === 'number' && q.min != null && Number.isFinite(Number(q.min))
        )
        : [];
      const isNumericFilterActive = (f) => {
        if (!f || !f.mode) return false;
        if (f.mode === 'between') return f.min != null && f.max != null;
        return f.value != null;
      };
      const matchesNumericFilter = (n, f) => {
        if (!isNumericFilterActive(f)) return true;
        if (n == null || Number.isNaN(Number(n))) return false;
        const v = Number(n);
        if (f.mode === 'between') return v >= Number(f.min) && v <= Number(f.max);
        if (f.mode === 'min') return v >= Number(f.value);
        if (f.mode === 'max') return v <= Number(f.value);
        if (f.mode === 'eq') return v === Number(f.value);
        return true;
      };
      const hasArrayFilter = (arr) => Array.isArray(arr) && arr.length > 0;
      if (
        !isNumericFilterActive(overall) &&
        !isNumericFilterActive(resume) &&
        assessmentMin == null &&
        !isNumericFilterActive(interview) &&
        activePerQuestion.length === 0 &&
        !isStatusFilterActive(status) &&
        !proctoring &&
        !experience &&
        !isNumericFilterActive(age) &&
        !currentCtc &&
        !noticePeriod &&
        !completedAtFrom &&
        !completedAtTo &&
        !hasArrayFilter(educationFilter) &&
        !hasArrayFilter(skillsFilter) &&
        !hasArrayFilter(locationFilter) &&
        !hasArrayFilter(companyFilter) &&
        !hasArrayFilter(currentRoleFilter) &&
        !hasArrayFilter(genderFilter)
      ) {
        return rows;
      }
      return rows.filter((c) => {
        if (!candidateMatchesStatusFilter(c, status)) return false;
        if (!matchesNumericFilter(getOverallScoreNumber(c), overall)) return false;
        if (!matchesNumericFilter(c.resumeScore, resume)) return false;
        if (assessmentMin != null) {
          const pct = getAssessmentPctNumber(c);
          if (pct == null || pct < assessmentMin) return false;
        }
        if (!matchesNumericFilter(c.interviewScore, interview)) return false;
        if (activePerQuestion.length > 0) {
          const perQ = Array.isArray(c?.interviewPerQuestionScores)
            ? c.interviewPerQuestionScores
            : [];
          for (const rule of activePerQuestion) {
            const found = perQ.find((q) => q && q.index === rule.index);
            // Exclude candidates without a score for this question, or scoring below threshold
            if (!found || typeof found.score !== 'number') return false;
            if (found.score < Number(rule.min)) return false;
          }
        }
        if (proctoring) {
          const label = getCombinedScreeningProctoringBadge(
            c?.interviewProctoringRiskScore,
            c?.assessmentProctoringRiskScore
          ).label;
          if (label !== proctoring) return false;
        }
        if (experience) {
          const bucket = EXPERIENCE_BUCKETS.find((b) => b.id === experience);
          const n = parseExperienceYearsForFilter(c);
          if (!bucket || !inBucket(n, bucket)) return false;
        }
        if (!matchesNumericFilter(parseAgeYearsForFilter(c), age)) return false;
        if (currentCtc) {
          const bucket = CTC_BUCKETS.find((b) => b.id === currentCtc);
          const n = parseCurrentCtcLacsForFilter(c);
          if (!bucket || !inBucket(n, bucket)) return false;
        }
        if (noticePeriod) {
          const np = c?.noticePeriod ? String(c.noticePeriod).trim() : '';
          if (!np) return false;
          if (np.toLowerCase() !== String(noticePeriod).trim().toLowerCase()) return false;
        }
        if (completedAtFrom || completedAtTo) {
          const rawCompletedAt = c?.completedAt;
          if (!rawCompletedAt) return false;
          const completedAt = new Date(rawCompletedAt);
          if (Number.isNaN(completedAt.getTime())) return false;

          const from = completedAtFrom ? new Date(`${String(completedAtFrom).slice(0, 10)}T00:00:00`) : null;
          const to = completedAtTo ? new Date(`${String(completedAtTo).slice(0, 10)}T23:59:59.999`) : null;
          if (from && !Number.isNaN(from.getTime()) && completedAt < from) return false;
          if (to && !Number.isNaN(to.getTime()) && completedAt > to) return false;
        }
        if (hasArrayFilter(educationFilter)) {
          if (!educationFilter.includes(c.maxEducationLevel || '')) return false;
        }
        if (hasArrayFilter(skillsFilter)) {
          const cSkills = Array.isArray(c.skills) ? c.skills.map((s) => String(s).toLowerCase()) : [];
          if (!skillsFilter.some((s) => cSkills.includes(String(s).toLowerCase()))) return false;
        }
        if (hasArrayFilter(locationFilter)) {
          const loc = (c.location || '').trim();
          if (!loc || !locationFilter.includes(loc)) return false;
        }
        if (hasArrayFilter(companyFilter)) {
          const comp = (c.company || '').trim();
          if (!comp || !companyFilter.includes(comp)) return false;
        }
        if (hasArrayFilter(currentRoleFilter)) {
          const role = (c.currentRole || '').trim();
          if (!role || !currentRoleFilter.includes(role)) return false;
        }
        if (hasArrayFilter(genderFilter)) {
          const g = (c.gender || '').trim();
          if (!g || !genderFilter.includes(g)) return false;
        }
        return true;
      });
    },
    [
      getAssessmentPctNumber,
      getOverallScoreNumber,
      parseExperienceYearsForFilter,
      parseAgeYearsForFilter,
      parseCurrentCtcLacsForFilter,
    ]
  );
  const applyScoreFilters = useCallback(
    (rows) => filterRowsWith(rows, scoreFilters, metaFilters),
    [filterRowsWith, scoreFilters, metaFilters]
  );
  const clearAllCandidateTableFilters = useCallback(
    ({ clearShortlistQuery = false } = {}) => {
      setScoreFilters({
        overall: { mode: null, value: null, min: null, max: null },
        resume: { mode: null, value: null, min: null, max: null },
        assessmentMin: null,
        interview: { mode: null, value: null, min: null, max: null },
        interviewPerQuestion: [],
        status: null,
      });
      setOpenScoreFilterId(null);
      setMetaFilters({
        proctoring: null,
        experience: null,
        age: { mode: null, value: null, min: null, max: null },
        currentCtc: null,
        noticePeriod: null,
        completedAtFrom: null,
        completedAtTo: null,
        education: [],
        skills: [],
        location: [],
        company: [],
        currentRole: [],
        gender: [],
      });
      setOpenMetaFilterId(null);
      if (clearShortlistQuery) setShortlistNameQuery('');
    },
    [setOpenMetaFilterId, setOpenScoreFilterId, setMetaFilters, setScoreFilters, setShortlistNameQuery]
  );
  // Comparator moved to ./candidateSort and shared with the public review page.
  const sortByOverallDesc = useMemo(
    () =>
      buildCandidateComparator({
        screeningMeta,
        weightsConfig,
        sortColumn: tableSortColumn,
        sortDirection: tableSortDirection,
      }),
    [screeningMeta, weightsConfig, tableSortColumn, tableSortDirection]
  );
  const filteredCandidatesToShow = useMemo(
    () => {
      const filtered = applyScoreFilters(candidatesToShow);
      return [...filtered].sort(sortByOverallDesc);
    },
    [candidatesToShow, applyScoreFilters, sortByOverallDesc]
  );
  const filteredShortlistCandidates = useMemo(
    () => {
      const filtered = applyScoreFilters(shortlistCandidates);
      return [...filtered].sort(sortByOverallDesc);
    },
    [shortlistCandidates, applyScoreFilters, sortByOverallDesc]
  );
  const filteredShortlistCandidatesToShow = useMemo(() => {
    // The Onboarded tab is the Selected view narrowed to onboarded candidates; Selected shows the rest.
    const showOnboarded = activeTab === 'onboarded';
    const inTab = filteredShortlistCandidates.filter((c) => isOnboardedCandidate(c) === showOnboarded);
    const byMonth = filterByMonth(inTab, 'shortlistedAt', shortlistMonth);
    const q = (shortlistNameQuery || '').trim().toLowerCase();
    if (!q) return byMonth;
    return byMonth.filter((c) => {
      const name = (resolveCandidateDisplayName(c) || '') + '';
      return name.toLowerCase().includes(q);
    });
  }, [filteredShortlistCandidates, shortlistNameQuery, shortlistMonth, activeTab, isOnboardedCandidate]);
  const shortlistMonthOptions = useMemo(
    () => buildMonthOptions(shortlistCandidates, 'shortlistedAt', shortlistMonth),
    [shortlistCandidates, shortlistMonth]
  );
  const filteredRejectedCandidates = useMemo(
    () => applyScoreFilters(rejectedCandidates),
    [rejectedCandidates, applyScoreFilters]
  );
  const filteredRejectedCandidatesToShow = useMemo(() => {
    const byMonth = filterByMonth(filteredRejectedCandidates, 'rejectedAt', rejectedMonth);
    const q = (rejectedNameQuery || '').trim().toLowerCase();
    if (!q) return byMonth;
    return byMonth.filter((c) => {
      const name = (resolveCandidateDisplayName(c) || '') + '';
      return name.toLowerCase().includes(q);
    });
  }, [filteredRejectedCandidates, rejectedNameQuery, rejectedMonth]);
  const rejectedMonthOptions = useMemo(
    () => buildMonthOptions(rejectedCandidates, 'rejectedAt', rejectedMonth),
    [rejectedCandidates, rejectedMonth]
  );
  const renderMonthSelect = (value, onChange, options, label) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      title={label}
      className={`px-3 py-2 text-sm font-medium rounded-lg border bg-white focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] transition-colors cursor-pointer ${value
        ? 'border-[#007BFF] text-[#007BFF] bg-[#007BFF]/5'
        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
    >
      <option value="">All months</option>
      {options.map((o) => (
        <option key={o.key} value={o.key}>
          {o.label} ({o.count})
        </option>
      ))}
    </select>
  );

  const filteredDroppedCandidates = useMemo(
    () => applyScoreFilters(droppedCandidates),
    [droppedCandidates, applyScoreFilters]
  );
  const filteredDroppedCandidatesToShow = useMemo(() => {
    const q = (droppedNameQuery || '').trim().toLowerCase();
    if (!q) return filteredDroppedCandidates;
    return filteredDroppedCandidates.filter((c) => {
      const name = (resolveCandidateDisplayName(c) || '') + '';
      return name.toLowerCase().includes(q);
    });
  }, [filteredDroppedCandidates, droppedNameQuery]);
  // Report title for shortlist exports (screening name)
  const shortlistReportTitle = screeningMeta?.name || 'Screening Shortlist';
  const candidatesReportTitle = screeningMeta?.name || 'Screening Candidates';
  const handleGenerateCandidatesReport = useCallback(() => {
    let candidatesToReport = [];
    if (selectedCandidatesForShortlist.length > 0) {
      candidatesToReport = filteredCandidatesToShow.filter((c) => selectedCandidatesForShortlist.includes(c.id || c._id)).sort(sortByOverallDesc);
    } else {
      candidatesToReport = filteredCandidatesToShow;
    }



    if (candidatesToReport.length === 0) {
      toast.error('No candidates selected for report');
      return;
    }

    const formatDate = (d) => {
      if (!d) return '-';
      try { return new Date(d).toLocaleDateString(); } catch { return '-'; }
    };

    const getAssessmentPct = (c) => {
      if (c.assessmentScore == null) return 'N/A';
      if (user?.orgSlug === 'ajantapharma-80unds') {
        const roundedScore = Math.round(c.assessmentScore * 10) / 10;
        return c.assessmentTotalPoints ? `${roundedScore}/${c.assessmentTotalPoints}` : `${roundedScore}`;
      }
      if (c.assessmentTotalPoints && c.assessmentTotalPoints > 0) {
        return `${Math.round((c.assessmentScore / c.assessmentTotalPoints) * 100)}%`;
      }
      return `${c.assessmentScore}%`;
    };

    const modules = normalizeAssessmentModulesFromScreening(screening?.assessmentId);

    // 1. Determine Excel Column Order
    // Fallback order matches the standard Candidate Columns Modal
    const EXCEL_DEFAULT_ORDER = [
      'status', 'overallScore', 'resumeScore', 'assessmentScore', 'interviewScore',
      'proctoring', 'duplicates', 'shortlistedDate', 'documents', 'candidateDetails',
      'screening', 'companyName', 'currentCtc', 'noticePeriod', 'designation', 'location',
      'industry', 'experience', 'experienceYears', 'latestEducation', 'skills', 'age', 'gender',
      'email', 'phone', 'social', 'appliedDate', 'completedAt'
    ];

    const EXCEL_DEFAULT_VISIBILITY = {
      status: false,
      overallScore: true,
      resumeScore: true,
      assessmentScore: true,
      interviewScore: true,
      proctoring: true,
      duplicates: true,
      shortlistedDate: true,
      documents: true,
      email: false,
      phone: true,
      appliedDate: true,
      completedAt: true,
      candidateDetails: false,
      screening: false,
      companyName: false,
      currentCtc: false,
      noticePeriod: false,
      designation: false,
      location: false,
      industry: false,
      experience: false,
      experienceYears: false,
      latestEducation: false,
      skills: false,
      age: false,
      gender: false,
      social: false,
    };

    const excelOrder = user?.ui?.defaultExcelColumnOrder || EXCEL_DEFAULT_ORDER;
    const excelVisibility = user?.ui?.defaultExcelColumnVisibility || {};

    const isColVisible = (id) => {
      if (Object.prototype.hasOwnProperty.call(excelVisibility, id)) {
        return excelVisibility[id] === true;
      }
      return EXCEL_DEFAULT_VISIBILITY[id] !== false; // defaults to true for anything not explicitly false in EXCEL_DEFAULT_VISIBILITY
    };

    // We will build `columns` (headers) and extract `dataRows` in one pass over excelOrder
    // We always prepend '#' and 'Name' since they are fundamental to identifying the row
    const columns = ['#', 'Name'];
    const rowExtractors = [
      (c, idx) => idx + 1,
      (c) => resolveCandidateDisplayName(c) || '-',
    ];

    excelOrder.forEach(colId => {
      if (!isColVisible(colId)) return; // Skip hidden columns

      // Dynamically skip score columns if the screening doesn't use those phases.
      // A Moved Candidates pool runs no phases of its own but its candidates carry scores
      // from their source screening, so the suppression must not apply there.
      const suppressUnusedPhases = screeningMeta?.isMovedCandidatesPool !== true;
      if (suppressUnusedPhases && colId === 'resumeScore' && screeningMeta?.hasResumeScoring === false) return;
      if (suppressUnusedPhases && colId === 'assessmentScore' && screeningMeta?.includeAssessment === false) return;
      if (suppressUnusedPhases && colId === 'interviewScore' && screeningMeta?.includeInterview === false) return;
      if (suppressUnusedPhases && colId === 'proctoring' && screeningMeta?.includeAssessment === false && screeningMeta?.includeInterview === false) return;

      // For assessmentScore, we INJECT the multi-modules right BEFORE it
      if (colId === 'assessmentScore') {
        modules.forEach(m => {
          columns.push(m.title);
          rowExtractors.push(c => {
            const scores = Array.isArray(c.assessmentModuleScores) ? c.assessmentModuleScores : [];
            const entry = scores.find((e) => String(e.moduleId) === String(m.moduleId));
            if (!entry || entry.score == null) return 'N/A';
            if (entry.personalityResult) return 'See work style columns';

            if (user?.orgSlug === 'ajantapharma-80unds') {
              const roundedScore = Math.round(entry.score * 10) / 10;
              return entry.maxScore ? `${roundedScore}/${entry.maxScore}` : `${roundedScore}`;
            }

            if (entry.maxScore == null || entry.maxScore <= 0) return 'N/A';
            return `${Math.round((entry.score / entry.maxScore) * 100)}%`;
          });
        });
        // Personality (IPIP-50) trait profile, standalone or as a module
        personalityExportColumns(candidatesToReport).forEach(({ header, get }) => {
          columns.push(header);
          rowExtractors.push(get);
        });
      }

      switch (colId) {
        case 'status':
          columns.push('Status');
          rowExtractors.push(c => c.status || '-');
          break;
        case 'overallScore':
          columns.push('Overall Score');
          rowExtractors.push(c => getOverallScoreNumber(c) != null ? `${getOverallScoreNumber(c)}%` : 'N/A');
          break;
        case 'resumeScore':
          columns.push('Resume Score');
          rowExtractors.push(c => c.resumeScore != null ? `${c.resumeScore}%` : 'N/A');
          break;
        case 'assessmentScore':
          columns.push('Assessment Score');
          rowExtractors.push(c => getAssessmentPct(c));
          break;
        case 'interviewScore':
          columns.push('Interview Score');
          rowExtractors.push(c => c.interviewScore != null ? `${c.interviewScore}%` : 'N/A');
          break;
        case 'proctoring':
          columns.push('Proctoring');
          rowExtractors.push(c => {
            if (c.interviewProctoringRiskScore == null && c.assessmentProctoringRiskScore == null) return 'N/A';
            const badge = getCombinedScreeningProctoringBadge(c.interviewProctoringRiskScore, c.assessmentProctoringRiskScore).label || 'N/A';
            let scoreStr = '';
            if (c.proctoringRiskScore != null) {
              scoreStr = c.proctoringRiskScore > 100 ? ` (${c.proctoringRiskScore}+)` : ` (${c.proctoringRiskScore})`;
            }
            return `${badge}${scoreStr}`;
          });
          break;
        case 'duplicates':
          columns.push('Duplicates');
          rowExtractors.push(c => (c.duplicateSummary?.count > 0 || c.duplicateOf) ? 'Yes' : 'No');
          break;
        case 'shortlistedDate':
          columns.push(isSamtaGroup ? 'Shortlisted Date' : 'Selected Date');
          rowExtractors.push(c => formatDate(c.shortlistedAt));
          break;
        case 'documents':
          columns.push('Documents');
          rowExtractors.push(c => c.documentsStatus || '-');
          break;
        case 'candidateDetails':
          // Skip, purely UI abstraction usually
          break;
        case 'screening':
          columns.push('Screening');
          rowExtractors.push(c => screeningMeta?.name || '-');
          break;
        case 'companyName':
          columns.push('Company');
          rowExtractors.push(c => c.companyName || c.currentCompany || '-');
          break;
        case 'currentCtc':
          columns.push('Current CTC');
          rowExtractors.push(c => c.currentCtc || c.current_ctc || '-');
          break;
        case 'noticePeriod':
          columns.push('Notice Period');
          rowExtractors.push(c => c.noticePeriod || c.notice_period || '-');
          break;
        case 'designation':
          columns.push('Current Role');
          rowExtractors.push(c => c.currentRole || c.designation || '-');
          break;
        case 'location':
          columns.push('Location');
          rowExtractors.push(c => c.location || '-');
          break;
        case 'industry':
          columns.push('Industry');
          rowExtractors.push(c => c.industry || '-');
          break;
        case 'experience':
          columns.push('Experience (Band)');
          rowExtractors.push(c => c.experience || '-');
          break;
        case 'experienceYears':
          columns.push('Experience (Years)');
          rowExtractors.push(c => c.experienceYears != null ? c.experienceYears : '-');
          break;
        case 'latestEducation':
          columns.push('Education');
          rowExtractors.push(c => c.education || '-');
          break;
        case 'skills':
          columns.push('Skills');
          rowExtractors.push(c => {
            if (Array.isArray(c.skills) && c.skills.length > 0) return c.skills.join(', ');
            return '-';
          });
          break;
        case 'age':
          columns.push('Age');
          rowExtractors.push(c => {
            let v = c.ageYears ?? c.age;
            if (v == null && c.dob) {
              const d = new Date(c.dob);
              if (!isNaN(d.getTime())) {
                const ageDifMs = Date.now() - d.getTime();
                const ageDate = new Date(ageDifMs);
                v = Math.abs(ageDate.getUTCFullYear() - 1970);
              }
            }
            return v != null ? v : '-';
          });
          break;
        case 'gender':
          columns.push('Gender');
          rowExtractors.push(c => c.gender || '-');
          break;
        case 'email':
          columns.push('Email');
          rowExtractors.push(c => c.email || '-');
          break;
        case 'phone':
          columns.push('Phone');
          rowExtractors.push(c => c.phone || '-');
          break;
        case 'social':
          columns.push('Social Links');
          rowExtractors.push(c => c.linkedinUrl || c.githubUrl || c.portfolioUrl || '-');
          break;
        case 'appliedDate':
          columns.push('Applied Date');
          rowExtractors.push(c => formatDate(c.appliedAt || c.createdAt));
          break;
        case 'completedAt':
          columns.push('Completed Date');
          rowExtractors.push(c => formatDate(c.completedAt));
          break;
        case 'cityCity':
          columns.push('City Name');
          rowExtractors.push(c => c.cityDetails?.city || '-');
          break;
        case 'cityState':
          columns.push('City State');
          rowExtractors.push(c => c.cityDetails?.state || '-');
          break;
        case 'cityRegion':
          columns.push('City Region');
          rowExtractors.push(c => c.cityDetails?.region || '-');
          break;
        default:
          if (colId.startsWith('custom_')) {
            const fieldId = colId.replace('custom_', '');
            const regField = user?.registrationFields?.find(f => f.fieldId === fieldId || `custom_${f.fieldId}` === colId || f.fieldId === colId);
            const label = regField?.label || fieldId;
            columns.push(label);
            rowExtractors.push(c => {
              let customValue = c.customFields?.[fieldId] || c.customFields?.[`custom_${fieldId}`] || c.customFields?.[colId] || c.customFields?.[label];

              if (!customValue && c.customFields && typeof c.customFields === 'object') {
                const targetLabel = (label || '').trim().toLowerCase();
                const keyMatch = Object.keys(c.customFields).find(k => k.trim().toLowerCase() === targetLabel);
                if (keyMatch) {
                  customValue = c.customFields[keyMatch];
                }
              }

              return customValue || '-';
            });
          }
          break;
      }
    });

    // Not in excelOrder (no saved layout knows it), so appended here; still hideable via
    // defaultExcelColumnVisibility.transcriptsAvailable = false.
    if (isTranscriptsColumnAvailable(user, screeningMeta) && excelVisibility.transcriptsAvailable !== false) {
      columns.push('Transcripts Available');
      rowExtractors.push((c) => getTranscriptsAvailableLabel(c));
    }

    const hasV2Candidates = candidatesToReport.some(
      (c) => c.scoringVersion && c.scoringVersion.startsWith('v2')
    );
    if (hasV2Candidates) {
      columns.push('AI Summary');
      rowExtractors.push(c => c.scoringSummary || '-');
    }

    const dataRows = candidatesToReport.map((c, idx) => rowExtractors.map(fn => fn(c, idx)));

    const allRows = [
      ['Candidates Report'],
      ['Screening', candidatesReportTitle],
      ['Generated', new Date().toLocaleString()],
      ['Total Candidates', candidatesToReport.length],
      [],
      columns,
      ...dataRows,
    ];

    const ws = XLSX.utils.aoa_to_sheet(allRows);

    const colWidths = columns.map((colName, colIdx) => {
      let maxLen = colName.length;
      dataRows.forEach((row) => {
        const cellVal = row[colIdx];
        if (cellVal != null) {
          const len = String(cellVal).length;
          if (len > maxLen) maxLen = len;
        }
      });
      return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
    const safeTitle = (candidatesReportTitle || 'Shortlist')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '_');
    const dateStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Candidates_${safeTitle}_${dateStr}.xlsx`);
    toast.success('Report downloaded');
  }, [
    filteredCandidatesToShow,
    selectedCandidatesForShortlist,
    candidatesReportTitle,
    user,
    getOverallScoreNumber,
    screeningMeta,
    sortByOverallDesc,
  ]);
  const handleGenerateShortlistReport = useCallback(() => {
    const candidatesToReport = shortlistCandidates
      .filter((c) => selectedShortlistCandidateIds.includes(c.id || c._id))
      .sort(sortByOverallDesc);

    if (candidatesToReport.length === 0) {
      toast.error('No candidates selected for report');
      return;
    }

    const formatDate = (d) => {
      if (!d) return '-';
      try { return new Date(d).toLocaleDateString(); } catch { return '-'; }
    };

    const getAssessmentPct = (c) => {
      if (c.assessmentScore == null) return 'N/A';
      if (user?.orgSlug === 'ajantapharma-80unds') {
        const roundedScore = Math.round(c.assessmentScore * 10) / 10;
        return c.assessmentTotalPoints ? `${roundedScore}/${c.assessmentTotalPoints}` : `${roundedScore}`;
      }
      if (c.assessmentTotalPoints && c.assessmentTotalPoints > 0) {
        return `${Math.round((c.assessmentScore / c.assessmentTotalPoints) * 100)}%`;
      }
      return `${c.assessmentScore}%`;
    };

    const modules = normalizeAssessmentModulesFromScreening(screening?.assessmentId);

    // 1. Determine Excel Column Order
    // Fallback order matches the standard Candidate Columns Modal
    const EXCEL_DEFAULT_ORDER = [
      'status', 'overallScore', 'resumeScore', 'assessmentScore', 'interviewScore',
      'proctoring', 'duplicates', 'shortlistedDate', 'documents', 'candidateDetails',
      'screening', 'companyName', 'currentCtc', 'noticePeriod', 'designation', 'location',
      'industry', 'experience', 'experienceYears', 'latestEducation', 'skills', 'age', 'gender',
      'email', 'phone', 'social', 'appliedDate', 'completedAt'
    ];

    const EXCEL_DEFAULT_VISIBILITY = {
      status: false,
      overallScore: true,
      resumeScore: true,
      assessmentScore: true,
      interviewScore: true,
      proctoring: true,
      duplicates: true,
      shortlistedDate: true,
      documents: true,
      email: false,
      phone: true,
      appliedDate: true,
      completedAt: true,
      candidateDetails: false,
      screening: false,
      companyName: false,
      currentCtc: false,
      noticePeriod: false,
      designation: false,
      location: false,
      industry: false,
      experience: false,
      experienceYears: false,
      latestEducation: false,
      skills: false,
      age: false,
      gender: false,
      social: false,
    };

    const excelOrder = user?.ui?.defaultExcelColumnOrder || EXCEL_DEFAULT_ORDER;
    const excelVisibility = user?.ui?.defaultExcelColumnVisibility || {};

    const isColVisible = (id) => {
      if (Object.prototype.hasOwnProperty.call(excelVisibility, id)) {
        return excelVisibility[id] === true;
      }
      return EXCEL_DEFAULT_VISIBILITY[id] !== false; // defaults to true for anything not explicitly false in EXCEL_DEFAULT_VISIBILITY
    };

    // We will build `columns` (headers) and extract `dataRows` in one pass over excelOrder
    // We always prepend '#' and 'Name' since they are fundamental to identifying the row
    const columns = ['#', 'Name'];
    const rowExtractors = [
      (c, idx) => idx + 1,
      (c) => resolveCandidateDisplayName(c) || '-',
    ];

    excelOrder.forEach(colId => {
      if (!isColVisible(colId)) return; // Skip hidden columns

      // Dynamically skip score columns if the screening doesn't use those phases.
      // A Moved Candidates pool runs no phases of its own but its candidates carry scores
      // from their source screening, so the suppression must not apply there.
      const suppressUnusedPhases = screeningMeta?.isMovedCandidatesPool !== true;
      if (suppressUnusedPhases && colId === 'resumeScore' && screeningMeta?.hasResumeScoring === false) return;
      if (suppressUnusedPhases && colId === 'assessmentScore' && screeningMeta?.includeAssessment === false) return;
      if (suppressUnusedPhases && colId === 'interviewScore' && screeningMeta?.includeInterview === false) return;
      if (suppressUnusedPhases && colId === 'proctoring' && screeningMeta?.includeAssessment === false && screeningMeta?.includeInterview === false) return;

      // For assessmentScore, we INJECT the multi-modules right BEFORE it
      if (colId === 'assessmentScore') {
        modules.forEach(m => {
          columns.push(m.title);
          rowExtractors.push(c => {
            const scores = Array.isArray(c.assessmentModuleScores) ? c.assessmentModuleScores : [];
            const entry = scores.find((e) => String(e.moduleId) === String(m.moduleId));
            if (!entry || entry.score == null) return 'N/A';
            if (entry.personalityResult) return 'See work style columns';

            if (user?.orgSlug === 'ajantapharma-80unds') {
              const roundedScore = Math.round(entry.score * 10) / 10;
              return entry.maxScore ? `${roundedScore}/${entry.maxScore}` : `${roundedScore}`;
            }

            if (entry.maxScore == null || entry.maxScore <= 0) return 'N/A';
            return `${Math.round((entry.score / entry.maxScore) * 100)}%`;
          });
        });
        // Personality (IPIP-50) trait profile, standalone or as a module
        personalityExportColumns(candidatesToReport).forEach(({ header, get }) => {
          columns.push(header);
          rowExtractors.push(get);
        });
      }

      switch (colId) {
        case 'status':
          columns.push('Status');
          rowExtractors.push(c => c.status || '-');
          break;
        case 'overallScore':
          columns.push('Overall Score');
          rowExtractors.push(c => getOverallScoreNumber(c) != null ? `${getOverallScoreNumber(c)}%` : 'N/A');
          break;
        case 'resumeScore':
          columns.push('Resume Score');
          rowExtractors.push(c => c.resumeScore != null ? `${c.resumeScore}%` : 'N/A');
          break;
        case 'assessmentScore':
          columns.push('Assessment Score');
          rowExtractors.push(c => getAssessmentPct(c));
          break;
        case 'interviewScore':
          columns.push('Interview Score');
          rowExtractors.push(c => c.interviewScore != null ? `${c.interviewScore}%` : 'N/A');
          break;
        case 'proctoring':
          columns.push('Proctoring');
          rowExtractors.push(c => {
            if (c.interviewProctoringRiskScore == null && c.assessmentProctoringRiskScore == null) return 'N/A';
            const badge = getCombinedScreeningProctoringBadge(c.interviewProctoringRiskScore, c.assessmentProctoringRiskScore).label || 'N/A';
            let scoreStr = '';
            if (c.proctoringRiskScore != null) {
              scoreStr = c.proctoringRiskScore > 100 ? ` (${c.proctoringRiskScore}+)` : ` (${c.proctoringRiskScore})`;
            }
            return `${badge}${scoreStr}`;
          });
          break;
        case 'duplicates':
          columns.push('Duplicates');
          rowExtractors.push(c => (c.duplicateSummary?.count > 0 || c.duplicateOf) ? 'Yes' : 'No');
          break;
        case 'shortlistedDate':
          columns.push(isSamtaGroup ? 'Shortlisted Date' : 'Selected Date');
          rowExtractors.push(c => formatDate(c.shortlistedAt));
          break;
        case 'documents':
          columns.push('Documents');
          rowExtractors.push(c => c.documentsStatus || '-');
          break;
        case 'candidateDetails':
          // Skip, purely UI abstraction usually
          break;
        case 'screening':
          columns.push('Screening');
          rowExtractors.push(c => screeningMeta?.name || '-');
          break;
        case 'companyName':
          columns.push('Company');
          rowExtractors.push(c => c.companyName || c.currentCompany || '-');
          break;
        case 'currentCtc':
          columns.push('Current CTC');
          rowExtractors.push(c => c.currentCtc || c.current_ctc || '-');
          break;
        case 'noticePeriod':
          columns.push('Notice Period');
          rowExtractors.push(c => c.noticePeriod || c.notice_period || '-');
          break;
        case 'designation':
          columns.push('Current Role');
          rowExtractors.push(c => c.currentRole || c.designation || '-');
          break;
        case 'location':
          columns.push('Location');
          rowExtractors.push(c => c.location || '-');
          break;
        case 'industry':
          columns.push('Industry');
          rowExtractors.push(c => c.industry || '-');
          break;
        case 'experience':
          columns.push('Experience (Band)');
          rowExtractors.push(c => c.experience || '-');
          break;
        case 'experienceYears':
          columns.push('Experience (Years)');
          rowExtractors.push(c => c.experienceYears != null ? c.experienceYears : '-');
          break;
        case 'latestEducation':
          columns.push('Education');
          rowExtractors.push(c => c.education || '-');
          break;
        case 'skills':
          columns.push('Skills');
          rowExtractors.push(c => {
            if (Array.isArray(c.skills) && c.skills.length > 0) return c.skills.join(', ');
            return '-';
          });
          break;
        case 'age':
          columns.push('Age');
          rowExtractors.push(c => {
            let v = c.ageYears ?? c.age;
            if (v == null && c.dob) {
              const d = new Date(c.dob);
              if (!isNaN(d.getTime())) {
                const ageDifMs = Date.now() - d.getTime();
                const ageDate = new Date(ageDifMs);
                v = Math.abs(ageDate.getUTCFullYear() - 1970);
              }
            }
            return v != null ? v : '-';
          });
          break;
        case 'gender':
          columns.push('Gender');
          rowExtractors.push(c => c.gender || '-');
          break;
        case 'email':
          columns.push('Email');
          rowExtractors.push(c => c.email || '-');
          break;
        case 'phone':
          columns.push('Phone');
          rowExtractors.push(c => c.phone || '-');
          break;
        case 'social':
          columns.push('Social Links');
          rowExtractors.push(c => c.linkedinUrl || c.githubUrl || c.portfolioUrl || '-');
          break;
        case 'appliedDate':
          columns.push('Applied Date');
          rowExtractors.push(c => formatDate(c.appliedAt || c.createdAt));
          break;
        case 'completedAt':
          columns.push('Completed Date');
          rowExtractors.push(c => formatDate(c.completedAt));
          break;
        case 'cityCode':
          columns.push('City Code');
          rowExtractors.push(c => c.cityId || '-');
          break;
        case 'cityCity':
          columns.push('City Name');
          rowExtractors.push(c => c.cityDetails?.city || '-');
          break;
        case 'cityState':
          columns.push('City State');
          rowExtractors.push(c => c.cityDetails?.state || '-');
          break;
        case 'cityRegion':
          columns.push('City Region');
          rowExtractors.push(c => c.cityDetails?.region || '-');
          break;
        case 'storeId':
          columns.push('Store Code');
          rowExtractors.push(c => c.storeId || '-');
          break;
        case 'storeName':
          columns.push('Store Name');
          rowExtractors.push(c => c.storeDetails?.storeName || '-');
          break;
        case 'storeRegion':
          columns.push('Store Region');
          rowExtractors.push(c => c.storeDetails?.region || '-');
          break;
        case 'storeCity':
          columns.push('Store City');
          rowExtractors.push(c => c.storeDetails?.city || '-');
          break;
        case 'storeState':
          columns.push('Store State');
          rowExtractors.push(c => c.storeDetails?.state || '-');
          break;
        case 'storeCategory':
          columns.push('Store Category');
          rowExtractors.push(c => c.storeDetails?.category || '-');
          break;
        case 'armName':
          columns.push('ARM Name');
          rowExtractors.push(c => c.storeDetails?.armName || '-');
          break;
        case 'armEmail':
          columns.push('ARM Email');
          rowExtractors.push(c => c.storeDetails?.armEmail || '-');
          break;
        default:
          if (colId.startsWith('custom_')) {
            const fieldId = colId.replace('custom_', '');
            const regField = user?.registrationFields?.find(f => f.fieldId === fieldId || `custom_${f.fieldId}` === colId || f.fieldId === colId);
            const label = regField?.label || fieldId;
            columns.push(label);
            rowExtractors.push(c => {
              let customValue = c.customFields?.[fieldId] || c.customFields?.[`custom_${fieldId}`] || c.customFields?.[colId] || c.customFields?.[label];

              if (!customValue && c.customFields && typeof c.customFields === 'object') {
                const targetLabel = (label || '').trim().toLowerCase();
                const keyMatch = Object.keys(c.customFields).find(k => k.trim().toLowerCase() === targetLabel);
                if (keyMatch) {
                  customValue = c.customFields[keyMatch];
                }
              }

              return customValue || '-';
            });
          }
          break;
      }
    });

    // Not in excelOrder (no saved layout knows it), so appended here; still hideable via
    // defaultExcelColumnVisibility.transcriptsAvailable = false.
    if (isTranscriptsColumnAvailable(user, screeningMeta) && excelVisibility.transcriptsAvailable !== false) {
      columns.push('Transcripts Available');
      rowExtractors.push((c) => getTranscriptsAvailableLabel(c));
    }

    const hasV2Candidates = candidatesToReport.some(
      (c) => c.scoringVersion && c.scoringVersion.startsWith('v2')
    );
    if (hasV2Candidates) {
      columns.push('AI Summary');
      rowExtractors.push(c => c.scoringSummary || '-');
    }

    const dataRows = candidatesToReport.map((c, idx) => rowExtractors.map(fn => fn(c, idx)));

    const allRows = [
      ['Selected Candidates Report'],
      ['Screening', shortlistReportTitle],
      ['Generated', new Date().toLocaleString()],
      ['Total Candidates', candidatesToReport.length],
      [],
      columns,
      ...dataRows,
    ];

    const ws = XLSX.utils.aoa_to_sheet(allRows);

    const colWidths = columns.map((colName, colIdx) => {
      let maxLen = colName.length;
      dataRows.forEach((row) => {
        const cellVal = row[colIdx];
        if (cellVal != null) {
          const len = String(cellVal).length;
          if (len > maxLen) maxLen = len;
        }
      });
      return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Selected Candidates');
    const safeTitle = (shortlistReportTitle || 'Shortlist')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '_');
    const dateStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Selected_Candidates_${safeTitle}_${dateStr}.xlsx`);
    toast.success('Report downloaded');
  }, [
    shortlistCandidates,
    selectedShortlistCandidateIds,
    shortlistReportTitle,
    user,
    getOverallScoreNumber,
    screeningMeta,
    sortByOverallDesc,
  ]);
  const proceedMoveToScreeningEmailStep = useCallback(
    async (candidateIdsToMove) => {
      if (!screeningId || !moveDestinationScreeningId) return;
      const destinationId = moveDestinationScreeningId;
      setMovingShortlistCandidates(true);
      try {
        setShowMissingEmailForMoveModal(false);
        setMissingEmailCandidatesForMove([]);
        setEligibleMoveCandidateIds([]);
        setShowShortlistNextStepsModal(false);
        setShortlistNextStepsChoice(null);
        setShortlistNextStepsStage('choice');
        setReportFormatChoice(null);
        setMoveDestinationScreeningId('');
        // Defer moving candidates until the user actually clicks "Send Email".
        setPendingMoveToScreening({
          destinationId,
          sourceScreeningId: screeningId,
          candidateIds: [...candidateIdsToMove],
        });
        // Open email modal for destination screening so user can send the email.
        setEmailModalScreeningId(destinationId);
        setEmailModalContext('invite');
        setEmailDraftForSend(null);
        try {
          const draftRes = await axios.get(`/api/screenings/${destinationId}/email-drafts`, {
            params: { context: 'invite' },
          });
          if (draftRes.data?.draft) setEmailDraftForSend(draftRes.data.draft);
        } catch (_) { }
        setShowSendEmailToCandidateModal(true);
      } finally {
        setMovingShortlistCandidates(false);
      }
    },
    [screeningId, moveDestinationScreeningId]
  );
  const handleMoveShortlistCandidates = useCallback(async () => {
    if (!screeningId || !moveDestinationScreeningId) {
      toast.error('Select a destination screening');
      return;
    }
    if (!selectedShortlistCandidateIds.length) {
      toast.error('Select at least one candidate first');
      return;
    }
    const selectedCandidates = shortlistCandidates.filter((c) =>
      selectedShortlistCandidateIds.includes(c.id || c._id)
    );
    const withEmail = selectedCandidates.filter((c) => !!(c?.email && String(c.email).trim()));
    const withoutEmail = selectedCandidates.filter((c) => !(c?.email && String(c.email).trim()));
    if (withEmail.length === 0) {
      setMissingEmailCandidatesForMove(withoutEmail);
      setEligibleMoveCandidateIds([]);
      setShowMissingEmailForMoveModal(true);
      return;
    }
    if (withoutEmail.length > 0) {
      setMissingEmailCandidatesForMove(withoutEmail);
      setEligibleMoveCandidateIds(withEmail.map((c) => c.id || c._id));
      setShowMissingEmailForMoveModal(true);
      return;
    }
    await proceedMoveToScreeningEmailStep(withEmail.map((c) => c.id || c._id));
  }, [
    screeningId,
    moveDestinationScreeningId,
    selectedShortlistCandidateIds,
    shortlistCandidates,
    proceedMoveToScreeningEmailStep,
  ]);
  const buildShortlistReportPDFDoc = useCallback((candidatesToReport) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const margin = 12;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const contentW = pageW - margin * 2;
    const hasAssessment = screeningMeta?.hasAssessment ?? false;
    const hasInterview = screeningMeta?.hasInterview ?? false;
    const THEME = {
      primary: [37, 99, 235],
      primaryDark: [30, 64, 175],
      primaryLight: [59, 130, 246],

      /* Clean white background with improved borders and contrast */
      textStrong: [15, 23, 42],

      text: [30, 41, 59],

      textMuted: [100, 116, 139],

      textSoft: [113, 128, 150],

      border: [203, 213, 225],

      borderLight: [226, 232, 240],

      pageCanvas: [255, 255, 255],

      surface: [255, 255, 255],
      white: [255, 255, 255],

      cardBg: [249, 250, 251],

      success: [22, 163, 74],
      warning: [217, 119, 6],
      danger: [220, 38, 38],
      statusPillFill: [255, 245, 230],
      statusPillBorder: [253, 230, 138],
      statusPillText: [180, 83, 9],
    };
    const fillPageCanvas = () => {
      doc.setFillColor(...THEME.pageCanvas);
      doc.rect(0, 0, pageW, pageH, 'F');
    };
    const reportGeneratedAt = new Date().toLocaleString();

    const reportId = `SR-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

    const reportRole = screeningMeta?.jobTitle || screeningMeta?.name || 'Candidate Screening Pipeline';
    const reportGeneratedBy = 'TalentCrux Pipeline';
    const formatDate = (d) => {
      if (!d) return '-';
      try {
        return new Date(d).toLocaleDateString();
      } catch {
        return '-';
      }
    };
    const coerceNumber = (v) => {
      if (v == null) return null;
      if (typeof v === 'number' && Number.isFinite(v)) return v;
      if (typeof v === 'string') {
        const trimmed = v.trim();
        if (!trimmed) return null;
        const direct = Number(trimmed);
        if (Number.isFinite(direct)) return direct;
        // Handle salary strings like "₹ 8.5 LPA", "8,50,000", "12 lacs".
        const cleaned = trimmed.replace(/,/g, '').replace(/[^\d.-]/g, '');
        if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) ? parsed : null;
      }
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const toPct = (v) => {
      const n = coerceNumber(v);
      return n == null ? 'N/A' : `${Math.round(n)}%`;
    };
    const getAssessmentPctNumber = (c) => {
      if (c.assessmentScore == null) return null;
      const score = coerceNumber(c.assessmentScore);
      const totalPoints = coerceNumber(c.assessmentTotalPoints);
      if (score == null) return null;
      if (c.assessmentTotalPoints && c.assessmentTotalPoints > 0) {
        if (totalPoints == null || totalPoints <= 0) return null;
        return Math.round((score / totalPoints) * 100);
      }
      return score;
    };
    const avg = (arr) => (arr.length ? Math.round(arr.reduce((s, x) => s + x, 0) / arr.length) : null);
    const safeText = (v) => (v == null || String(v).trim() === '' ? '-' : String(v).trim());
    const createdByName =
      (user?.name || user?.displayName || user?.email || 'Workspace Owner').toString().trim() ||
      'Workspace Owner';
    const emailLikeRe = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
    const stripEmailLike = (s) =>
      String(s || '')
        .replace(emailLikeRe, '')
        .replace(/\s+/g, ' ')
        .trim();
    const getCandidateDisplayName = (cand) => {
      const rawName = safeText(cand?.name);
      const stripped = stripEmailLike(rawName);
      const joined = [safeText(cand?.firstName), safeText(cand?.lastName)]
        .filter((x) => x !== '-')
        .join(' ')
        .trim();
      if (joined) return stripEmailLike(joined);
      return stripped && stripped !== '-' ? stripped : rawName;
    };
    const truncate = (text, max = 260) => {
      const t = safeText(text);
      if (t === '-') return t;
      return t.length > max ? `${t.slice(0, max - 3)}...` : t;
    };
    const parseBullets = (text, limit = 4) => {
      if (!text || typeof text !== 'string') return [];
      return text
        .split('\n')
        .map((line) => line.replace(/^-\s*\*\*(.+?)\*\*:\s*/g, '$1: ').replace(/^[-*]\s*/g, '').trim())
        .filter(Boolean)
        .slice(0, limit);
    };
    const pickFirst = (...vals) => vals.find((v) => v != null && String(v).trim() !== '');
    const buildRoleLine = (c) =>
      [safeText(c.currentRole), safeText(c.experience), safeText(c.location)].filter((x) => x !== '-').join(' | ');
    const buildMetaLine = (c) =>
      [safeText(c.email), safeText(c.phone), `Source: ${safeText(c.source)}`]
        .filter((x) => x !== '-' && x !== 'Source: -')
        .join(' | ');
    const getScoreBreakdownBullets = (c, limit = 4) => {
      const sb = c?.scoreBreakdown || {};
      const entries = [
        ['Key responsibilities', sb.keyResponsibilitiesMatch, 45],
        ['Experience & level', sb.experienceAndLevel, 25],
        ['Impact & achievements', sb.impactAchievements, 15],
        ['Career trajectory', sb.careerTrajectory, 5],
        ['Education & credentials', sb.educationCredentials, 10],
      ];
      return entries
        .filter(([, v, max]) => typeof v === 'number' && max > 0)
        .map(([label, v, max]) => `${label}: ${Math.round((v / max) * 100)}% signal`)
        .slice(0, limit);
    };
    const getInterviewFallbackBullets = (c, limit = 3) => {
      const out = [];
      const interviewScore = coerceNumber(c.interviewScore);
      if (interviewScore != null) {
        out.push(`Interview performance scored at ${Math.round(interviewScore)}%.`);
      }
      const assessmentScore = coerceNumber(c.assessmentScore);
      if (assessmentScore != null) {
        const ap = getAssessmentPctNumber(c);
        if (typeof ap === 'number') out.push(`Assessment outcome recorded at ${ap}%.`);
      }
      const risk = coerceNumber(c.proctoringRiskScore);
      if (risk != null) {
        out.push(`Proctoring risk signal: ${Math.round(risk)}%.`);
      }
      if (!out.length) out.push('Interview summary not available; use score sheet and transcript for review.');
      return out.slice(0, limit);
    };
    const getStandoutMoments = (c, limit = 3) => {
      if (Array.isArray(c?.standoutMoments) && c.standoutMoments.length) {
        return c.standoutMoments.map((x) => String(x).trim()).filter(Boolean).slice(0, limit);
      }
      const textSources = [c?.interviewSummary, c?.aiAnalysis].filter((v) => typeof v === 'string');
      for (const text of textSources) {
        const m = text.match(/standout moments?\s*:\s*([^\n]+)/i);
        if (m?.[1]) {
          const parts = m[1]
            .split(/;|,|\|/)
            .map((x) => x.trim())
            .filter(Boolean)
            .slice(0, limit);
          if (parts.length) return parts;
        }
      }
      return [];
    };
    /** Proctoring integrity band for PDF/insights (interview scale halved vs legacy; assessment unchanged). */
    const getProctoringIntegrityBucket = (c) => {
      const tierInterview = (() => {
        const v = coerceNumber(c?.interviewProctoringRiskScore);
        if (v == null) return -1;
        if (v < 50) return 0;
        if (v < 100) return 1;
        return 2;
      })();
      const tierAssessment = (() => {
        const v = coerceNumber(c?.assessmentProctoringRiskScore);
        if (v == null) return -1;
        if (v < 50) return 0;
        if (v < 100) return 1;
        return 2;
      })();
      const t = Math.max(tierInterview, tierAssessment);
      if (t < 0) return 'Unknown';
      if (t >= 2) return 'High';
      if (t >= 1) return 'Moderate';
      return 'Low';
    };
    const getRiskColor = (bucket) => {
      if (bucket === 'High') return [220, 38, 38];
      if (bucket === 'Moderate') return [234, 88, 12];
      if (bucket === 'Low') return [5, 150, 105];
      return [100, 116, 139];
    };
    const getIntegrityNote = (c) => {
      const riskRaw = coerceNumber(c?.proctoringRiskScore);
      const risk = riskRaw != null ? Math.round(riskRaw) : null;
      const bucket = getProctoringIntegrityBucket(c);
      if (bucket === 'Low') {
        return 'Proctoring signals are within acceptable limits with no major integrity concerns across observed sessions.';
      }
      if (bucket === 'Moderate') {
        return `Moderate integrity signals were observed (risk ${risk}%). Recommend a focused verification round on key claims before final closure.`;
      }
      if (bucket === 'High') {
        return `High integrity risk signal detected (risk ${risk}%). Recommend supervised reassessment and strict manual validation before advancing.`;
      }
      return 'Proctoring evidence is not available for this candidate. Validate claims through structured follow-up interview questions.';
    };
    const getConfidenceMeta = (c) => {
      let score = 0;
      if (coerceNumber(c.resumeScore) != null) score += 1;
      if (typeof getAssessmentPctNumber(c) === 'number') score += 1;
      if (coerceNumber(c.interviewScore) != null) score += 1;
      if (typeof c.aiAnalysis === 'string' && c.aiAnalysis.trim()) score += 1;
      if (typeof c.interviewSummary === 'string' && c.interviewSummary.trim()) score += 1;
      if (score >= 4) return { label: 'High Confidence', fill: [236, 253, 245], text: [22, 163, 74], border: [134, 239, 172] };
      if (score >= 2) return { label: 'Medium Confidence', fill: [255, 251, 235], text: [217, 119, 6], border: [253, 230, 138] };
      return { label: 'Low Confidence', fill: [254, 242, 242], text: [220, 38, 38], border: [252, 165, 165] };
    };
    const getScoreSignals = (c) => {
      const sb = c?.scoreBreakdown || {};
      return [
        ['Key responsibilities', sb.keyResponsibilitiesMatch, 45],
        ['Experience & level', sb.experienceAndLevel, 25],
        ['Impact & achievements', sb.impactAchievements, 15],
        ['Career trajectory', sb.careerTrajectory, 5],
        ['Education & credentials', sb.educationCredentials, 10],
      ]
        .map(([label, v, max]) => {
          const vv = coerceNumber(v);
          const mm = coerceNumber(max);
          if (vv == null || mm == null || mm <= 0) return null;
          return { label, pct: Math.round((vv / mm) * 100) };
        })
        .filter(Boolean);
    };
    const byBand = (arr, min, max) =>
      arr.filter((x) => typeof x === 'number' && x >= min && x <= max).length;
    const getPercentile = (value, sortedAsc) => {
      if (value == null || !sortedAsc.length) return null;
      const idx = sortedAsc.findIndex((v) => v >= value);
      const rankIdx = idx === -1 ? sortedAsc.length - 1 : idx;
      return Math.max(1, Math.round(((rankIdx + 1) / sortedAsc.length) * 100));
    };
    const getNextStepPrompts = (score, hasInterviewData) => {
      if (score == null) {
        return [
          'Validate role-fit depth with one hands-on scenario.',
          'Confirm communication clarity using a structured behavioral round.',
        ];
      }
      if (score >= 80) {
        return [
          'Probe ownership scope in the strongest achievement claim.',
          'Validate stakeholder handling in one high-pressure situation.',
        ];
      }
      if (score >= 65) {
        return [
          'Deep-dive into one JD-critical responsibility with specifics.',
          hasInterviewData
            ? 'Clarify any moderate-signal area from interview responses.'
            : 'Complete interview round to validate communication and problem-solving depth.',
        ];
      }
      if (score >= 50) {
        return [
          'Re-check core JD fit through targeted technical/functional questions.',
          'Validate consistency between resume claims and practical examples.',
        ];
      }
      return [
        'Proceed only if niche must-have criteria strongly match role needs.',
        'Run strict validation round before advancing.',
      ];
    };
    const candidateOverall = (c) => {
      const parts = [];
      const resumeScore = coerceNumber(c.resumeScore);
      if (resumeScore != null) parts.push(resumeScore);
      const a = getAssessmentPctNumber(c);
      if (typeof a === 'number') parts.push(a);
      const interviewScore = coerceNumber(c.interviewScore);
      if (interviewScore != null) parts.push(interviewScore);
      return parts.length ? Math.round(parts.reduce((s, x) => s + x, 0) / parts.length) : null;
    };
    const decisionLabel = (score) => {
      if (score == null) return 'REVIEW';
      if (score >= 80) return 'ADVANCE';
      if (score >= 65) return 'CONSIDER';
      if (score >= 50) return 'HOLD';
      return 'REJECT';
    };
    const coverageCount = (rows, getter) => rows.reduce((n, c) => (getter(c) != null ? n + 1 : n), 0);
    const pct = (count, total) => (total > 0 ? Math.round((count / total) * 100) : 0);
    const poolResume = candidates.map((c) => coerceNumber(c.resumeScore)).filter((v) => typeof v === 'number');
    const poolAssessment = candidates.map(getAssessmentPctNumber).filter((v) => typeof v === 'number');
    const poolInterview = candidates.map((c) => coerceNumber(c.interviewScore)).filter((v) => typeof v === 'number');
    const reportResume = candidatesToReport.map((c) => coerceNumber(c.resumeScore)).filter((v) => typeof v === 'number');
    const reportAssessment = candidatesToReport.map(getAssessmentPctNumber).filter((v) => typeof v === 'number');
    const reportInterview = candidatesToReport
      .map((c) => coerceNumber(c.interviewScore))
      .filter((v) => typeof v === 'number');
    const poolOverall = candidates.map((c) => candidateOverall(c)).filter((v) => typeof v === 'number');
    const shortlistOverall = candidatesToReport
      .map((c) => candidateOverall(c))
      .filter((v) => typeof v === 'number');
    const drawCard = ({ x, y, w, h, title, value, sub = null, meter = null }) => {
      doc.setDrawColor(...THEME.border);

      doc.setLineWidth(0.4);

      doc.setFillColor(...THEME.white);
      doc.roundedRect(x, y, w, h, 3.2, 3.2, 'FD');
      doc.setFontSize(7.7);
      doc.setTextColor(...THEME.textMuted);
      doc.text(title, x + 3, y + 4.8);
      const hasMeter = typeof meter === 'number' && Number.isFinite(meter);
      const valueText = String(value);
      const valueSize = valueText.length > 10 ? 9.5 : valueText.length > 8 ? 10.8 : 13.2;
      doc.setFontSize(valueSize);
      doc.setTextColor(...THEME.textStrong);
      // Keep the main value comfortably above the meter bar (when present).
      doc.text(valueText, x + 3, y + (sub ? 9.8 : 10.7));
      if (sub) {
        doc.setFontSize(7.1);
        doc.setTextColor(...THEME.textSoft);
        // If a meter is present, move the subtext upward to avoid overlap.
        doc.text(sub, x + 3, y + h - (hasMeter ? 4.2 : 2.4));
      }
      if (hasMeter) {
        const v = Math.max(0, Math.min(100, meter));
        const meterColor = v >= 80 ? THEME.success : v >= 65 ? THEME.warning : v >= 50 ? THEME.primaryLight : THEME.danger;
        doc.setFillColor(...THEME.border);
        doc.roundedRect(x + 3, y + h - 3.2, w - 6, 1.8, 1, 1, 'F');
        doc.setFillColor(...meterColor);
        doc.roundedRect(x + 3, y + h - 3.2, Math.max(1, ((w - 6) * v) / 100), 1.8, 1, 1, 'F');
      }
    };
    const drawMiniBars = ({ x, y, w, h, title, items }) => {
      doc.setDrawColor(...THEME.border);

      doc.setLineWidth(0.4);

      doc.setFillColor(...THEME.white);
      doc.roundedRect(x, y, w, h, 3.2, 3.2, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...THEME.text);
      doc.text(title, x + 3, y + 4.8);
      // Match the geometry behavior used by Pipeline Funnel / Average Scores:
      // label text sits slightly above the bar, and the bar starts at x + 3 with width w - 6.
      let by = y + 10;
      items.forEach((it) => {
        // Scale bars proportionally to the percent value out of 100.
        const v = typeof it.value === 'number' ? it.value : 0;
        const vClamped = Math.max(0, Math.min(100, v));
        const trackX = x + 3;
        const trackW = w - 6;
        const bw = Math.max(2, (trackW * vClamped) / 100);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.2);
        doc.setTextColor(...THEME.textMuted);
        doc.text(it.label, x + 3, by + 2.2);
        // Value text aligned to the right of the row (like funnel/risk).
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.1);
        doc.setTextColor(...THEME.textStrong);
        const valueText = typeof it.value === 'number' ? `${Math.round(vClamped)}%` : 'N/A';
        doc.text(valueText, x + w - 3, by + 2.2, { align: 'right' });
        // Track + fill bars.
        const trackY = by + 3.2;
        doc.setFont('helvetica', 'normal');
        doc.setFillColor(...THEME.border);
        doc.roundedRect(trackX, trackY, trackW, 2.2, 1, 1, 'F');
        doc.setFillColor(...it.color);
        doc.roundedRect(trackX, trackY, bw, 2.2, 1, 1, 'F');
        by += 7.4;
      });
    };
    const drawFunnel = ({ x, y, w, h, title, stages }) => {
      doc.setDrawColor(...THEME.border);

      doc.setLineWidth(0.4);

      doc.setFillColor(...THEME.white);
      doc.roundedRect(x, y, w, h, 3.2, 3.2, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...THEME.text);
      doc.text(title, x + 3, y + 4.8);
      const maxVal = Math.max(1, ...stages.map((s) => s.value || 0));
      let sy = y + 10;
      stages.forEach((s) => {
        const bw = Math.max(2, ((w - 14) * (s.value || 0)) / maxVal);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.2);
        doc.setTextColor(...THEME.textMuted);
        doc.text(s.label, x + 3, sy + 2.2);
        doc.setTextColor(...THEME.textStrong);
        doc.text(String(s.value || 0), x + w - 3, sy + 2.2, { align: 'right' });
        doc.setFillColor(...THEME.border);
        doc.roundedRect(x + 3, sy + 3.2, w - 6, 2.2, 1, 1, 'F');
        doc.setFillColor(...THEME.primaryLight);
        doc.roundedRect(x + 3, sy + 3.2, bw, 2.2, 1, 1, 'F');
        sy += 7.4;
      });
    };
    const drawSimpleGauge = ({ x, y, w, title, value, subtitle }) => {
      const v = Math.max(0, Math.min(100, Number(value) || 0));
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(30, 41, 59);
      doc.text(title, x, y);
      doc.setFillColor(226, 232, 240);
      doc.roundedRect(x, y + 2, w, 4, 1.2, 1.2, 'F');
      const color = v >= 75 ? [34, 197, 94] : v >= 50 ? [245, 158, 11] : [239, 68, 68];
      doc.setFillColor(...color);
      doc.roundedRect(x, y + 2, (w * v) / 100, 4, 1.2, 1.2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.8);
      doc.setTextColor(...THEME.textStrong);
      doc.text(`${v}%`, x + w + 2, y + 5.5);
      if (subtitle) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.2);
        doc.setTextColor(...THEME.textSoft);
        doc.text(subtitle, x, y + 9.8);
      }
    };
    const drawSpotlightColumns = ({ x, y, w, h, groups }) => {
      const gap = 3;
      const colW = (w - gap * 2) / 3;
      const maxTextY = y + h - 1.2; // keep content inside column boxes
      groups.forEach((g, i) => {
        const cx = x + i * (colW + gap);
        doc.setDrawColor(226, 232, 240);
        doc.setFillColor(...g.bg);
        doc.roundedRect(cx, y, colW, h, 2, 2, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.6);
        doc.setTextColor(...g.titleColor);
        doc.text(g.title, cx + 3, y + 5.5);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.6);
        doc.setTextColor(...THEME.text);
        let ly = y + 10;
        const itemsToRender = g.items && g.items.length ? g.items : ['No strong signal detected'];
        for (const it of itemsToRender.slice(0, 3)) {
          const wrapped = doc.splitTextToSize(`- ${truncate(it, 70)}`, colW - 6);
          const estHeight = wrapped.length * 3.2 + 0.6;
          if (ly + estHeight > maxTextY) break;
          doc.text(wrapped, cx + 3, ly);
          ly += estHeight;
        }
      });
    };
    const drawSectionCard = ({ x, y, w, h, fill = THEME.white, border = THEME.border }) => {
      doc.setDrawColor(...border);

      doc.setLineWidth(0.4);

      doc.setFillColor(...fill);
      doc.roundedRect(x, y, w, h, 3.2, 3.2, 'FD');
    };
    // Section header block used by Resume/Assessment/Interview cards.
    // Mimics the Figma layout pattern: colored icon tile + bold title.
    const drawAnalysisHeader = ({ x, y, w, title, icon = 'resume', scoreText }) => {
      let tileFill = [239, 246, 255];
      let tileInk = [37, 99, 235];
      if (icon === 'assessment') {
        tileFill = [236, 253, 245];
        tileInk = [5, 150, 105];
      } else if (icon === 'interview') {
        tileFill = [245, 243, 255];
        tileInk = [124, 58, 237];
      }
      const tileX = x + 3;
      const tileY = y + 2.2;
      const tileW = 6.2;
      const tileH = 6.2;
      const iconCx = tileX + tileW / 2;
      const iconCy = tileY + tileH / 2;
      doc.setFillColor(...tileFill);
      doc.setDrawColor(...tileFill);
      doc.roundedRect(tileX, tileY, tileW, tileH, 1.2, 1.2, 'FD');
      doc.setDrawColor(...tileInk);
      doc.setTextColor(...tileInk);
      doc.setLineWidth(0.35);
      if (icon === 'resume') {
        // Small document glyph
        doc.rect(iconCx - 1.15, iconCy - 1.55, 2.3, 3.1);
        doc.line(iconCx - 0.7, iconCy - 0.55, iconCx + 0.7, iconCy - 0.55);
        doc.line(iconCx - 0.7, iconCy + 0.1, iconCx + 0.7, iconCy + 0.1);
      } else if (icon === 'assessment') {
        // Clipboard + check glyph
        doc.rect(iconCx - 1.2, iconCy - 1.45, 2.4, 2.9);
        doc.rect(iconCx - 0.55, iconCy - 1.95, 1.1, 0.45);
        doc.line(iconCx - 0.65, iconCy + 0.15, iconCx - 0.2, iconCy + 0.55);
        doc.line(iconCx - 0.2, iconCy + 0.55, iconCx + 0.75, iconCy - 0.35);
      } else {
        // Chat bubble glyph
        doc.roundedRect(iconCx - 1.35, iconCy - 1.2, 2.7, 2.25, 0.5, 0.5, 'S');
        doc.line(iconCx - 0.25, iconCy + 1.05, iconCx - 0.9, iconCy + 1.55);
        doc.line(iconCx - 0.6, iconCy - 0.3, iconCx + 0.6, iconCy - 0.3);
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.setTextColor(...THEME.text);
      doc.text(title, x + 11, y + 6.3);
      // Colored score chip + meter bar (replaces the old "Score: xx%" label).
      const parsedScore = (() => {
        if (scoreText == null) return null;
        if (typeof scoreText === 'number' && Number.isFinite(scoreText)) return scoreText;
        const s = String(scoreText || '');
        if (!s) return null;
        const stripped = String(s).replace(/[^0-9.]/g, '');
        if (!stripped) return null;
        const num = Number(stripped);
        return Number.isFinite(num) ? num : null;
      })();
      const hasNumScore = parsedScore != null;
      const v = hasNumScore ? Math.max(0, Math.min(100, parsedScore)) : 0;
      const scoreColor = hasNumScore
        ? v >= 80
          ? THEME.success
          : v >= 65
            ? THEME.warning
            : v >= 50
              ? THEME.primaryLight
              : THEME.danger
        : THEME.textSoft;
      const chipFill = scoreColor;
      const chipTextColor = hasNumScore ? [255, 255, 255] : THEME.white;
      const chipText = hasNumScore ? `${Math.round(v)}%` : 'N/A';
      const chipW = 20;
      const chipH = 4.6;
      const chipX = x + w - 3 - chipW;
      const chipY = y + 2.0;
      const meterW = chipW;
      const meterH = 1.5;
      const meterX = chipX;
      const meterY = chipY + chipH + 0.7;
      doc.setFillColor(...chipFill);
      doc.setDrawColor(...chipFill);
      doc.roundedRect(chipX, chipY, chipW, chipH, 2, 2, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.4);
      doc.setTextColor(...chipTextColor);
      doc.text(chipText, chipX + chipW / 2, chipY + chipH / 2 + 1.0, { align: 'center' });
      // Meter track + fill.
      doc.setFillColor(...THEME.border);
      doc.setDrawColor(...THEME.border);
      doc.roundedRect(meterX, meterY, meterW, meterH, 1, 1, 'F');
      if (hasNumScore) {
        const fillW = Math.max(0.5, (meterW * v) / 100);
        doc.setFillColor(...chipFill);
        doc.setDrawColor(...chipFill);
        doc.roundedRect(meterX, meterY, fillW, meterH, 1, 1, 'F');
      }
    };
    const drawBulletedList = ({ x, y, maxW, items, bullet = '-', color = THEME.text, maxItems = 3, lineH = 3.3 }) => {
      let currentY = y;
      (items || []).slice(0, maxItems).forEach((item) => {
        const wrapped = doc.splitTextToSize(`${bullet} ${truncate(item, 180)}`, maxW);
        doc.setTextColor(...color);
        doc.text(wrapped, x, currentY);
        currentY += wrapped.length * lineH + 0.4;
      });
      return currentY;
    };
    const drawPill = ({ x, y, text, fill, textColor, border = fill, paddingX = 3.2, h = 5.2, fontSize = 7.2 }) => {
      const label = String(text || '').trim();
      const w = Math.max(16, doc.getTextWidth(label) + paddingX * 2);
      doc.setFillColor(...fill);
      doc.setDrawColor(...border);

      doc.setLineWidth(0.3);

      doc.roundedRect(x, y, w, h, 2.2, 2.2, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fontSize);
      doc.setTextColor(...textColor);
      doc.text(label, x + w / 2, y + h / 2 + 0.95, { align: 'center' });
      return w;
    };
    const drawDetailCard = ({ x, y, w, title, items, h = 28 }) => {

      drawSectionCard({ x, y, w, h, fill: THEME.white, border: THEME.border });

      doc.setFont('helvetica', 'bold');

      doc.setFontSize(10);

      doc.setTextColor(...THEME.textStrong);
      doc.text(title, x + 3.2, y + 5.1);
      let lineY = y + 10.3;
      (items || []).slice(0, 4).forEach(({ label, value }) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(...THEME.textMuted);
        doc.text(String(label).toUpperCase(), x + 3.2, lineY);

        doc.setFont('helvetica', 'normal');

        doc.setFontSize(9.3);
        doc.setTextColor(...THEME.textStrong);
        const wrapped = doc.splitTextToSize(safeText(value), w - 24);
        doc.text(wrapped, x + w - 3.2, lineY, { align: 'right' });
        lineY += Math.max(6.2, wrapped.length * 3.4 + 1.4);
      });
    };
    const drawAnalysisSection = ({
      x,
      y,
      w,
      title,
      icon: _icon,
      scoreText,
      bodyText,
      bodyParagraphs = null,
      secondaryValue = null,
      secondaryLabel = null,
      maxLines = 5,
      minHeight = 30,
      showBody = true,
      verticalCenter = false,
      boxHeight = null,
    }) => {

      const metricColumnW = 24;
      const bodyTop = y + 11.5;
      const lineStep = 4.9;
      const hasPrimaryScore = scoreText != null && String(scoreText).trim() !== '' && String(scoreText).trim() !== 'N/A';
      // Reserve space for score column (60mm) if score exists, otherwise use full width minus padding
      const bodyW = hasPrimaryScore ? Math.max(50, w - 60) : Math.max(50, w - 8);
      const scoreValueText = hasPrimaryScore ? `${Math.round(Number(String(scoreText).replace(/[^0-9.]/g, '')) || 0)}%` : 'N/A';
      const rawParagraphs = Array.isArray(bodyParagraphs)
        ? bodyParagraphs
        : String(bodyText || '')
          .replace(/\*\*/g, '')
          .replace(/\u00A0/g, ' ')
          .trim()
          .split('\n');
      const renderParagraphs = rawParagraphs
        .map((line) => String(line || '').trim())
        .filter(Boolean);
      const bodyLines = showBody
        ? renderParagraphs.flatMap((line) => doc.splitTextToSize(line, bodyW))
          .slice(0, maxLines)
        : [];
      const bodyH = bodyLines.length ? Math.max(10.5, bodyLines.length * lineStep) : 0;
      const h = Math.max(minHeight, showBody && bodyLines.length ? bodyTop - y + bodyH + 2.5 : 12);
      const actualBoxHeight = boxHeight || h;
      const metricCenterY = verticalCenter ? y + actualBoxHeight / 2 : y + h / 2;
      const titleY = verticalCenter ? y + actualBoxHeight / 2 - 3 : y + 4;
      const scoreX = x + w - 24;
      const leftMetricX = x + w - 52;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12.5);
      doc.setTextColor(...THEME.textStrong);
      doc.text(title, x, titleY);

      if (hasPrimaryScore) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(15.5);
        doc.setTextColor(...THEME.textStrong);

        // Draw separator line before score column (consistent across all sections)
        doc.setDrawColor(203, 213, 225);
        doc.line(x + w - 28, metricCenterY - 5.8, x + w - 28, metricCenterY + 5.8);

        if (secondaryValue != null && secondaryLabel) {
          doc.text(String(secondaryValue), leftMetricX, metricCenterY - 0.6);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          doc.setTextColor(...THEME.textSoft);
          doc.text(String(secondaryLabel).toUpperCase(), leftMetricX, metricCenterY + 3.9);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(15.5);
          doc.setTextColor(...THEME.textStrong);
        }

        doc.text(scoreValueText, scoreX, metricCenterY - 0.6);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(...THEME.textSoft);
        doc.text('SCORE', scoreX, metricCenterY + 3.9);
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(51, 65, 85);
      if (showBody) {
        bodyLines.forEach((ln, idx) => {

          doc.text(ln, x, bodyTop + idx * lineStep);

        });
      }
      return h;
    };
    const drawFooter = (_text, pageNumber, totalPages) => {
      doc.setFontSize(7.1);
      doc.setTextColor(...THEME.textSoft);
      doc.setDrawColor(...THEME.border);
      doc.line(margin, pageH - 11, pageW - margin, pageH - 11);
      // Report ID: keep in the footer's original position.
      doc.text(`Report ID: ${reportId}`, margin, pageH - 7);
      doc.text(`Page ${pageNumber} of ${totalPages}`, pageW - margin - 17.5, pageH - 7, { align: 'right' });
      doc.text('Confidential', pageW - margin, pageH - 7, { align: 'right' });
    };
    const drawReportHeader = (title, { showReportId = true } = {}) => {
      const headerH = 35;
      for (let i = 0; i < 32; i += 1) {
        const t = i / 31;
        const r = Math.round(THEME.primaryDark[0] + (THEME.primaryLight[0] - THEME.primaryDark[0]) * t);
        const g = Math.round(THEME.primaryDark[1] + (THEME.primaryLight[1] - THEME.primaryDark[1]) * t);
        const b = Math.round(THEME.primaryDark[2] + (THEME.primaryLight[2] - THEME.primaryDark[2]) * t);
        doc.setFillColor(r, g, b);
        const stripeW = pageW / 32;
        doc.rect(i * stripeW, 0, stripeW + 0.4, headerH, 'F');
      }

      const iconSize = 11;
      const iconX = margin + 2;
      const iconY = headerH / 2 - (iconSize * 1.3) / 2;

      doc.setDrawColor(255, 255, 255);
      doc.setFillColor(255, 255, 255);
      doc.setLineWidth(0.6);
      doc.roundedRect(iconX, iconY, iconSize, iconSize * 1.3, 1, 1, 'S');
      doc.setLineWidth(0.4);
      doc.line(iconX + 2.5, iconY + 4, iconX + iconSize - 2.5, iconY + 4);
      doc.line(iconX + 2.5, iconY + 6.5, iconX + iconSize - 2.5, iconY + 6.5);
      doc.line(iconX + 2.5, iconY + 9, iconX + iconSize - 2.5, iconY + 9);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(255, 255, 255);
      doc.text(title, iconX + iconSize + 6, headerH / 2 + 2);

      const rightX = pageW - margin - 3;
      const userY = headerH / 2 - 5;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text(createdByName, rightX, userY, { align: 'right' });

      const dateY = headerH / 2 + 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(reportGeneratedAt, rightX, dateY, { align: 'right' });
    };
    const ranked = [...candidatesToReport]
      .map((c) => ({ c, score: candidateOverall(c) }))
      .sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
    // Total PDF pages are dynamic once candidates are allowed to flow.
    // PAGE 1: dashboard (Figma flow: Snapshot -> KPIs -> Visuals -> Legend)
    drawReportHeader('Candidate Interview Report', { showReportId: false });
    const poolTotal = candidates.length;
    const shortlistTotal = shortlistCandidates.length;
    const selectedTotal = candidatesToReport.length;
    const conversion = pct(shortlistTotal, poolTotal);
    // More breathing room on the first (dashboard) page.
    let y = 48;
    const coverageResume = coverageCount(candidates, (c) => c.resumeScore);
    const coverageAssessment = coverageCount(candidates, getAssessmentPctNumber);
    const coverageInterview = coverageCount(candidates, (c) => c.interviewScore);
    const riskDistribution = candidates.reduce(
      (acc, cand) => {
        const bucket = getProctoringIntegrityBucket(cand);
        if (bucket === 'Low') acc.low += 1;
        else if (bucket === 'Moderate') acc.moderate += 1;
        else if (bucket === 'High') acc.high += 1;
        else acc.unknown += 1;
        return acc;
      },
      { low: 0, moderate: 0, high: 0, unknown: 0 }
    );
    const completedPool = candidates.filter((c) => String(c.status || '').toLowerCase() === 'completed').length;

    // 1) Shortlisted Candidates - Title with blue underline
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...THEME.textStrong);
    doc.text('Selected Candidates', margin, y);

    // Blue underline
    doc.setDrawColor(...THEME.primary);
    doc.setLineWidth(1.2);
    doc.line(margin, y + 1.5, margin + 45, y + 1.5);

    // Shortlist card: table is flush with the card (no inner white gutter). Header fill uses the same
    // origin and corner radius as the card so blue meets the rounded top border.
    const shortlistCardX = margin;
    const shortlistCardY = y + 8;
    const shortlistCardW = contentW;
    const shortlistCardR = 3.2;
    const shortlistCardPad = 0;
    const shortlistTableX = shortlistCardX;
    const shortlistTableY = shortlistCardY;
    const shortlistTableW = shortlistCardW;
    const shortlistCardMaxH = Math.max(38, Math.min(140, pageH - shortlistCardY - 28));
    let shortlistCardBgDrawn = false;
    autoTable(doc, {
      startY: shortlistTableY,
      tableWidth: shortlistTableW,
      // Hide the plugin's square outer border; we frame the table with a rounded "card" instead.
      tableLineWidth: 0,
      head: [[
        '#',
        'Candidate',
        'Location',
        'Organisation',
        'Designation',
        'Qualification',
        'Experience',
      ]],
      body: ranked.map(({ c }, idx) => {
        const ageVal = c.ageYears ?? c.age;
        const ageText =
          ageVal != null && !Number.isNaN(Number(ageVal))
            ? String(Math.round(Number(ageVal)))
            : safeText(ageVal);
        const totalExpVal = c.experienceYears ?? c.experience;
        const totalExpNum = coerceNumber(totalExpVal);
        const totalExpText =
          totalExpNum != null ? `${Math.round(totalExpNum * 10) / 10} Years` : safeText(totalExpVal);

        const qualificationText = [
          safeText(c.education),
          safeText(c.maxEducationLevel || c.professionalQualification),
        ]
          .filter((x) => x && x !== '-')
          .join('\n');
        const formatLacs = (primary, fallback) => {
          const n = coerceNumber(primary);
          if (n != null) return `${Math.round(n * 100) / 100} Lacs`;
          const s = safeText(fallback);
          return s && s !== '-' ? s : '-';
        };
        const formatSalaryLpa = (primary, fallback) => {
          const primaryNum = coerceNumber(primary);
          if (primaryNum != null) return `${Math.round(primaryNum * 100) / 100} LPA`;
          const fallbackNum = coerceNumber(fallback);
          if (fallbackNum != null) return `${Math.round(fallbackNum * 100) / 100} LPA`;
          return '-';
        };
        const currentCtcText = formatLacs(c.currentCtcLacs, c.currentCtc);
        const expectedCtcText = formatSalaryLpa(c.expectedCtcLacs, c.salaryExpectation);
        return [
          idx + 1,
          truncate(getCandidateDisplayName(c), 42),
          safeText(c.location),
          safeText(c.company),
          safeText(c.currentRole),
          qualificationText,
          totalExpText,
        ];
      }),
      margin: { left: shortlistTableX, right: shortlistTableX },
      willDrawPage: () => {
        // Draw a generous rounded card background before the table renders.
        // We'll trim the excess below the table once we know the final height, then
        // draw the final border only once so no phantom edge leaks below the content.
        if (shortlistCardBgDrawn) return;
        shortlistCardBgDrawn = true;
        doc.setFillColor(...THEME.white);
        doc.roundedRect(shortlistCardX, shortlistCardY, shortlistCardW, shortlistCardMaxH, shortlistCardR, shortlistCardR, 'F');
      },
      willDrawCell: (data) => {
        // Full-width header cap aligned to the card box (not an inset table), rounded top = card radius.
        if (data.section === 'head' && data.row.index === 0 && data.column.index === 0) {
          const headerH = data.row.height;
          const r = shortlistCardR;
          doc.setFillColor(...THEME.primaryLight);
          doc.roundedRect(shortlistCardX, shortlistCardY, shortlistCardW, headerH, r, r, 'F');
          doc.rect(shortlistCardX, shortlistCardY + headerH - r, shortlistCardW, r + 0.2, 'F');
        }
      },
      didParseCell: (data) => {
        // Keep internal grid lines, but remove outermost borders so the table fits a rounded frame cleanly.
        try {
          const baseLineWidth =
            typeof data?.cell?.styles?.lineWidth === 'number' ? data.cell.styles.lineWidth : 0.2;
          const colCount = Array.isArray(data?.table?.columns) ? data.table.columns.length : 0;
          const lastColIdx = colCount ? colCount - 1 : null;
          const isFirstCol = data?.column?.index === 0;
          const isLastCol = lastColIdx != null && data?.column?.index === lastColIdx;
          const isHead = data?.section === 'head';
          const isBody = data?.section === 'body';
          const isFirstRow = data?.row?.index === 0;
          const bodyLen = Array.isArray(data?.table?.body) ? data.table.body.length : null;
          const isLastBodyRow = isBody && bodyLen != null && data?.row?.index === bodyLen - 1;
          const lineWidths = {
            top: baseLineWidth,
            right: baseLineWidth,
            bottom: baseLineWidth,
            left: baseLineWidth,
          };
          if (isFirstCol) lineWidths.left = 0;
          if (isLastCol) lineWidths.right = 0;
          if (isHead && isFirstRow) lineWidths.top = 0;
          if (isLastBodyRow) lineWidths.bottom = 0;
          if (data?.cell?.styles) data.cell.styles.lineWidth = lineWidths;
        } catch {
          // Non-fatal: fall back to default borders if the plugin data shape changes.
        }
      },
      styles: {

        fontSize: 8,

        cellPadding: { top: 3, right: 2, bottom: 3, left: 2 },

        textColor: THEME.text,
        lineColor: THEME.border,

        lineWidth: 0.3,

        fillColor: false,
        valign: 'middle',
        halign: 'center',
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: false,
        textColor: 255,
        fontStyle: 'bold',

        fontSize: 7.5,

        cellPadding: { top: 3, right: 1.5, bottom: 3, left: 1.5 },

        halign: 'center',

        valign: 'middle',

        lineColor: THEME.primaryLight,

        lineWidth: 0.2,

      },

      // Widths sum to contentW (full-width table, no horizontal inset).
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 40, halign: 'left' },
        2: { cellWidth: 25, halign: 'left' },
        3: { cellWidth: 35, halign: 'left' },
        4: { cellWidth: 35, halign: 'left' },
        5: { cellWidth: 30, halign: 'left' },
        6: { cellWidth: 15, halign: 'center' },
      },
      alternateRowStyles: { fillColor: false },
    });
    // Trim the pre-drawn card background to the table's true height, then draw the final rounded frame.
    try {
      const t = doc.lastAutoTable;
      const finalY = typeof t?.finalY === 'number' ? t.finalY : null;
      const cardHRaw = finalY != null ? finalY - shortlistCardY + 0.25 : null;
      const cardH = cardHRaw != null ? Math.max(34, Math.min(shortlistCardMaxH, cardHRaw)) : null;
      if (cardH != null && cardH > 0) {
        const excessH = shortlistCardMaxH - cardH;
        if (excessH > 0.5) {
          doc.setFillColor(...THEME.pageCanvas);
          doc.rect(shortlistCardX, shortlistCardY + cardH, shortlistCardW, excessH + 0.8, 'F');
        }
        doc.setDrawColor(...THEME.border);
        doc.setLineWidth(0.25);
        doc.roundedRect(shortlistCardX, shortlistCardY, shortlistCardW, cardH, shortlistCardR, shortlistCardR, 'S');
      }
    } catch {
      // Keep PDF generation resilient; the table itself is the important part.
    }
    // 2) Shortlist Performance Overview - New Design
    const afterTableY = (doc.lastAutoTable?.finalY || y + 34) + 14;
    const overviewY = afterTableY + 8;

    // Title with blue underline
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...THEME.textStrong);
    doc.text('Shortlist Performance Overview', margin, overviewY);

    // Blue underline
    doc.setDrawColor(...THEME.primary);
    doc.setLineWidth(1.2);
    doc.line(margin, overviewY + 1.5, margin + 35, overviewY + 1.5);

    const cardsY = overviewY + 10;
    const cardGap = 10;
    const cardW = (contentW - cardGap) / 2;
    const cardH = 85;

    // Left Card: Pipeline Funnel
    const leftCardX = margin;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(leftCardX, cardsY, cardW, cardH, 4, 4, 'FD');

    // Pipeline Funnel Icon (users icon in blue rounded square)
    const iconX = leftCardX + 6;
    const iconY = cardsY + 6;
    const iconSize = 9;
    doc.setFillColor(219, 234, 254);
    doc.roundedRect(iconX, iconY, iconSize, iconSize, 2.5, 2.5, 'F');
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.6);
    // Left user
    doc.circle(iconX + 2.5, iconY + 2.5, 0.8, 'S');
    doc.setLineWidth(0.8);
    doc.line(iconX + 1.3, iconY + 6.5, iconX + 1.8, iconY + 4.2);
    doc.line(iconX + 1.8, iconY + 4.2, iconX + 3.2, iconY + 4.2);
    doc.line(iconX + 3.2, iconY + 4.2, iconX + 3.7, iconY + 6.5);
    // Right user
    doc.setLineWidth(0.6);
    doc.circle(iconX + 6.5, iconY + 2.5, 0.8, 'S');
    doc.setLineWidth(0.8);
    doc.line(iconX + 5.3, iconY + 6.5, iconX + 5.8, iconY + 4.2);
    doc.line(iconX + 5.8, iconY + 4.2, iconX + 7.2, iconY + 4.2);
    doc.line(iconX + 7.2, iconY + 4.2, iconX + 7.7, iconY + 6.5);

    // Pipeline Funnel Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.text('Pipeline Funnel', iconX + 10, iconY + 5);

    // Funnel bars
    let barY = cardsY + 18;
    const barX = leftCardX + 6;
    const barMaxW = cardW - 12;

    // Applicant Pool
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text('Applicant Pool', barX, barY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(String(poolTotal), barX + barMaxW, barY, { align: 'right' });
    doc.setFillColor(226, 232, 240);
    doc.roundedRect(barX, barY + 2, barMaxW, 4, 2, 2, 'F');
    doc.setFillColor(59, 130, 246);
    doc.roundedRect(barX, barY + 2, barMaxW, 4, 2, 2, 'F');
    barY += 12;

    // Completed
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text('Completed', barX, barY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(String(completedPool), barX + barMaxW, barY, { align: 'right' });
    const completedPct = poolTotal > 0 ? (completedPool / poolTotal) : 0;
    doc.setFillColor(226, 232, 240);
    doc.roundedRect(barX, barY + 2, barMaxW, 4, 2, 2, 'F');
    doc.setFillColor(99, 102, 241);
    doc.roundedRect(barX, barY + 2, barMaxW * completedPct, 4, 2, 2, 'F');
    barY += 12;

    // Shortlisted
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text('Selected', barX, barY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(String(shortlistTotal), barX + barMaxW, barY, { align: 'right' });
    const shortlistPct = poolTotal > 0 ? (shortlistTotal / poolTotal) : 0;
    doc.setFillColor(226, 232, 240);
    doc.roundedRect(barX, barY + 2, barMaxW, 4, 2, 2, 'F');
    doc.setFillColor(139, 92, 246);
    doc.roundedRect(barX, barY + 2, barMaxW * shortlistPct, 4, 2, 2, 'F');

    // Conversion and Success Rate
    const metricsY = cardsY + 60;
    const metricW = (cardW - 12) / 2;

    // Conversion Rate
    const conversionRate = poolTotal > 0 ? Math.round((shortlistTotal / poolTotal) * 1000) / 10 : 0;
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.5);
    // Circle with diagonal line (prohibition/conversion symbol)
    doc.circle(leftCardX + 8, metricsY, 2, 'S');
    doc.line(leftCardX + 6.8, metricsY - 1.2, leftCardX + 9.2, metricsY + 1.2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text('Conversion Rate', leftCardX + 11, metricsY + 1);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    doc.text(`${conversionRate}%`, leftCardX + 6 + metricW / 2, metricsY + 9, { align: 'center' });

    // Success Rate
    const successRate = completedPool > 0 ? Math.round((shortlistTotal / completedPool) * 1000) / 10 : 0;
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.5);
    // Target/bullseye icon (concentric circles)
    doc.circle(leftCardX + cardW / 2 + 8, metricsY, 2, 'S');
    doc.circle(leftCardX + cardW / 2 + 8, metricsY, 1.2, 'S');
    doc.circle(leftCardX + cardW / 2 + 8, metricsY, 0.5, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text('Success Rate', leftCardX + cardW / 2 + 11, metricsY + 1);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    doc.text(`${successRate}%`, leftCardX + cardW / 2 + 6 + metricW / 2, metricsY + 9, { align: 'center' });

    // Right Card: Average Scores
    const rightCardX = margin + cardW + cardGap;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(rightCardX, cardsY, cardW, cardH, 4, 4, 'FD');

    // Average Scores Icon (trending up arrow in green rounded square)
    const rightIconX = rightCardX + 6;
    const rightIconY = cardsY + 6;
    const rightIconSize = 9;
    doc.setFillColor(220, 252, 231);
    doc.roundedRect(rightIconX, rightIconY, rightIconSize, rightIconSize, 2.5, 2.5, 'F');
    doc.setDrawColor(34, 197, 94);
    doc.setLineWidth(1);
    // Trending line (zigzag upward)
    doc.line(rightIconX + 1.5, rightIconY + 6.5, rightIconX + 4, rightIconY + 4.5);
    doc.line(rightIconX + 4, rightIconY + 4.5, rightIconX + 7.5, rightIconY + 2.5);
    // Arrow head pointing up-right
    doc.setLineWidth(0.8);
    doc.line(rightIconX + 6.2, rightIconY + 2.5, rightIconX + 7.5, rightIconY + 2.5);
    doc.line(rightIconX + 7.5, rightIconY + 2.5, rightIconX + 7.5, rightIconY + 3.8);

    // Average Scores Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.text('Average Scores', rightIconX + 10, rightIconY + 5);

    // Bar Chart
    const chartY = cardsY + 22;
    const chartX = rightCardX + 15;
    const chartW = cardW - 30;
    const chartH = 32;
    const barWidth = 18;
    const barGap = (chartW - barWidth * 3) / 4;

    const resumeAvg = avg(reportResume) ?? 0;
    const assessmentAvg = hasAssessment ? (avg(reportAssessment) ?? 0) : 0;
    const interviewAvg = hasInterview ? (avg(reportInterview) ?? 0) : 0;

    // Y-axis labels
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text('100', chartX - 3, chartY + 1, { align: 'right' });
    doc.text('75', chartX - 3, chartY + chartH * 0.25 + 1, { align: 'right' });
    doc.text('50', chartX - 3, chartY + chartH * 0.5 + 1, { align: 'right' });
    doc.text('25', chartX - 3, chartY + chartH * 0.75 + 1, { align: 'right' });
    doc.text('0', chartX - 3, chartY + chartH + 1, { align: 'right' });

    // Resume Score Bar
    const resumeBarH = Math.max(2, (resumeAvg / 100) * chartH);
    doc.setFillColor(59, 130, 246);
    doc.roundedRect(chartX + barGap, chartY + chartH - resumeBarH, barWidth, resumeBarH, 2, 2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(71, 85, 105);
    const resumeLabel = doc.splitTextToSize('Resume Score', barWidth + 2);
    doc.text(resumeLabel, chartX + barGap + barWidth / 2, chartY + chartH + 4, { align: 'center' });

    // Assessment Score Bar
    const assessBarH = Math.max(2, (assessmentAvg / 100) * chartH);
    doc.setFillColor(34, 197, 94);
    doc.roundedRect(chartX + barGap * 2 + barWidth, chartY + chartH - assessBarH, barWidth, assessBarH, 2, 2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(71, 85, 105);
    doc.text('Assessment', chartX + barGap * 2 + barWidth + barWidth / 2, chartY + chartH + 4, { align: 'center' });

    // Interview Score Bar
    const interviewBarH = Math.max(2, (interviewAvg / 100) * chartH);
    doc.setFillColor(251, 146, 60);
    doc.roundedRect(chartX + barGap * 3 + barWidth * 2, chartY + chartH - interviewBarH, barWidth, interviewBarH, 2, 2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(71, 85, 105);
    const interviewLabel = doc.splitTextToSize('Interview Score', barWidth + 2);
    doc.text(interviewLabel, chartX + barGap * 3 + barWidth * 2 + barWidth / 2, chartY + chartH + 4, { align: 'center' });

    // Score percentages below chart
    const percentY = chartY + chartH + 14;

    // Resume percentage
    doc.setFillColor(59, 130, 246);
    doc.circle(chartX + barGap + barWidth / 2, percentY, 1.2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(`${Math.round(resumeAvg)}%`, chartX + barGap + barWidth / 2, percentY + 6, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text('Resume', chartX + barGap + barWidth / 2, percentY + 10, { align: 'center' });

    // Assessment percentage
    doc.setFillColor(34, 197, 94);
    doc.circle(chartX + barGap * 2 + barWidth + barWidth / 2, percentY, 1.2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(`${Math.round(assessmentAvg)}%`, chartX + barGap * 2 + barWidth + barWidth / 2, percentY + 6, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text('Assessment', chartX + barGap * 2 + barWidth + barWidth / 2, percentY + 10, { align: 'center' });

    // Interview percentage
    doc.setFillColor(251, 146, 60);
    doc.circle(chartX + barGap * 3 + barWidth * 2 + barWidth / 2, percentY, 1.2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(`${Math.round(interviewAvg)}%`, chartX + barGap * 3 + barWidth * 2 + barWidth / 2, percentY + 6, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text('Interview', chartX + barGap * 3 + barWidth * 2 + barWidth / 2, percentY + 10, { align: 'center' });
    // Footers are rendered after all pages are generated (page count is dynamic).
    // Candidate dossier pages: enforce one candidate per page.
    // Page 1 remains the overall dashboard; every candidate starts on a fresh page.
    ranked.forEach(({ c }) => {
      doc.addPage('portrait', 'a4');
      fillPageCanvas();
      const candidateDisplayName = getCandidateDisplayName(c);
      drawReportHeader(truncate(candidateDisplayName, 42), { showReportId: false });
      const HEADER_H_DOSSIER = 35;
      const SLATE_200 = [255, 255, 255];
      doc.setFillColor(...SLATE_200);
      doc.rect(0, HEADER_H_DOSSIER, pageW, pageH - HEADER_H_DOSSIER, 'F');
      // Use 90% of page width for better readability
      const sheetX = pageW * 0.05;
      const sheetY = HEADER_H_DOSSIER + 8;
      const sheetW = pageW * 0.9;
      const sheetBottomPad = 14;
      const sheetH = pageH - sheetY - sheetBottomPad;
      doc.setFillColor(255, 255, 255);
      doc.rect(sheetX, sheetY, sheetW, sheetH, 'F');
      const innerPad = 6;
      const innerX = sheetX + innerPad;
      const innerW = sheetW - innerPad * 2;
      const valueRgb = [15, 23, 42];
      const mutedRgb = [100, 116, 139];
      const formatLpaField = (primary, fallback) => {
        const primaryNum = coerceNumber(primary);
        if (primaryNum != null) return `${Math.round(primaryNum * 100) / 100} LPA`;
        const s = safeText(fallback);
        return s !== '-' ? s : '-';
      };
      const qualText = safeText(c.maxEducationLevel || c.professionalQualification);
      const totalExpYears = coerceNumber(c.experienceYears ?? c.experience);
      const totalExpField = totalExpYears != null ? `${Math.round(totalExpYears * 10) / 10} Years` : safeText(c.experience);
      const shortlistDateStrField = formatDate(c.shortlistedAt);
      const normalizeResumePdf = (tx) =>
        String(tx || '')
          .replace(/\u00A0/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      const cleanResumeAnalysisText = (tx) =>
        String(tx || '')
          .replace(/\*\*/g, '')
          .replace(/\b(?:https?:\/\/|www\.)\S+\b/gi, '')
          .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '')
          .replace(/\+?\d[\d\s()-]{7,}\d/g, '')
          .replace(/\b(?:link|linkedin|email|phone)\s*:?\s*/gi, '')
          .replace(/\s+\|\s+/g, ' ')
          .replace(/\s{2,}/g, ' ')
          .trim();
      const buildResumeAnalysisBullets = () => {
        const sb = c?.scoreBreakdown || {};
        const bulletSource = typeof c?.scoringSummary === 'string' && c.scoringSummary.trim()
          ? c.scoringSummary
          : typeof c?.aiAnalysis === 'string' && c.aiAnalysis.trim()
            ? c.aiAnalysis
            : '';
        const scoreBreakdownBullets = [
          ['Key Responsibilities', sb.responsibilitiesReasoning],
          ['Experience & Level', sb.experienceReasoning || sb.experienceAndLevelReasoning],
          ['Impact & Achievements', sb.impactReasoning],
          ['Career Trajectory', sb.trajectoryReasoning],
          ['Education & Credentials', sb.educationReasoning || sb.educationCredentialsReasoning],
        ]
          .map(([label, text]) => {
            const cleaned = cleanResumeAnalysisText(text);
            return cleaned ? `${label}: ${cleaned}` : null;
          })
          .filter(Boolean);
        if (scoreBreakdownBullets.length) return scoreBreakdownBullets.slice(0, 4);
        const parsedSummaryBullets = String(bulletSource || '')
          .split(/\s*-\s*(?=[A-Z][A-Za-z &]+:\s*)/g)
          .map((part) => cleanResumeAnalysisText(part))
          .filter(Boolean)
          .map((part) => part.replace(/^[-•]\s*/, ''))
          .slice(0, 4);
        if (parsedSummaryBullets.length) return parsedSummaryBullets;
        const fallback = cleanResumeAnalysisText(bulletSource);
        if (fallback) {
          const wrapped = fallback
            .split(/(?<=[.?!])\s+/)
            .map((part) => part.trim())
            .filter(Boolean)
            .slice(0, 3);
          if (wrapped.length) return wrapped;
        }
        const rs = coerceNumber(c.resumeScore ?? c.score);
        if (rs != null) {
          return [`The candidate has a resume alignment score of approximately ${Math.round(rs)}%. Open the profile for full detail.`];
        }
        return ['Resume analysis is available in the platform.'];
      };
      let rY = sheetY + innerPad;
      const profileCardH = 24;
      const detailsSpacing = 6;
      drawSectionCard({ x: innerX, y: rY, w: innerW, h: profileCardH, fill: THEME.cardBg, border: THEME.border });

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16.5);
      doc.setTextColor(...THEME.textStrong);
      doc.text(truncate(candidateDisplayName, 52), innerX + 4, rY + 8.5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10.5);
      doc.setTextColor(...THEME.text);
      doc.text(
        truncate([safeText(c.currentRole), safeText(c.company)].filter((x) => x !== '-').join(' at ') || 'Profile details available in platform.', 110),
        innerX + 4,
        rY + 14,
      );
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.3);
      doc.setTextColor(...THEME.textMuted);
      doc.text(
        truncate(
          [safeText(c.location), safeText(c.email), safeText(c.phone)]
            .filter((x) => x !== '-')
            .join('  |  ') || '-',
          150,
        ),
        innerX + 4,
        rY + 19.2,
      );
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.4);
      doc.setTextColor(...THEME.textSoft);
      doc.text(
        shortlistDateStrField !== '-' ? `Added ${shortlistDateStrField}` : 'Added to shortlist',
        innerX + innerW - 4,
        rY + 15.9,
        { align: 'right' },
      );

      const detailsGap = 6;
      const detailsY = rY + profileCardH + detailsSpacing;
      const detailCardW = (innerW - detailsGap) / 2;
      const detailCardH = 33;
      drawDetailCard({
        x: innerX,
        y: detailsY,
        w: detailCardW,
        h: detailCardH,
        title: 'Profile Details',
        items: [
          { label: 'Qualification', value: qualText },
          { label: 'Education', value: safeText(c.education) },
          { label: 'Total Experience', value: totalExpField },
          { label: 'Current Role', value: safeText(c.currentRole) },
        ],
      });
      drawDetailCard({
        x: innerX + detailCardW + detailsGap,
        y: detailsY,
        w: detailCardW,
        h: detailCardH,
        title: 'Contact Details',
        items: [
          { label: 'Email', value: safeText(c.email) },
          { label: 'Phone', value: safeText(c.phone) },
          { label: 'Location', value: safeText(c.location) },
        ],
      });

      rY = detailsY + detailCardH + detailsSpacing + 8;

      // Check if Resume Analysis should be rendered
      const resumeBullets = buildResumeAnalysisBullets().map((item) => item.replace(/^[-•]\s*/, '').trim()).slice(0, 6);
      const resumeScore = coerceNumber(c.resumeScore ?? c.score);
      // Convert bullets to paragraph text to prevent overlapping
      const resumeBodyText = resumeBullets.length > 0 ? resumeBullets.join(' ') : '';
      const hasResumeData = resumeScore != null || resumeBodyText.length > 0;

      if (hasResumeData) {
        // Draw Resume Analysis box
        const resumeBoxY = rY;
        const resumeBoxPad = 5;
        const resumeBoxH = 55; // Fixed height for resume section
        // Draw box first
        drawSectionCard({ x: innerX, y: resumeBoxY, w: innerW, h: resumeBoxH, fill: THEME.white, border: THEME.border });
        // Then draw content on top
        drawAnalysisSection({
          x: innerX + resumeBoxPad,
          y: rY + resumeBoxPad,
          w: innerW - resumeBoxPad * 2,
          title: 'RESUME ANALYSIS',
          icon: 'resume',
          scoreText: resumeScore,
          bodyText: resumeBodyText || 'Resume analysis is available in the platform.',
          secondaryValue: null,
          secondaryLabel: null,
          maxLines: 6,
          minHeight: 40,
        });
        rY += resumeBoxH + 6;
      }

      // Check if Assessment Analysis should be rendered
      const hasAssessmentEnabled = hasAssessment && screeningMeta?.assessmentId;
      const assessPct = coerceNumber(c.__assessmentDossierPct) ?? getAssessmentPctNumber(c);
      const qn = Number(c.__assessmentQuestionCount);
      const qc = Number(c.__assessmentCorrectCount);
      const hasAssessmentData = assessPct != null || (Number.isFinite(qn) && qn > 0);

      if (hasAssessmentEnabled && hasAssessmentData) {
        const correctTxt =
          Number.isFinite(qn) && qn > 0 && Number.isFinite(qc) ? `${Math.round(qc)}/${Math.round(qn)}` : '—';

        // Draw Assessment Analysis box
        const assessBoxY = rY;
        const assessBoxPad = 5;
        const assessBoxH = 24; // Fixed height for assessment section
        // Draw box first
        drawSectionCard({ x: innerX, y: assessBoxY, w: innerW, h: assessBoxH, fill: THEME.white, border: THEME.border });
        // Then draw content on top with vertical centering
        drawAnalysisSection({
          x: innerX + assessBoxPad,
          y: rY + assessBoxPad,
          w: innerW - assessBoxPad * 2,
          title: 'ASSESSMENT ANALYSIS',
          icon: 'assessment',
          scoreText: assessPct ?? 0,
          bodyText: '',
          secondaryValue: correctTxt,
          secondaryLabel: 'Correct',
          maxLines: 0,
          minHeight: 14,
          showBody: false,
          verticalCenter: true,
          boxHeight: assessBoxH - assessBoxPad * 2,
        });
        rY += assessBoxH + 6;
      }

      // Check if Interview Analysis should be rendered
      const hasInterviewEnabled = hasInterview;
      const interviewScore = coerceNumber(c.__interviewDossierPct) ?? coerceNumber(c.interviewScore);
      const interviewPara = (() => {
        const ils = Array.isArray(c.__interviewDossierLines) ? c.__interviewDossierLines.filter(Boolean) : [];
        if (ils.length) return normalizeResumePdf(ils.join(' '));
        if (typeof c?.interviewSummary === 'string' && c.interviewSummary.trim()) {
          return normalizeResumePdf(c.interviewSummary.trim());
        }
        return '';
      })();
      const hasInterviewData = interviewScore != null || interviewPara.length > 0;

      if (hasInterviewEnabled && hasInterviewData) {
        // Draw Interview Analysis box
        const interviewBoxY = rY;
        const interviewBoxPad = 5;
        const interviewBoxH = 55; // Fixed height for interview section
        // Draw box first
        drawSectionCard({ x: innerX, y: interviewBoxY, w: innerW, h: interviewBoxH, fill: THEME.white, border: THEME.border });
        // Then draw content on top
        drawAnalysisSection({
          x: innerX + interviewBoxPad,
          y: rY + interviewBoxPad,
          w: innerW - interviewBoxPad * 2,
          title: 'INTERVIEW ANALYSIS',
          icon: 'interview',
          scoreText: interviewScore,
          bodyText: interviewPara || 'Interview analysis is available in the platform.',
          secondaryValue: null,
          secondaryLabel: null,
          maxLines: 6,
          minHeight: 40,
        });
        rY += interviewBoxH;
      }

      // Candidate page ends after Interview Analysis.
    });
    // Render footers after all pages are created (page count is dynamic).
    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p += 1) {
      doc.setPage(p);
      drawFooter('', p, totalPages);
    }
    return doc;
  }, [candidates, shortlistCandidates, shortlistReportTitle, screeningMeta, user?.name, user?.displayName, user?.email]);
  /**
   * The candidate report PDF (Selected -> Next steps -> Generate Report -> PDF).
   *
   * This used to be assembled here in the browser: for EVERY selected candidate it fired about
   * five chained requests (full candidate, assessment results, assessment proctoring, interview
   * replay, interview proctoring), then drew the document with jsPDF across ~1,500 lines of
   * coordinate maths, truncating as it went - MAX_ASSESS_LINES = 3, MAX_INTERVIEW_LINES = 6.
   * Six lines to cover an eighteen-question interview. That truncation, not any gap in the
   * data, is why the old report read thin.
   *
   * The server now builds it in one pass and streams back the finished PDF, so all this has to
   * do is save the blob.
   *
   * Takes an explicit id list rather than reading the shortlist selection, because the same
   * PDF is now reachable from three places: the Selected tab's Next Steps modal, the
   * Candidates tab's bulk Actions dropdown, and a single row's three-dot menu. Only the id
   * list differs. Nothing here depends on a candidate's decision and neither does the
   * dossier route - it reads shortlistDecision purely to print an outcome label.
   */
  const downloadCandidateDossierPDF = useCallback(async (candidateIdsInput) => {
    const candidateIds = (candidateIdsInput || []).map((id) => String(id)).filter(Boolean);

    if (candidateIds.length === 0) {
      toast.error('No candidates selected for report');
      return;
    }

    const toastId = toast.loading(
      `Preparing ${candidateIds.length} candidate report${candidateIds.length === 1 ? '' : 's'}…`
    );

    try {
      const res = await axios.post(
        '/api/reports/candidate-dossier',
        { screeningId, candidateIds },
        { responseType: 'blob' }
      );

      const blob = res.data;
      if (!blob || blob.size === 0) {
        toast.error('The report came back empty', { id: toastId });
        return;
      }

      // An error thrown after the headers were sent arrives as JSON in a blob, not as a PDF.
      const contentType = res.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await blob.text();
        let msg = 'Failed to generate report';
        try {
          msg = JSON.parse(text).message || msg;
        } catch (_) { }
        toast.error(msg, { id: toastId });
        return;
      }

      let filename = 'Candidate_Report.pdf';
      const disposition = res.headers['content-disposition'];
      if (disposition) {
        const match = /filename="?([^";\n]+)"?/.exec(disposition);
        if (match) filename = match[1].trim();
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // If a candidate could not be built, say so rather than letting a short report look complete.
      const skipped = Number(res.headers['x-dossier-skipped'] || 0);
      if (skipped > 0) {
        const included = Number(res.headers['x-dossier-count'] || candidateIds.length);
        toast.success(
          `Report ready with ${included} of ${candidateIds.length} candidates. ${skipped} could not be included.`,
          { id: toastId }
        );
      } else {
        toast.success('Report Download Ready', { id: toastId });
      }
    } catch (error) {
      let message = 'Failed to generate report';
      if (error.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await error.response.data.text()).message || message;
        } catch (_) { }
      } else {
        message = error.response?.data?.message || error.message || message;
      }
      console.error('Error generating candidate report PDF:', error);
      toast.error(message, { id: toastId });
    }
  }, [screeningId]);

  // The Selected tab's Next Steps modal still routes through here, so its behaviour is unchanged.
  const handleGenerateShortlistReportPDF = useCallback(
    () =>
      downloadCandidateDossierPDF(
        shortlistCandidates
          .filter((c) => selectedShortlistCandidateIds.includes(c.id || c._id))
          .map((c) => c.id || c._id)
      ),
    [shortlistCandidates, selectedShortlistCandidateIds, downloadCandidateDossierPDF]
  );
  const handleGenerateShortlistReportZIP = useCallback(async () => {
    const candidatesToReportLite = shortlistCandidates.filter((c) =>
      selectedShortlistCandidateIds.includes(c.id || c._id)
    );
    if (candidatesToReportLite.length === 0) {
      toast.error('No candidates selected for report');
      return;
    }
    // Behavior 2: enrich dossier rendering with full candidate docs (resume-parse fields).
    const candidatesToReportIds = candidatesToReportLite
      .map((c) => c.id || c._id)
      .filter(Boolean)
      .map((x) => String(x));
    const fullById = new Map();
    try {
      await Promise.all(
        candidatesToReportIds.map(async (candidateId) => {
          try {
            const res = await axios.get(`/api/screenings/${screeningId}/candidates/${candidateId}`);
            const fullCandidate = res.data?.candidate;
            if (fullCandidate?._id) fullById.set(String(fullCandidate._id), fullCandidate);
            else fullById.set(candidateId, fullCandidate);
          } catch (err) {
            console.warn(`Failed to fetch full candidate ${candidateId}:`, err?.response?.data || err);
          }
        })
      );
    } catch (err) {
      toast.error('Some candidate details could not be loaded');
    }
    const candidatesToReport = candidatesToReportLite.map((c) => {
      const id = String(c.id || c._id);
      const full = fullById.get(id);
      if (!full) return c;
      return {
        ...c,
        ...full,
        id: c.id || full._id,
        _id: c._id || full._id,
      };
    });
    // Enrich dossier rendering with assessment + interview details (same logic as PDF).
    const assessmentId =
      screeningMeta?.assessmentId && screeningMeta?.assessmentId?._id
        ? screeningMeta.assessmentId._id
        : screeningMeta?.assessmentId;
    const hasAssessmentForDossier = !!screeningMeta?.hasAssessment && !!assessmentId;
    const hasInterviewForDossier = !!screeningMeta?.hasInterview;
    const MAX_ASSESS_LINES = 3;
    const MAX_INTERVIEW_LINES = 6;
    if (hasAssessmentForDossier || hasInterviewForDossier) {
      await Promise.all(
        candidatesToReport.map(async (c) => {
          if (hasAssessmentForDossier && c?.email) {
            try {
              const resp = await axios.get(
                `/api/assessments/${assessmentId}/results/${encodeURIComponent(String(c.email))}`
              );
              const details = resp.data || {};
              const results = Array.isArray(details.results) ? details.results : [];
              const proctoringSessionId = details?.candidate?.proctoringSessionId || null;
              let score = typeof details.score === 'number' ? details.score : null;
              if (score == null) {
                score = results.reduce((sum, r) => {
                  if (typeof r.earnedPoints === 'number') return sum + r.earnedPoints;
                  if (r.isCorrect) return sum + (typeof r.points === 'number' ? r.points : 1);
                  return sum;
                }, 0);
              }
              let totalPoints =
                typeof details.totalPoints === 'number'
                  ? details.totalPoints
                  : results.reduce((sum, r) => sum + (typeof r.points === 'number' ? r.points : 1), 0);
              if (!Number.isFinite(totalPoints) || totalPoints <= 0) totalPoints = 0;
              const pct = totalPoints > 0 && Number.isFinite(score) ? Math.round((score / totalPoints) * 100) : null;
              let assessmentNarrative = '';
              if (proctoringSessionId) {
                try {
                  const proctorResp = await axios.get(`/api/proctoring/session/${encodeURIComponent(String(proctoringSessionId))}`);
                  const s = proctorResp.data?.session || proctorResp.data || {};
                  const riskRaw = String(s?.riskLevel || '').toLowerCase();
                  const mappedRisk =
                    riskRaw === 'low' ? 'Low' : riskRaw === 'medium' ? 'Moderate' : (riskRaw === 'high' || riskRaw === 'critical') ? 'High' : 'Unknown';
                  const totalViolations = Number(s?.violations?.totalCount || 0);
                  if (mappedRisk === 'Low') {
                    assessmentNarrative =
                      totalViolations > 0
                        ? `Assessment proctoring indicates low integrity risk with ${totalViolations} minor flag${totalViolations === 1 ? '' : 's'} recorded; no major concerns were detected.`
                        : 'Assessment proctoring indicates low integrity risk with no violations detected during the attempt.';
                  } else if (mappedRisk === 'Moderate') {
                    assessmentNarrative = `Assessment proctoring indicates moderate integrity risk with ${totalViolations} flag${totalViolations === 1 ? '' : 's'} detected. Review flagged events before finalizing the decision.`;
                  } else if (mappedRisk === 'High') {
                    assessmentNarrative = `Assessment proctoring indicates high integrity risk with ${totalViolations} flag${totalViolations === 1 ? '' : 's'} detected. A detailed integrity review or reassessment is recommended.`;
                  } else {
                    assessmentNarrative = 'Assessment proctoring integrity could not be confidently determined for this attempt; manual verification is recommended.';
                  }
                } catch (err) {
                  assessmentNarrative = 'Assessment proctoring summary could not be loaded; review assessment session integrity details in the platform if needed.';
                }
              } else {
                assessmentNarrative = 'Assessment proctoring summary is unavailable for this attempt. Review assessment session integrity details in the platform if needed.';
              }
              c.__assessmentDossierPct = pct;
              c.__assessmentDossierLines = [assessmentNarrative].filter(Boolean);
            } catch (err) {
              console.warn('Failed to load assessment results for dossier:', err?.response?.data || err);
              c.__assessmentDossierLines = ['Assessment details unavailable.'];
              c.__assessmentDossierPct = null;
            }
          }
          const interviewSessionId = await resolveInterviewSessionIdForCandidate(c);
          if (hasInterviewForDossier && interviewSessionId) {
            try {
              const resp = await axios.get(
                `/api/ai-interviews/session/${encodeURIComponent(String(interviewSessionId))}/replay`
              );
              const session = resp.data?.session || {};
              const overall =
                session?.scores?.overall ??
                session?.score ??
                (typeof c.interviewScore === 'number' ? c.interviewScore : null);
              let summaryText =
                session?.analysisSummary ||
                session?.analytics?.detailedAnalysis?.analysisSummary ||
                session?.analytics?.analysisSummary ||
                session?.analytics?.summary ||
                c?.interviewSummary ||
                '';
              const rawLines = String(summaryText || '')
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean);
              const stripMd = (s) =>
                String(s || '')
                  .replace(/\*\*/g, '')
                  .replace(/^\s*[-*]\s*/, '')
                  .trim();
              const cleaned = rawLines
                .slice(0, 30)
                .map((l) => {
                  const m = l.match(/^\s*[-*]?\s*\*\*(.+?)\*\*\s*:\s*(.*)$/);
                  if (m) return `${stripMd(m[1])}: ${stripMd(m[2])}`;
                  return stripMd(l);
                })
                .filter(Boolean)
                .slice(0, MAX_INTERVIEW_LINES);
              const pct =
                typeof overall === 'number' && Number.isFinite(overall) ? Math.round(overall) : null;
              c.__interviewDossierPct = pct;
              c.__interviewDossierLines = cleaned.length ? cleaned : ['Interview summary unavailable.'];
              // Requirement: show the Interview Replay & Analysis modal's Proctoring Details "Interview Summary"
              // inside the Candidate's Assessment Analysis box.
              // Also: if replay does not contain a summary yet, use proctoring summary as fallback.
              try {
                const procResp = await axios.get(
                  `/api/ai-interviews/session/${encodeURIComponent(String(interviewSessionId))}/proctoring`
                );
                const proc = procResp.data || {};
                const procSummary = proc?.aiSummary?.summary;
                if (typeof procSummary === 'string' && procSummary.trim()) {
                  const s = stripMd(procSummary);
                  c.__assessmentDossierLines = [s];
                  if (!String(summaryText || '').trim() && s) {
                    c.__interviewDossierLines = [s];
                  }
                } else if (cleaned.length) {
                  c.__assessmentDossierLines = [cleaned.join(' ')];
                }
              } catch (err) {
                // Leave any previously-set assessment dossier lines as-is.
              }
            } catch (err) {
              console.warn('Failed to load interview replay for dossier:', err?.response?.data || err);
              c.__interviewDossierLines = ['Interview details unavailable.'];
              c.__interviewDossierPct = null;
            }
          }
        })
      );
    }
    const safeTitle = (shortlistReportTitle || 'Shortlist')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '_');
    const dateStr = new Date().toISOString().split('T')[0];
    const zip = new JSZip();
    try {
      const doc = buildShortlistReportPDFDoc(candidatesToReport);
      const pdfBlob = doc.output('blob');
      zip.file(`Selected_Candidates_Report_${safeTitle}_${dateStr}.pdf`, pdfBlob);
      const resumesFolder = zip.folder('resumes');
      let addedCount = 0;
      for (const c of candidatesToReport) {
        const resumeUrl = c.resumeUrl || c.resume?.url;
        if (!resumeUrl || !String(resumeUrl).trim()) continue;
        const safeName = (resolveCandidateDisplayName(c) || 'Unknown').replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');
        const safeEmail = (c.email || '').replace(/[^\w@.-]/g, '_');
        const baseName = `${safeName}_${safeEmail}`.slice(0, 80) || `candidate_${c.id || c._id}`;
        try {
          const cId = String(c.id || c._id);
          const res = await axios.get(`/api/screenings/candidates/${cId}/download-resume`, { responseType: 'blob' });
          if (res.status === 200 && res.data) {
            const blob = res.data;
            const ext =
              blob.type?.includes('word') || blob.type?.includes('document') ? '.docx' : '.pdf';
            resumesFolder.file(`${baseName}_Resume${ext}`, blob);
            addedCount += 1;
          } else {
            throw new Error(`HTTP ${res.status}`);
          }
        } catch (err) {
          console.warn(`Could not fetch resume for ${resolveCandidateDisplayName(c) || 'Unknown'}:`, err);
        }
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Selected_Candidates_${safeTitle}_${dateStr}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report Download Ready');
    } catch (error) {
      console.error('Error creating ZIP:', error);
      toast.error('Failed to create ZIP');
    }
  }, [
    shortlistCandidates,
    selectedShortlistCandidateIds,
    shortlistReportTitle,
    buildShortlistReportPDFDoc,
    resolveInterviewSessionIdForCandidate,
    screeningId,
    screeningMeta,
  ]);

  // Go Colors: stamp the "Downloaded" chip locally once the server has recorded the download.
  const applyPayrollDownloaded = useCallback((recorded) => {
    if (!recorded?.candidateIds?.length) return;
    const ids = new Set(recorded.candidateIds.map(String));
    const byName = user?.name || user?.email || null;
    setCandidates((prev) =>
      prev.map((x) => {
        if (!ids.has(String(x.id || x._id))) return x;
        if (recorded.downloaded === false) return { ...x, payrollDownload: null };
        return {
          ...x,
          payrollDownload: {
            ...(x.payrollDownload || {}),
            at: recorded.at,
            byName,
            count: (x.payrollDownload?.count || 0) + 1,
          },
        };
      })
    );
  }, [user?.name, user?.email]);

  // The Downloaded chip edited by hand (Yes ↔ No).
  const handleSetPayrollDownloaded = useCallback(async (candidate, downloaded) => {
    try {
      const recorded = await recordPayrollDownload([String(candidate.id || candidate._id)], downloaded, true);
      if (!recorded?.candidateIds?.length) {
        toast.error('Only candidates with a salary request (pending or filled) can be marked.');
        return;
      }
      applyPayrollDownloaded(recorded);
      toast.success(downloaded ? 'Marked as downloaded' : 'Marked as not downloaded');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update the Downloaded status.');
    }
  }, [applyPayrollDownloaded]);

  const handleGenerateGoColorsZIP = useCallback(async () => {
    const candidatesToReport = shortlistCandidates.filter((c) =>
      selectedShortlistCandidateIds.includes(c.id || c._id)
    );
    if (candidatesToReport.length === 0) {
      toast.error('No candidates selected for report');
      return;
    }

    const dateStr = new Date().toISOString().split('T')[0];
    const safeTitle = safeFileTitle(shortlistReportTitle || screeningMeta?.jobTitle || 'Report');
    // goColorsPayrollExport.js owns the row/folder logic, shared with the job page's Download.
    const entries = candidatesToReport.map((c) => {
      const cId = String(c.id || c._id);
      return buildGoColorsPayrollEntry(
        c,
        documentStatusMap?.[cId]?.documents,
        salaryRequestsMap?.[cId] || {},
        screeningMeta,
        documentStatusMap?.[cId]
      );
    });

    const loadingId = toast.loading('Generating ZIP with documents & Excel...');
    let recordFailed = false;
    try {
      const { incomplete } = await saveGoColorsPayrollZipParts({
        entries,
        zipBase: `GoColors_Report_${safeTitle}_${dateStr}`,
        excelBase: `Selected_Candidates_${safeTitle}_${dateStr}`,
        onProgress: ({ part, parts, done, total }) =>
          toast.loading(
            `Downloading documents${parts > 1 ? ` (part ${part} of ${parts})` : ''}: ${done}/${total} candidates`,
            { id: loadingId }
          ),
        // Only a package with DOJ/salary filled and every document in it counts as downloaded.
        onPartSaved: async (completeEntries, partIncomplete) => {
          const filledIds = completeEntries
            .map((e) => e.candidateId)
            .filter((id) => salaryRequestsMap?.[id]?.status === 'completed');
          try {
            applyPayrollDownloaded(await recordPayrollDownload(filledIds, true, false, partIncomplete));
          } catch (err) {
            recordFailed = true;
            console.error('Failed to record payroll download:', err);
          }
        },
      });
      toast.dismiss(loadingId);
      if (incomplete.length) {
        toast.error(
          `ZIP saved, but documents are missing for:\n${missingSummary(incomplete)}\nThey are not marked "Downloaded" — download them again.`,
          { duration: 15000 }
        );
      } else {
        toast.success('ZIP Download Ready, with all documents');
      }
      if (recordFailed) {
        toast.error('Downloaded, but the "Downloaded" status could not be saved. Please refresh and check.');
      }
    } catch (error) {
      console.error('Error creating GoColors ZIP:', error);
      toast.dismiss(loadingId);
      if (error instanceof IncompleteDownloadError) {
        recordPayrollDownload([], true, false, error.incomplete).catch(() => {});
        toast.error(
          `Download stopped, nothing more was saved. Documents could not be downloaded for:\n${missingSummary(error.incomplete)}\nCheck your internet connection and try again.`,
          { duration: 15000 }
        );
      } else {
        toast.error('Failed to create ZIP');
      }
    }
  }, [
    shortlistCandidates,
    selectedShortlistCandidateIds,
    shortlistReportTitle,
    screeningMeta,
    documentStatusMap,
    salaryRequestsMap,
    applyPayrollDownloaded,
  ]);
  /**
   * Candidates-tab entry points for the two actions that previously only existed behind the
   * Selected tab's Next Steps modal. Neither touches a candidate's decision - a recruiter can
   * report on or share someone without first moving them to Selected, and that tab keeps
   * meaning "who we acted on" rather than "who we happened to look at".
   */
  const handleDownloadReportForSelection = useCallback(() => {
    if (selectedCandidatesForShortlist.length === 0) {
      toast.error('Select at least one candidate to download a report');
      return;
    }
    return downloadCandidateDossierPDF(selectedCandidatesForShortlist);
  }, [selectedCandidatesForShortlist, downloadCandidateDossierPDF]);

  const handleShareSelectionWithHiringManager = useCallback(() => {
    if (selectedCandidatesForShortlist.length === 0) {
      toast.error('Select at least one candidate to share for review');
      return;
    }
    setHiringManagerShareCandidateIds(selectedCandidatesForShortlist);
    setHiringManagerShareOrigin('candidates');
    setShowSendEmailToHiringManagerModal(true);
  }, [selectedCandidatesForShortlist]);

  const candidatesTableProps = {
    screeningId,
    screeningMeta,
    stores,
    scoreFilters,
    setScoreFilters,
    metaFilters,
    setMetaFilters,
    openScoreFilterId,
    setOpenScoreFilterId,
    openMetaFilterId,
    setOpenMetaFilterId,
    openActionsCandidate,
    setOpenActionsCandidate,
    actionMenuPosition,
    setActionMenuPosition,
    parseExperienceYearsForFilter,
    parseAgeYearsForFilter,
    parseCurrentCtcLacsForFilter,
    openCandidateResumeBreakdown,
    openInterviewSessionForCandidate,
    openWhatsAppSessionForCandidate,
    openQualifyingSessionForCandidate,
    openQualifyingAnswersForCandidate,
    handleRemoveFromShortlistRow,
    handleInlineViewAssessmentScore,
    handleInlineViewAssessmentProctoring,
    setCandidateToDelete,
    setCandidateToResend,
    setCandidateToShortlist,
    setCandidateToReject,
    setShowResendConfirmModal,
    setShowShortlistConfirmModal,
    setShowRejectConfirmModal,
    handleRestoreFromRejectedRow,
    handleRestoreFromDroppedRow,
    documentStatusMap,
    onViewDocumentsCandidate: setViewDocumentsCandidate,
    shortlistDecisions,
    setShortlistDecisions,
    shortlistNotes,
    setShortlistNotes,
    patchCandidateShortlistField,
    setDuplicateModalCandidate,
    duplicateModalCandidate,
    onMarkCandidateViewed: markCandidateViewed,
    onMarkCandidateUnviewed: markCandidateUnviewed,
    handleMarkDropped,
    isGoColors,
    salaryRequestsMap,
    onSetPayrollDownloaded: isGoColors ? handleSetPayrollDownloaded : undefined,
    onOpenSalaryDetailsModal: (candidate, salaryData) => {
      setSelectedCandidateForSalaryModal(candidate);
      setSelectedSalaryDataForModal(salaryData);
      setIsSalaryModalOpen(true);
    },
    onCallCandidate: (candidate) => {
      setCandidateToCall(candidate);
      setShowCallConfirmModal(true);
    },
    onRecallQualifyingCall: screeningMeta?.includeQualifyingAgent ? setCandidateToRecall : undefined,
    weightsConfig,
    onMoveCandidateJobPost: handleOpenMoveJobPost,
    onUndoMoveCandidateJobPost: handleUndoMoveJobPost,
    onDownloadCandidateReport: (candidate) =>
      downloadCandidateDossierPDF([candidate?._id || candidate?.id]),
    onReviewCandidateWithHiringManager: (candidate) => {
      const candidateId = candidate?._id || candidate?.id;
      if (!candidateId) {
        return;
      }
      setHiringManagerShareCandidateIds([candidateId]);
      setHiringManagerShareOrigin('candidates');
      setShowSendEmailToHiringManagerModal(true);
    },
  };

  const renderCandidatesTable = (rows, options = {}) => (
    <CandidatesTable rows={rows} options={options} {...candidatesTableProps} />
  );
  const insightsTabProps = {
    insightsStats,
    invitedCount,
    completedCount,
    shortlistedCount,
    insightsQuestionsCount,
    insightsModalCard,
    setInsightsModalCard,
    setInsightsModalQuestionsSubTab,
    insightsModalQuestionsSubTab,
    insightsModalAssessmentQuestions,
    insightsModalInterviewQuestions,
    insightsModalQuestionsLoading,
    candidates,
    screeningMeta,
    candidatePoolInsights,
    getOverallScoreNumber,
    insightsReevaluateLoading,
    screeningId,
    refreshScreeningInsights,
    openCandidateResumeBreakdown,
  };
  let customUrlPrefix = '';

  if (screening?.orgSlug === 'ajantapharma-80unds') {
    customUrlPrefix = 'ajantapharma-fieldhr'; // The vanity URL prefix for Ajanta
  }

  const publicLink = screeningMeta?.id
    ? (customUrlPrefix
      ? `${window.location.origin}/${customUrlPrefix}/apply/${screeningMeta.id}`
      : `${window.location.origin}/apply/${screeningMeta.id}`)
    : '';
  const SkeletonBlock = ({ className = '' }) => (
    <div className={`animate-pulse rounded-md bg-gray-200/90 ${className}`} />
  );
  const candidatesSkeletonRows = 6;
  if (screeningError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F9FC]">
        <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-4 text-red-700">
          {screeningError}
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen w-full min-w-0 max-w-full overflow-x-hidden bg-[#F7F9FC] sm:px-6 lg:px-8 desktop:px-10 pt-6 pb-12">
      <style dangerouslySetInnerHTML={{ __html: inviteFlowModalStyles }} />
      <CandidatesFiltersModal
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        anchorRef={filtersButtonRef}
        screeningMeta={screeningMeta}
        tableOptions={
          // storeListEnabled must match what CandidatesTable passes, otherwise the
          // filters modal and the table disagree about which store columns exist.
          filtersModalContext === 'shortlist'
            ? { showStatus: true, showShortlistedDate: true, hideColumnFilters: true, storeListEnabled: user?.features?.store_list === true }
            : filtersModalContext === 'rejected'
              ? { showStatus: true, showRejectedDate: true, hideColumnFilters: true, storeListEnabled: user?.features?.store_list === true }
              : { showStatus: true, showShortlistedDate: false, hideColumnFilters: true, storeListEnabled: user?.features?.store_list === true }
        }
        rows={filtersModalContext === 'shortlist' ? shortlistCandidates : filtersModalContext === 'rejected' ? rejectedCandidates : candidatesToShow}
        parseExperienceYearsForFilter={parseExperienceYearsForFilter}
        parseAgeYearsForFilter={parseAgeYearsForFilter}
        parseCurrentCtcLacsForFilter={parseCurrentCtcLacsForFilter}
        scoreFilters={scoreFilters}
        setScoreFilters={setScoreFilters}
        metaFilters={metaFilters}
        setMetaFilters={setMetaFilters}
        interviewQuestions={interviewQuestions}
        filterRowsWith={filterRowsWith}
        onClearAll={() =>
          clearAllCandidateTableFilters({
            clearShortlistQuery: filtersModalContext === 'shortlist',
          })
        }
      />
      {/* Screening header — aligned with job-post (ResultsAnalytics) */}
      <div className="">
        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <button
              type="button"
              onClick={() => navigate(`/dashboard/job-posts/jobs/${jobId}/screenings`)}
              className="flex-shrink-0 inline-flex items-center justify-center rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              aria-label="Back to screenings"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3 min-w-0">
              {screeningLoading ? (
                <SkeletonBlock className="h-9 w-64 sm:w-80 max-w-full" />
              ) : (
                <h1 className="text-2xl sm:text-3xl font-bold text-[#242124] truncate min-w-0">
                  {screeningMeta?.name || 'Screening'}
                </h1>
              )}
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setShowViewDetailsModal(true)}
                  className="flex-shrink-0 inline-flex items-center justify-center rounded-lg p-2 text-gray-500 hover:text-[#007BFF] hover:bg-[#007BFF]/10 transition-colors"
                  aria-label="View screening details"
                  title="View details"
                >
                  <DocumentTextIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setNotificationSessionReadIds(new Set());
                    setShowNotificationsModal(true);
                    setNotificationsTab('focused');
                    fetchNotificationReadState();
                    fetchShareReviewActivity();
                  }}
                  className="flex-shrink-0 inline-flex items-center justify-center rounded-lg p-2 text-gray-500 hover:text-[#007BFF] hover:bg-[#007BFF]/10 transition-colors relative"
                  aria-label="View candidate notifications"
                  title="View candidate notifications"
                >
                  <BellIcon className="h-5 w-5" />
                  {notificationsBadgeCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-[#FF5A5F] text-white text-[10px] font-semibold min-w-[16px] h-4 px-1">
                      {notificationsBadgeCount}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAutomationsModal(true)}
                  className="flex-shrink-0 inline-flex items-center justify-center rounded-lg p-2 text-gray-500 hover:text-[#007BFF] hover:bg-[#007BFF]/10 transition-colors relative"
                  aria-label="View screening automations"
                  title="Automations"
                >
                  <BoltIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          {screeningLoading ? (
            <div className="mt-1 pb-4 border-b border-gray-200">
              <SkeletonBlock className="h-4 w-full mb-2" />
              <SkeletonBlock className="h-4 w-2/3" />
            </div>
          ) : screening?.description ? (
            <div className="mt-1 pb-4 border-b border-gray-200">
              <p className="text-sm text-gray-600 line-clamp-2 min-w-0">
                {screening.description}
              </p>
            </div>
          ) : screening && !screeningLoading ? (
            <div className="pb-4 border-b border-gray-200">
              <p className="text-sm text-gray-600 mt-1">
                Manage candidates, {isSamtaGroup ? 'shortlisted' : 'selected'}, and view insights for this screening.
              </p>
            </div>
          ) : (
            <div className="pb-4 border-b border-gray-200" />
          )}
        </div>
      </div>
      {/* Tab Navigation — consistent #007BFF theme, on page background */}
      <div className="pl-0 pr-0 pb-4 bg-[#F7F9FC] mb-0">
        <nav className="flex items-center gap-1 sm:gap-2 px-3 sm:px-6 overflow-x-auto no-scrollbar border-b border-gray-200" aria-label="Tabs">
          <button
            data-testid="tab-candidates"
            onClick={() => setActiveTab('candidates')}
            className={`px-2 py-2 sm:px-4 sm:py-3 text-sm font-medium border-b-2 transition-all duration-200 -mb-px ${activeTab === 'candidates'
              ? 'border-[#007BFF] text-[#007BFF]'
              : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center gap-2">
              <UsersIcon
                className={`h-4 w-4 ${activeTab === 'candidates' ? 'text-[#007BFF]' : 'text-gray-500'}`}
              />
              <span>Candidates</span>
              <span
                className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-medium rounded-full ${activeTab === 'candidates'
                  ? 'bg-[#007BFF]/10 text-[#007BFF]'
                  : 'bg-gray-200 text-gray-600'
                  }`}
              >
                {screeningLoading ? <SkeletonBlock className="h-3 w-5 rounded-full" /> : candidatesTabCount}
              </span>
            </div>
          </button>
          <button
            data-testid="tab-selected"
            onClick={() => {
              // Selected and Onboarded share one selection; don't carry it across.
              if (activeTab === 'onboarded') setSelectedShortlistCandidateIds([]);
              setActiveTab('shortlist');
            }}
            className={`px-2 py-2 sm:px-4 sm:py-3 text-sm font-medium border-b-2 transition-all duration-200 -mb-px ${activeTab === 'shortlist'
              ? 'border-[#007BFF] text-[#007BFF]'
              : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center gap-2">
              <StarIcon
                className={`h-4 w-4 ${activeTab === 'shortlist' ? 'text-[#007BFF]' : 'text-gray-500'}`}
              />
              <span>{isSamtaGroup ? 'Shortlisted' : 'Selected'}</span>
              <span
                className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-medium rounded-full ${activeTab === 'shortlist'
                  ? 'bg-[#007BFF]/10 text-[#007BFF]'
                  : 'bg-gray-200 text-gray-600'
                  }`}
              >
                {screeningLoading ? <SkeletonBlock className="h-3 w-5 rounded-full" /> : selectedTabCount}
              </span>
            </div>
          </button>
          {isGoColors && (
            <button
              data-testid="tab-onboarded"
              onClick={() => {
                if (activeTab === 'shortlist') setSelectedShortlistCandidateIds([]);
                setActiveTab('onboarded');
              }}
              className={`px-2 py-2 sm:px-4 sm:py-3 text-sm font-medium border-b-2 transition-all duration-200 -mb-px ${activeTab === 'onboarded'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center gap-2">
                <BriefcaseIcon
                  className={`h-4 w-4 ${activeTab === 'onboarded' ? 'text-emerald-500' : 'text-gray-500'}`}
                />
                <span>Onboarded</span>
                <span
                  className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-medium rounded-full ${activeTab === 'onboarded'
                    ? 'bg-emerald-100 text-emerald-600'
                    : 'bg-gray-200 text-gray-600'
                    }`}
                >
                  {screeningLoading ? <SkeletonBlock className="h-3 w-5 rounded-full" /> : onboardedCount}
                </span>
              </div>
            </button>
          )}
          <button
            data-testid="tab-rejected"
            onClick={() => setActiveTab('rejected')}
            className={`px-2 py-2 sm:px-4 sm:py-3 text-sm font-medium border-b-2 transition-all duration-200 -mb-px ${activeTab === 'rejected'
              ? 'border-red-500 text-red-600'
              : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center gap-2">
              <XCircleIcon
                className={`h-4 w-4 ${activeTab === 'rejected' ? 'text-red-500' : 'text-gray-500'}`}
              />
              <span>Rejected</span>
              {rejectedCount > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-medium rounded-full ${activeTab === 'rejected'
                    ? 'bg-red-100 text-red-600'
                    : 'bg-gray-200 text-gray-600'
                    }`}
                >
                  {screeningLoading ? <SkeletonBlock className="h-3 w-5 rounded-full" /> : rejectedCount}
                </span>
              )}
            </div>
          </button>

          {isGoColors && (
            <button
              data-testid="tab-dropped"
              onClick={() => setActiveTab('dropped')}
              className={`px-2 py-2 sm:px-4 sm:py-3 text-sm font-medium border-b-2 transition-all duration-200 -mb-px ${activeTab === 'dropped'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center gap-2">
                <XCircleIcon
                  className={`h-4 w-4 ${activeTab === 'dropped' ? 'text-orange-500' : 'text-gray-500'}`}
                />
                <span>Dropped</span>
                <span
                  className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-medium rounded-full ${activeTab === 'dropped'
                    ? 'bg-orange-100 text-orange-600'
                    : 'bg-gray-200 text-gray-600'
                    }`}
                >
                  {screeningLoading ? <SkeletonBlock className="h-3 w-5 rounded-full" /> : droppedCount}
                </span>
              </div>
            </button>
          )}
          <button
            data-testid="tab-insights"
            onClick={() => setActiveTab('insights')}
            className={`px-2 py-2 sm:px-4 sm:py-3 text-sm font-medium border-b-2 transition-all duration-200 -mb-px ${activeTab === 'insights'
              ? 'border-[#007BFF] text-[#007BFF]'
              : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center gap-2">
              <ChartBarIcon
                className={`h-4 w-4 ${activeTab === 'insights' ? 'text-[#007BFF]' : 'text-gray-500'}`}
              />
              <span>Insights</span>
            </div>
          </button>
        </nav>
      </div>
      {/* Tab content — Candidates: search + section header + table (like All Screenings list) */}
      {activeTab === 'candidates' && (
        <>
          {/* Candidates header + actions — on page background */}
          <div className="pl-0 pr-0 pb-4 bg-[#F7F9FC] mb-0 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="text-base font-semibold text-[#242124] truncate min-w-0">
                  Candidates
                  <span className="text-gray-500 font-normal ml-1.5 text-sm whitespace-nowrap">
                    ({screeningLoading ? '...' : candidatesTabCount} in this screening)
                  </span>
                </h2>
                {screening?.includeQualifyingAgent === true && (
                  <button
                    type="button"
                    onClick={() => void refreshQualifyingSummary()}
                    disabled={qualifyingSummaryRefreshing}
                    className="inline-flex items-center justify-center p-1.5 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-60"
                    title="Refresh call status"
                    aria-label="Refresh call status"
                    data-testid="qualifying-summary-refresh"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${qualifyingSummaryRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                )}
                {/* <button
                  type="button"
                  onClick={() => {
                    if (!screeningId) return;
                    fetchCandidates({ forceRefresh: true });
                  }}
                  className="inline-flex items-center justify-center p-1.5 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-200 transition-colors"
                  title="Refresh candidates"
                  aria-label="Refresh candidates"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${candidatesLoading ? 'animate-spin' : ''}`} />
                </button> */}
              </div>
              <div className="flex flex-wrap items-center gap-2">

                <button
                  type="button"
                  data-testid="invite-candidates-btn"
                  onClick={() => {
                    setOpenBulkActionsMenu(false);
                    if (screeningMeta?.includeWhatsAppAgent) {
                      // WhatsApp Agent screenings get their own dedicated modal — not the
                      // shared Email/Single/Bulk invite flow (see WhatsAppInviteModal.js).
                      setShowWhatsAppInviteModal(true);
                      return;
                    }
                    if (screeningMeta?.includeQualifyingAgent) {
                      // Qualifying Agent screenings get their own dedicated modal — places a
                      // call, not a link/email invite (see QualifyingAgentInviteModal.js).
                      setShowQualifyingAgentInviteModal(true);
                      return;
                    }
                    setIsInviteOpen(true);
                    setInviteStep(1);
                    setInviteMode(null);
                  }}
                  className="px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-medium bg-[#007BFF] text-white rounded-lg hover:bg-[#0062CC] transition-colors whitespace-nowrap"
                >
                  Invite
                </button>
                <div className="relative">
                  <button
                    type="button"
                    data-bulk-actions-trigger
                    aria-haspopup="menu"
                    aria-expanded={openBulkActionsMenu}
                    disabled={selectedCandidatesForShortlist.length === 0}
                    onClick={(e) => {
                      if (selectedCandidatesForShortlist.length === 0) return;
                      setOpenBulkActionsMenu((v) => {
                        const next = !v;
                        if (!next) setBulkActionsMenuPosition(null);
                        return next;
                      });
                      if (!openBulkActionsMenu) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
                        const rightOffset = Math.max(8, viewportWidth - rect.right - 8);
                        setBulkActionsMenuPosition({ top: rect.bottom + 8, right: rightOffset });
                      }
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 sm:px-5 sm:py-2.5 text-sm font-semibold rounded-lg border-2 border-gray-300 text-gray-500 bg-white transition-colors whitespace-nowrap ${selectedCandidatesForShortlist.length === 0
                      ? 'opacity-70 cursor-not-allowed'
                      : 'hover:bg-gray-50 hover:border-gray-400'
                      }`}
                    title="Actions"
                  >
                    Actions
                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                  </button>
                  {openBulkActionsMenu && bulkActionsMenuPosition && createPortal(
                    <div
                      data-bulk-actions-menu
                      role="menu"
                      className="fixed w-[220px] bg-white rounded-xl shadow-xl border border-gray-100 z-[9999] overflow-hidden text-left flex flex-col"
                      style={{
                        top: `${bulkActionsMenuPosition.top}px`,
                        right: `${bulkActionsMenuPosition.right}px`,
                        // The menu is fixed-positioned from the trigger, so it can only ever run
                        // off the bottom of the viewport - cap it at whatever room is left below
                        // the trigger (less an 8px breathing gap) and let the item list scroll.
                        maxHeight: `calc(100vh - ${bulkActionsMenuPosition.top + 16}px)`,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex-shrink-0 px-3 py-2 border-b border-gray-100 bg-gray-50">
                        <div className="text-xs font-semibold text-gray-700">Bulk actions</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          Applies to {selectedCandidatesForShortlist.length} selected candidate{selectedCandidatesForShortlist.length === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-1">
                        <button
                          type="button"
                          role="menuitem"
                          disabled={selectedCandidatesForShortlist.length === 0 || resendingInvite}
                          onClick={() => {
                            if (selectedCandidatesForShortlist.length === 0) {
                              toast.error('Select at least one candidate to resend the invitation email');
                              return;
                            }
                            setOpenBulkActionsMenu(false);
                            setShowResendConfirmModal(true);
                          }}
                          className={`flex items-start justify-start w-full gap-3 px-4 py-2.5 text-sm transition-colors text-left ${selectedCandidatesForShortlist.length > 0 && !resendingInvite
                            ? 'text-amber-700 hover:bg-gray-50'
                            : 'text-gray-400 cursor-not-allowed'
                            }`}
                        >
                          <EnvelopeIcon className="h-4 w-4 mt-0.5" />
                          <div className="flex flex-col">
                            {resendingInvite ? (
                              <span className="inline-flex items-center gap-2">
                                <span className="inline-block w-3.5 h-3.5 border-2 border-amber-700 border-t-transparent rounded-full animate-spin" />
                                Sending…
                              </span>
                            ) : (
                              <span className="text-sm font-medium">Resend</span>
                            )}
                            <span className="text-xs text-gray-500 mt-0.5">Send a new application link</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          disabled={!canBulkShortlist}
                          onClick={() => {
                            if (!canBulkShortlist) {
                              toast.error('Select at least one candidate first');
                              return;
                            }
                            setOpenBulkActionsMenu(false);
                            setShowShortlistConfirmModal(true);
                          }}
                          className={`flex items-start justify-start w-full gap-3 px-4 py-2.5 text-sm transition-colors text-left ${canBulkShortlist
                            ? 'text-[#007BFF] hover:bg-gray-50'
                            : 'text-gray-400 cursor-not-allowed'
                            }`}
                        >
                          <StarIcon className="h-4 w-4 mt-0.5" />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{isSamtaGroup ? 'Shortlist Candidates' : 'Select Candidates'}</span>
                            <span className="text-xs text-gray-500 mt-0.5">Move candidates to {isSamtaGroup ? 'shortlisted' : 'selected'}</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          disabled={!canBulkReject}
                          onClick={() => {
                            if (!canBulkReject) {
                              toast.error('Select at least one candidate first');
                              return;
                            }
                            setOpenBulkActionsMenu(false);
                            setShowRejectConfirmModal(true);
                          }}
                          className={`flex items-start justify-start w-full gap-3 px-4 py-2.5 text-sm transition-colors text-left ${canBulkReject
                            ? 'text-red-600 hover:bg-red-50'
                            : 'text-gray-400 cursor-not-allowed'
                            }`}
                        >
                          <XCircleIcon className="h-4 w-4 mt-0.5" />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">Reject</span>
                            <span className="text-xs text-gray-500 mt-0.5">Move candidates to rejected</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          disabled={selectedCandidatesForShortlist.length === 0 || bulkDeletingCandidates}
                          onClick={() => {
                            if (selectedCandidatesForShortlist.length === 0) {
                              toast.error('Select at least one candidate to delete');
                              return;
                            }
                            setOpenBulkActionsMenu(false);
                            setShowBulkDeleteConfirmModal(true);
                          }}
                          className={`flex items-start justify-start w-full gap-3 px-4 py-2.5 text-sm transition-colors text-left ${selectedCandidatesForShortlist.length > 0 && !bulkDeletingCandidates
                            ? 'text-red-600 hover:bg-red-50'
                            : 'text-gray-400 cursor-not-allowed'
                            }`}
                        >
                          <TrashIcon className="h-4 w-4 mt-0.5" />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">Delete</span>
                            <span className="text-xs text-gray-500 mt-0.5">Delete candidates from this screening</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          disabled={selectedCandidatesForShortlist.length === 0 || downloadingAllResumes}
                          onClick={() => {
                            if (selectedCandidatesForShortlist.length === 0) {
                              toast.error('Select at least one candidate to download resumes');
                              return;
                            }
                            setOpenBulkActionsMenu(false);
                            handleDownloadSelectedResumes();
                          }}
                          className={`flex items-start justify-start w-full gap-3 px-4 py-2.5 text-sm transition-colors text-left ${selectedCandidatesForShortlist.length > 0 && !downloadingAllResumes
                            ? 'text-green-700 hover:bg-gray-50'
                            : 'text-gray-400 cursor-not-allowed'
                            }`}
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 mt-0.5" />
                          <div className="flex flex-col">
                            {downloadingAllResumes ? (
                              <span className="inline-flex items-center gap-2">
                                <span className="inline-block w-3.5 h-3.5 border-2 border-green-700 border-t-transparent rounded-full animate-spin" />
                                Preparing…
                              </span>
                            ) : (
                              <span className="text-sm font-medium">Download Resumes</span>
                            )}
                            <span className="text-xs text-gray-500 mt-0.5">Download selected resumes as ZIP</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          data-testid="download-candidates-xlsx-btn"
                          disabled={selectedCandidatesForShortlist.length === 0}
                          onClick={() => {
                            setOpenBulkActionsMenu(false);
                            handleGenerateCandidatesReport();
                          }}
                          className={`flex items-start justify-start w-full gap-3 px-4 py-2.5 text-sm transition-colors text-left ${selectedCandidatesForShortlist.length > 0
                            ? 'text-[#16A34A] hover:bg-gray-50'
                            : 'text-gray-400 cursor-not-allowed'
                            }`}
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 mt-0.5" />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">Download Excel</span>
                            <span className="text-xs text-gray-500 mt-0.5">Download candidates data as XLSX file</span>
                          </div>
                        </button>
                        <div className="my-1 border-t border-gray-100" />
                        <button
                          type="button"
                          role="menuitem"
                          data-testid="bulk-download-report-btn"
                          disabled={selectedCandidatesForShortlist.length === 0}
                          onClick={() => {
                            setOpenBulkActionsMenu(false);
                            handleDownloadReportForSelection();
                          }}
                          className={`flex items-start justify-start w-full gap-3 px-4 py-2.5 text-sm transition-colors text-left ${selectedCandidatesForShortlist.length > 0
                            ? 'text-indigo-700 hover:bg-gray-50'
                            : 'text-gray-400 cursor-not-allowed'
                            }`}
                        >
                          <DocumentTextIcon className="h-4 w-4 mt-0.5" />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">Download Report</span>
                            <span className="text-xs text-gray-500 mt-0.5">Detailed PDF report for the selected candidates</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          data-testid="bulk-hiring-manager-review-btn"
                          disabled={selectedCandidatesForShortlist.length === 0}
                          onClick={() => {
                            setOpenBulkActionsMenu(false);
                            handleShareSelectionWithHiringManager();
                          }}
                          className={`flex items-start justify-start w-full gap-3 px-4 py-2.5 text-sm transition-colors text-left ${selectedCandidatesForShortlist.length > 0
                            ? 'text-blue-700 hover:bg-gray-50'
                            : 'text-gray-400 cursor-not-allowed'
                            }`}
                        >
                          <UserIcon className="h-4 w-4 mt-0.5" />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">Review with Hiring Manager</span>
                            <span className="text-xs text-gray-500 mt-0.5">Create a shareable review link</span>
                          </div>
                        </button>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
                {screeningMeta?.hasInterview && showHiddenAiReprocessButtons && (
                  <button
                    type="button"
                    disabled={bulkScreeningAiReprocessLoading || !screeningId}
                    title="Re-run interview AI (transcript from recording, proctoring, scoring) and resume LLM scoring where missing—uses API credits"
                    onClick={async () => {
                      if (!screeningId) return;
                      if (
                        !window.confirm(
                          'Queue background jobs to recover missing interview AI (uses S3 recording + Deepgram/OpenAI) and re-run resume scoring for candidates who have a resume but were never scored? This uses your AI credits and may take several minutes.'
                        )
                      ) {
                        return;
                      }
                      setBulkScreeningAiReprocessLoading(true);
                      try {
                        const res = await axios.post(`/api/screenings/${screeningId}/reprocess-ai`, {
                          interviews: true,
                          resumes: true,
                        });
                        toast.success(
                          `Queued ${res.data?.interviewSessionsQueued ?? 0} interview session(s) and ${res.data?.resumesQueued ?? 0} resume job(s). Refresh candidates in a few minutes.`
                        );
                      } catch (e) {
                        toast.error(e.response?.data?.message || 'Failed to queue reprocessing');
                      } finally {
                        setBulkScreeningAiReprocessLoading(false);
                      }
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${!bulkScreeningAiReprocessLoading
                      ? 'border-2 border-violet-500 text-violet-700 bg-white hover:bg-violet-50'
                      : 'border-2 border-gray-300 text-gray-500 bg-white cursor-not-allowed opacity-70'
                      }`}
                  >
                    {bulkScreeningAiReprocessLoading ? (
                      <>
                        <span className="inline-block w-3.5 h-3.5 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
                        Queuing…
                      </>
                    ) : (
                      <>
                        <ArrowPathIcon className="h-4 w-4" />
                        Re-run missing AI
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Search bar — on page background */}
          <div className="mb-6">
            <AICandidateSearchBar
              value={aiSearchQuery}
              onChange={setAiSearchQuery}
              onSearch={handleAISearch}
              onClear={clearAISearch}
              isSearching={isAISearching}
              isActive={isAISearchActive}
              placeholder="Search candidates with natural language (e.g., candidates with React and 5+ years experience)..."
              disabled={screeningLoading || !screeningId}
              rightActions={
                <div className="flex items-center gap-2">
                  <button
                    ref={filtersButtonRef}
                    type="button"
                    onClick={() => {
                      setFiltersModalContext('candidates');
                      setIsFiltersModalOpen(true);
                    }}
                    className="px-3 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] transition-colors inline-flex items-center gap-2"
                    title="Filters"
                  >
                    <FunnelIcon className="h-5 w-5" />
                    {activeFiltersCount > 0 && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-[#007BFF]/10 px-2 py-0.5 text-[11px] font-semibold text-[#007BFF]">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setColumnsModalContext('candidates');
                      setIsColumnsModalOpen(true);
                    }}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] transition-colors inline-flex items-center gap-2"
                    title="Customize columns"
                  >
                    <ViewColumnsIcon className="h-4 w-4" />
                    <span>Columns</span>
                  </button>
                </div>
              }
            />
          </div>
          {/* Table card only — same pattern as All Screenings table */}
          <div className="bg-[#F7F9FC] rounded-xl shadow-sm overflow-hidden min-w-0 max-w-full">
            {candidatesLoading || documentStatusMap === null ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#007BFF]">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                        Candidate
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                        Overall score
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">

                        Resume score
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                        Interview score
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">
                        Proctoring
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-white uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: candidatesSkeletonRows }).map((_, index) => (
                      <tr
                        key={`candidates-skeleton-row-${index}`}
                        className="bg-white border-b border-l border-r border-gray-300/50"
                      >
                        <td className="px-4 py-4">
                          <SkeletonBlock className="h-4 w-44 max-w-full mb-2" />
                          <SkeletonBlock className="h-3 w-56 max-w-full" />
                        </td>
                        <td className="px-4 py-4">
                          <SkeletonBlock className="h-6 w-20 rounded-full" />
                        </td>
                        <td className="px-4 py-4">
                          <SkeletonBlock className="h-6 w-14 rounded-md" />
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <SkeletonBlock className="h-6 w-14 rounded-md" />
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <SkeletonBlock className="h-6 w-14 rounded-md" />
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          <SkeletonBlock className="h-6 w-24 rounded-full" />
                        </td>
                        <td className="px-4 py-4 text-right">
                          <SkeletonBlock className="h-8 w-8 rounded-lg ml-auto" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : candidatesToShow.length === 0 ? (
              <div data-testid="empty-candidates-state" className="text-center py-16 px-6 bg-white rounded-xl border border-gray-200">
                <p className="text-gray-600">No candidates in this screening yet.</p>
              </div>
            ) : (
              renderCandidatesTable(filteredCandidatesToShow, {
                selectedIds: selectedCandidatesForShortlist,
                onSelectionChange: setSelectedCandidatesForShortlist,
                showShortlistedDate: false,
                showRejectedDate: false,
                showStatus: true,
                hideColumnFilters: true,
                showInlineEmptyState: true,
                isAISearchActiveForTable: isAISearchActive,
                onClearFilters: () => clearAllCandidateTableFilters(),
                isShortlistView: false,
                columnVisibility: candidateColumnVisibility,
                columnOrder: candidateColumnOrder,
                orgDefaultColumnOrder: user?.ui?.defaultCandidateColumnOrder || [],
                showDuplicateColumn: duplicateFeatureEnabled,
                showRecommendedChip: recommendedChipEnabled,
                recommendedChipThreshold: recommendedChipThreshold,
                showDocumentsColumn: screeningMeta?.includeDocumentCollection === true || Object.keys(documentStatusMap || {}).length > 0,
              })
            )}
          </div>
        </>
      )}
      {/* Tab content — Shortlist: same layout as Candidates (header + table card) */}
      {(activeTab === 'shortlist' || activeTab === 'onboarded') && (
        <>
          {/* Shortlist header + actions — same as Candidates (on page background) */}
          <div className="pl-0 pr-0 pb-4 bg-[#F7F9FC]">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-base font-semibold text-[#242124]">
                {activeTab === 'onboarded' ? 'Onboarded' : isSamtaGroup ? 'Shortlisted' : 'Selected'}
                <span className="text-gray-500 font-normal ml-1.5 text-sm">
                  ({activeTab === 'onboarded' ? onboardedCount : selectedTabCount} in this screening)
                </span>
              </h2>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    if (!canShortlistNextSteps) {
                      toast.error('Select at least one candidate first');
                      return;
                    }
                    setShortlistNextStepsChoice(null);
                    setShowShortlistNextStepsModal(true);
                  }}
                  title={!canShortlistNextSteps ? 'Select at least one candidate first' : undefined}
                  className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-colors text-white whitespace-nowrap ${canShortlistNextSteps
                    ? 'bg-[#007BFF] hover:bg-[#0062CC]'
                    : 'bg-[#007BFF]/50 text-white cursor-not-allowed opacity-60'
                    }`}
                  data-testid="next-steps-trigger"
                >
                  Next Steps
                </button>
                <button
                  type="button"
                  data-testid="bulk-action-remove"
                  onClick={() => {
                    if (!canBulkRemoveFromShortlist) {
                      toast.error('Select at least one candidate first');
                      return;
                    }
                    bulkRemoveFromShortlist();
                  }}
                  title={
                    !canBulkRemoveFromShortlist ? 'Select at least one candidate first' : undefined
                  }
                  className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${canBulkRemoveFromShortlist
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                    }`}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
          {/* Name search — same visual as Candidates search bar (but name-only filter) */}
          <div className="mb-6">
            <AICandidateSearchBar
              value={shortlistNameQuery}
              onChange={setShortlistNameQuery}
              onSearch={() => { }}
              onClear={() => { }}
              isSearching={false}
              isActive={!!(shortlistNameQuery && shortlistNameQuery.trim())}
              placeholder={`Search ${activeTab === 'onboarded' ? 'onboarded' : 'selected'} candidates by name...`}
              disabled={false}
              searchButtonLabel="Search"
              rightActions={
                <div className="flex items-center gap-2">
                  {renderMonthSelect(
                    shortlistMonth,
                    changeShortlistMonth,
                    shortlistMonthOptions,
                    `Filter by ${isSamtaGroup ? 'shortlisted' : 'selected'} month`
                  )}
                  <button
                    ref={filtersButtonRef}
                    type="button"
                    onClick={() => {
                      setFiltersModalContext('shortlist');
                      setIsFiltersModalOpen(true);
                    }}
                    className="px-3 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] transition-colors inline-flex items-center gap-2"
                    title="Filters"
                  >
                    <FunnelIcon className="h-5 w-5" />
                    {activeFiltersCount > 0 && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-[#007BFF]/10 px-2 py-0.5 text-[11px] font-semibold text-[#007BFF]">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setColumnsModalContext('shortlist');
                      setIsColumnsModalOpen(true);
                    }}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] transition-colors inline-flex items-center gap-2"
                    title="Customize columns"
                  >
                    <ViewColumnsIcon className="h-4 w-4" />
                    <span>Columns</span>
                  </button>
                </div>
              }
            />
          </div>
          {/* Table card only — same pattern as Candidates tab */}
          <div className="bg-[#F7F9FC] rounded-xl shadow-sm overflow-hidden">
            {(activeTab === 'onboarded' ? onboardedCount : selectedTabCount) === 0 ? (
              <div className="text-center py-24 bg-white rounded-xl border border-dashed border-gray-200">
                <StarIcon className="mx-auto h-10 w-10 text-gray-300 mb-3" />
                <p className="text-gray-600">
                  {activeTab === 'onboarded'
                    ? 'No onboarded candidates yet. Selected candidates move here once the ARM fills their DOJ and gross salary.'
                    : `No ${isSamtaGroup ? 'shortlisted' : 'selected'} candidates yet.`}
                </p>
              </div>
            ) : (
              renderCandidatesTable(filteredShortlistCandidatesToShow, {
                selectedIds: selectedShortlistCandidateIds,
                onSelectionChange: setSelectedShortlistCandidateIds,
                showShortlistedDate: true,
                showRejectedDate: false,
                showStatus: true,
                hideColumnFilters: true,
                showInlineEmptyState: true,
                isAISearchActiveForTable: false,
                onClearFilters: () => {
                  clearAllCandidateTableFilters({ clearShortlistQuery: true });
                  changeShortlistMonth('');
                },
                isShortlistView: true,
                showDownloadedColumn: true,
                columnVisibility: shortlistColumnVisibility,
                columnOrder: shortlistColumnOrder,
                orgDefaultColumnOrder: user?.ui?.defaultShortlistColumnOrder?.length > 0
                  ? user.ui.defaultShortlistColumnOrder
                  : buildShortlistDefaultOrder(user?.ui),
                hasArmSalaryDetails: armSalaryDetailsEnabled,
                showDocumentsColumn: screeningMeta?.includeDocumentCollection === true || Object.keys(documentStatusMap || {}).length > 0,
              })
            )}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            {activeTab === 'onboarded'
              ? 'Selected candidates whose DOJ and gross salary have been filled are shown here.'
              : `Only ${isSamtaGroup ? 'shortlisted' : 'selected'} candidates are shown here.`}{' '}
            Select candidates and use Next Steps to send email or generate a report.
          </p>
        </>
      )}
      {/* Tab content — Rejected: same layout as Shortlist */}
      {activeTab === 'rejected' && (
        <>
          <div className="pl-0 pr-0 pb-4 bg-[#F7F9FC]">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-base font-semibold text-[#242124]">
                Rejected
                <span className="text-gray-500 font-normal ml-1.5 text-sm">
                  ({rejectedCount} in this screening)
                </span>
              </h2>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  data-testid="bulk-action-restore"
                  onClick={() => {
                    if (!canBulkRestoreFromRejected) {
                      toast.error('Select at least one candidate first');
                      return;
                    }
                    bulkRestoreFromRejected();
                  }}
                  title={!canBulkRestoreFromRejected ? 'Select at least one candidate first' : undefined}
                  className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${canBulkRestoreFromRejected
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                    }`}
                >
                  {restoringFromRejected ? 'Restoring…' : 'Restore'}
                </button>
              </div>
            </div>
          </div>
          <div className="mb-6">
            <AICandidateSearchBar
              value={rejectedNameQuery}
              onChange={setRejectedNameQuery}
              onSearch={() => { }}
              onClear={() => { }}
              isSearching={false}
              isActive={!!(rejectedNameQuery && rejectedNameQuery.trim())}
              placeholder="Search rejected candidates by name..."
              disabled={false}
              searchButtonLabel="Search"
              rightActions={
                <div className="flex items-center gap-2">
                  {renderMonthSelect(rejectedMonth, changeRejectedMonth, rejectedMonthOptions, 'Filter by rejected month')}
                  <button
                    type="button"
                    onClick={() => {
                      setColumnsModalContext('rejected');
                      setIsColumnsModalOpen(true);
                    }}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] transition-colors inline-flex items-center gap-2"
                    title="Customize columns"
                  >
                    <ViewColumnsIcon className="h-4 w-4" />
                    <span>Columns</span>
                  </button>
                </div>
              }
            />
          </div>
          <div className="bg-[#F7F9FC] rounded-xl shadow-sm overflow-hidden">
            {rejectedCandidates.length === 0 ? (
              <div className="text-center py-16 px-6 bg-white rounded-xl border border-gray-200">
                <p className="text-gray-600">No rejected candidates yet.</p>
              </div>
            ) : (
              renderCandidatesTable(filteredRejectedCandidatesToShow, {
                selectedIds: selectedRejectedCandidateIds,
                onSelectionChange: setSelectedRejectedCandidateIds,
                showShortlistedDate: false,
                showRejectedDate: true,
                showStatus: true,
                hideColumnFilters: true,
                showInlineEmptyState: true,
                isAISearchActiveForTable: false,
                onClearFilters: () => {
                  setRejectedNameQuery('');
                  changeRejectedMonth('');
                },
                isRejectedView: true,
                notesDefaultVisible: rejectionReasonRequired,
                columnVisibility: rejectedColumnVisibility,
                columnOrder: rejectedColumnOrder,
                orgDefaultColumnOrder: user?.ui?.defaultRejectedColumnOrder?.length > 0
                  ? user.ui.defaultRejectedColumnOrder
                  : buildRejectedDefaultOrder(user?.ui),
                showDocumentsColumn: screeningMeta?.includeDocumentCollection === true || Object.keys(documentStatusMap || {}).length > 0,
              })
            )}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Only rejected candidates are shown here. Select candidates and use Restore to move them back.
          </p>
        </>
      )}

      {/* Tab content - Dropped */}
      {isGoColors && activeTab === 'dropped' && (
        <>
          <div className="pl-0 pr-0 pb-4 bg-[#F7F9FC]">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                Dropped Candidates
                <span className="bg-gray-200 text-gray-700 py-0.5 px-2.5 rounded-full text-sm font-medium">
                  {droppedCandidates.length}
                </span>
              </h2>
            </div>
          </div>
          <div className="mb-6">
            <AICandidateSearchBar
              value={droppedNameQuery}
              onChange={setDroppedNameQuery}
              onSearch={() => { }}
              onClear={() => { }}
              isSearching={false}
              isActive={!!(droppedNameQuery && droppedNameQuery.trim())}
              placeholder="Search dropped candidates by name..."
              disabled={false}
              searchButtonLabel="Search"
            />
          </div>
          <div className="bg-[#F7F9FC] rounded-xl shadow-sm overflow-hidden">
            {droppedCandidates.length === 0 ? (
              <div className="text-center py-16 px-6 bg-white rounded-xl border border-gray-200">
                <p className="text-gray-600">No dropped candidates yet.</p>
              </div>
            ) : (
              renderCandidatesTable(filteredDroppedCandidatesToShow, {
                selectedIds: selectedDroppedCandidateIds,
                onSelectionChange: setSelectedDroppedCandidateIds,
                showShortlistedDate: false,
                showRejectedDate: false,
                showStatus: true,
                hideColumnFilters: true,
                showInlineEmptyState: true,
                isAISearchActiveForTable: false,
                onClearFilters: () => setDroppedNameQuery(''),
                isRejectedView: false,
                isDroppedView: true,
                columnVisibility: rejectedColumnVisibility, // reuse rejected column visibility for now
                columnOrder: rejectedColumnOrder,
                orgDefaultColumnOrder: buildRejectedDefaultOrder(user?.ui),
              })
            )}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Only dropped candidates are shown here.
          </p>
        </>
      )}
      {activeTab === 'insights' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden desktop:max-w-[1600px]">
          <div className="px-6 py-4 desktop:px-8 desktop:py-5 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <ChartBarIcon className="h-5 w-5 text-[#007BFF]" />
              <div>
                <h3 className="text-base font-semibold text-[#242124]">Insights</h3>
                <p className="text-xs text-gray-500">Summary metrics for this screening</p>
              </div>
            </div>
          </div>
          <div className="px-6 pb-6"><InsightsTab {...insightsTabProps} /></div>
        </div>
      )}
      {/* Shortlist Next Steps Modal (same UI as Job Results) */}
      {showShortlistNextStepsModal && (
        <div
          data-testid="modal-backdrop" className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowShortlistNextStepsModal(false);
            setShortlistNextStepsChoice(null);
            setShortlistNextStepsStage('choice');
            setReportFormatChoice(null);
            setMoveDestinationScreeningId('');
          }}
        >
          <div
            className="w-full max-w-4xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between flex-shrink-0 px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-slate-50 via-white to-slate-50">
              <div className="flex items-center gap-3">
                {shortlistNextStepsStage !== 'choice' && (
                  <button
                    type="button"
                    onClick={() => {
                      setShortlistNextStepsStage('choice');
                      setReportFormatChoice(null);
                      setMoveDestinationScreeningId('');
                    }}
                    className="text-gray-400 hover:text-gray-600 mr-1"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                )}
                <div className="space-y-1">
                  <h3 data-testid="next-steps-modal-heading" className="text-lg font-semibold text-gray-900 tracking-tight">
                    {shortlistNextStepsStage === 'choice' && 'Next Steps'}
                    {shortlistNextStepsStage === 'report_options' && 'Generate Report'}
                    {shortlistNextStepsStage === 'move_destination' && 'Move to Another Screening'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {shortlistNextStepsStage === 'choice' &&
                      'Choose what you want to do with the selected shortlisted candidates.'}
                    {shortlistNextStepsStage === 'report_options' &&
                      'Select the format(s) you want to download.'}
                    {shortlistNextStepsStage === 'move_destination' &&
                      'Select the screening (for this job) where these candidates should be added.'}
                  </p>
                  <div className="inline-flex items-center gap-2 mt-1.5 rounded-full bg-slate-100 px-3 py-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    <span className="text-xs font-medium text-slate-600">
                      {shortlistReportTitle} · Shortlist workflow
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowShortlistNextStepsModal(false);
                  setShortlistNextStepsChoice(null);
                  setShortlistNextStepsStage('choice');
                  setReportFormatChoice(null);
                  setMoveDestinationScreeningId('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-5 flex flex-col gap-4 bg-slate-50/60">
              {shortlistNextStepsStage === 'choice' && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                        Choose the next step
                      </p>
                      <p className="mt-1 text-sm text-slate-600 max-w-2xl">
                        Decide how you want to progress the selected shortlisted candidates.
                      </p>
                    </div>
                    {selectedShortlistCandidateIds.length > 0 && (
                      <div className="flex flex-col items-end text-right">
                        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 shadow-sm">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                          <span className="text-xs font-medium text-slate-700">
                            {selectedShortlistCandidateIds.length} candidate
                            {selectedShortlistCandidateIds.length !== 1 ? 's' : ''} selected
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="grid gap-3 md:gap-4 md:grid-cols-3">
                    {/* temporary removing send to hr manager button */}
                    {/* <div
                      className="relative flex flex-col items-stretch justify-between rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 text-left opacity-60 cursor-not-allowed"
                      title="Coming soon"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-full bg-blue-100 text-blue-600 shrink-0">
                          <UserIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-700">Send to HR Manager</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Share the selected shortlisted candidates with your HR team for further
                            review.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-blue-600">
                          Handoff to HR
                        </span>
                        <span className="inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full bg-slate-200 text-slate-600">
                          Coming soon
                        </span>
                      </div>
                    </div> */}
                    <button
                      type="button"
                      data-testid="option-send-email"
                      onClick={() => setShortlistNextStepsChoice('send_email')}
                      className={`relative flex flex-col items-stretch justify-between rounded-xl border bg-white px-5 py-4 text-left shadow-sm transition-all ${shortlistNextStepsChoice === 'send_email'
                        ? 'border-green-500 ring-2 ring-green-100'
                        : 'border-slate-200 hover:border-green-400 hover:shadow-md'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-full bg-green-100 text-green-600 shrink-0">
                          <EnvelopeIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">
                            Send Email to Candidate
                          </p>
                          <p className="mt-1 text-xs text-slate-600">
                            Send an email notification to the selected candidates about their
                            shortlist status.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-green-600">
                          Candidate outreach
                        </span>
                        <span
                          className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${shortlistNextStepsChoice === 'send_email'
                            ? 'border-green-600 bg-green-600 text-white'
                            : 'border-slate-300 bg-white text-slate-400'
                            }`}
                        >
                          {shortlistNextStepsChoice === 'send_email' ? '✓' : ''}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid="option-generate-report"
                      onClick={() => setShortlistNextStepsChoice('generate_report')}
                      className={`relative flex flex-col items-stretch justify-between rounded-xl border bg-white px-5 py-4 text-left shadow-sm transition-all ${shortlistNextStepsChoice === 'generate_report'
                        ? 'border-indigo-500 ring-2 ring-indigo-100'
                        : 'border-slate-200 hover:border-indigo-400 hover:shadow-md'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-full bg-indigo-100 text-indigo-700 shrink-0">
                          <DocumentTextIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">Generate Report</p>
                          <p className="mt-1 text-xs text-slate-600">
                            Create a report of the selected shortlisted candidates with scores and
                            details.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-indigo-600">
                          Export & share
                        </span>
                        <span
                          className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${shortlistNextStepsChoice === 'generate_report'
                            ? 'border-indigo-600 bg-indigo-600 text-white'
                            : 'border-slate-300 bg-white text-slate-400'
                            }`}
                        >
                          {shortlistNextStepsChoice === 'generate_report' ? '✓' : ''}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid="option-move-screening"
                      onClick={() => setShortlistNextStepsChoice('move_to_screening')}
                      className={`relative flex flex-col items-stretch justify-between rounded-xl border bg-white px-5 py-4 text-left shadow-sm transition-all ${shortlistNextStepsChoice === 'move_to_screening'
                        ? 'border-blue-500 ring-2 ring-blue-100'
                        : 'border-slate-200 hover:border-blue-400 hover:shadow-md'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-full bg-blue-100 text-blue-600 shrink-0">
                          <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">Move to Another Screening</p>
                          <p className="mt-1 text-xs text-slate-600">
                            Keep the candidate in the current screening and add them to another one.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-blue-600">
                          Transfer
                        </span>
                        <span
                          className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${shortlistNextStepsChoice === 'move_to_screening'
                            ? 'border-blue-600 bg-blue-600 text-white'
                            : 'border-slate-300 bg-white text-slate-400'
                            }`}
                        >
                          {shortlistNextStepsChoice === 'move_to_screening' ? '✓' : ''}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid="option-schedule-interview"
                      onClick={() => setShortlistNextStepsChoice('schedule_interview')}
                      className={`relative flex flex-col items-stretch justify-between rounded-xl border bg-white px-5 py-4 text-left shadow-sm transition-all ${shortlistNextStepsChoice === 'schedule_interview'
                        ? 'border-amber-500 ring-2 ring-amber-100'
                        : 'border-slate-200 hover:border-amber-400 hover:shadow-md'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-full bg-amber-100 text-amber-600 shrink-0">
                          <CalendarDaysIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">
                            Schedule Interview
                          </p>
                          <p className="mt-1 text-xs text-slate-600">
                            Set your availability and let candidates pick a time slot for their interview.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-amber-600">
                          Calendar
                        </span>
                        <span
                          className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${shortlistNextStepsChoice === 'schedule_interview'
                            ? 'border-amber-600 bg-amber-600 text-white'
                            : 'border-slate-300 bg-white text-slate-400'
                            }`}
                        >
                          {shortlistNextStepsChoice === 'schedule_interview' ? '✓' : ''}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid="option-document-collection"
                      onClick={() => setShortlistNextStepsChoice('collect_documents')}
                      className={`relative flex flex-col items-stretch justify-between rounded-xl border bg-white px-5 py-4 text-left shadow-sm transition-all ${shortlistNextStepsChoice === 'collect_documents'
                        ? 'border-slate-700 ring-2 ring-slate-200'
                        : 'border-slate-200 hover:border-slate-400 hover:shadow-md'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-full bg-slate-100 text-slate-700 shrink-0">
                          <DocumentTextIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">Document Collection</p>
                          <p className="mt-1 text-xs text-slate-600">
                            Request documents from candidates for verification.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-700">
                          DOCUMENTS
                        </span>
                        <span
                          className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${shortlistNextStepsChoice === 'collect_documents'
                            ? 'border-slate-700 bg-slate-700 text-white'
                            : 'border-slate-300 bg-white text-slate-400'
                            }`}
                        >
                          {shortlistNextStepsChoice === 'collect_documents' ? '✓' : ''}
                        </span>
                      </div>
                    </button>
                    {/* Send Email to Hiring Manager */}
                    <button
                      type="button"
                      data-testid="option-review-hiring-manager"
                      onClick={() => setShortlistNextStepsChoice('send_to_hiring_manager')}
                      className={`relative flex flex-col items-stretch justify-between rounded-xl border bg-white px-5 py-4 text-left shadow-sm transition-all ${shortlistNextStepsChoice === 'send_to_hiring_manager'
                        ? 'border-violet-500 ring-2 ring-violet-100'
                        : 'border-slate-200 hover:border-violet-400 hover:shadow-md'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-full bg-violet-100 text-violet-600 shrink-0">
                          <UserIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">
                            Review with Hiring Manager
                          </p>
                          <p className="mt-1 text-xs text-slate-600">
                            Generate a shareable link for hiring managers to review candidates and make decisions without platform login.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-violet-600">
                          Hiring manager
                        </span>
                        <span
                          className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${shortlistNextStepsChoice === 'send_to_hiring_manager'
                            ? 'border-violet-600 bg-violet-600 text-white'
                            : 'border-slate-300 bg-white text-slate-400'
                            }`}
                        >
                          {shortlistNextStepsChoice === 'send_to_hiring_manager' ? '✓' : ''}
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              )}
              {shortlistNextStepsStage === 'move_destination' && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                        Destination screening
                      </p>
                      <p className="mt-1 text-sm text-slate-600 max-w-2xl">
                        Candidates won&apos;t be removed from the current screening.
                      </p>
                    </div>
                    {selectedShortlistCandidateIds.length > 0 && (
                      <div className="flex flex-col items-end text-right">
                        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 shadow-sm">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                          <span className="text-xs font-medium text-slate-700">
                            {selectedShortlistCandidateIds.length} candidate
                            {selectedShortlistCandidateIds.length !== 1 ? 's' : ''} selected
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    {moveDestinationLoading ? (
                      <div className="flex items-center gap-3 text-sm text-slate-600">
                        <span className="inline-block w-4 h-4 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
                        Loading screenings…
                      </div>
                    ) : moveDestinationScreenings.length === 0 ? (
                      <p className="text-sm text-slate-600">No other screenings found for this job.</p>
                    ) : (
                      <>
                        <label className="block text-sm font-medium text-slate-800 mb-2">
                          Choose where to add the candidates
                        </label>
                        <select
                          value={moveDestinationScreeningId}
                          onChange={(e) => setMoveDestinationScreeningId(e.target.value)}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                        >
                          {moveDestinationScreenings.map((s) => (
                            <option key={s._id} value={String(s._id)}>
                              {s.name || 'Screening'}
                            </option>
                          ))}
                        </select>
                        <p className="mt-3 text-xs text-slate-500">
                          Each selected candidate will get a new application invitation for the destination screening.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
              {shortlistNextStepsStage === 'report_options' && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                        Choose report format
                      </p>
                      <p className="mt-1 text-sm text-slate-600 max-w-2xl">
                        Select one or more formats to download. ZIP includes the report plus
                        candidate resumes.
                      </p>
                    </div>
                    {selectedShortlistCandidateIds.length > 0 && (
                      <div className="flex flex-col items-end text-right">
                        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 shadow-sm">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                          <span className="text-xs font-medium text-slate-700">
                            {selectedShortlistCandidateIds.length} candidate
                            {selectedShortlistCandidateIds.length !== 1 ? 's' : ''} selected
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="grid gap-3 md:gap-4 md:grid-cols-3">
                    {!isGoColors && (
                      <>
                        <button
                          type="button"
                      data-testid="report-format-excel"
                      onClick={() =>
                        setReportFormatChoice((prev) => (prev === 'excel' ? null : 'excel'))
                      }
                      className={`relative flex flex-col items-stretch justify-between rounded-xl border bg-white px-5 py-4 text-left shadow-sm transition-all ${reportFormatChoice === 'excel'
                        ? 'border-green-500 ring-2 ring-green-100'
                        : 'border-slate-200 hover:border-green-400 hover:shadow-md'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-full bg-green-100 text-green-700 shrink-0">
                          <DocumentTextIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">Excel (.xlsx)</p>
                          <p className="mt-1 text-xs text-slate-600">
                            Spreadsheet with all candidate details. Easy to filter and sort.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-green-600">
                          Spreadsheet
                        </span>
                        <span
                          className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${reportFormatChoice === 'excel'
                            ? 'border-green-600 bg-green-600 text-white'
                            : 'border-slate-300 bg-white text-slate-400'
                            }`}
                        >
                          {reportFormatChoice === 'excel' ? '✓' : ''}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid="report-format-pdf"
                      onClick={() =>
                        setReportFormatChoice((prev) => (prev === 'pdf' ? null : 'pdf'))
                      }
                      className={`relative flex flex-col items-stretch justify-between rounded-xl border bg-white px-5 py-4 text-left shadow-sm transition-all ${reportFormatChoice === 'pdf'
                        ? 'border-red-500 ring-2 ring-red-100'
                        : 'border-slate-200 hover:border-red-400 hover:shadow-md'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-full bg-red-100 text-red-700 shrink-0">
                          <DocumentTextIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">PDF (.pdf)</p>
                          <p className="mt-1 text-xs text-slate-600">
                            Printable report with candidate table. Ready to share.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-red-600">
                          Printable
                        </span>
                        <span
                          className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${reportFormatChoice === 'pdf'
                            ? 'border-red-600 bg-red-600 text-white'
                            : 'border-slate-300 bg-white text-slate-400'
                            }`}
                        >
                          {reportFormatChoice === 'pdf' ? '✓' : ''}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid="report-format-zip"
                      onClick={() =>
                        setReportFormatChoice((prev) => (prev === 'zip' ? null : 'zip'))
                      }
                      className={`relative flex flex-col items-stretch justify-between rounded-xl border bg-white px-5 py-4 text-left shadow-sm transition-all ${reportFormatChoice === 'zip'
                        ? 'border-indigo-500 ring-2 ring-indigo-100'
                        : 'border-slate-200 hover:border-indigo-400 hover:shadow-md'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-full bg-indigo-100 text-indigo-700 shrink-0">
                          <DocumentArrowUpIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">
                            ZIP (Report + Resumes)
                          </p>
                          <p className="mt-1 text-xs text-slate-600">
                            Report plus all candidate resumes in one archive.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-indigo-600">
                          Complete package
                        </span>
                        <span
                          className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${reportFormatChoice === 'zip'
                            ? 'border-indigo-600 bg-indigo-600 text-white'
                            : 'border-slate-300 bg-white text-slate-400'
                            }`}
                        >
                          {reportFormatChoice === 'zip' ? '✓' : ''}
                        </span>
                      </div>
                    </button>
                      </>
                    )}
                    {isGoColors && (
                      <button
                        type="button"
                        data-testid="report-format-gocolors-zip"
                        onClick={() =>
                          setReportFormatChoice((prev) => (prev === 'gocolors_zip' ? null : 'gocolors_zip'))
                        }
                        className={`relative flex flex-col items-stretch justify-between rounded-xl border bg-white px-5 py-4 text-left shadow-sm transition-all ${reportFormatChoice === 'gocolors_zip'
                          ? 'border-indigo-500 ring-2 ring-indigo-100'
                          : 'border-slate-200 hover:border-indigo-400 hover:shadow-md'
                          }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2.5 rounded-full bg-indigo-100 text-indigo-700 shrink-0">
                            <DocumentArrowUpIcon className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900">
                              ZIP (Documents + Excel)
                            </p>
                            <p className="mt-1 text-xs text-slate-600">
                              Custom report for Go Colors containing candidate documents and excel sheet.
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <span className="text-[11px] font-medium uppercase tracking-wide text-indigo-600">
                            GoColors custom package
                          </span>
                          <span
                            className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${reportFormatChoice === 'gocolors_zip'
                              ? 'border-indigo-600 bg-indigo-600 text-white'
                              : 'border-slate-300 bg-white text-slate-400'
                              }`}
                          >
                            {reportFormatChoice === 'gocolors_zip' ? '✓' : ''}
                          </span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex-shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-white">
              {shortlistNextStepsStage !== 'move_destination' && (
                <button data-testid="modal-close-btn"
                  type="button"
                  onClick={() => {
                    setShowShortlistNextStepsModal(false);
                    setShortlistNextStepsChoice(null);
                    setShortlistNextStepsStage('choice');
                    setReportFormatChoice(null);
                    setMoveDestinationScreeningId('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
              )}
              {shortlistNextStepsStage === 'choice' && (
                <button
                  type="button"
                  onClick={async () => {
                    if (shortlistNextStepsChoice === 'generate_report') {
                      setShortlistNextStepsStage('report_options');
                      setReportFormatChoice(null);
                    } else if (shortlistNextStepsChoice === 'move_to_screening') {
                      setShortlistNextStepsStage('move_destination');
                      setMoveDestinationScreeningId('');
                    } else if (shortlistNextStepsChoice === 'send_email') {
                      setShowShortlistNextStepsModal(false);
                      setShortlistNextStepsChoice(null);
                      setShortlistNextStepsStage('choice');
                      setEmailModalContext('shortlist');
                      setEmailDraftForSend(null);
                      setEmailModalScreeningId(null);
                      setShowSendEmailToCandidateModal(true);
                    } else if (shortlistNextStepsChoice === 'schedule_interview') {
                      setShowShortlistNextStepsModal(false);
                      setShortlistNextStepsChoice(null);
                      setShortlistNextStepsStage('choice');
                      setShowScheduleInterviewModal(true);
                    } else if (shortlistNextStepsChoice === 'collect_documents') {
                      try {
                        const dpaRes = await axios.get('/api/documents/check-dpa');
                        if (!dpaRes.data?.dpaAccepted) {
                          setShowShortlistNextStepsModal(false);
                          setShortlistNextStepsChoice(null);
                          setShortlistNextStepsStage('choice');
                          setShowDpaRequiredModal(true);
                          return;
                        }
                        setShowShortlistNextStepsModal(false);
                        setShortlistNextStepsChoice(null);
                        setShortlistNextStepsStage('choice');
                        setShowCollectDocumentsModal(true);
                      } catch (e) {
                        toast.error('Failed to verify DPA status');
                      }
                    } else if (shortlistNextStepsChoice === 'send_to_hiring_manager') {
                      setShowShortlistNextStepsModal(false);
                      setShortlistNextStepsChoice(null);
                      setShortlistNextStepsStage('choice');
                      setHiringManagerShareCandidateIds(selectedShortlistCandidateIds);
                      setHiringManagerShareOrigin('shortlist');
                      setShowSendEmailToHiringManagerModal(true);
                    }
                  }}
                  disabled={!shortlistNextStepsChoice}
                  data-testid="modal-continue-btn"
                  className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  Continue
                </button>
              )}
              {shortlistNextStepsStage === 'report_options' && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!reportFormatChoice) return;
                    if (reportFormatChoice === 'excel') {
                      handleGenerateShortlistReport();
                    } else if (reportFormatChoice === 'pdf') {
                      handleGenerateShortlistReportPDF();
                    } else if (reportFormatChoice === 'zip') {
                      await handleGenerateShortlistReportZIP();
                    } else if (reportFormatChoice === 'gocolors_zip') {
                      await handleGenerateGoColorsZIP();
                    }
                    setShowShortlistNextStepsModal(false);
                    setShortlistNextStepsChoice(null);
                    setShortlistNextStepsStage('choice');
                    setReportFormatChoice(null);
                  }}
                  disabled={!reportFormatChoice}
                  data-testid="download-report-btn"
                  className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  Download
                </button>
              )}
              {shortlistNextStepsStage === 'move_destination' && (
                <button
                  type="button"
                  onClick={handleMoveShortlistCandidates}
                  disabled={!moveDestinationScreeningId || movingShortlistCandidates}
                  className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-all shadow-sm inline-flex items-center gap-2"
                >
                  {movingShortlistCandidates ? (
                    <>
                      <span className="inline-block w-3.5 h-3.5 border-2 border-white/80 border-t-white rounded-full animate-spin" />
                      Moving…
                    </>
                  ) : (
                    'Configure Invite Email'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Bulk delete candidates confirmation modal */}
      {showBulkDeleteConfirmModal && (
        <div
          data-testid="modal-backdrop" className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
          onClick={() => !bulkDeletingCandidates && setShowBulkDeleteConfirmModal(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Delete selected candidates?</h3>
              <p className="mt-2 text-sm text-gray-600">
                This will permanently delete all data in this screening for{' '}
                <span className="font-medium text-gray-900">
                  {selectedCandidatesForShortlist.length} candidate
                  {selectedCandidatesForShortlist.length === 1 ? '' : 's'}
                </span>
                :
              </p>
              <ul className="mt-2 list-disc list-inside text-sm text-gray-600 space-y-1">
                <li>Assessment data (scores, answers)</li>
                <li>Resume score and resume-related data</li>
                <li>AI interview details (sessions, transcripts, scores)</li>
              </ul>
              <p className="mt-3 text-sm font-medium text-gray-700">
                This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50">
              <button data-testid="modal-close-btn"
                type="button"
                onClick={() => setShowBulkDeleteConfirmModal(false)}
                disabled={bulkDeletingCandidates}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!screeningId || selectedCandidatesForShortlist.length === 0) return;
                  const ids = [...selectedCandidatesForShortlist];
                  setBulkDeletingCandidates(true);
                  try {
                    const results = await Promise.allSettled(
                      ids.map((candidateId) =>
                        axios.delete(`/api/screenings/${screeningId}/candidates/${candidateId}`)
                      )
                    );
                    const successCount = results.filter(
                      (r) => r.status === 'fulfilled'
                    ).length;
                    const failureCount = ids.length - successCount;
                    if (successCount > 0) {
                      toast.success(
                        `Deleted ${successCount} candidate${successCount === 1 ? '' : 's'
                        } successfully`
                      );
                    }
                    if (failureCount > 0) {
                      toast.error(
                        `Failed to delete ${failureCount} candidate${failureCount === 1 ? '' : 's'
                        }. Please try again.`
                      );
                    }
                    setShowBulkDeleteConfirmModal(false);
                    setSelectedCandidatesForShortlist((prev) =>
                      prev.filter((id) => !ids.includes(id))
                    );
                    fetchCandidates();
                  } catch (err) {
                    toast.error(
                      err.response?.data?.message || 'Failed to delete selected candidates'
                    );
                  } finally {
                    setBulkDeletingCandidates(false);
                  }
                }}
                disabled={bulkDeletingCandidates}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {bulkDeletingCandidates ? (
                  <>
                    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Deleting…
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete candidate confirmation modal */}
      {candidateToDelete && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !deleteCandidateLoading && setCandidateToDelete(null)}
        >
          <div
            className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Delete candidate?</h3>
              <p className="mt-2 text-sm text-gray-600">
                This will permanently delete all data for{' '}
                <span className="font-medium text-gray-900">
                  {candidateToDelete.name || candidateToDelete.email || 'this candidate'}
                </span>
                :
              </p>
              <ul className="mt-2 list-disc list-inside text-sm text-gray-600 space-y-1">
                <li>Assessment data (scores, answers)</li>
                <li>Resume score and resume-related data</li>
                <li>AI interview details (session, transcript, scores)</li>
                <li>Any scheduled follow-up invites</li>
              </ul>
              <p className="mt-3 text-sm font-medium text-gray-700">
                This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50">
              <button data-testid="modal-close-btn"
                type="button"
                onClick={() => setCandidateToDelete(null)}
                disabled={deleteCandidateLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const id = candidateToDelete.id || candidateToDelete._id;
                  if (!screeningId || !id) return;
                  setDeleteCandidateLoading(true);
                  try {
                    await axios.delete(`/api/screenings/${screeningId}/candidates/${id}`);
                    toast.success('Candidate deleted');
                    setCandidateToDelete(null);
                    fetchCandidates();
                  } catch (err) {
                    toast.error(err.response?.data?.message || 'Failed to delete candidate');
                  } finally {
                    setDeleteCandidateLoading(false);
                  }
                }}
                disabled={deleteCandidateLoading}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {deleteCandidateLoading ? (
                  <>
                    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Deleting…
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add to shortlist confirmation modal */}
      {showShortlistConfirmModal && (
        <div
          data-testid="modal-backdrop" className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
          onClick={() =>
            !bulkShortlisting && (setShowShortlistConfirmModal(false), setCandidateToShortlist(null))
          }
        >
          <div
            className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Mark as {isSamtaGroup ? 'shortlisted' : 'selected'}?</h3>
              <p className="mt-2 text-sm text-gray-600">
                {candidateToShortlist ? (
                  <>
                    You are about to mark{' '}
                    <span className="font-medium text-gray-900">
                      {candidateToShortlist.name || candidateToShortlist.email || 'this candidate'}
                    </span>{' '}
                    as {isSamtaGroup ? 'shortlisted' : 'selected'}.
                  </>
                ) : (
                  <>
                    You are about to mark{' '}
                    <span className="font-medium text-gray-900">
                      {selectedCandidatesForShortlist.length} candidate
                      {selectedCandidatesForShortlist.length === 1 ? '' : 's'}
                    </span>{' '}
                    as {isSamtaGroup ? 'shortlisted' : 'selected'}.
                  </>
                )}
              </p>
              <p className="mt-3 text-sm text-gray-600">
                {isSamtaGroup ? 'Shortlisted' : 'Selected'} candidates will appear in the <strong>{isSamtaGroup ? 'Shortlisted' : 'Selected'}</strong> tab, where you
                can take next steps such as generating reports, sharing with your team, or removing
                them from {isSamtaGroup ? 'shortlisted' : 'selected'}.
              </p>
              {/* <div className="mt-4 flex items-center">
                <input
                  id="moveToOnboarding"
                  type="checkbox"
                  className="h-4 w-4 text-[#007BFF] focus:ring-[#007BFF] border-gray-300 rounded cursor-pointer"
                  checked={moveToOnboarding}
                  onChange={(e) => setMoveToOnboarding(e.target.checked)}
                />
                <label htmlFor="moveToOnboarding" className="ml-2 block text-sm text-gray-900 font-medium cursor-pointer">
                  Move to onboarding flow
                </label>
              </div> */}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50">
              <button data-testid="modal-close-btn"
                type="button"
                onClick={() => {
                  setShowShortlistConfirmModal(false);
                  setCandidateToShortlist(null);
                }}
                disabled={bulkShortlisting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="modal-action-select"
                onClick={async () => {
                  if (candidateToShortlist) {
                    const cid = candidateToShortlist.id || candidateToShortlist._id;
                    await toggleShortlist(cid);
                    toast.success('Candidate selected');
                    setCandidateToShortlist(null);
                    setShowShortlistConfirmModal(false);
                  } else {
                    await bulkShortlistCandidates();
                    setShowShortlistConfirmModal(false);
                  }
                }}
                disabled={bulkShortlisting}
                className="px-4 py-2 text-sm font-medium text-white bg-[#007BFF] rounded-md hover:bg-[#0062CC] disabled:opacity-50"
              >
                {bulkShortlisting ? 'Adding…' : (isSamtaGroup ? 'Mark as shortlisted' : 'Mark as selected')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Reject candidate confirmation modal */}
      {showRejectConfirmModal && (
        <div
          data-testid="modal-backdrop" className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
          onClick={() => !bulkRejecting && closeRejectModal()}
        >
          <div
            className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Reject candidate?</h3>
              <p className="mt-2 text-sm text-gray-600">
                {candidateToReject ? (
                  <>
                    You are about to reject{' '}
                    <span className="font-medium text-gray-900">
                      {candidateToReject.name || candidateToReject.email || 'this candidate'}
                    </span>
                    .
                  </>
                ) : (
                  <>
                    You are about to reject{' '}
                    <span className="font-medium text-gray-900">
                      {selectedCandidatesForShortlist.length} candidate
                      {selectedCandidatesForShortlist.length === 1 ? '' : 's'}
                    </span>
                    .
                  </>
                )}
              </p>
              <p className="mt-3 text-sm text-gray-600">
                Rejected candidates will be moved to the <strong>Rejected</strong> tab and removed
                from the main candidates list. You can restore them at any time.
              </p>
              {rejectionReasonRequired && (
                <div className="mt-4">
                  {isBurgerKing ? (
                    <>
                      <label
                        htmlFor="bkRejectDropdownReason"
                        className="block text-sm font-medium text-gray-900"
                      >
                        Reason <span className="text-red-600">*</span>
                      </label>
                      <Listbox
                        value={bkRejectDropdownReason}
                        onChange={(val) => {
                          setBkRejectDropdownReason(val);
                          if (rejectReasonError) setRejectReasonError('');
                        }}
                        disabled={bulkRejecting}
                      >
                        {({ open }) => (
                          <div className="relative mt-1.5 w-full">
                            <Listbox.Button
                              data-testid="bk-reject-reason-select"
                              className={`relative w-full cursor-default rounded-lg border bg-gray-50 py-2.5 pl-3 pr-10 text-left text-sm shadow-sm transition-all focus:bg-white focus:outline-none focus:ring-2 disabled:opacity-60 ${
                                rejectReasonError && !bkRejectDropdownReason
                                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-200 focus:border-[#007BFF] focus:ring-[#007BFF]/20'
                              }`}
                            >
                              <span className={`block truncate ${!bkRejectDropdownReason ? 'text-gray-400' : 'text-gray-800'}`}>
                                {bkRejectDropdownReason || 'Select a reason...'}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </span>
                            </Listbox.Button>
                            <Transition
                              show={open}
                              as={React.Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="absolute z-[100] mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none border border-gray-100">
                                {BK_REJECT_REASONS.map((reason) => (
                                  <Listbox.Option
                                    key={reason}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2.5 pl-4 pr-9 transition-colors ${
                                        active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                                      }`
                                    }
                                    value={reason}
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold text-[#007BFF]' : 'font-normal'}`}>
                                          {reason}
                                        </span>
                                        {selected ? (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-3 ${active ? 'text-blue-900' : 'text-[#007BFF]'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        ) : null}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        )}
                      </Listbox>

                      <label
                        htmlFor="bkRejectRemarks"
                        className="mt-4 block text-sm font-medium text-gray-900"
                      >
                        Remarks <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        id="bkRejectRemarks"
                        data-testid="bk-reject-remarks-input"
                        rows={3}
                        maxLength={MAX_REJECT_REASON_LENGTH}
                        value={bkRejectRemarks}
                        disabled={bulkRejecting}
                        onChange={(e) => {
                          setBkRejectRemarks(e.target.value);
                          if (rejectReasonError) setRejectReasonError('');
                        }}
                        placeholder="e.g. Not enough hands-on React experience for this role"
                        className={`mt-1.5 w-full resize-none rounded-lg border bg-gray-50 px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 transition-all focus:bg-white focus:outline-none focus:ring-2 disabled:opacity-60 ${
                          rejectReasonError && !bkRejectRemarks.trim()
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-gray-200 focus:border-[#007BFF] focus:ring-[#007BFF]/20'
                        }`}
                      />
                    </>
                  ) : (
                    <>
                      <label
                        htmlFor="rejectReason"
                        className="block text-sm font-medium text-gray-900"
                      >
                        Reason for rejection <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        id="rejectReason"
                        data-testid="reject-reason-input"
                        rows={3}
                        autoFocus
                        maxLength={MAX_REJECT_REASON_LENGTH}
                        value={rejectReason}
                        disabled={bulkRejecting}
                        onChange={(e) => {
                          setRejectReason(e.target.value);
                          if (rejectReasonError) setRejectReasonError('');
                        }}
                        placeholder="e.g. Not enough hands-on React experience for this role"
                        className={`mt-1.5 w-full resize-none rounded-lg border bg-gray-50 px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 transition-all focus:bg-white focus:outline-none focus:ring-2 disabled:opacity-60 ${rejectReasonError
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-gray-200 focus:border-[#007BFF] focus:ring-[#007BFF]/20'
                          }`}
                      />
                    </>
                  )}
                  <div className="mt-1.5 flex items-start justify-between gap-3">
                    <p
                      className={`text-xs ${rejectReasonError ? 'text-red-600' : 'text-gray-500'}`}
                      data-testid="reject-reason-help"
                    >
                      {rejectReasonError ||
                        (candidateToReject
                          ? 'Saved to this candidate’s notes along with your name.'
                          : `Saved to all ${selectedCandidatesForShortlist.length} candidates’ notes along with your name.`)}
                    </p>
                    <span className="flex-shrink-0 text-[11px] text-gray-400">
                      {isBurgerKing
                        ? `${(bkRejectDropdownReason ? bkRejectDropdownReason.length + 3 : 0) + bkRejectRemarks.length}/${MAX_REJECT_REASON_LENGTH}`
                        : `${rejectReason.length}/${MAX_REJECT_REASON_LENGTH}`}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50">
              <button data-testid="modal-close-btn"
                type="button"
                onClick={closeRejectModal}
                disabled={bulkRejecting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRejectFromModal}
                disabled={bulkRejecting || (rejectionReasonRequired && (isBurgerKing ? (!bkRejectDropdownReason || !bkRejectRemarks.trim()) : !rejectReason.trim()))}
                data-testid="confirm-reject-btn"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkRejecting ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Telephonic Call Confirmation Modal */}
      {showCallConfirmModal && candidateToCall && (
        <div
          data-testid="modal-backdrop" className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
          onClick={() => !callingCandidate && (setShowCallConfirmModal(false), setCandidateToCall(null))}
        >
          <div
            className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Call Candidate via AI?</h3>
              <p className="mt-2 text-sm text-gray-600">
                You are about to call{' '}
                <span className="font-semibold text-gray-900">
                  {candidateToCall.name || candidateToCall.email}
                </span>{' '}
                at{' '}
                <span className="font-semibold text-gray-900">
                  {candidateToCall.phone}
                </span>.
              </p>
              <p className="mt-3 text-sm text-gray-600">
                This will use the LiveKit SIP outbound trunk to call the candidate's phone and automatically connect them to the AI interviewer.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50">
              <button data-testid="modal-close-btn"
                type="button"
                onClick={() => {
                  setShowCallConfirmModal(false);
                  setCandidateToCall(null);
                }}
                disabled={callingCandidate}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCallCandidateConfirm}
                disabled={callingCandidate}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {callingCandidate ? 'Calling...' : 'Call'}
              </button>
            </div>
          </div>
        </div>
      )}
      {candidateToRecall && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
          onClick={() => !recallingCandidate && setCandidateToRecall(null)}
        >
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Recall candidate?</h3>
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{candidateToRecall.name || candidateToRecall.phone}</span>'s
                qualifying call answers, transcript and score will be deleted, and the call starts again from attempt 1.
              </p>
              <p className="mt-3 text-sm text-amber-700">This cannot be undone.</p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50">
              <button
                type="button"
                onClick={() => setCandidateToRecall(null)}
                disabled={recallingCandidate}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="confirm-recall-qualifying"
                onClick={handleRecallQualifyingConfirm}
                disabled={recallingCandidate}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {recallingCandidate ? 'Recalling...' : 'Delete & recall'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Resend invitation and clear data confirmation modal */}
      <ResendInviteModal

        isOpen={showResendConfirmModal}
        onClose={() => {
          setShowResendConfirmModal(false);
          setCandidateToResend(null);
        }}
        screeningId={screeningId}
        jobId={jobId}
        candidateToResend={candidateToResend}
        selectedCandidatesForShortlist={selectedCandidatesForShortlist}
        candidates={candidates}
        screeningMeta={screeningMeta}
        fetchCandidates={fetchCandidates}
      />
      {isGoColors && (
        <MoveCandidateJobPostModal
          isOpen={showMoveJobPostModal}
          onClose={() => {
            setShowMoveJobPostModal(false);
            setCandidateToMoveJobPost(null);
          }}
          screeningId={screeningId}
          candidate={candidateToMoveJobPost}
          onMoved={handleCandidateMoved}
        />
      )}
      <AvailabilitySetupModal
        isOpen={showScheduleInterviewModal}
        onClose={() => setShowScheduleInterviewModal(false)}
        onBack={() => {
          setShowScheduleInterviewModal(false);
          setShowShortlistNextStepsModal(true);
          setShortlistNextStepsStage('choice');
          setShortlistNextStepsChoice('schedule_interview');
        }}
        screeningId={screeningId}
        jobPostingId={screening?.jobId?._id || screening?.jobId}
        selectedCandidateIds={selectedShortlistCandidateIds}
        onConfigureEmail={handleScheduleConfigureEmail}
      />
      <DpaRequiredModal
        isOpen={showDpaRequiredModal}
        onClose={() => setShowDpaRequiredModal(false)}
      />
      <CollectDocumentsModal
        isOpen={showCollectDocumentsModal}
        onClose={() => setShowCollectDocumentsModal(false)}
        onBackToNextSteps={() => {
          setShowCollectDocumentsModal(false);
          setShowShortlistNextStepsModal(true);
          setShortlistNextStepsStage('choice');
          setShortlistNextStepsChoice('collect_documents');
        }}
        selectedCandidates={shortlistCandidates.filter((c) =>
          selectedShortlistCandidateIds.includes(c._id || c.id)
        )}
        screeningId={screeningId}
        jobPostingId={jobId}
        companyName={screening?.organisationId?.name || screening?.organisationId?.companyName || ''}
        jobTitle={screening?.jobId?.title || screening?.jobTitle || ''}
      />
      {viewDocumentsCandidate && (
        <DocumentViewerModal
          isOpen={!!viewDocumentsCandidate}
          onClose={() => setViewDocumentsCandidate(null)}
          candidate={viewDocumentsCandidate}
          documentData={documentStatusMap?.[viewDocumentsCandidate._id || viewDocumentsCandidate.id]}
          screeningId={screeningId}
          onDocumentsChanged={({ candidates: refreshCandidates } = {}) => {
            setDocumentStatusRefreshKey((k) => k + 1);
            if (refreshCandidates) fetchCandidates({ forceRefresh: true, quiet: true });
          }}
        />
      )}
      <CandidateColumnsModal
        isOpen={isColumnsModalOpen}
        onClose={() => setIsColumnsModalOpen(false)}
        columns={columnsModalColumns}
        value={columnsModalValue}
        columnOrder={columnsModalOrder}
        sortColumn={tableSortColumn}
        sortDirection={tableSortDirection}
        onApply={handleApplyColumns}
        tabContext={columnsModalContext}
        title={
          columnsModalContext === 'shortlist'
            ? 'Customize shortlist columns'
            : 'Customize candidate columns'
        }
        subtitle={
          columnsModalContext === 'shortlist'
            ? 'Choose the columns that should appear in the shortlist table.'
            : 'Choose the columns that should appear in the candidates table.'
        }
        tableOptions={{
          orgDefaultColumnOrder: user?.ui?.defaultCandidateColumnOrder || [],
          user: user
        }}
      />
      <ColumnSaveScopeModal
        isOpen={scopeModalOpen}
        saving={savingColumnScope}
        onClose={() => {
          if (savingColumnScope) return;
          setScopeModalOpen(false);
          setPendingColumnSave(null);
        }}
        onConfirm={commitColumnSave}
      />
      <SendEmailToCandidatesModal
        isOpen={showScheduleEmailModal}
        modalTitle="Send Interview Invites"
        onClose={() => {
          setShowScheduleEmailModal(false);
          setPendingSchedulingPayload(null);
        }}
        onBack={() => {
          setShowScheduleEmailModal(false);
          setShowScheduleInterviewModal(true);
        }}
        selectedCandidates={shortlistCandidates.filter((c) =>
          (pendingSchedulingPayload?.candidateIds || selectedShortlistCandidateIds).includes(
            c.id || c._id
          )
        )}
        defaultFromEmail="recruiter@talentcrux.ai"
        initialSubject={INTERVIEW_INVITE_SUBJECT}
        bodyTemplatesOverride={
          pendingSchedulingPayload?.meetingType === 'in_person'
            ? IN_PERSON_INTERVIEW_EMAIL_TEMPLATES
            : VIRTUAL_INTERVIEW_EMAIL_TEMPLATES
        }
        emailContext="schedule_interview"
        screeningId={screeningId}
        screeningName={screening?.name}
        nextButtonLabel="Next"
        sendButtonLabel="Send Invites"
        skipDefaultSend={true}
        extraPlaceholderContext={{
          interviewAddress: pendingSchedulingPayload?.inPersonAddress || '',
          slotDuration:
            pendingSchedulingPayload?.slotDuration != null
              ? String(pendingSchedulingPayload.slotDuration)
              : '',
          interviewMode:
            pendingSchedulingPayload?.meetingType === 'in_person' ? 'In-person' : 'Virtual',
          bookingLink: '[Booking link will be auto-generated for each candidate]',
        }}
        roleName={screening?.roleName}
        companyName={screening?.companyName}
        onBeforeSend={handleScheduleEmailSend}
        onAfterSendSuccess={() => {
          setShowScheduleEmailModal(false);
          const sentCount = pendingSchedulingPayload?.candidateIds?.length || 0;
          setPendingSchedulingPayload(null);
          toast.success(`Interview invites sent to ${sentCount} candidate(s)!`);
          fetchCandidates();
        }}
      />
      <SendEmailToCandidatesModal
        isOpen={showSendEmailToCandidateModal}
        modalTitle={
          emailModalContext === 'invite' || (bulkUploadEmailCandidates?.length > 0)
            ? pendingChannelInvites
              ? pendingChannelInvites.channels?.email === false && pendingChannelInvites.channels?.whatsapp === true
                ? 'Send WhatsApp Invite to Candidates'
                : 'Send Invite to Candidates'
              : 'Send Invite Email to Candidates'
            : 'Notify Selected Candidates'
        }
        screeningName={screening?.name}
        nextButtonLabel="Next"
        onBack={() => {
          // For move-to-screening flow, "Back" should return to destination selection modal.
          if (emailModalContext === 'invite' && emailModalScreeningId) {
            setShowSendEmailToCandidateModal(false);
            setShowShortlistNextStepsModal(true);
            setShortlistNextStepsStage('move_destination');
            setMoveDestinationScreeningId(String(emailModalScreeningId));
            setPendingMoveToScreening(null);
            return;
          }
          setShowSendEmailToCandidateModal(false);
          setBulkUploadEmailCandidates([]);
          setEmailDraftForSend(null);
          setEmailModalScreeningId(null);
          setEmailModalContext(undefined);
          setPendingMoveToScreening(null);
          setPendingChannelInvites(null);
        }}
        onClose={() => {
          // After a successful move+send flow, close directly without showing discard confirmation.
          if (skipAbandonMoveConfirmOnCloseRef.current) {
            skipAbandonMoveConfirmOnCloseRef.current = false;
            setShowSendEmailToCandidateModal(false);
            setBulkUploadEmailCandidates([]);
            setEmailDraftForSend(null);
            setEmailModalScreeningId(null);
            setEmailModalContext(undefined);
            setPendingMoveToScreening(null);
            setPendingChannelInvites(null);
            fetchCandidates();
            return;
          }
          // In the move-to-screening invite flow, closing the email modal means candidates will NOT be moved.
          if (pendingMoveToScreening) {
            setShowAbandonMoveConfirmModal(true);
            return;
          }
          setShowSendEmailToCandidateModal(false);
          setBulkUploadEmailCandidates([]);
          setEmailDraftForSend(null);
          setEmailModalScreeningId(null);
          setEmailModalContext(undefined);
          setPendingChannelInvites(null);
          setPendingMoveToScreening(null);
          fetchCandidates();
        }}
        selectedCandidates={
          bulkUploadEmailCandidates && bulkUploadEmailCandidates.length
            ? bulkUploadEmailCandidates
            : shortlistCandidates.filter((c) =>
              selectedShortlistCandidateIds.includes(c.id || c._id)
            )
        }
        defaultFromEmail="recruiter@talentcrux.ai"
        initialSubject={
          emailDraftForSend
            ? emailDraftForSend.subject
            : bulkUploadEmailCandidates && bulkUploadEmailCandidates.length
              ? 'Congratulations – Next Step for {{jobrole}} Role at {{companyname}}'
              : emailModalContext === 'invite'
                ? 'Congratulations – Next Step for {{jobrole}} Role at {{companyname}}'
                : SHORTLIST_EMAIL_SUBJECT
        }
        initialBodyHtml={emailDraftForSend?.bodyHtml}
        initialFollowUp={emailDraftForSend?.followUp}
        initialIncludeSignature={
          typeof emailDraftForSend?.includeSignature === 'boolean'
            ? emailDraftForSend.includeSignature
            : undefined
        }
        bodyTemplatesOverride={
          !emailDraftForSend
            ? emailModalContext === 'invite' || (bulkUploadEmailCandidates && bulkUploadEmailCandidates.length)
              ? (() => {
                const applyUrl = generateSharingUrl(`apply/${emailModalScreeningId || screeningId}`);
                return {
                  Default: `<p>Dear {{First Name}},</p>
<p>Thank you for your interest in the {{jobTitle}} position at {{company name}}. We would like to invite you to proceed to the next stage of the selection process.</p>
<p>We are pleased to inform you that your profile stands out as a strong match for this role, and we would like to invite you to proceed to the next stage of the selection process.</p>
<p>Please find the screening link below.</p>
<p><a href={{applyLink}}>{{applyLink}}</a></p>
<p>We request that you set aside approximately 30 minutes to complete the screening. We recommend that you complete it within 2-3 days.</p>
<p>Reply to this email if you have any questions.</p>
<p>Best regards,<br/>TalentCrux</p>`,
                  Invite: `<p>Dear {{First Name}},</p>
<p>Thank you for your interest in the {{jobTitle}} position at {{company name}}. We would like to invite you to proceed to the next stage of the selection process.</p>
<p>We are pleased to inform you that your profile stands out as a strong match for this role, and we would like to invite you to proceed to the next stage of the selection process.</p>
<p>Please find the screening link below.</p>
<p><a href={{applyLink}}>{{applyLink}}</a></p>
<p>We request that you set aside approximately 30 minutes to complete the screening. We recommend that you complete it within 2-3 days.</p>
<p>Reply to this email if you have any questions.</p>
<p>Best regards,<br/>TalentCrux</p>`,
                };
              })()
              : SHORTLIST_EMAIL_TEMPLATES
            : undefined
        }
        screeningId={emailModalScreeningId || screeningId}
        editingDraftId={emailDraftForSend?._id ?? undefined}
        emailContext={
          emailModalContext || (bulkUploadEmailCandidates?.length > 0 ? 'invite' : undefined)
        }
        onBeforeSend={async () => {
          if (emailModalContext === 'invite' && bulkUploadEmailCandidates?.length > 0) {
            const provisional = bulkUploadEmailCandidates.filter((c) => String(c.id).startsWith('temp-'));
            if (provisional.length > 0) {
              const results = await Promise.allSettled(
                provisional.map((row) => {
                  const fullName = [row.firstName, row.lastName].filter(Boolean).join(' ').trim();
                  return axios.post(`/api/screenings/${emailModalScreeningId || screeningId}/invite`, {
                    name: fullName || undefined,
                    email: row.email,
                    phone: row.mobile,
                    mobile: row.mobile,
                    storeId: row.storeId || undefined,
                  });
                })
              );
              let failedCount = 0;
              results.forEach((outcome) => {
                if (outcome.status === 'rejected') {
                  const errMsg = String(outcome.reason?.response?.data?.message || '').toLowerCase();
                  if (
                    errMsg.includes('already') ||
                    errMsg.includes('duplicate') ||
                    errMsg.includes('exist')
                  ) {
                    // Candidate is already in the screening/database, which is perfectly fine for emailing them!
                    return;
                  }
                  failedCount += 1;
                } else if (!outcome.value?.data?.candidate) {
                  failedCount += 1;
                }
              });
              if (failedCount === results.length && results.length > 0) {
                const error = new Error('All candidates were duplicates or invalid.');
                error.name = 'CandidateCreationError';
                throw error;
              } else if (failedCount > 0) {
                toast.error(`${failedCount} candidate(s) skipped (duplicates or invalid).`);
              }
            }
          }
        }}
        onAfterSendSuccess={
          pendingMoveToScreening
            ? async () => {
              const { destinationId, sourceScreeningId, candidateIds } = pendingMoveToScreening || {};
              if (!destinationId || !sourceScreeningId || !candidateIds?.length) return;
              const toastId = toast.loading('Adding candidates to destination…');
              try {
                const res = await axios.post(`/api/screenings/${destinationId}/move-candidates`, {
                  sourceScreeningId,
                  candidateIds,
                });
                const { createdCount = 0, existingCount = 0, destinationScreeningName } =
                  res.data || {};
                const destLabel = destinationScreeningName ? ` to ${destinationScreeningName}` : '';
                const totalAdded = createdCount + existingCount;
                toast.success(
                  `Added ${totalAdded} candidate${totalAdded === 1 ? '' : 's'}${destLabel}`,
                  { id: toastId, duration: 5000 }
                );
                skipAbandonMoveConfirmOnCloseRef.current = true;
                setPendingMoveToScreening(null);
              } catch (e) {
                toast.error(e.response?.data?.message || 'Failed to move candidates', {
                  id: toastId,
                  duration: 6000,
                });
                throw e;
              }
            }
            : async () => {
              if (emailModalContext === 'invite' && bulkUploadEmailCandidates?.length > 0) {
                const provisional = bulkUploadEmailCandidates.filter((c) => String(c.id).startsWith('temp-'));
                if (provisional.length > 0) {
                  void fetchCandidates({ forceRefresh: true, quiet: true });
                }
              }
            }
        }
        roleName={
          emailModalScreeningId && String(emailModalScreeningId) !== String(screeningId)
            ? undefined
            : screening?.roleName
        }
        companyName={
          emailModalScreeningId && String(emailModalScreeningId) !== String(screeningId)
            ? undefined
            : screening?.companyName
        }
        pendingChannelInvites={pendingChannelInvites}
        sendChannelInvitesForChannel={sendChannelInvitesForChannel}
      />
      {/* Share Candidate Review Modal */}
      <ShareCandidateReviewModal
        isOpen={showSendEmailToHiringManagerModal}
        onBack={
          hiringManagerShareOrigin === 'shortlist'
            ? () => {
              setShowSendEmailToHiringManagerModal(false);
              setShowShortlistNextStepsModal(true);
              setShortlistNextStepsChoice('send_to_hiring_manager');
            }
            : undefined
        }
        onClose={() => {
          setShowSendEmailToHiringManagerModal(false);
        }}
        screeningId={screeningId}
        selectedCandidates={hiringManagerShareCandidates}
        screening={screening}
        onRefresh={() => fetchCandidates({ quiet: true })}
      />
      {/* Confirm abandon move-to-screening flow */}
      {showAbandonMoveConfirmModal && (
        <div
          data-testid="modal-backdrop" className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-4"
          onClick={() => setShowAbandonMoveConfirmModal(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-slate-50">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900">Discard this move?</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    If you close now, the selected candidate(s) <span className="font-medium">won&apos;t be added</span> to the
                    destination screening. They&apos;ll only be moved after you click <span className="font-medium">Send Email</span>.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAbandonMoveConfirmModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 bg-white">
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAbandonMoveConfirmModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200"
                >
                  Keep editing
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAbandonMoveConfirmModal(false);
                    setShowSendEmailToCandidateModal(false);
                    setBulkUploadEmailCandidates([]);
                    setEmailDraftForSend(null);
                    setEmailModalScreeningId(null);
                    setEmailModalContext(undefined);
                    setPendingMoveToScreening(null);
                    fetchCandidates();
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700"
                >
                  Yes, close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Missing email validation for move-to-screening flow */}
      {showMissingEmailForMoveModal && (
        <div
          data-testid="modal-backdrop" className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-4"
          onClick={() => !movingShortlistCandidates && setShowMissingEmailForMoveModal(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-slate-50">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900">
                    {eligibleMoveCandidateIds.length === 0 ? 'No candidate email found' : 'Some candidates are missing email'}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    {eligibleMoveCandidateIds.length === 0
                      ? 'None of the selected candidates have an email address, so we can’t proceed to the invite email step.'
                      : 'These candidates don’t have an email address. Only candidates with email can be moved to new screening.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMissingEmailForMoveModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 bg-white">
              {missingEmailCandidatesForMove?.length > 0 && (
                <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                    Missing email
                  </p>
                  <ul className="mt-2 max-h-40 overflow-auto space-y-1 text-sm text-slate-700">
                    {missingEmailCandidatesForMove.slice(0, 12).map((c) => {
                      const label =
                        resolveCandidateDisplayName(c) ||
                        c?.phone ||
                        String(c?.id || c?._id || 'Candidate');
                      return (
                        <li key={String(c?.id || c?._id)} className="truncate">
                          {label}
                        </li>
                      );
                    })}
                    {missingEmailCandidatesForMove.length > 12 && (
                      <li className="text-xs text-slate-500">
                        +{missingEmailCandidatesForMove.length - 12} more
                      </li>
                    )}
                  </ul>
                </div>
              )}
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  disabled={movingShortlistCandidates}
                  onClick={() => setShowMissingEmailForMoveModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-50"
                >
                  {eligibleMoveCandidateIds.length === 0 ? 'OK' : 'Cancel'}
                </button>
                {eligibleMoveCandidateIds.length > 0 && (
                  <button
                    type="button"
                    disabled={movingShortlistCandidates}
                    onClick={() => proceedMoveToScreeningEmailStep(eligibleMoveCandidateIds)}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {movingShortlistCandidates ? (
                      <>
                        <span className="inline-block w-3.5 h-3.5 border-2 border-white/80 border-t-white rounded-full animate-spin" />
                        Continuing…
                      </>
                    ) : (
                      'Proceed with candidates who have email'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* QR Code Modal */}
      {showQRModal && (
        <div
          data-testid="modal-backdrop" className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-4"
          onClick={() => setShowQRModal(false)}
        >
          <div
            className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 p-8 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowQRModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="text-center mb-6">
              <h3 className="text-lg font-bold text-slate-900">Scan Here</h3>
              <p className="text-sm text-slate-500 mt-1">Candidates can scan this QR code to access the screening form directly.</p>
            </div>
            <div className="relative p-4 bg-white border border-slate-200 rounded-xl shadow-sm mb-6 flex flex-col items-center">
              <QRCodeCanvas
                value={publicLink}
                size={220}
                id="apply-qr-code"
                level="H"
                includeMargin={true}
              />
              <p className="mt-2 text-[11px] text-slate-500 font-medium break-all text-center w-full max-w-[220px]">
                {publicLink}
              </p>
              <button
                type="button"
                onClick={() => {
                  const qrCanvas = document.getElementById('apply-qr-code');
                  if (!qrCanvas) return;

                  const toastId = toast.loading('Preparing QR code...');

                  /* ── Layout Constants (design-first) ── */
                  const scale = 2;
                  const width = 600;
                  const gap = 36;           // uniform spacing between every element
                  const logoH = 110;            // logo render height
                  const logoW = 110;            // logo render width (square, SVG is square)
                  const textH = 28;            // "Scan to Apply" line height
                  const qrSize = 300;           // QR code render size
                  const borderPad = 20;           // padding inside QR border box
                  const linkH = 18;            // link text line height
                  const topPad = 56;            // breathing room at top
                  const botPad = 56;            // breathing room at bottom

                  // Total height: topPad + logo + gap + text + gap + (qr+border) + gap + link + botPad
                  const qrBoxH = qrSize + borderPad * 2;
                  const height = topPad + logoH + gap + textH + gap + qrBoxH + gap + linkH + botPad;

                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  canvas.width = width * scale;
                  canvas.height = height * scale;
                  ctx.scale(scale, scale);

                  /* ── Background ── */
                  ctx.fillStyle = '#ffffff';
                  ctx.fillRect(0, 0, width, height);

                  /* ── Subtle top accent line ── */
                  ctx.fillStyle = '#4F46E5';
                  ctx.fillRect(0, 0, width, 4);

                  /* ── Load logo and render everything ── */
                  const logoImg = new Image();
                  logoImg.crossOrigin = 'Anonymous';

                  const renderFlyer = (logoLoaded) => {
                    try {
                      let y = topPad;

                      /* ── 1. Logo (centered) ── */
                      if (logoLoaded) {
                        ctx.drawImage(logoImg, (width - logoW) / 2, y, logoW, logoH);
                      }
                      y += logoH + gap;

                      /* ── 2. "Scan to Apply" (regular weight, not bold) ── */
                      ctx.font = '400 24px -apple-system, "Segoe UI", sans-serif';
                      ctx.fillStyle = '#334155';
                      ctx.textAlign = 'center';
                      ctx.textBaseline = 'top';
                      ctx.fillText('Scan Here', width / 2, y);
                      y += textH + gap;

                      /* ── 3. QR Code with clean border (centered) ── */
                      const qrBoxX = (width - qrSize - borderPad * 2) / 2;
                      const qrBoxY = y;

                      // Draw border box
                      ctx.fillStyle = '#ffffff';
                      ctx.strokeStyle = '#e2e8f0';
                      ctx.lineWidth = 1.5;
                      ctx.beginPath();
                      if (ctx.roundRect) {
                        ctx.roundRect(qrBoxX, qrBoxY, qrSize + borderPad * 2, qrBoxH, 16);
                      } else {
                        ctx.rect(qrBoxX, qrBoxY, qrSize + borderPad * 2, qrBoxH);
                      }
                      ctx.fill();
                      ctx.stroke();

                      // Draw QR inside the border box (centered)
                      ctx.imageSmoothingEnabled = false;
                      ctx.drawImage(qrCanvas, qrBoxX + borderPad, qrBoxY + borderPad, qrSize, qrSize);
                      ctx.imageSmoothingEnabled = true;
                      y += qrBoxH + gap;

                      /* ── 4. Link text (centered) ── */
                      ctx.font = '400 13px -apple-system, "Segoe UI", monospace';
                      ctx.fillStyle = '#94a3b8';
                      ctx.fillText(publicLink, width / 2, y);

                      /* ── Export ── */
                      const jobName = screening?.roleName || 'Job Application';
                      const pngUrl = canvas.toDataURL('image/png', 1.0);
                      const dl = document.createElement('a');
                      dl.href = pngUrl;
                      dl.download = `QR_${jobName.replace(/[^a-zA-Z0-9]/g, '_')}.png`;
                      document.body.appendChild(dl);
                      dl.click();
                      document.body.removeChild(dl);

                      toast.success('QR Code downloaded!', { id: toastId });
                    } catch (err) {
                      console.error('Error generating QR image:', err);
                      toast.error('Failed to generate image.', { id: toastId });
                    }
                  };

                  logoImg.onload = () => renderFlyer(true);
                  logoImg.onerror = () => renderFlyer(false);   // still works without logo
                  logoImg.src = '/logo-lockup.svg';
                }}
                className="absolute -top-3 -right-3 p-2 bg-white rounded-full shadow-md border border-slate-100 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                title="Download Professional QR Flyer"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
            </div>
            <button data-testid="modal-close-btn"
              type="button"
              onClick={() => setShowQRModal(false)}
              className="w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {/* Invite modal */}
      {isInviteOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 sm:p-6 invite-flow-modal-overlay">
          <div className="invite-flow-modal-shell flex flex-col bg-white">
            {/* Gradient accent bar */}
            <div style={{ height: 3, background: 'linear-gradient(90deg, #4F46E5, #6366F1, #818CF8)', borderRadius: '20px 20px 0 0' }} />
            <header
              className="flex items-center justify-between shrink-0 border-b px-6 py-5"
              style={{ borderColor: INVITE_MODAL_COLORS.headerBorder }}
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-9 h-9 rounded-xl" style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}>
                  <UserPlusIcon className="h-4.5 w-4.5 text-white" style={{ width: 18, height: 18 }} />
                </div>
                <div>
                  <h2 className="text-lg font-bold tracking-tight" style={{ color: INVITE_MODAL_COLORS.heading }}>
                    {inviteStep === 1 ? 'Invite Candidates' : inviteStep === 2 && (inviteMode === 'single' || inviteMode === 'bulk') ? 'Communication Channels' : inviteStep === 2 && inviteMode === 'link' ? 'Share Link' : inviteStep === 3 ? (inviteMode === 'single' ? 'Add Candidates' : 'Upload File') : 'Invite'}
                  </h2>
                  <p className="text-xs mt-0.5" style={{ color: INVITE_MODAL_COLORS.textSecondary }}>
                    {inviteStep === 1 ? 'Choose how to invite' : inviteStep === 2 && (inviteMode === 'single' || inviteMode === 'bulk') ? 'Select outreach channels' : inviteStep === 2 && inviteMode === 'link' ? 'Copy & share your link' : `Step ${inviteStep} of 3`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {/* Step dots */}
                <div className="invite-step-indicator hidden sm:flex">
                  {[1, 2, 3].map(step => (
                    <div
                      key={step}
                      className={`invite-step-dot ${step === inviteStep ? 'invite-step-dot--active' : step < inviteStep ? 'invite-step-dot--done' : 'invite-step-dot--upcoming'}`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsInviteOpen(false);
                    setInviteStep(1);
                    setInviteMode(null);
                  }}
                  className="rounded-xl p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </header>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 flex flex-col">
              {inviteStep === 1 && (

                <div className="flex-1 flex flex-col min-h-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 flex-1 min-h-0 sm:min-h-[40vh] w-full">
                    <button
                      type="button"
                      data-testid="invite-opt-link"
                      onClick={() => {
                        setInviteMode('link');
                        setInviteStep(2);
                      }}
                      className="invite-card-link relative flex flex-col items-center justify-center rounded-2xl border-2 bg-white px-6 py-8 text-center"
                      style={{
                        borderColor:
                          inviteMode === 'link'
                            ? '#0EA5E9'
                            : '#E2E8F0',
                        boxShadow:
                          inviteMode === 'link'
                            ? '0 0 0 3px rgba(14, 165, 233, 0.12)'
                            : '0 1px 3px rgba(0,0,0,0.04)',
                      }}
                    >
                      <div className="flex flex-col items-center gap-4 text-center w-full">
                        <div
                          className="invite-card-icon shrink-0 rounded-2xl p-3.5 flex items-center justify-center"
                          style={{
                            backgroundColor: INVITE_MODAL_COLORS.cardLinkIconBg,
                          }}
                        >
                          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex flex-col items-center gap-1.5">
                          <p className="text-lg font-bold" style={{ color: '#0F172A' }}>
                            Share link
                          </p>
                          <p className="text-sm leading-relaxed" style={{ color: '#64748B' }}>
                            Share a link on LinkedIn, Naukri, WhatsApp, or any platform to invite candidates.
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setInviteMode('single');
                        setInviteStep(2);
                      }}
                      data-testid="invite-opt-single"
                      className="invite-card-single relative flex flex-col items-center justify-center rounded-2xl border-2 bg-white px-6 py-8 text-center"
                      style={{
                        borderColor:
                          inviteMode === 'single'
                            ? '#4F46E5'
                            : '#E2E8F0',
                        boxShadow:
                          inviteMode === 'single'
                            ? '0 0 0 3px rgba(79, 70, 229, 0.12)'
                            : '0 1px 3px rgba(0,0,0,0.04)',
                      }}
                    >
                      <div className="flex flex-col items-center gap-4 text-center w-full">
                        <div
                          className="invite-card-icon shrink-0 rounded-2xl p-3.5 flex items-center justify-center"
                          style={{
                            backgroundColor: INVITE_MODAL_COLORS.cardSingleIconBg,
                          }}
                        >
                          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10" cy="8" r="4" stroke="#4F46E5" strokeWidth="2" fill="none" />
                            <path d="M2 20c0-3.314 3.582-6 8-6s8 2.686 8 6" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
                            <path d="M20 8v6M23 11h-6" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex flex-col items-center gap-1.5">
                          <p className="text-lg font-bold" style={{ color: '#0F172A' }}>
                            Single candidate
                          </p>
                          <p className="text-sm leading-relaxed" style={{ color: '#64748B' }}>
                            Send a personalized invitation email to one or more candidates directly.
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setInviteMode('bulk');
                        setInviteStep(2);
                      }}
                      data-testid="invite-opt-bulk"
                      className="invite-card-bulk relative flex flex-col items-center justify-center rounded-2xl border-2 bg-white px-6 py-8 text-center"
                      style={{
                        borderColor:
                          inviteMode === 'bulk'
                            ? '#10B981'
                            : '#E2E8F0',
                        boxShadow:
                          inviteMode === 'bulk'
                            ? '0 0 0 3px rgba(16, 185, 129, 0.12)'
                            : '0 1px 3px rgba(0,0,0,0.04)',
                      }}
                    >
                      <div className="flex flex-col items-center gap-4 text-center w-full">
                        <div
                          className="invite-card-icon shrink-0 rounded-2xl p-3.5 flex items-center justify-center"
                          style={{
                            backgroundColor: INVITE_MODAL_COLORS.cardBulkIconBg,
                          }}
                        >
                          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="3" y="3" width="18" height="18" rx="3" stroke="#10B981" strokeWidth="2" fill="none" />
                            <path d="M12 8v8M8 12h8" stroke="#10B981" strokeWidth="2" strokeLinecap="round" />
                            <path d="M7 3V1M17 3V1M3 8h18" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex flex-col items-center gap-1.5">
                          <p className="text-lg font-bold" style={{ color: '#0F172A' }}>
                            Invite in bulk
                          </p>
                          <p className="text-sm leading-relaxed" style={{ color: '#64748B' }}>
                            Upload a spreadsheet or ZIP of resumes to invite many candidates at once.
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
              {inviteStep === 2 && inviteMode === 'link' && (
                <div className="invite-step2-content space-y-5 w-full">
                  <div className="rounded-2xl border border-slate-100 bg-white px-6 py-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <p className="text-sm leading-relaxed" style={{ color: '#64748B' }}>
                      Share this link in your job post on LinkedIn, Naukri, or WhatsApp. Candidates
                      will use it to start the screening, and we'll capture their name and email for
                      tracking.
                    </p>
                  </div>
                  {(() => {
                    const isLinkExpiredByDate = screeningMeta?.applyLinkExpiresAt && new Date(screeningMeta.applyLinkExpiresAt).getTime() <= new Date().getTime();
                    const isLinkExpiredByLimit = screeningMeta?.maxCandidatesAllowed && (screeningMeta?.candidateCount || 0) >= screeningMeta?.maxCandidatesAllowed;
                    const isLinkExpired = isLinkExpiredByDate || isLinkExpiredByLimit;
                    const isEffectivelyActive = screeningMeta?.applyLinkActive && !isLinkExpired;
                    return (
                      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white px-5 py-3.5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <p
                          className={`text-sm font-semibold ${isEffectivelyActive ? 'text-emerald-600' : (isLinkExpired && screeningMeta?.applyLinkActive ? 'text-red-600' : 'text-slate-500')
                            }`}
                        >
                          Application link is {isEffectivelyActive ? 'active' : (isLinkExpired && screeningMeta?.applyLinkActive ? 'expired' : 'inactive')}
                        </p>
                        <button
                          type="button"
                          role="switch"
                          data-testid="link-active-toggle"
                          aria-checked={isEffectivelyActive}
                          aria-label={`Application link is ${isEffectivelyActive ? 'active' : (isLinkExpired && screeningMeta?.applyLinkActive ? 'expired' : 'inactive')}`}
                          disabled={linkToggleUpdating}
                          onClick={async () => {
                            const next = !isEffectivelyActive;
                            setLinkToggleUpdating(true);
                            try {
                              await axios.patch(`/api/screenings/${screeningId}`, {
                                applyLinkActive: next,
                                ...(next && isLinkExpiredByDate ? { applyLinkExpiresAt: null } : {}),
                                ...(next && isLinkExpiredByLimit ? { maxCandidatesAllowed: null } : {})
                              });
                              await refetchScreening();
                              toast.success(next ? 'Application link is now active' : 'Application link is now inactive');
                            } catch (e) {
                              toast.error(e.response?.data?.message || 'Failed to update link status');
                            } finally {
                              setLinkToggleUpdating(false);
                            }
                          }}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007BFF] disabled:opacity-50 disabled:cursor-not-allowed ${!isEffectivelyActive ? 'bg-gray-200' : ''
                            }`}
                          style={isEffectivelyActive ? { backgroundColor: '#007BFF' } : undefined}
                        >
                          {isEffectivelyActive && (
                            <span className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-white" aria-hidden>
                              <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
                            </span>
                          )}
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${isEffectivelyActive ? 'translate-x-5' : 'translate-x-0'
                              }`}
                          />
                        </button>
                      </div>
                    );
                  })()}
                  {screeningMeta?.applyLinkActive && (
                    <div className="flex flex-col gap-2 rounded-2xl border border-slate-100 bg-white px-5 py-3.5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <label className="text-sm font-semibold text-slate-700">Link expires in</label>
                          <select
                            className="rounded-lg border border-slate-200 bg-gray-50 px-3 py-1.5 text-sm font-medium text-slate-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full"
                            disabled={linkToggleUpdating}
                            value={(() => {
                              if (!screeningMeta?.applyLinkExpiresAt) return 'never';
                              const diff = (new Date(screeningMeta.applyLinkExpiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24);
                              if (diff <= 0) return 'expired';
                              if (diff <= 1.05) return '1_day';
                              if (diff <= 3.05) return '3_days';
                              if (diff <= 5.05) return '5_days';
                              if (diff <= 7.05) return '1_week';
                              return 'custom';
                            })()}
                            onChange={async (e) => {
                              const val = e.target.value;
                              if (val === 'custom' || val === 'expired') return;

                              let newDate = null;
                              if (val !== 'never') {
                                const days = val === '1_day' ? 1 : val === '3_days' ? 3 : val === '5_days' ? 5 : 7;
                                newDate = new Date();
                                newDate.setDate(newDate.getDate() + days);
                              }
                              setLinkToggleUpdating(true);
                              try {
                                await axios.patch(`/api/screenings/${screeningId}`, {
                                  applyLinkExpiresAt: newDate ? newDate.toISOString() : null,
                                });
                                await refetchScreening();
                                toast.success('Expiration updated successfully');
                              } catch (err) {
                                toast.error(err.response?.data?.message || 'Failed to update expiration');
                              } finally {
                                setLinkToggleUpdating(false);
                              }
                            }}
                          >
                            <option value="1_day">1 day</option>
                            <option value="3_days">3 days</option>
                            <option value="5_days">5 days</option>
                            <option value="1_week">1 week</option>
                            <option value="never">Never</option>
                            {(() => {
                              const diff = screeningMeta?.applyLinkExpiresAt ? (new Date(screeningMeta.applyLinkExpiresAt).getTime() - new Date().getTime()) : 0;
                              if (diff < 0 && diff !== 0) return <option value="expired">Expired</option>;
                              if (screeningMeta?.applyLinkExpiresAt && diff > (7.05 * 24 * 60 * 60 * 1000)) return <option value="custom">Custom</option>;
                              return null;
                            })()}
                          </select>
                        </div>

                        <div className="flex flex-col gap-2">
                          <label className="text-sm font-semibold text-slate-700">Max candidates allowed</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              placeholder="No limit"
                              className="rounded-lg border border-slate-200 bg-gray-50 px-3 py-1.5 text-sm font-medium text-slate-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full"
                              disabled={linkToggleUpdating}
                              value={maxCandidatesInput}
                              onChange={(e) => setMaxCandidatesInput(e.target.value)}
                            />
                            <button
                              type="button"
                              disabled={linkToggleUpdating || (maxCandidatesInput === '' && !screeningMeta?.maxCandidatesAllowed) || (parseInt(maxCandidatesInput, 10) === screeningMeta?.maxCandidatesAllowed)}
                              onClick={async () => {
                                const val = maxCandidatesInput ? parseInt(maxCandidatesInput, 10) : null;
                                if (val !== null && val < 1) return;
                                setLinkToggleUpdating(true);
                                try {
                                  await axios.patch(`/api/screenings/${screeningId}`, {
                                    maxCandidatesAllowed: val
                                  });
                                  await refetchScreening();
                                  toast.success('Candidate limit updated successfully');
                                } catch (err) {
                                  toast.error(err.response?.data?.message || 'Failed to update candidate limit');
                                } finally {
                                  setLinkToggleUpdating(false);
                                }
                              }}
                              className="rounded-lg bg-indigo-50 text-indigo-600 px-3 py-1.5 text-sm font-medium hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      </div>

                      {screeningMeta?.applyLinkExpiresAt && new Date(screeningMeta.applyLinkExpiresAt).getTime() > new Date().getTime() && (
                        <p className="text-xs text-slate-500">
                          Automatically deactivates on <span className="font-medium text-slate-700">{new Date(screeningMeta.applyLinkExpiresAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}</span>
                        </p>
                      )}
                      {screeningMeta?.applyLinkExpiresAt && new Date(screeningMeta.applyLinkExpiresAt).getTime() <= new Date().getTime() && (
                        <p className="text-xs text-red-500 font-medium">
                          Link has expired. Please update expiration or deactivate.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Qualification Gating (Journey Automation) */}
                  <div className="flex flex-col gap-2 rounded-2xl border border-slate-100 bg-white px-5 py-3.5 mt-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-800">Qualification Gating (Journey Automation)</h4>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Automatically reject candidates who do not meet the minimum scores.
                        </p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={journeyAutomationEnabled}
                        onClick={async () => {
                          const next = !journeyAutomationEnabled;
                          setJourneyAutomationEnabled(next);
                          try {
                            await axios.patch(`/api/screenings/${screeningId}`, {
                              journeyAutomation: {
                                enabled: next,
                                resumeMinScore: journeyAutomationResumeMinScore,
                                assessmentMinScore: journeyAutomationAssessmentMinScore,
                              }
                            });
                            toast.success(next ? 'Journey automation enabled' : 'Journey automation disabled');
                          } catch (err) {
                            toast.error(err.response?.data?.message || 'Failed to update automation settings');
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007BFF] ${!journeyAutomationEnabled ? 'bg-gray-200' : ''}`}
                        style={journeyAutomationEnabled ? { backgroundColor: '#007BFF' } : undefined}
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${journeyAutomationEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>
                    {journeyAutomationEnabled && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-slate-100 pt-3">
                        {screening?.hasResumeScoring && (
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold text-slate-700">Min Resume Score (%)</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={journeyAutomationResumeMinScore}
                                onChange={(e) => setJourneyAutomationResumeMinScore(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                className="rounded-lg border border-slate-200 bg-gray-50 px-3 py-1.5 text-sm font-medium text-slate-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full"
                              />
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await axios.patch(`/api/screenings/${screeningId}`, {
                                      journeyAutomation: {
                                        enabled: true,
                                        resumeMinScore: journeyAutomationResumeMinScore,
                                        assessmentMinScore: journeyAutomationAssessmentMinScore,
                                      }
                                    });
                                    toast.success('Resume threshold updated successfully');
                                  } catch (err) {
                                    toast.error('Failed to update resume threshold');
                                  }
                                }}
                                className="rounded-lg bg-indigo-50 text-indigo-600 px-3 py-1.5 text-sm font-medium hover:bg-indigo-100 transition-colors"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        )}
                        {screening?.includeAssessment && (
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold text-slate-700">Min Assessment Score (%)</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={journeyAutomationAssessmentMinScore}
                                onChange={(e) => setJourneyAutomationAssessmentMinScore(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                className="rounded-lg border border-slate-200 bg-gray-50 px-3 py-1.5 text-sm font-medium text-slate-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full"
                              />
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await axios.patch(`/api/screenings/${screeningId}`, {
                                      journeyAutomation: {
                                        enabled: true,
                                        resumeMinScore: journeyAutomationResumeMinScore,
                                        assessmentMinScore: journeyAutomationAssessmentMinScore,
                                      }
                                    });
                                    toast.success('Assessment threshold updated successfully');
                                  } catch (err) {
                                    toast.error('Failed to update assessment threshold');
                                  }
                                }}
                                className="rounded-lg bg-indigo-50 text-indigo-600 px-3 py-1.5 text-sm font-medium hover:bg-indigo-100 transition-colors"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ARM Salary Request (Go Colors only) */}
                  {armSalaryDetailsEnabled && (
                    <div className="flex flex-col gap-2 rounded-2xl border border-slate-100 bg-white px-5 py-3.5 mt-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-800">ARM Salary Details Request</h4>
                          <p className="text-xs text-slate-500 mt-0.5">
                            When a candidate is moved to Selected, automatically email the store ARM to fill in salary &amp; joining date.
                          </p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!screening?.enableArmSalaryRequest}
                          onClick={async () => {
                            const next = !screening?.enableArmSalaryRequest;
                            try {
                              const res = await axios.patch(`/api/screenings/${screeningId}`, {
                                enableArmSalaryRequest: next,
                              });
                              if (res.data?.screening) {
                                setScreening(res.data.screening);
                              }
                              toast.success(next ? 'ARM salary request enabled for this screening' : 'ARM salary request disabled');
                            } catch (err) {
                              toast.error(err.response?.data?.message || 'Failed to update setting');
                            }
                          }}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 ${!screening?.enableArmSalaryRequest ? 'bg-gray-200' : ''}`}
                          style={screening?.enableArmSalaryRequest ? { backgroundColor: '#10b981' } : undefined}
                        >
                          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${screening?.enableArmSalaryRequest ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Link</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        data-testid="public-link-input"
                        value={publicLink}
                        className="flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(publicLink);
                          toast.success('Link copied to clipboard');
                        }}
                        data-testid="copy-public-link-btn"
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors hover:opacity-90"
                        style={{ backgroundColor: INVITE_MODAL_COLORS.primaryBg }}
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" />
                        <span>Copy link</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowQRModal(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm14 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                        <span>Generate QR</span>
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                      Copy text for your job post
                    </p>
                    <div className="relative rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <p className="text-sm text-gray-700 leading-relaxed pr-10 whitespace-pre-wrap">
                        {'Dear Candidate,\n\n'}
                        {'Thank you for your interest in the '}
                        <span className="font-medium" style={{ color: INVITE_MODAL_COLORS.primaryBg }}>
                          {screening?.roleName || '{{job title}}'}
                        </span>
                        {' position at '}
                        <span className="font-medium" style={{ color: INVITE_MODAL_COLORS.primaryBg }}>
                          {screening?.companyName || 'Indara Technologies Private Limited'}
                        </span>
                        {'. We would like to invite you to proceed to the next stage of the selection process.\n\n'}
                        {
                          'We are pleased to inform you that your profile stands out as a strong match for this role, and we would like to invite you to proceed to the next stage of the selection process.\n\n'
                        }
                        {'Please find the screening link below.\n'}
                        <span
                          className="break-all"
                          style={{ color: INVITE_MODAL_COLORS.primaryBg }}
                        >
                          {publicLink}
                        </span>
                        {'\n\n'}
                        {
                          'We request that you set aside approximately 30 minutes to complete the screening. We recommend that you complete it within 2-3 days.\n\n'
                        }
                        {'Reply to this email if you have any questions.\n\n'}
                        {'Best regards,\nTalentCrux'}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          const jobTitle = screening?.roleName || '{{job title}}';
                          const company = screening?.companyName || 'Indara Technologies Private Limited';
                          const jobPostText =
                            `Dear Candidate,\n\n` +
                            `Thank you for your interest in the ${jobTitle} position at ${company}. We would like to invite you to proceed to the next stage of the selection process.\n\n` +
                            `We are pleased to inform you that your profile stands out as a strong match for this role, and we would like to invite you to proceed to the next stage of the selection process.\n\n` +
                            `Please find the screening link below.\n` +
                            `${publicLink}\n\n` +
                            `We request that you set aside approximately 30 minutes to complete the screening. We recommend that you complete it within 2-3 days.\n\n` +
                            `Reply to this email if you have any questions.\n\n` +
                            `Best regards,\nTalentCrux`;
                          navigator.clipboard.writeText(jobPostText);
                          toast.success('Job post text copied to clipboard');
                        }}
                        className="absolute bottom-3 right-3 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-200"
                        title="Copy full text"
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {inviteStep === 2 && (inviteMode === 'single' || inviteMode === 'bulk') && (
                <div className="invite-step2-content space-y-6 w-full flex flex-col">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 flex-1 min-h-0 w-full max-w-2xl mx-auto">
                    {/* ── Email Channel Card ── */}
                    <button
                      type="button"
                      onClick={() => setInviteChannels(prev => ({ ...prev, email: !prev.email }))}
                      className={`invite-channel-card invite-channel-card--email ${inviteChannels.email ? 'invite-channel-card--selected' : ''} relative flex flex-col items-center justify-center rounded-2xl border-2 px-6 py-7 text-center bg-white`}
                      style={{
                        borderColor: inviteChannels.email ? '#4F46E5' : '#E2E8F0',
                      }}
                    >
                      <div className="flex flex-col items-center gap-4 text-center w-full">
                        {/* Email SVG icon */}
                        <div
                          className="invite-channel-icon shrink-0 rounded-2xl p-3.5 flex items-center justify-center"
                          style={{
                            backgroundColor: inviteChannels.email ? 'rgba(79, 70, 229, 0.12)' : 'rgba(79, 70, 229, 0.06)',
                            transition: 'background-color 0.28s ease',
                          }}
                        >
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="2" y="4" width="20" height="16" rx="3" stroke="#4F46E5" strokeWidth="1.8" fill="none" />
                            <path d="M2 7L10.8906 12.2604C11.5624 12.6761 12.4376 12.6761 13.1094 12.2604L22 7" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex flex-col items-center gap-1.5">
                          <p className="text-lg font-bold" style={{ color: '#0F172A' }}>Email</p>
                          <p className="text-sm leading-relaxed" style={{ color: '#64748B' }}>
                            Reach candidates in their inbox with a personalized invitation link.
                          </p>
                        </div>
                      </div>
                      {/* Animated checkbox */}
                      <div
                        className={`invite-channel-check ${inviteChannels.email ? 'invite-channel-check--checked' : ''} absolute top-4 right-4 h-6 w-6 rounded-lg flex items-center justify-center`}
                        style={{
                          border: inviteChannels.email ? 'none' : '2px solid #CBD5E1',
                          background: inviteChannels.email ? 'linear-gradient(135deg, #4F46E5, #6366F1)' : 'transparent',
                          boxShadow: inviteChannels.email ? '0 2px 8px rgba(79, 70, 229, 0.3)' : 'none',
                        }}
                      >
                        {inviteChannels.email && <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                      </div>
                    </button>

                    {/* ── WhatsApp Channel Card ── */}
                    <button
                      type="button"
                      data-testid="whatsapp-toggle"
                      onClick={() => setInviteChannels(prev => ({ ...prev, whatsapp: !prev.whatsapp }))}
                      className={`invite-channel-card invite-channel-card--whatsapp ${inviteChannels.whatsapp ? 'invite-channel-card--selected' : ''} relative flex flex-col items-center justify-center rounded-2xl border-2 px-6 py-7 text-center bg-white`}
                      style={{
                        borderColor: inviteChannels.whatsapp ? '#25D366' : '#E2E8F0',
                      }}
                    >
                      <div className="flex flex-col items-center gap-4 text-center w-full">
                        {/* WhatsApp SVG icon */}
                        <div
                          className="invite-channel-icon shrink-0 rounded-2xl p-3.5 flex items-center justify-center"
                          style={{
                            backgroundColor: inviteChannels.whatsapp ? 'rgba(37, 211, 102, 0.12)' : 'rgba(37, 211, 102, 0.06)',
                            transition: 'background-color 0.28s ease',
                          }}
                        >
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.477 2 2 6.477 2 12C2 13.89 2.525 15.66 3.438 17.168L2.546 20.2C2.417 20.652 2.83 21.074 3.285 20.953L6.44 20.09C7.896 20.928 9.564 21.429 11.349 21.484C11.565 21.495 11.782 21.5 12 21.5C17.523 21.5 22 17.023 22 11.5C22 6.477 17.523 2 12 2Z" stroke="#25D366" strokeWidth="1.8" fill="none" />
                            <path d="M8.5 10.5C8.5 10.5 9.5 13 11.25 14.25C13 15.5 15 15.5 15 15.5L14 13.5L15 11.5C15 11.5 11.5 10 10.5 10.5L8.5 10.5Z" fill="#25D366" fillOpacity="0.4" />
                            <path d="M16.5 14.5C16.5 14.5 15.3 15.7 14.5 15.9C13.7 16.1 12.5 15.7 11.3 14.7C10.1 13.7 9.1 12.3 8.7 11.1C8.3 9.9 8.5 9 8.5 9L10.3 9.1L10.8 11L9.5 12.5C9.5 12.5 10.5 14 12 14.5L13.5 13.2L15.5 13.7L16.5 14.5Z" stroke="#25D366" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex flex-col items-center gap-1.5">
                          <p className="text-lg font-bold" style={{ color: '#0F172A' }}>WhatsApp</p>
                          <p className="text-sm leading-relaxed" style={{ color: '#64748B' }}>
                            Higher engagement and fast responses via WhatsApp.
                          </p>
                        </div>
                      </div>
                      {/* Animated checkbox */}
                      <div
                        className={`invite-channel-check ${inviteChannels.whatsapp ? 'invite-channel-check--checked' : ''} absolute top-4 right-4 h-6 w-6 rounded-lg flex items-center justify-center`}
                        style={{
                          border: inviteChannels.whatsapp ? 'none' : '2px solid #CBD5E1',
                          background: inviteChannels.whatsapp ? 'linear-gradient(135deg, #25D366, #22C55E)' : 'transparent',
                          boxShadow: inviteChannels.whatsapp ? '0 2px 8px rgba(37, 211, 102, 0.3)' : 'none',
                        }}
                      >
                        {inviteChannels.whatsapp && <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                      </div>
                    </button>
                  </div>

                </div>
              )}

              {inviteStep === 3 && inviteMode === 'single' && (
                <form
                  id="single-invite-form"
                  onSubmit={handleSingleInvite}
                  className="invite-step2-content flex min-h-[55vh] flex-col rounded-2xl border border-slate-100 bg-white overflow-hidden"
                  style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                >
                  <div className="shrink-0 border-b border-slate-100 px-6 py-3" style={{ background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.04), rgba(99, 102, 241, 0.02))' }}>
                    <div className={`grid ${inviteChannels.whatsapp
                        ? user?.features?.store_list === true
                          ? 'grid-cols-[1fr,1fr,1fr,1fr,1fr,0.6fr]'
                          : 'grid-cols-[1fr,1fr,1.2fr,1.2fr,0.6fr]'
                        : user?.features?.store_list === true
                          ? 'grid-cols-[1.1fr,1.1fr,1.4fr,1.2fr,0.8fr]'
                          : 'grid-cols-[1.1fr,1.1fr,1.4fr,0.8fr]'
                      } items-center text-xs font-bold`} style={{ color: '#334155' }}>
                      <span>First name</span>
                      <span>Last name</span>
                      <span>Email</span>
                      {inviteChannels.whatsapp && <span>Mobile</span>}
                      {user?.features?.store_list === true && <span>Store Code</span>}
                      <span className="sr-only">Actions</span>
                    </div>
                  </div>
                  <div className="flex-1 min-h-0 flex flex-col px-6 py-4">
                    <div className="flex-1 space-y-3 overflow-y-auto">
                      {singleInviteRows.map((row, index) => (
                        <div
                          key={row.id}
                          className={`grid ${inviteChannels.whatsapp
                              ? user?.features?.store_list === true
                                ? 'grid-cols-[1fr,1fr,1fr,1fr,1fr,0.6fr]'
                                : 'grid-cols-[1fr,1fr,1.2fr,1.2fr,0.6fr]'
                              : user?.features?.store_list === true
                                ? 'grid-cols-[1.1fr,1.1fr,1.4fr,1.2fr,0.8fr]'
                                : 'grid-cols-[1.1fr,1.1fr,1.4fr,0.8fr]'
                            } items-center gap-3`}
                        >
                          <input
                            type="text"
                            value={row.firstName}
                            onChange={(e) =>
                              setSingleInviteRows((prev) =>
                                prev.map((r) =>
                                  r.id === row.id ? { ...r, firstName: e.target.value } : r
                                )
                              )
                            }
                            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                            placeholder="First name"
                            data-testid="invite-first-name-input"
                            style={{ color: '#0F172A' }}
                          />
                          <input
                            type="text"
                            value={row.lastName}
                            onChange={(e) =>
                              setSingleInviteRows((prev) =>
                                prev.map((r) =>
                                  r.id === row.id ? { ...r, lastName: e.target.value } : r
                                )
                              )
                            }
                            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                            placeholder="Last name"
                            data-testid="invite-last-name-input"
                            style={{ color: '#0F172A' }}
                          />
                          <input
                            type="email"
                            required={index === 0 && !(!inviteChannels.email && inviteChannels.whatsapp)}
                            value={row.email}
                            onChange={(e) =>
                              setSingleInviteRows((prev) =>
                                prev.map((r) =>
                                  r.id === row.id ? { ...r, email: e.target.value } : r
                                )
                              )
                            }
                            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                            placeholder={(!inviteChannels.email && inviteChannels.whatsapp) ? "Email (Optional)" : "Email *"}
                            data-testid="invite-email-input"
                            style={{ color: '#0F172A' }}
                          />
                          {inviteChannels.whatsapp && (
                            <input
                              type="text"
                              required={index === 0 && (!inviteChannels.email && inviteChannels.whatsapp)}
                              value={row.mobile || ''}
                              onChange={(e) => handlePhoneChange(row.id, e.target.value)}
                              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                              placeholder={(!inviteChannels.email && inviteChannels.whatsapp) ? "Mobile *" : "Mobile"}
                              data-testid="invite-mobile-input"
                              style={{ color: '#0F172A' }}
                            />
                          )}
                          {user?.features?.store_list === true && (
                            <>
                              <input
                                list="stores-list-invite"
                                value={row.storeId || ''}
                                onChange={(e) =>
                                  setSingleInviteRows((prev) =>
                                    prev.map((r) =>
                                      r.id === row.id ? { ...r, storeId: e.target.value } : r
                                    )
                                  )
                                }
                                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 bg-white"
                                placeholder="Type store code..."
                                style={{ color: '#0F172A' }}
                              />
                              <datalist id="stores-list-invite">
                                {jobStores.map((store) => (
                                  <option key={store.storeId} value={store.storeId}>
                                    {store.storeId} - {store.name} ({store.city})
                                  </option>
                                ))}
                              </datalist>
                            </>
                          )}
                          {index === 0 ? (
                            <button
                              type="button"
                              onClick={() =>
                                setSingleInviteRows((prev) => [
                                  ...prev,
                                  {
                                    id: prev[prev.length - 1].id + 1,
                                    firstName: '',
                                    lastName: '',
                                    email: '',
                                    mobile: '+91',
                                    storeId: '',
                                  },
                                ])
                              }
                              className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold transition-all hover:border-indigo-300 hover:bg-indigo-50/30"
                              style={{ color: '#4F46E5' }}
                            >
                              <PlusCircleIcon className="h-4 w-4" />
                              <span>Add</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                setSingleInviteRows((prev) =>
                                  prev.length === 1 ? prev : prev.filter((r) => r.id !== row.id)
                                )
                              }
                              className="inline-flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-medium text-slate-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600 transition-all"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </form>
              )}
              {inviteStep === 3 && inviteMode === 'bulk' && (
                <div className="invite-step2-content flex flex-col flex-1 min-h-0 gap-6">
                  <p className="text-sm shrink-0" style={{ color: '#475569' }}>
                    Invite multiple candidates at once by uploading a spreadsheet (CSV or XLSX) with
                    their details, or a ZIP/RAR archive containing resumes (PDF, DOCX, or DOC).
                  </p>
                  <div className="grid gap-8 md:grid-cols-2 flex-1 min-h-0">
                    {/* Left panel: instructions and templates */}
                    <div className="space-y-6 flex flex-col min-h-0">
                      <div>
                        <h3 className="text-base font-semibold" style={{ color: '#0F172A' }}>Keep in mind</h3>
                        <p className="mt-3 text-sm" style={{ color: '#475569' }}>
                          Make sure you use {inviteChannels.whatsapp ? 'four' : 'three'} columns in your file in the following order:{' '}
                          <strong style={{ color: '#0F172A' }}>First name</strong>, <strong style={{ color: '#0F172A' }}>Last name</strong>,{' '}
                          <strong style={{ color: '#0F172A' }}>Email</strong>{inviteChannels.whatsapp && <>, <strong style={{ color: '#0F172A' }}>Mobile</strong></>}. You can add up to 500 candidates per file.
                        </p>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold" style={{ color: '#0F172A' }}>Example</h3>
                        <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
                          <table className="min-w-full text-xs">
                            <thead>
                              <tr style={{ background: '#F8FAFC' }}>
                                <th className="border-b border-r border-slate-200 px-3 py-2 text-left font-medium" style={{ color: '#334155' }}>
                                  First name
                                </th>
                                <th className="border-b border-r border-slate-200 px-3 py-2 text-left font-medium" style={{ color: '#334155' }}>
                                  Last name
                                </th>
                                <th className={`border-b ${inviteChannels.whatsapp ? 'border-r' : ''} border-slate-200 px-3 py-2 text-left font-medium`} style={{ color: '#334155' }}>
                                  Email
                                </th>
                                {inviteChannels.whatsapp && (
                                  <th className="border-b border-slate-200 px-3 py-2 text-left font-medium" style={{ color: '#334155' }}>
                                    Mobile
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border-b border-r border-slate-200 px-3 py-2" style={{ color: '#475569' }}>
                                  Rahul
                                </td>
                                <td className="border-b border-r border-slate-200 px-3 py-2" style={{ color: '#475569' }}>
                                  Singh
                                </td>
                                <td className={`border-b ${inviteChannels.whatsapp ? 'border-r' : ''} border-slate-200 px-3 py-2`} style={{ color: '#475569' }}>
                                  rahul@email.com
                                </td>
                                {inviteChannels.whatsapp && (
                                  <td className="border-b border-slate-200 px-3 py-2" style={{ color: '#475569' }}>
                                    +91 98765 43210
                                  </td>
                                )}
                              </tr>
                              <tr>
                                <td className="border-r border-slate-200 px-3 py-2" style={{ color: '#475569' }}>
                                  Yogesh
                                </td>
                                <td className="border-r border-slate-200 px-3 py-2" style={{ color: '#475569' }}>
                                  Parmar
                                </td>
                                <td className={`${inviteChannels.whatsapp ? 'border-r' : ''} border-slate-200 px-3 py-2`} style={{ color: '#475569' }}>
                                  yogesh@email.com
                                </td>
                                {inviteChannels.whatsapp && (
                                  <td className="border-slate-200 px-3 py-2" style={{ color: '#475569' }}>
                                    +91 98765 43211
                                  </td>
                                )}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: '#334155' }}>
                          You can download one of these templates:
                        </p>
                        <div className="mt-3 flex gap-3">
                          <button
                            type="button"
                            onClick={() => downloadBulkTemplate('csv')}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium shadow-sm transition-all hover:border-indigo-300 hover:shadow"
                            style={{ color: '#334155' }}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" style={{ color: '#4F46E5' }} />
                            CSV
                          </button>
                          <button
                            type="button"
                            onClick={() => downloadBulkTemplate('xlsx')}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium shadow-sm transition-all hover:border-emerald-300 hover:shadow"
                            style={{ color: '#334155' }}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" style={{ color: '#10B981' }} />
                            XLSX
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Right panel: file upload */}
                    <div className="space-y-5 flex flex-col min-h-0">
                      <>
                        <div className="flex items-start gap-2.5 rounded-xl border px-4 py-3 shrink-0" style={{ borderColor: 'rgba(79, 70, 229, 0.2)', background: 'rgba(79, 70, 229, 0.04)' }}>
                          <InformationCircleIcon className="h-5 w-5 shrink-0" style={{ color: '#4F46E5' }} />
                          <p className="text-sm" style={{ color: '#334155' }}>
                            You can upload up to 500 candidate resumes per archive. Max file size:
                            50MB per file.
                          </p>
                        </div>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => document.getElementById('bulk-upload-input')?.click()}
                          onKeyDown={(e) =>
                            e.key === 'Enter' &&
                            document.getElementById('bulk-upload-input')?.click()
                          }
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setBulkDragOver(true);
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setBulkDragOver(false);
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setBulkDragOver(false);
                            const file = e.dataTransfer?.files?.[0];
                            if (file) setBulkFile(file);
                          }}
                          className={`
                              flex flex-1 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition-all duration-200 min-h-[120px]
                              ${bulkDragOver ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-300 bg-slate-50/50 hover:border-indigo-300 hover:bg-indigo-50/20'}
                            `}
                        >
                          <input
                            type="file"
                            id="bulk-upload-input"
                            data-testid="bulk-upload-input"
                            className="hidden"
                            accept=".csv,.xlsx,.xls,.zip,.rar,application/zip,application/x-zip-compressed,application/x-rar-compressed,application/vnd.rar"
                            onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                          />
                          <ArrowUpTrayIcon className="h-10 w-10" style={{ color: '#94A3B8' }} />
                          <span className="mt-3 text-base font-medium" style={{ color: '#334155' }}>
                            Drag and drop file here or click to upload
                          </span>
                          <span className="mt-2 text-sm" style={{ color: '#94A3B8' }}>50MB max file size</span>
                        </div>
                        <p className="text-sm shrink-0" style={{ color: '#64748B' }}>
                          Supported file types: .csv, .xls, .xlsx, .zip/.rar containing (PDF, DOCX,
                          or DOC)
                        </p>
                        {bulkFile && (
                          <div className="flex items-center gap-3 rounded-xl px-4 py-3 border" style={{ borderColor: 'rgba(16, 185, 129, 0.3)', background: 'rgba(16, 185, 129, 0.06)' }}>
                            <CheckCircleIcon className="h-5 w-5 shrink-0" style={{ color: '#10B981' }} />
                            <p className="text-sm" style={{ color: '#0F172A' }}>
                              Selected: <span className="font-medium">{bulkFile.name}</span>
                            </p>
                          </div>
                        )}
                      </>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <footer
              className="flex items-center justify-between shrink-0 border-t px-6 py-4 bg-white"
              style={{ borderColor: INVITE_MODAL_COLORS.footerBorder }}
            >
              <button
                type="button"
                onClick={() => {
                  if (inviteStep === 3) {
                    setInviteStep(2);
                    return;
                  }
                  if (inviteStep === 2) {
                    setInviteStep(1);
                    setInviteMode(null);
                    return;
                  }
                  setIsInviteOpen(false);
                  setInviteStep(1);
                  setInviteMode(null);
                }}
                data-testid="close-invite-modal-btn"
                className="invite-flow-back-btn inline-flex items-center gap-1.5 text-sm font-medium"
                style={{ color: INVITE_MODAL_COLORS.secondaryText }}
              >
                {inviteStep === 1 ? (
                  <XMarkIcon className="h-4 w-4" />
                ) : (
                  <ArrowLeftIcon className="h-4 w-4" />
                )}
                {inviteStep === 1 ? 'Close' : 'Back'}
              </button>
              {inviteStep === 2 && (inviteMode === 'single' || inviteMode === 'bulk') && (
                <button
                  type="button"
                  data-testid="invite-next-step-btn"
                  disabled={!inviteChannels.email && !inviteChannels.whatsapp}
                  onClick={() => setInviteStep(3)}
                  className="invite-flow-primary-btn inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span>Next step</span>
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              )}
              {inviteStep === 3 && inviteMode === 'single' && (
                <button
                  type="submit"
                  form="single-invite-form"
                  data-testid="invite-configure-single-btn"
                  disabled={
                    singleInviteSubmitting ||
                    !singleInviteRows.some((r) =>
                      (!inviteChannels.email && inviteChannels.whatsapp)
                        ? (r.mobile && r.mobile !== '+91' && r.mobile.trim())
                        : (r.email && r.email.trim())
                    )
                  }
                  className="invite-flow-primary-btn inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <UserPlusIcon className="h-4 w-4" />
                  <span>{singleInviteSubmitting ? 'Adding…' : 'Configure & Invite'}</span>
                </button>
              )}
              {inviteStep === 3 && inviteMode === 'bulk' && (
                <button
                  type="button"
                  data-testid="invite-configure-bulk-btn"
                  disabled={!bulkFile || bulkUploading}
                  onClick={handleBulkUpload}
                  className="invite-flow-primary-btn inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <UsersIcon className="h-4 w-4" />
                  <span>{bulkUploading ? 'Uploading…' : 'Configure & Invite'}</span>
                </button>
              )}
            </footer>
          </div>
        </div>
      )}
      {/* WhatsApp Agent screenings use their own dedicated invite modal — not the
          Email/Single/Bulk flow above, since it starts a live WhatsApp conversation
          rather than sending a link/email invite. */}
      {showWhatsAppInviteModal && (
        <WhatsAppInviteModal
          screeningId={screeningId}
          onClose={() => setShowWhatsAppInviteModal(false)}
          onInvited={async () => {
            await refetchScreening();
            await fetchCandidates({ forceRefresh: true });
          }}
        />
      )}
      {/* Qualifying Agent screenings use their own dedicated invite modal — places an
          outbound call rather than sending a link/email invite. */}
      {showQualifyingAgentInviteModal && (
        <QualifyingAgentInviteModal
          screeningId={screeningId}
          mode={screening?.qualifyingAgentMode || 'both'}
          onClose={() => setShowQualifyingAgentInviteModal(false)}
          onInvited={async () => {
            await refetchScreening();
            await fetchCandidates({ forceRefresh: true });
          }}
        />
      )}
      {whatsAppReplayCandidate && (
        <WhatsAppInterviewReplayModal
          screeningId={screeningId}
          candidate={whatsAppReplayCandidate}
          onClose={() => setWhatsAppReplayCandidate(null)}
        />
      )}
      {qualifyingAnswersCandidate && (
        <QualifyingAgentBoundary surface="answers card" fallback={null}>
          <QualifyingAnswersCard
            screeningId={screeningId}
            candidate={qualifyingAnswersCandidate}
            onClose={closeQualifyingSessionView(qualifyingAnswersCandidate, setQualifyingAnswersCandidate)}
            onOpenFull={() => {
              setQualifyingReplayCandidate(qualifyingAnswersCandidate);
              setQualifyingAnswersCandidate(null);
            }}
          />
        </QualifyingAgentBoundary>
      )}
      {qualifyingReplayCandidate && (
        <QualifyingAgentBoundary
          surface="replay modal"
          fallback={null}
        >
          <QualifyingAgentReplayModal
            screeningId={screeningId}
            candidate={qualifyingReplayCandidate}
            onClose={closeQualifyingSessionView(qualifyingReplayCandidate, setQualifyingReplayCandidate)}
          />
        </QualifyingAgentBoundary>
      )}
      {showQualifyingAgentDetailsModal && (
        <QualifyingAgentBoundary surface="screening details modal" fallback={null}>
          <QualifyingAgentDetailsModal
            screening={screening}
            onClose={() => setShowQualifyingAgentDetailsModal(false)}
            onSaved={refetchScreening}
          />
        </QualifyingAgentBoundary>
      )}
      {/* Create Assessment modal – attach to screening (matches JobResults shell) */}
      {showCreateAssessmentModal && screening?.jobId && (
        <div className="fixed inset-0 z-[200] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-50 backdrop-blur-sm"
              onClick={() => setShowCreateAssessmentModal(false)}
              aria-hidden
            />
            {/* Modal container */}
            <div className="relative z-10 inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full h-[90vh] max-h-[90vh]">
              <AssessmentCreateFlowModal
                jobPostingId={screening.jobId}
                skipInviteStep
                onClose={async (result) => {
                  const didCreate = typeof result === 'object' && result?.didCreate;
                  const newAssessmentId = typeof result === 'object' ? result?.assessmentId : null;
                  if (didCreate && newAssessmentId) {
                    try {
                      await axios.patch(`/api/screenings/${screeningId}`, {
                        assessmentId: newAssessmentId,
                      });
                      await refetchScreening();
                      toast.success('Assessment created and attached to this screening');
                    } catch (e) {
                      toast.error(e.response?.data?.message || 'Failed to attach assessment');
                    }
                  }
                  setShowCreateAssessmentModal(false);
                  if (openInterviewModalAfterAssessmentClose) {
                    setOpenInterviewModalAfterAssessmentClose(false);
                    setShowCreateInterviewModal(true);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
      {/* Create AI Interview modal – attach to screening (matches JobResults shell style) */}
      {showCreateInterviewModal && screening?.jobId && (
        <div className="fixed inset-0 z-[200] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity bg-black bg-opacity-50"
              onClick={() => setShowCreateInterviewModal(false)}
              aria-hidden
            />
            {/* Modal container */}
            <div className="relative z-10 inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full max-h-[90vh]">
              <Suspense
                fallback={<div className="flex items-center justify-center h-full">Loading…</div>}
              >
                <AIInterviewCreate
                  isModal
                  skipInviteStep
                  jobPostingId={screening.jobId}
                  onClose={async (createdInterviewId) => {
                    if (createdInterviewId) {
                      try {
                        await axios.patch(`/api/screenings/${screeningId}`, {
                          interviewId: createdInterviewId,
                        });
                        await refetchScreening();
                        toast.success('AI interview created and attached to this screening');
                      } catch (e) {
                        toast.error(e.response?.data?.message || 'Failed to attach interview');
                      }
                    }
                    setShowCreateInterviewModal(false);
                  }}
                />
              </Suspense>
            </div>
          </div>
        </div>
      )}
      {/* AI Interview Session Details Modal (replay & analysis) */}
      {showInterviewReplayModal && (
        <div data-testid="modal-backdrop" className="fixed inset-0 bg-black/40 backdrop-blur-[2px] overflow-y-auto h-full w-full z-50">
          <div className="relative top-5 mx-auto p-0 border w-11/12 md:w-5/6 lg:w-4/5 xl:w-3/4 shadow-xl rounded-lg bg-white max-h-[95vh] overflow-hidden flex flex-col">
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3
                    className="text-2xl font-bold text-gray-900 select-none"
                    onClick={() => setInterviewTitleTapCount((prev) => prev + 1)}
                  >
                    Interview Replay & Analysis
                  </h3>
                  <p className="mt-1 text-gray-600 text-sm">
                    {selectedInterviewSession?.candidateName ||
                      interviewSessionMeta.candidateName ||
                      'Anonymous Candidate'}{' '}
                    •{' '}
                    {interviewSessionMeta.interviewTitle || screening?.interviewId?.title || ''}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {showInterviewLanguageToggle && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">View</span>
                      <div className="inline-flex items-center rounded-md border border-gray-200 bg-white p-0.5 h-7" role="group" aria-label="View language">
                        <button
                          type="button"
                          onClick={() => setInterviewDisplayLang('hi')}
                          className={`inline-flex items-center justify-center rounded font-medium transition-colors px-2 text-xs ${interviewDisplayLang === 'hi' ? 'bg-[#007BFF] text-white' : 'text-gray-600 hover:text-gray-900'}`}
                          aria-pressed={interviewDisplayLang === 'hi'}
                        >Original</button>
                        <button
                          type="button"
                          onClick={() => setInterviewDisplayLang('en')}
                          className={`inline-flex items-center justify-center rounded font-medium transition-colors px-2 text-xs ${interviewDisplayLang === 'en' ? 'bg-[#007BFF] text-white' : 'text-gray-600 hover:text-gray-900'}`}
                          aria-pressed={interviewDisplayLang === 'en'}
                        >English</button>
                      </div>
                    </div>
                  )}
                  {showInterviewTranslateButton && (
                    <button
                      type="button"
                      onClick={handleInterviewTranslateClick}
                      className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 transition-colors"
                    >
                      Translate to English
                    </button>
                  )}
                  {showInterviewTranslatingSpinner && (
                    <div className="inline-flex items-center gap-2 h-7 px-3 rounded-md border border-gray-200 bg-white text-gray-600 text-xs">
                      <svg className="animate-spin h-3.5 w-3.5 text-indigo-500" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                        <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Translating…
                    </div>
                  )}
                  <button
                    onClick={closeInterviewReplayModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              {/* Quick Score Summary Bar removed per design — content shifted up */}
            </div>
            {/* Tabs Navigation */}
            <div className="border-b border-gray-200 bg-gray-50 px-6 flex items-center justify-between gap-4">
              <nav className="flex space-x-8 -mb-px">
                <button
                  disabled={interviewSessionLoading || !selectedInterviewSession}
                  onClick={() => setInterviewModalActiveTab('analysis')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${interviewModalActiveTab === 'analysis'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <ChartBarIcon className="h-5 w-5" />
                  Analysis & Transcript
                </button>
                <button
                  disabled={interviewSessionLoading || !selectedInterviewSession}
                  onClick={() => setInterviewModalActiveTab('proctoring')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${interviewModalActiveTab === 'proctoring'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <ShieldExclamationIcon className="h-5 w-5" />
                  Proctoring Details
                </button>
              </nav>
              {showScoringCompareMode && (
                <button
                  type="button"
                  onClick={triggerDualInterviewRescore}
                  disabled={dualScoringLoading || interviewSessionLoading || !selectedInterviewSession}
                  className="ml-auto shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-60"
                >
                  {dualScoringLoading ? 'Rescoring...' : 'Rescore Old + New'}
                </button>
              )}
            </div>
            {/* Confidence notes removed */}
            {/* Tab Content - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {interviewSessionLoading && (
                <div className="space-y-5 animate-pulse">
                  {/* Skeleton: Video Player + Tabs Row */}
                  <div className="flex flex-col xl:flex-row gap-5" style={{ minHeight: '300px' }}>
                    {/* Video player skeleton */}
                    <div className="w-full xl:w-[40%] bg-slate-800 rounded-2xl overflow-hidden flex items-center justify-center relative">
                      <div className="w-14 h-14 rounded-full bg-slate-700" />
                      <div className="absolute bottom-5 left-5 space-y-2">
                        <div className="h-4 w-32 rounded-md bg-slate-700" />
                        <div className="h-3 w-48 rounded-md bg-slate-700/60" />
                      </div>
                    </div>
                    {/* Question tabs skeleton */}
                    <div className="w-full xl:w-[60%] bg-white border border-gray-100 rounded-2xl p-4 space-y-4">
                      <div className="flex gap-4 border-b border-gray-100 pb-3">
                        <div className="h-4 w-28 rounded bg-gray-200" />
                        <div className="h-4 w-20 rounded bg-gray-100" />
                      </div>
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gray-100 flex-shrink-0" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-3 w-12 rounded bg-gray-200" />
                            <div className="h-2.5 w-4/5 rounded bg-gray-100" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Skeleton: 3 Score Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Score card */}
                    <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white p-5 flex flex-col items-center gap-3">
                      <div className="w-24 h-24 rounded-full bg-emerald-100/60" />
                      <div className="h-3 w-28 rounded bg-gray-200" />
                      <div className="h-3 w-20 rounded bg-gray-100" />
                      <div className="h-8 w-24 rounded-full bg-gray-200 mt-1" />
                    </div>
                    {/* Radar chart card */}
                    <div className="rounded-2xl bg-white border border-gray-100 p-5 flex flex-col items-center gap-3">
                      <div className="h-3 w-32 rounded bg-gray-200" />
                      <div className="w-36 h-36 rounded-full bg-gray-50 border-2 border-dashed border-gray-100" />
                      <div className="flex gap-4">
                        <div className="h-2.5 w-16 rounded bg-gray-100" />
                        <div className="h-2.5 w-16 rounded bg-gray-100" />
                      </div>
                    </div>
                    {/* Insights card */}
                    <div className="rounded-2xl bg-white border border-gray-100 p-5 space-y-3">
                      <div className="h-3 w-24 rounded bg-gray-200" />
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="flex items-center gap-3 py-1.5">
                          <div className="flex-1 h-2.5 rounded bg-gray-100" />
                          <div className="h-4 w-14 rounded-full bg-gray-200" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {!interviewSessionLoading && interviewSessionLoadError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
                  {interviewSessionLoadError}
                </div>
              )}
              {!interviewSessionLoading && !interviewSessionLoadError && selectedInterviewSession && (
                <>
                  {/* ═══ COMBINED ANALYSIS & TRANSCRIPT TAB ═══ */}
                  {interviewModalActiveTab === 'analysis' && (
                    <InterviewAnalysisTranscript
                      session={selectedInterviewSession}
                      analysisSession={interviewAnalysisSession}
                      interviewDetails={interviewDetails}
                      candidateNameFallback={interviewSessionMeta.candidateName}
                      interviewTitleFallback={
                        interviewSessionMeta.interviewTitle || screening?.interviewId?.title || ''
                      }
                      recordingUrl={interviewRecordingUrl}
                      recordingLoading={interviewRecordingLoading}
                      onFetchRecording={async () => {
                        setInterviewRecordingLoading(true);
                        try {
                          const res = await axios.get(`/api/ai-interviews/session/${selectedInterviewSession.id}/recording-url`);
                          setInterviewRecordingUrl(res.data.url);
                        } catch (err) {
                          if (err.response?.status === 202) {
                            toast('Recording is still being processed. Try again shortly.');
                          } else {
                            toast.error('Could not load recording');
                          }
                        } finally {
                          setInterviewRecordingLoading(false);
                        }
                      }}
                      videoRef={interviewVideoRef}
                      innerTab={interviewInnerTab}
                      onInnerTabChange={setInterviewInnerTab}
                      expandedQuestions={expandedQuestions}
                      onExpandedQuestionsChange={setExpandedQuestions}
                      translateText={(response) =>
                        showInterviewEnglish && hasStoredInterviewTranslation
                          ? englishByOriginalInterview.get(normalizeInterviewText(response)) || response
                          : response
                      }
                      pickTurnText={pickInterviewTurnText}
                      renderScoreCards={
                        screening?.includeApplicationForm === true
                          ? (cards) => (
                              <InterviewAssessmentCards
                                {...cards}
                                screeningId={screeningId}
                                candidateId={interviewSessionMeta.candidateId}
                                sessionId={selectedInterviewSession.id}
                                candidateName={interviewSessionMeta.candidateName}
                              />
                            )
                          : undefined
                      }
                    />
                  )}
                  {/* Proctoring Details Tab */}
                  {interviewModalActiveTab === 'proctoring' && (
                    <div className="space-y-6">
                      {!interviewProctoringData ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                          <span className="ml-3 text-gray-600">Loading proctoring data...</span>
                        </div>
                      ) : (
                        <>
                          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                            {/* Body */}
                            <div className="px-6 py-6">
                              {/* ── Premium Stats Header ── */}
                              {(() => {
                                const score = interviewProctoringData.riskScore;
                                const riskColor = score >= 100 ? '#dc2626' : score >= 50 ? '#ea580c' : '#059669';
                                // Gauge config: semi-circle arc from 180° to 0° (left to right)
                                const gaugeRadius = 72;
                                const gaugeStroke = 12;
                                const gaugeCx = 90;
                                const gaugeCy = 82;
                                // Semi-circle circumference (half of full circle)
                                const semiCircumference = Math.PI * gaugeRadius;
                                // Map score to gauge fill (0-75 mapped to 0-100%; penalties halved vs legacy)
                                const maxScore = 75; // practical max for display
                                const gaugePct = Math.min(score / maxScore, 1);
                                const gaugeOffset = semiCircumference - (gaugePct * semiCircumference);
                                return (
                                  <>
                                    <div className="flex items-center gap-6 mb-6">
                                      {/* Gauge Meter */}
                                      <div className="flex-shrink-0 relative" style={{ width: 180, height: 110 }}>
                                        <svg width="180" height="110" viewBox="0 0 180 110">
                                          <defs>
                                            <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                              <stop offset="0%" stopColor="#10b981" />
                                              <stop offset="30%" stopColor="#f59e0b" />
                                              <stop offset="60%" stopColor="#f97316" />
                                              <stop offset="100%" stopColor="#ef4444" />
                                            </linearGradient>
                                            <filter id="gaugeShadow" x="-10%" y="-10%" width="120%" height="120%">
                                              <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor={riskColor} floodOpacity="0.25" />
                                            </filter>
                                          </defs>
                                          {/* Background track */}
                                          <path
                                            d={`M ${gaugeCx - gaugeRadius} ${gaugeCy} A ${gaugeRadius} ${gaugeRadius} 0 0 1 ${gaugeCx + gaugeRadius} ${gaugeCy}`}
                                            fill="none"
                                            stroke="#f1f5f9"
                                            strokeWidth={gaugeStroke}
                                            strokeLinecap="round"
                                          />
                                          {/* Filled arc */}
                                          <path
                                            d={`M ${gaugeCx - gaugeRadius} ${gaugeCy} A ${gaugeRadius} ${gaugeRadius} 0 0 1 ${gaugeCx + gaugeRadius} ${gaugeCy}`}
                                            fill="none"
                                            stroke="url(#gaugeGradient)"
                                            strokeWidth={gaugeStroke}
                                            strokeLinecap="round"
                                            strokeDasharray={semiCircumference}
                                            strokeDashoffset={gaugeOffset}
                                            filter="url(#gaugeShadow)"
                                            style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)' }}
                                          />
                                          {/* Needle dot at the end of arc */}
                                          {score > 0 && (() => {
                                            const angle = Math.PI - (gaugePct * Math.PI);
                                            const dotX = gaugeCx + gaugeRadius * Math.cos(angle);
                                            const dotY = gaugeCy - gaugeRadius * Math.sin(angle);
                                            return (
                                              <circle
                                                cx={dotX} cy={dotY} r={gaugeStroke / 2 + 2}
                                                fill="white" stroke={riskColor} strokeWidth="3"
                                                style={{ filter: `drop-shadow(0 1px 3px ${riskColor}60)`, transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)' }}
                                              />
                                            );
                                          })()}
                                        </svg>
                                        {/* Score text in center of gauge */}
                                        <div className="absolute" style={{ bottom: 6, left: '50%', transform: 'translateX(-50%)' }}>
                                          <div className="flex flex-col items-center">
                                            <span className="text-3xl font-black tracking-tighter leading-none" style={{ color: riskColor }}>{score}</span>
                                            <span className="text-[9px] text-gray-400 font-semibold uppercase tracking-widest mt-0.5">Risk Score</span>
                                          </div>
                                        </div>
                                      </div>
                                      {/* Metric Cards - 2 stacked cards filling height */}
                                      <div className="flex-1 grid grid-cols-2 gap-3">
                                        {/* Decision Card */}
                                        <div className="rounded-xl border border-gray-100 bg-gray-50/70 px-5 py-4 flex items-center gap-4">
                                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${interviewProctoringData.riskLevel === 'Compliant' ? 'bg-emerald-100' : 'bg-orange-100'}`}>
                                            {interviewProctoringData.riskLevel === 'Compliant' ? (
                                              <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                              </svg>
                                            ) : (
                                              <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                              </svg>
                                            )}
                                          </div>
                                          <div>
                                            <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mb-1">Decision</p>
                                            <p className={`text-base font-bold ${interviewProctoringData.riskLevel === 'Compliant' ? 'text-emerald-600' : 'text-orange-600'}`}>
                                              {interviewProctoringData.riskLevel === 'Compliant' ? 'Not Flagged' : 'Flagged'}
                                            </p>
                                            <p className="text-[11px] text-gray-400 mt-0.5">
                                              {interviewProctoringData.riskLevel === 'Compliant'
                                                ? 'Session passed all integrity checks'
                                                : `${(interviewProctoringData.flagReasonsInEnglish || []).length} issue${(interviewProctoringData.flagReasonsInEnglish || []).length !== 1 ? 's' : ''} detected during session`}
                                            </p>
                                          </div>
                                        </div>
                                        {/* Risk Level Card */}
                                        <div className="rounded-xl border border-gray-100 bg-gray-50/70 px-5 py-4 flex items-center gap-4">
                                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${interviewProctoringData.riskLevel === 'High Risk' ? 'bg-red-100' : interviewProctoringData.riskLevel === 'Flagged' ? 'bg-orange-100' : 'bg-emerald-100'}`}>
                                            <svg className={`w-5 h-5 ${interviewProctoringData.riskLevel === 'High Risk' ? 'text-red-600' : interviewProctoringData.riskLevel === 'Flagged' ? 'text-orange-600' : 'text-emerald-600'}`} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                                            </svg>
                                          </div>
                                          <div>
                                            <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mb-1">Risk Level</p>
                                            <span
                                              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${interviewProctoringData.riskLevel === 'High Risk' ? 'bg-red-50 text-red-600 ring-1 ring-red-200'
                                                : interviewProctoringData.riskLevel === 'Flagged' ? 'bg-orange-50 text-orange-600 ring-1 ring-orange-200'
                                                  : 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200'
                                                }`}
                                            >
                                              <span className={`w-1.5 h-1.5 rounded-full ${interviewProctoringData.riskLevel === 'High Risk' ? 'bg-red-500'
                                                : interviewProctoringData.riskLevel === 'Flagged' ? 'bg-orange-500'
                                                  : 'bg-emerald-500'
                                                }`} style={{ boxShadow: `0 0 6px ${riskColor}50` }} />
                                              {interviewProctoringData.riskLevel}
                                            </span>
                                            <p className="text-[11px] text-gray-400 mt-1">
                                              {interviewProctoringData.riskLevel === 'High Risk'
                                                ? 'Immediate review recommended'
                                                : interviewProctoringData.riskLevel === 'Flagged'
                                                  ? 'Review at your discretion'
                                                  : 'No action needed'}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                );
                              })()}
                              {/* ── AI Analysis — Dynamic, light indigo themed ── */}
                              {(() => {
                                const reasons = interviewProctoringData.flagReasonsInEnglish || [];
                                const score = interviewProctoringData.riskScore;
                                const cats = interviewProctoringData.categories || {};
                                const catEntries = Object.entries(cats).filter(([, c]) => c != null);
                                const totalCount = catEntries.length;
                                // Use AI-generated summary from backend if available
                                const aiSummary = interviewProctoringData.aiSummary;
                                const hasAiSummary = aiSummary && aiSummary.summary;
                                // Fallback summary if AI is unavailable
                                const fallbackSummary = score === 0
                                  ? `This candidate's session was analyzed across ${totalCount} proctoring dimensions. All monitored signals remained within expected thresholds.`
                                  : `Flagged ${reasons.length} integrity check${reasons.length > 1 ? 's' : ''} with a cumulative risk score of ${score}. Detailed review recommended.`;
                                // Render markdown bold (**text**) as <strong>
                                const renderBold = (text) => {
                                  if (!text) return null;
                                  const parts = text.split(/(\*\*[^*]+\*\*)/g);
                                  return parts.map((part, i) => {
                                    if (part.startsWith('**') && part.endsWith('**')) {
                                      return <strong key={i} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
                                    }
                                    return part;
                                  });
                                };
                                // Severity color mapping
                                const severityStyles = {
                                  Critical: { color: '#dc2626', bg: '#fef2f2' },
                                  High: { color: '#ea580c', bg: '#fff7ed' },
                                  Medium: { color: '#d97706', bg: '#fffbeb' },
                                  Low: { color: '#65a30d', bg: '#f7fee7' },
                                };
                                // Build findings list – prefer AI findings, fallback to flagReasons
                                const findings = hasAiSummary && aiSummary.findings?.length > 0
                                  ? aiSummary.findings
                                  : reasons.map(r => {
                                    const lower = r.toLowerCase();
                                    let severity = 'Medium';
                                    if (lower.includes('identity') || lower.includes('phone')) severity = 'Critical';
                                    else if (lower.includes('ai-assisted') || lower.includes('multiple')) severity = 'High';
                                    return { text: r, severity };
                                  });
                                return (
                                  <div className="mb-1">
                                    {/* Header */}
                                    <div className="flex items-center justify-between rounded-t-xl bg-blue-50 border border-blue-100 border-b-0 px-5 py-3">
                                      <div className="flex items-center gap-2.5">
                                        <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shadow-sm">
                                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                                          </svg>
                                        </div>
                                        <div>
                                          <h4 className="text-sm font-bold text-gray-900 tracking-tight leading-none">Interview Summary</h4>
                                        </div>
                                      </div>
                                      <button
                                        onClick={() =>
                                          setShowDetailedAnalysis(prev => {
                                            const next = !prev;
                                            if (next) {
                                              setSelectedProctoringCategory('browserActivity');
                                            }
                                            return next;
                                          })
                                        }
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300 shadow-sm"
                                      >
                                        {showDetailedAnalysis ? (
                                          <>
                                            <ChevronUpIcon className="w-3.5 h-3.5" />
                                            Hide advanced details
                                          </>
                                        ) : (
                                          <>
                                            <ChevronDownIcon className="w-3.5 h-3.5" />
                                            View advanced details
                                          </>
                                        )}
                                      </button>
                                    </div>
                                    {/* Body */}
                                    <div className="rounded-b-xl border border-blue-100 bg-white px-5 py-4">
                                      {/* AI-Generated Summary */}
                                      <div className="mb-4">
                                        <p className="text-[13px] text-gray-600 leading-relaxed">
                                          {hasAiSummary ? renderBold(aiSummary.summary) : fallbackSummary}
                                        </p>
                                      </div>
                                      {/* Findings */}
                                      {findings.length > 0 ? (
                                        <div>
                                          <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-widest mb-2">Key Observations</p>
                                          <div className="space-y-2">
                                            {findings.slice(0, 3).map((finding, idx) => {
                                              const sev = severityStyles[finding.severity] || severityStyles.Medium;
                                              return (
                                                <div key={idx} className="flex items-start gap-2.5 rounded-lg border border-gray-100 px-3 py-2.5">
                                                  <div className="w-2 h-2 mt-1.5 rounded-full bg-blue-500" />
                                                  <p className="text-[13px] text-gray-700 leading-relaxed flex-1">
                                                    {renderBold(finding.text)}
                                                  </p>
                                                  <span
                                                    className="flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide mt-0.5"
                                                    style={{ color: sev.color, backgroundColor: sev.bg }}
                                                  >
                                                    {finding.severity}
                                                  </span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3.5">
                                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                            <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
                                          </div>
                                          <div>
                                            <p className="text-sm font-semibold text-emerald-800">All checks passed</p>
                                            <p className="text-xs text-emerald-600 mt-0.5">No integrity violations were detected during this session.</p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                          {showDetailedAnalysis && (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ animation: 'fadeSlideIn 0.3s ease-out' }}>
                              {/* Left Column: Score Breakdown only (risk summary card removed) */}
                              <div className="lg:col-span-1 space-y-4" ref={scoreBreakdownRef}>
                                {/* Score Breakdown */}
                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                  <div className="px-5 py-4">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Score Breakdown</h4>
                                    <div className="space-y-3">
                                      {[
                                        {
                                          label: 'Environment (Browser & Setup)',
                                          key: 'environment',
                                          keys: ['browserActivity', 'transcriptAnalysis', 'responseDelay'],
                                        },
                                        {
                                          label: 'Audio Signals',
                                          key: 'audio',
                                          keys: ['speechPattern', 'speakerDetection'],
                                        },
                                        {
                                          label: 'Video Signals',
                                          key: 'video',
                                          keys: [
                                            'videoPresence',
                                            'videoMultipleFaces',
                                            'videoIdentity',
                                            'videoEyeGaze',
                                            'videoHeadPose',
                                            'videoPhoneDetection',
                                          ],
                                        },
                                      ].map(section => {
                                        const cats = interviewProctoringData.categories || {};
                                        const hasAny = section.keys.some(k => cats[k]);
                                        if (!hasAny) return null;
                                        return (
                                          <div key={section.key} className="space-y-1.5">
                                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                              {section.label}
                                            </p>
                                            {section.keys.map(key => {
                                              const cat = cats[key];
                                              if (!cat) return null;
                                              const isSelected = selectedProctoringCategory === key;
                                              const clickableKeys = [
                                                'browserActivity',
                                                'transcriptAnalysis',
                                                'responseDelay',
                                                'speechPattern',
                                                'speakerDetection',
                                                'videoPresence',
                                                'videoMultipleFaces',
                                                'videoIdentity',
                                                'videoEyeGaze',
                                                'videoHeadPose',
                                                'videoPhoneDetection',
                                              ];
                                              const clickable = clickableKeys.includes(key);
                                              return (
                                                <button
                                                  key={key}
                                                  type="button"
                                                  onClick={() => setSelectedProctoringCategory(clickable ? key : null)}
                                                  className={`w-full flex items-center justify-between text-sm rounded-lg px-2 py-1.5 transition-colors ${clickable
                                                    ? isSelected
                                                      ? 'bg-indigo-50 text-indigo-900'
                                                      : 'hover:bg-gray-50 text-gray-800'
                                                    : 'cursor-default text-gray-700'
                                                    }`}
                                                >
                                                  <span className="capitalize truncate">
                                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                                  </span>
                                                  <span
                                                    className={`ml-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cat.score > 0
                                                      ? 'bg-rose-50 text-rose-600 ring-1 ring-rose-100'
                                                      : 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100'
                                                      }`}
                                                  >
                                                    +{cat.score} pts
                                                  </span>
                                                </button>
                                              );
                                            })}
                                          </div>
                                        );
                                      })}
                                      <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200 mt-3">
                                        <span className="font-semibold text-gray-900">Total</span>
                                        <span className="font-bold text-gray-900">
                                          {interviewProctoringData.riskScore} pts
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* Right Column: Detailed Categories (grouped into Environment, Audio, Video) */}
                              <div className="lg:col-span-2 space-y-4 transition-all duration-300 ease-out">
                                {selectedProctoringCategory === 'browserActivity' &&
                                  interviewProctoringData.categories?.browserActivity ? (
                                  /* Browser Activity focused card */
                                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                                          <svg
                                            className="w-4.5 h-4.5 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M3.75 6.75h16.5M3.75 9.75h10.5M3.75 12.75h7.5M3.75 15.75h4.5M16.5 9.75h3.75m-3.75 3h3.75m-3.75 3h3.75"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                                            Environment Detail
                                          </p>
                                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                                            Browser Activity Breakdown
                                          </h2>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                                            Contribution
                                          </p>
                                          <p className="text-sm font-bold text-gray-900">
                                            +{interviewProctoringData.categories.browserActivity.score} pts
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-5 pt-4 pb-5 bg-gray-50/40">
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                        <div className="col-span-2 rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                            Overview
                                          </p>
                                          <p className="text-sm text-gray-700">
                                            We analyze tab focus, fullscreen behaviour, and navigation events to detect potential
                                            external help or distractions during the interview.
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 flex flex-col justify-center">
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                            Total Violations
                                          </p>
                                          <p className="text-lg font-bold text-gray-900">
                                            {interviewProctoringData.categories.browserActivity.items?.reduce(
                                              (sum, item) => sum + (item.count || 0),
                                              0
                                            ) || 0}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
                                        <div className="px-4 pt-3 pb-2 border-b border-gray-100 flex items-center justify-between">
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                            Violation Breakdown
                                          </p>
                                          <span className="text-[11px] text-gray-500">
                                            Each row: Count × Pts each = Total
                                          </span>
                                        </div>
                                        <div className="px-4 pb-3 pt-1">
                                          <table className="min-w-full divide-y divide-gray-100">
                                            <thead>
                                              <tr>
                                                <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-2">
                                                  Violation
                                                </th>
                                                <th className="text-right text-xs font-semibold text-gray-500 uppercase pb-2">
                                                  Count
                                                </th>
                                                <th className="text-right text-xs font-semibold text-gray-500 uppercase pb-2">
                                                  Pts Each
                                                </th>
                                                <th className="text-right text-xs font-semibold text-gray-500 uppercase pb-2">
                                                  Total
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                              {interviewProctoringData.categories.browserActivity.items?.map((item, idx) => (
                                                <tr key={idx}>
                                                  <td className="py-2.5 text-sm text-gray-900">{item.label}</td>
                                                  <td className="py-2.5 text-sm text-gray-600 text-right">{item.count}</td>
                                                  <td className="py-2.5 text-sm text-gray-600 text-right">{item.pointsEach}</td>
                                                  <td className="py-2.5 text-sm font-semibold text-gray-900 text-right">
                                                    {item.points}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                          {/* Calculated Criteria — dashed underline, tooltip on hover */}
                                          <div className="group mt-4 relative inline-block cursor-help">
                                            <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                                              Calculated Criteria
                                            </span>
                                            <div
                                              role="tooltip"
                                              className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                                            >
                                              <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                                              <p className="text-xs text-gray-100 mb-2 relative">
                                                {interviewProctoringData.categories.browserActivity.score === 0
                                                  ? 'Browser Activity adds 0 points because no violations were detected. Total = Count × Pts each for each row (all 0 here).'
                                                  : `Browser Activity total: ${interviewProctoringData.categories.browserActivity.score} pts. Each row: Count × Pts each = Total. Sum of those = score.`}
                                              </p>
                                              <div className="space-y-0.5 relative">
                                                {interviewProctoringData.categories.browserActivity.items?.map((item, idx) => (
                                                  <p key={idx} className="text-xs text-gray-300">
                                                    <span className="font-medium text-gray-100">{item.label}</span>{' '}
                                                    {item.count} × {item.pointsEach} = {item.points} pts
                                                  </p>
                                                ))}
                                              </div>
                                              <p className="text-xs text-gray-400 mt-2 relative">
                                                Example: if Fullscreen Exit had count 4 and 2.5 pts each → 4 × 2.5 = 10 pts for that row.
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : selectedProctoringCategory === 'transcriptAnalysis' ? (
                                  /* Transcript Analysis detailed card (HTML adapted from copy file) */
                                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                                          <svg
                                            className="w-4.5 h-4.5 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M4 5.5A2.5 2.5 0 016.5 3h11A2.5 2.5 0 0120 5.5v8A2.5 2.5 0 0117.5 16H9l-3.5 3 .5-3H6.5A2.5 2.5 0 014 13.5v-8z"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                            AI-Assisted Signal
                                          </p>
                                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                                            Transcript Analysis Breakdown
                                          </h2>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                                            Contribution
                                          </p>
                                          <p className="text-sm font-bold text-gray-900">
                                            +{(interviewProctoringData.categories.transcriptAnalysis || {}).score ?? 0} pts
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                                      {/* How this works — full-width card */}
                                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                          How this works
                                        </p>
                                        <p className="text-sm text-gray-700">
                                          We use an AI model to look at wording patterns in answers and flag segments that
                                          appear machine-generated or overly generic. This is a supporting signal and should
                                          be reviewed alongside other evidence.
                                        </p>
                                      </div>
                                      {/* Four metric cards on a single row (desktop) */}
                                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Samples
                                          </p>
                                          <p className="text-lg font-semibold text-gray-900">
                                            {(interviewProctoringData.categories.transcriptAnalysis || {}).details
                                              ?.sampleCount ?? 0}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Suspicious
                                          </p>
                                          <p className="text-lg font-semibold text-rose-600">
                                            {(interviewProctoringData.categories.transcriptAnalysis || {}).details
                                              ?.suspiciousCount ?? 0}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Suspicious Rate
                                          </p>
                                          <p className="text-lg font-semibold text-gray-900">
                                            {(interviewProctoringData.categories.transcriptAnalysis || {}).details
                                              ?.suspiciousRate ?? 0}
                                            %
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Pattern
                                          </p>
                                          <p
                                            className={`text-sm font-semibold ${(interviewProctoringData.categories.transcriptAnalysis || {}).details
                                              ?.isPatternSuspicious
                                              ? 'text-rose-600'
                                              : 'text-emerald-600'
                                              }`}
                                          >
                                            {(interviewProctoringData.categories.transcriptAnalysis || {}).details
                                              ?.isPatternSuspicious
                                              ? 'Suspicious'
                                              : 'Normal'}
                                          </p>
                                        </div>
                                      </div>
                                      {(interviewProctoringData.categories.transcriptAnalysis || {}).perQuestion?.length >
                                        0 && (
                                          <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 max-h-64 overflow-y-auto space-y-2">
                                            <p className="text-xs font-semibold text-gray-700 uppercase tracking-[0.12em]">
                                              Per-Question Analysis
                                            </p>
                                            {(interviewProctoringData.categories.transcriptAnalysis || {}).perQuestion.map(
                                              (q, idx) => (
                                                <div
                                                  key={idx}
                                                  className={`p-3 rounded-lg border ${q.isSuspicious ? 'bg-rose-50 border-rose-200' : 'bg-gray-50 border-gray-200'
                                                    }`}
                                                >
                                                  <div className="flex items-center justify-between mb-1.5">
                                                    <span className="text-sm font-medium text-gray-900">
                                                      Candidate Response {q.questionIndex + 1}
                                                    </span>
                                                    {q.isSuspicious && (
                                                      <span className="text-xs font-medium text-rose-600">Flagged</span>
                                                    )}
                                                  </div>
                                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                                    <div>
                                                      <span className="text-gray-500">AI Likeness:</span>{' '}
                                                      <span
                                                        className={`font-medium ${(q.aiLikeness ?? 0) > 0.7 ? 'text-rose-600' : 'text-gray-900'
                                                          }`}
                                                      >
                                                        {q.aiLikeness != null
                                                          ? Math.round(q.aiLikeness * 100) + '%'
                                                          : '—'}
                                                      </span>
                                                    </div>
                                                    <div>
                                                      <span className="text-gray-500">Genericness:</span>{' '}
                                                      <span className="font-medium text-gray-900">
                                                        {q.genericness != null
                                                          ? Math.round(q.genericness * 100) + '%'
                                                          : '—'}
                                                      </span>
                                                    </div>
                                                    <div>
                                                      <span className="text-gray-500">Structure:</span>{' '}
                                                      <span className="font-medium text-gray-900">
                                                        {q.structureDensity != null
                                                          ? Math.round(q.structureDensity * 100) + '%'
                                                          : '—'}
                                                      </span>
                                                    </div>
                                                    <div>
                                                      <span className="text-gray-500">Real Exp:</span>{' '}
                                                      <span
                                                        className={`font-medium ${(q.realExperience ?? 0) < 0.3 ? 'text-rose-600' : 'text-emerald-600'
                                                          }`}
                                                      >
                                                        {q.realExperience != null
                                                          ? Math.round(q.realExperience * 100) + '%'
                                                          : '—'}
                                                      </span>
                                                    </div>
                                                  </div>
                                                  {q.notes && (
                                                    <p className="text-xs text-gray-600 mt-2 italic">{q.notes}</p>
                                                  )}
                                                </div>
                                              )
                                            )}
                                          </div>
                                        )}
                                      <div className="group inline-block relative cursor-help">
                                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                                          Calculated Criteria
                                        </span>
                                        <div
                                          role="tooltip"
                                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                                        >
                                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                                          {(() => {
                                            const details =
                                              (interviewProctoringData.categories.transcriptAnalysis || {}).details || {};
                                            const sampleCount = details.sampleCount ?? 0;
                                            const suspiciousCount = details.suspiciousCount ?? 0;
                                            const suspiciousRatePercent = details.suspiciousRate ?? 0;
                                            const suspiciousRate = (suspiciousRatePercent || 0) / 100;
                                            const baseFromCount = suspiciousCount * 1.5;
                                            const curveFromRate = Math.pow(suspiciousRate || 0, 2) * 35;
                                            const rawScore = baseFromCount + curveFromRate;
                                            const roundedScore = Math.round(rawScore);
                                            if (!sampleCount) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  AI-assisted score is 0 because there were no answers available to analyse.
                                                </p>
                                              );
                                            }
                                            if (!suspiciousCount) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  All {sampleCount} analysed answers looked human. With 0 suspicious answers,
                                                  the AI-assisted score stays at 0 pts.
                                                </p>
                                              );
                                            }
                                            return (
                                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                                <p className="font-semibold text-gray-50">
                                                  How we reached {roundedScore} pts:
                                                </p>
                                                <p>
                                                  • Suspicious answers: {suspiciousCount} × 1.5 pts ={' '}
                                                  <span className="font-semibold">{baseFromCount.toFixed(1)} pts</span>
                                                </p>
                                                <p>
                                                  • Suspicious rate curve: ({(suspiciousRate * 100).toFixed(0)}% ÷ 100)² × 35
                                                  ≈ <span className="font-semibold">{curveFromRate.toFixed(1)} pts</span>
                                                </p>
                                                <p>
                                                  • Total: {baseFromCount.toFixed(1)} + {curveFromRate.toFixed(1)} ≈{' '}
                                                  <span className="font-semibold">{rawScore.toFixed(1)} pts</span> → rounded
                                                  to <span className="font-semibold">{roundedScore} pts</span> shown above.
                                                </p>
                                                <p className="text-gray-300">
                                                  In short, more AI-like answers and a higher suspicious rate push this score
                                                  up.
                                                </p>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : selectedProctoringCategory === 'responseDelay' &&
                                  interviewProctoringData.categories?.responseDelay ? (
                                  /* Response Timing detailed card */
                                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                                          <svg
                                            className="w-4.5 h-4.5 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M12 6v6l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                            Behaviour Signal
                                          </p>
                                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                                            Response Timing Breakdown
                                          </h2>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                                            Contribution
                                          </p>
                                          <p className="text-sm font-bold text-gray-900">
                                            +{interviewProctoringData.categories.responseDelay.score} pts
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                          How this works
                                        </p>
                                        <p className="text-sm text-gray-700">
                                          We look at how long the candidate waits before answering after each question ends.
                                          Very similar delays or extremely long pauses can indicate scripted answers or
                                          external help.
                                        </p>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Samples
                                          </p>
                                          <p className="text-lg font-semibold text-gray-900">
                                            {interviewProctoringData.categories.responseDelay.details?.sampleCount ?? 0}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Mean Delay
                                          </p>
                                          <p className="text-lg font-semibold text-gray-900">
                                            {interviewProctoringData.categories.responseDelay.details?.meanDelayMs != null
                                              ? `${interviewProctoringData.categories.responseDelay.details.meanDelayMs}ms`
                                              : '—'}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Std Dev
                                          </p>
                                          <p className="text-lg font-semibold text-gray-900">
                                            {interviewProctoringData.categories.responseDelay.details?.stdDevDelayMs != null
                                              ? `${interviewProctoringData.categories.responseDelay.details.stdDevDelayMs}ms`
                                              : '—'}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Pattern
                                          </p>
                                          <p
                                            className={`text-sm font-semibold ${interviewProctoringData.categories.responseDelay.details?.isPatternSuspicious
                                              ? 'text-rose-600'
                                              : 'text-emerald-600'
                                              }`}
                                          >
                                            {interviewProctoringData.categories.responseDelay.details?.isPatternSuspicious
                                              ? 'Suspicious'
                                              : 'Normal'}
                                          </p>
                                        </div>
                                      </div>
                                      {interviewProctoringData.categories.responseDelay.details?.maxDelayMs != null && (
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 text-xs text-gray-700">
                                          <span className="text-gray-500">Max delay:</span>{' '}
                                          <span className="font-medium text-gray-900">
                                            {Math.round(
                                              (interviewProctoringData.categories.responseDelay.details.maxDelayMs / 1000) *
                                              10
                                            ) / 10}
                                            s
                                          </span>
                                          {interviewProctoringData.categories.responseDelay.details?.longPauseCount !=
                                            null && (
                                              <>
                                                <span className="mx-2 text-gray-300">•</span>
                                                <span className="text-gray-500">≥1 min pauses:</span>{' '}
                                                <span
                                                  className={`font-semibold ${interviewProctoringData.categories.responseDelay.details.longPauseCount > 0
                                                    ? 'text-rose-700'
                                                    : 'text-gray-900'
                                                    }`}
                                                >
                                                  {
                                                    interviewProctoringData.categories.responseDelay.details.longPauseCount
                                                  }
                                                </span>
                                              </>
                                            )}
                                        </div>
                                      )}
                                      {/* Calculated Criteria — Response Timing */}
                                      <div className="group inline-block relative cursor-help mt-4">
                                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                                          Calculated Criteria
                                        </span>
                                        <div
                                          role="tooltip"
                                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                                        >
                                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                                          {(() => {
                                            const details =
                                              interviewProctoringData.categories.responseDelay.details || {};
                                            const sampleCount = details.sampleCount ?? 0;
                                            const delayIsSuspicious = details.isPatternSuspicious ?? false;
                                            const longPauseCount = details.longPauseCount ?? 0;
                                            if (!sampleCount) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  Response Timing score is 0 because there were no timing samples to analyse.
                                                </p>
                                              );
                                            }
                                            let rawDelayScore = 0;
                                            if (delayIsSuspicious) rawDelayScore += 10;
                                            if (longPauseCount > 0) {
                                              rawDelayScore += 40 + Math.min(longPauseCount - 1, 3) * 10;
                                            }
                                            const delayWeight = 0.5;
                                            const weightedScore = rawDelayScore * delayWeight;
                                            const finalScore = Math.round(weightedScore);
                                            if (!rawDelayScore) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  Wait times looked natural (pattern normal and no ≥1 minute pauses), so
                                                  Response Timing adds 0 pts.
                                                </p>
                                              );
                                            }
                                            return (
                                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                                <p className="font-semibold text-gray-50">
                                                  How we reached {finalScore} pts:
                                                </p>
                                                <p>
                                                  • Pattern flag:{' '}
                                                  <span className="font-semibold">
                                                    {delayIsSuspicious ? '+10' : '0'} raw pts
                                                  </span>
                                                </p>
                                                <p>
                                                  • Long pauses (≥1 min): {longPauseCount} →{' '}
                                                  <span className="font-semibold">
                                                    {longPauseCount > 0
                                                      ? 40 + Math.min(longPauseCount - 1, 3) * 10
                                                      : 0}{' '}
                                                    raw pts
                                                  </span>
                                                </p>
                                                <p>
                                                  • Raw total: {rawDelayScore} pts × weight 0.5 ={' '}
                                                  <span className="font-semibold">
                                                    {weightedScore.toFixed(1)} pts
                                                  </span>{' '}
                                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                                  above.
                                                </p>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : selectedProctoringCategory === 'speechPattern' &&
                                  interviewProctoringData.categories?.speechPattern ? (
                                  /* Speech Patterns detailed card */
                                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                                          <svg
                                            className="w-4.5 h-4.5 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M5 8.25v7.5M9 6v12m4-9v6m4-8.25v10.5"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                            Behaviour Signal
                                          </p>
                                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                                            Speech Patterns Breakdown
                                          </h2>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                                            Contribution
                                          </p>
                                          <p className="text-sm font-bold text-gray-900">
                                            +{interviewProctoringData.categories.speechPattern.score} pts
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                          How this works
                                        </p>
                                        <p className="text-sm text-gray-700">
                                          We analyse speaking speed and how much it varies. Very flat speed across the whole
                                          interview can look like someone reading from a script instead of speaking naturally.
                                        </p>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Samples
                                          </p>
                                          <p className="text-lg font-semibold text-gray-900">
                                            {interviewProctoringData.categories.speechPattern.details?.sampleCount ?? 0}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Mean WPM
                                          </p>
                                          <p className="text-lg font-semibold text-gray-900">
                                            {interviewProctoringData.categories.speechPattern.details?.meanWpm != null
                                              ? Math.round(
                                                interviewProctoringData.categories.speechPattern.details.meanWpm,
                                              )
                                              : '—'}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            CV (WPM)
                                          </p>
                                          <p className="text-lg font-semibold text-gray-900">
                                            {interviewProctoringData.categories.speechPattern.details?.cvWpm != null
                                              ? interviewProctoringData.categories.speechPattern.details.cvWpm.toFixed(2)
                                              : '—'}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Pattern
                                          </p>
                                          <p
                                            className={`text-sm font-semibold ${interviewProctoringData.categories.speechPattern.details?.isPatternSuspicious
                                              ? 'text-rose-600'
                                              : 'text-emerald-600'
                                              }`}
                                          >
                                            {interviewProctoringData.categories.speechPattern.details?.isPatternSuspicious
                                              ? 'Suspicious'
                                              : 'Normal'}
                                          </p>
                                        </div>
                                      </div>
                                      {/* Calculated Criteria — Speech Patterns */}
                                      <div className="group inline-block relative cursor-help mt-4">
                                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                                          Calculated Criteria
                                        </span>
                                        <div
                                          role="tooltip"
                                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                                        >
                                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                                          {(() => {
                                            const details =
                                              interviewProctoringData.categories.speechPattern.details || {};
                                            const sampleCount = details.sampleCount ?? 0;
                                            const isPatternSuspicious = details.isPatternSuspicious ?? false;
                                            if (!sampleCount) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  Speech Patterns score is 0 because there were no speech samples to analyse.
                                                </p>
                                              );
                                            }
                                            const rawSpeechScore = isPatternSuspicious ? 12.5 : 0;
                                            const speechWeight = 0.5;
                                            const weightedScore = rawSpeechScore * speechWeight;
                                            const finalScore = Math.round(weightedScore);
                                            if (!rawSpeechScore) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  Variation in speaking speed looked normal, so Speech Patterns adds 0 pts.
                                                </p>
                                              );
                                            }
                                            return (
                                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                                <p className="font-semibold text-gray-50">
                                                  How we reached {finalScore} pts:
                                                </p>
                                                <p>
                                                  • Pattern flag:{' '}
                                                  <span className="font-semibold">
                                                    {isPatternSuspicious ? '12.5' : '0'} raw pts
                                                  </span>
                                                </p>
                                                <p>
                                                  • Raw total × weight: {rawSpeechScore} × 0.5 ={' '}
                                                  <span className="font-semibold">
                                                    {weightedScore.toFixed(1)} pts
                                                  </span>{' '}
                                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                                  above.
                                                </p>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : selectedProctoringCategory === 'videoPresence' &&
                                  interviewProctoringData.categories?.videoPresence ? (
                                  /* Video Presence detailed card */
                                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                                          <svg
                                            className="w-4.5 h-4.5 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M15.75 10.5V6A2.25 2.25 0 0013.5 3.75h-9A2.25 2.25 0 002.25 6v8.25A2.25 2.25 0 004.5 16.5h9a2.25 2.25 0 002.25-2.25v-1.128l3.22 1.932A.75.75 0 0021 13.5v-3a.75.75 0 00-.38-.654L15.75 8.25"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                            Video Signal
                                          </p>
                                          <h2 className="text-base font-bold text-gray-900 leading-tight">Video Presence</h2>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                                            Contribution
                                          </p>
                                          <p className="text-sm font-bold text-gray-900">
                                            +{interviewProctoringData.categories.videoPresence.score} pts
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                          How this works
                                        </p>
                                        <p className="text-sm text-gray-700">
                                          We track how often and how long the candidate disappears from camera. More time
                                          off-screen means more risk points.
                                        </p>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Not Visible Events
                                          </p>
                                          <p
                                            className={`text-lg font-semibold ${interviewProctoringData.categories.videoPresence.details
                                              ?.faceNotVisibleEvents > 0
                                              ? 'text-rose-600'
                                              : 'text-gray-900'
                                              }`}
                                          >
                                            {interviewProctoringData.categories.videoPresence.details
                                              ?.faceNotVisibleEvents ?? 0}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Total Duration (s)
                                          </p>
                                          <p
                                            className={`text-lg font-semibold ${interviewProctoringData.categories.videoPresence.details
                                              ?.faceNotVisibleTotalSeconds > 0
                                              ? 'text-rose-600'
                                              : 'text-gray-900'
                                              }`}
                                          >
                                            {interviewProctoringData.categories.videoPresence.details
                                              ?.faceNotVisibleTotalSeconds ?? 0}
                                            s
                                          </p>
                                        </div>
                                      </div>
                                      {/* Calculated Criteria — Video Presence */}
                                      <div className="group inline-block relative cursor-help mt-4">
                                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                                          Calculated Criteria
                                        </span>
                                        <div
                                          role="tooltip"
                                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                                        >
                                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                                          {(() => {
                                            const details =
                                              interviewProctoringData.categories.videoPresence.details || {};
                                            const faceNotVisibleEvents = details.faceNotVisibleEvents ?? 0;
                                            const totalSeconds = details.faceNotVisibleTotalSeconds ?? 0;
                                            if (!faceNotVisibleEvents && !totalSeconds) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  The candidate stayed on camera the whole time, so Video Presence adds 0 pts.
                                                </p>
                                              );
                                            }
                                            const basePresencePts = faceNotVisibleEvents * 5;
                                            const extraPresencePts =
                                              Math.min(Math.floor(totalSeconds / 10), 6) * 1.5;
                                            const rawPresenceScore = basePresencePts + extraPresencePts;
                                            const presenceWeight = 0.8;
                                            const weightedScore = rawPresenceScore * presenceWeight;
                                            const finalScore = Math.round(weightedScore);
                                            return (
                                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                                <p className="font-semibold text-gray-50">
                                                  How we reached {finalScore} pts:
                                                </p>
                                                <p>
                                                  • Off‑camera events: {faceNotVisibleEvents} × 5 ={' '}
                                                  <span className="font-semibold">
                                                    {basePresencePts} raw pts
                                                  </span>
                                                </p>
                                                <p>
                                                  • Extra time off‑screen: floor({totalSeconds} ÷ 10) capped at 6 →{' '}
                                                  <span className="font-semibold">
                                                    {Math.min(Math.floor(totalSeconds / 10), 6)} × 1.5 = {extraPresencePts} raw
                                                    pts
                                                  </span>
                                                </p>
                                                <p>
                                                  • Raw total × weight: {rawPresenceScore} × 0.8 ={' '}
                                                  <span className="font-semibold">
                                                    {weightedScore.toFixed(1)} pts
                                                  </span>{' '}
                                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                                  above.
                                                </p>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : selectedProctoringCategory === 'videoMultipleFaces' &&
                                  interviewProctoringData.categories?.videoMultipleFaces ? (
                                  /* Multiple Faces Detected detailed card */
                                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                                          <svg
                                            className="w-4.5 h-4.5 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M9.75 6.75a3 3 0 11-6 0 3 3 0 016 0zm10.5 0a3 3 0 11-6 0 3 3 0 016 0zM4.5 15.75a4.5 4.5 0 019 0v.75H4.5v-.75zm9.75 0a4.5 4.5 0 018.25 2.25v.75H14.25v-.75a4.5 4.5 0 010-2.25z"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                            Video Signal
                                          </p>
                                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                                            Multiple Faces Detected
                                          </h2>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                                            Contribution
                                          </p>
                                          <p className="text-sm font-bold text-gray-900">
                                            +{interviewProctoringData.categories.videoMultipleFaces.score} pts
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                          How this works
                                        </p>
                                        <p className="text-sm text-gray-700">
                                          We look for more than one face in the frame. Every time another person shows up, it
                                          increases the risk score.
                                        </p>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Multiple Faces Events
                                          </p>
                                          <p
                                            className={`text-lg font-semibold ${interviewProctoringData.categories.videoMultipleFaces.details?.multiFaceEvents >
                                              0
                                              ? 'text-rose-600'
                                              : 'text-gray-900'
                                              }`}
                                          >
                                            {interviewProctoringData.categories.videoMultipleFaces.details?.multiFaceEvents ??
                                              0}
                                          </p>
                                        </div>
                                      </div>
                                      {/* Calculated Criteria — Multiple Faces Detected */}
                                      <div className="group inline-block relative cursor-help mt-4">
                                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                                          Calculated Criteria
                                        </span>
                                        <div
                                          role="tooltip"
                                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                                        >
                                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                                          {(() => {
                                            const details =
                                              interviewProctoringData.categories.videoMultipleFaces.details || {};
                                            const multiFaceEvents = details.multiFaceEvents ?? 0;
                                            if (!multiFaceEvents) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  No extra faces were detected in the frame, so Multiple Faces Detected adds 0
                                                  pts.
                                                </p>
                                              );
                                            }
                                            const eventsCapped = Math.min(multiFaceEvents, 3);
                                            const rawMultiFaceScore = eventsCapped * 10;
                                            const multiFaceWeight = 1.0;
                                            const weightedScore = rawMultiFaceScore * multiFaceWeight;
                                            const finalScore = Math.round(weightedScore);
                                            return (
                                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                                <p className="font-semibold text-gray-50">
                                                  How we reached {finalScore} pts:
                                                </p>
                                                <p>
                                                  • Multiple‑face events: {multiFaceEvents} (capped at 3) → {eventsCapped} × 10
                                                  = <span className="font-semibold">{rawMultiFaceScore} raw pts</span>
                                                </p>
                                                <p>
                                                  • Raw total × weight: {rawMultiFaceScore} × 1.0 ={' '}
                                                  <span className="font-semibold">
                                                    {weightedScore.toFixed(1)} pts
                                                  </span>{' '}
                                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                                  above.
                                                </p>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                      {interviewProctoringData.categories.videoMultipleFaces.details?.evidence?.length >
                                        0 && (
                                          <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 mt-2">
                                            <p className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-[0.12em]">
                                              Evidence Frames
                                            </p>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                              {interviewProctoringData.categories.videoMultipleFaces.details.evidence.map(
                                                (url, i) => (
                                                  <a
                                                    key={i}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="block relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:opacity-90 transition-opacity"
                                                  >
                                                    <img
                                                      src={url}
                                                      alt="Evidence"
                                                      className="absolute inset-0 w-full h-full object-cover"
                                                    />
                                                  </a>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                ) : selectedProctoringCategory === 'videoEyeGaze' &&
                                  interviewProctoringData.categories?.videoEyeGaze ? (
                                  /* Eye Gaze detailed card */
                                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                                          <svg
                                            className="w-4.5 h-4.5 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12 18 18.75 12 18.75 2.25 12 2.25 12zm9.75 3a3 3 0 100-6 3 3 0 000 6z"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                            Video Signal
                                          </p>
                                          <h2 className="text-base font-bold text-gray-900 leading-tight">Eye Gaze</h2>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                                            Contribution
                                          </p>
                                          <p className="text-sm font-bold text-gray-900">
                                            +{interviewProctoringData.categories.videoEyeGaze.score} pts
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                          How this works
                                        </p>
                                        <p className="text-sm text-gray-700">
                                          We track how often the candidate’s eyes clearly move away from the screen or camera
                                          for noticeable chunks of time. More long looks away add more points.
                                        </p>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Looking Away Events
                                          </p>
                                          <p
                                            className={`text-lg font-semibold ${interviewProctoringData.categories.videoEyeGaze.details?.eyeGazeAwayEvents > 0
                                              ? 'text-orange-600'
                                              : 'text-gray-900'
                                              }`}
                                          >
                                            {interviewProctoringData.categories.videoEyeGaze.details
                                              ?.eyeGazeAwayEvents ?? 0}
                                          </p>
                                        </div>
                                      </div>
                                      {/* Calculated Criteria — Eye Gaze */}
                                      <div className="group inline-block relative cursor-help mt-4">
                                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                                          Calculated Criteria
                                        </span>
                                        <div
                                          role="tooltip"
                                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                                        >
                                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                                          {(() => {
                                            const details =
                                              interviewProctoringData.categories.videoEyeGaze.details || {};
                                            const eyeGazeAwayEvents = details.eyeGazeAwayEvents ?? 0;
                                            if (!eyeGazeAwayEvents) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  Clear eye contact was maintained (no long looks away), so Eye Gaze adds 0
                                                  pts.
                                                </p>
                                              );
                                            }
                                            const cat = interviewProctoringData.categories.videoEyeGaze;
                                            const { eventCap, rawPtsPerEvent, weight: defaultWeight } =
                                              PROCTORING_EYE_GAZE_CRITERIA;
                                            const eventsCapped = Math.min(eyeGazeAwayEvents, eventCap);
                                            const rawEyeGazeScore =
                                              typeof cat?.rawScore === 'number'
                                                ? cat.rawScore
                                                : eventsCapped * rawPtsPerEvent;
                                            const eyeGazeWeight =
                                              typeof cat?.weight === 'number' ? cat.weight : defaultWeight;
                                            const weightedScore = rawEyeGazeScore * eyeGazeWeight;
                                            const finalScore =
                                              typeof cat?.score === 'number'
                                                ? cat.score
                                                : Math.round(weightedScore);
                                            return (
                                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                                <p className="font-semibold text-gray-50">
                                                  How we reached {finalScore} pts:
                                                </p>
                                                <p>
                                                  • Looking‑away events: {eyeGazeAwayEvents} (capped at {eventCap}) →{' '}
                                                  {eventsCapped} × {rawPtsPerEvent} ={' '}
                                                  <span className="font-semibold">{rawEyeGazeScore} raw pts</span>
                                                </p>
                                                <p>
                                                  • Raw total × weight: {rawEyeGazeScore} × {eyeGazeWeight} ={' '}
                                                  <span className="font-semibold">
                                                    {weightedScore.toFixed(1)} pts
                                                  </span>{' '}
                                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                                  above.
                                                </p>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                      {interviewProctoringData.categories.videoEyeGaze.details?.evidence?.length > 0 && (
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 mt-2">
                                          <p className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-[0.12em]">
                                            Evidence Frames
                                          </p>
                                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {interviewProctoringData.categories.videoEyeGaze.details.evidence.map(
                                              (url, i) => (
                                                <a
                                                  key={i}
                                                  href={url}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="block relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:opacity-90 transition-opacity"
                                                >
                                                  <img
                                                    src={url}
                                                    alt="Evidence"
                                                    className="absolute inset-0 w-full h-full object-cover"
                                                  />
                                                </a>
                                              ),
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : selectedProctoringCategory === 'videoHeadPose' &&
                                  interviewProctoringData.categories?.videoHeadPose ? (
                                  /* Head Pose detailed card */
                                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                                          <svg
                                            className="w-4.5 h-4.5 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M12 4.5c-2.485 0-4.5 2.015-4.5 4.5S9.515 13.5 12 13.5 16.5 11.485 16.5 9 14.485 4.5 12 4.5zM6.75 18.75A5.25 5.25 0 0112 15h0a5.25 5.25 0 015.25 3.75"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                            Video Signal
                                          </p>
                                          <h2 className="text-base font-bold text-gray-900 leading-tight">Head Pose</h2>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                                            Contribution
                                          </p>
                                          <p className="text-sm font-bold text-gray-900">
                                            +{interviewProctoringData.categories.videoHeadPose.score} pts
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                          How this works
                                        </p>
                                        <p className="text-sm text-gray-700">
                                          We watch for moments where the candidate’s head is turned far away from the screen,
                                          like reading from something off to the side. More strong turns away increase the
                                          risk.
                                        </p>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Head Turned Away Events
                                          </p>
                                          <p
                                            className={`text-lg font-semibold ${interviewProctoringData.categories.videoHeadPose.details
                                              ?.headPoseAwayEvents > 0
                                              ? 'text-rose-600'
                                              : 'text-gray-900'
                                              }`}
                                          >
                                            {interviewProctoringData.categories.videoHeadPose.details
                                              ?.headPoseAwayEvents ?? 0}
                                          </p>
                                        </div>
                                      </div>
                                      {/* Calculated Criteria — Head Pose */}
                                      <div className="group inline-block relative cursor-help mt-4">
                                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                                          Calculated Criteria
                                        </span>
                                        <div
                                          role="tooltip"
                                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                                        >
                                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                                          {(() => {
                                            const details =
                                              interviewProctoringData.categories.videoHeadPose.details || {};
                                            const headPoseAwayEvents = details.headPoseAwayEvents ?? 0;
                                            if (!headPoseAwayEvents) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  The candidate did not clearly turn their head away from the screen, so Head
                                                  Pose adds 0 pts.
                                                </p>
                                              );
                                            }
                                            const cat = interviewProctoringData.categories.videoHeadPose;
                                            const eventsCapped = Math.min(headPoseAwayEvents, 5);
                                            const rawHeadPoseScore =
                                              typeof cat?.rawScore === 'number'
                                                ? cat.rawScore
                                                : eventsCapped * 4;
                                            const headPoseWeight =
                                              typeof cat?.weight === 'number' ? cat.weight : 1.0;
                                            const weightedScore = rawHeadPoseScore * headPoseWeight;
                                            const finalScore =
                                              typeof cat?.score === 'number'
                                                ? cat.score
                                                : Math.round(weightedScore);
                                            return (
                                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                                <p className="font-semibold text-gray-50">
                                                  How we reached {finalScore} pts:
                                                </p>
                                                <p>
                                                  • Head‑turned‑away events: {headPoseAwayEvents} (capped at 5) → {eventsCapped}
                                                  × 4 = <span className="font-semibold">{rawHeadPoseScore} raw pts</span>
                                                </p>
                                                <p>
                                                  • Raw total × weight: {rawHeadPoseScore} × {headPoseWeight} ={' '}
                                                  <span className="font-semibold">
                                                    {weightedScore.toFixed(1)} pts
                                                  </span>{' '}
                                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                                  above.
                                                </p>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                      {interviewProctoringData.categories.videoHeadPose.details?.evidence?.length > 0 && (
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 mt-2">
                                          <p className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-[0.12em]">
                                            Evidence Frames
                                          </p>
                                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {interviewProctoringData.categories.videoHeadPose.details.evidence.map(
                                              (url, i) => (
                                                <a
                                                  key={i}
                                                  href={url}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="block relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:opacity-90 transition-opacity"
                                                >
                                                  <img
                                                    src={url}
                                                    alt="Evidence"
                                                    className="absolute inset-0 w-full h-full object-cover"
                                                  />
                                                </a>
                                              ),
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : selectedProctoringCategory === 'videoPhoneDetection' &&
                                  interviewProctoringData.categories?.videoPhoneDetection ? (
                                  /* Phone Detection detailed card */
                                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                                          <svg
                                            className="w-4.5 h-4.5 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M7.5 3.75h9A1.75 1.75 0 0118.25 5.5v13A1.75 1.75 0 0116.5 20.25h-9A1.75 1.75 0 015.75 18.5v-13A1.75 1.75 0 017.5 3.75zm4.5 12.5h.01"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                            Video Signal
                                          </p>
                                          <h2 className="text-base font-bold text-gray-900 leading-tight">Phone Detected</h2>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                                            Contribution
                                          </p>
                                          <p className="text-sm font-bold text-gray-900">
                                            +{interviewProctoringData.categories.videoPhoneDetection.score} pts
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                          How this works
                                        </p>
                                        <p className="text-sm text-gray-700">
                                          We flag moments where a phone or similar device is clearly visible in the frame. Each
                                          clear sighting is treated as a serious incident.
                                        </p>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Critical Incidents
                                          </p>
                                          <p className="text-lg font-bold text-rose-700">
                                            {interviewProctoringData.categories.videoPhoneDetection.details
                                              ?.phoneDetectedEvents ?? 0}
                                          </p>
                                        </div>
                                      </div>
                                      {/* Calculated Criteria — Phone Detected */}
                                      <div className="group inline-block relative cursor-help mt-4">
                                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                                          Calculated Criteria
                                        </span>
                                        <div
                                          role="tooltip"
                                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                                        >
                                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                                          {(() => {
                                            const details =
                                              interviewProctoringData.categories.videoPhoneDetection.details || {};
                                            const phoneDetectedEvents = details.phoneDetectedEvents ?? 0;
                                            if (!phoneDetectedEvents) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  No phones were detected on camera, so Phone Detected adds 0 pts.
                                                </p>
                                              );
                                            }
                                            const cat = interviewProctoringData.categories.videoPhoneDetection;
                                            const extraEvents = Math.min(phoneDetectedEvents - 1, 3);
                                            const rawPhoneScore =
                                              typeof cat?.rawScore === 'number'
                                                ? cat.rawScore
                                                : 20 + extraEvents * 7.5;
                                            const phoneWeight = typeof cat?.weight === 'number' ? cat.weight : 1.0;
                                            const weightedScore = rawPhoneScore * phoneWeight;
                                            const finalScore =
                                              typeof cat?.score === 'number'
                                                ? cat.score
                                                : Math.round(weightedScore);
                                            return (
                                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                                <p className="font-semibold text-gray-50">
                                                  How we reached {finalScore} pts:
                                                </p>
                                                <p>
                                                  • First phone detection:{' '}
                                                  <span className="font-semibold">20 raw pts</span>
                                                </p>
                                                <p>
                                                  • Extra detections (capped at 3): {phoneDetectedEvents - 1} → min(
                                                  {phoneDetectedEvents - 1}, 3) × 7.5 ={' '}
                                                  <span className="font-semibold">{extraEvents * 7.5} raw pts</span>
                                                </p>
                                                <p>
                                                  • Raw total × weight: {rawPhoneScore} × {phoneWeight} ={' '}
                                                  <span className="font-semibold">
                                                    {weightedScore.toFixed(1)} pts
                                                  </span>{' '}
                                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                                  above.
                                                </p>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                      {interviewProctoringData.categories.videoPhoneDetection.details?.evidence?.length >
                                        0 && (
                                          <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 mt-2">
                                            <p className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-[0.12em]">
                                              Evidence Frames
                                            </p>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                              {interviewProctoringData.categories.videoPhoneDetection.details.evidence.map(
                                                (url, i) => (
                                                  <a
                                                    key={i}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="block relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:opacity-90 transition-opacity"
                                                  >
                                                    <img
                                                      src={url}
                                                      alt="Evidence"
                                                      className="absolute inset-0 w-full h-full object-cover"
                                                    />
                                                  </a>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                ) : selectedProctoringCategory === 'videoIdentity' &&
                                  interviewProctoringData.categories?.videoIdentity ? (
                                  /* Face Identity detailed card */
                                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                                          <svg
                                            className="w-4.5 h-4.5 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 1.5c-3.038 0-5.5 2.015-5.5 4.5v1a.75.75 0 00.75.75h9.5a.75.75 0 00.75-.75v-1c0-2.485-2.462-4.5-5.5-4.5z"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                            Video Signal
                                          </p>
                                          <h2 className="text-base font-bold text-gray-900 leading-tight">Face Identity</h2>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                                            Contribution
                                          </p>
                                          <p className="text-sm font-bold text-gray-900">
                                            +{interviewProctoringData.categories.videoIdentity.score} pts
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                          How this works
                                        </p>
                                        <p className="text-sm text-gray-700">
                                          We compare the face on camera to the main candidate’s face. If a different person
                                          appears, the system adds risk points.
                                        </p>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Identity Mismatches
                                          </p>
                                          <p
                                            className={`text-lg font-semibold ${interviewProctoringData.categories.videoIdentity.details
                                              ?.identityMismatchEvents > 0
                                              ? 'text-rose-600'
                                              : 'text-gray-900'
                                              }`}
                                          >
                                            {interviewProctoringData.categories.videoIdentity.details
                                              ?.identityMismatchEvents ?? 0}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Min Similarity
                                          </p>
                                          <p className="text-lg font-semibold text-gray-900">
                                            {interviewProctoringData.categories.videoIdentity.details
                                              ?.identityMinSimilarity != null
                                              ? Math.round(
                                                interviewProctoringData.categories.videoIdentity.details
                                                  .identityMinSimilarity * 100,
                                              ) + '%'
                                              : '—'}
                                          </p>
                                        </div>
                                      </div>
                                      {/* Calculated Criteria — Face Identity */}
                                      <div className="group inline-block relative cursor-help mt-4">
                                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                                          Calculated Criteria
                                        </span>
                                        <div
                                          role="tooltip"
                                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                                        >
                                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                                          {(() => {
                                            const details =
                                              interviewProctoringData.categories.videoIdentity.details || {};
                                            const identityMismatchEvents = details.identityMismatchEvents ?? 0;
                                            if (!identityMismatchEvents) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  The face on camera matched the candidate throughout, so Face Identity adds 0
                                                  pts.
                                                </p>
                                              );
                                            }
                                            const cat = interviewProctoringData.categories.videoIdentity;
                                            const rawIdentityScore =
                                              typeof cat?.rawScore === 'number'
                                                ? cat.rawScore
                                                : 40 + Math.min(identityMismatchEvents - 1, 3) * 2.5;
                                            const identityWeight =
                                              typeof cat?.weight === 'number' ? cat.weight : 1.0;
                                            const weightedScore = rawIdentityScore * identityWeight;
                                            const finalScore =
                                              typeof cat?.score === 'number'
                                                ? cat.score
                                                : Math.round(weightedScore);
                                            return (
                                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                                <p className="font-semibold text-gray-50">
                                                  How we reached {finalScore} pts:
                                                </p>
                                                <p>
                                                  • First mismatch: base{' '}
                                                  <span className="font-semibold">40 raw pts</span>
                                                </p>
                                                <p>
                                                  • Extra mismatches (capped at 3): {identityMismatchEvents - 1} → min(
                                                  {identityMismatchEvents - 1}, 3) × 2.5 ={' '}
                                                  <span className="font-semibold">
                                                    {Math.min(identityMismatchEvents - 1, 3) * 2.5} raw pts
                                                  </span>
                                                </p>
                                                <p>
                                                  • Raw total × weight: {rawIdentityScore} × {identityWeight} ={' '}
                                                  <span className="font-semibold">
                                                    {weightedScore.toFixed(1)} pts
                                                  </span>{' '}
                                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                                  above.
                                                </p>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : selectedProctoringCategory === 'speakerDetection' &&
                                  interviewProctoringData.categories?.speakerDetection ? (
                                  /* Multiple Speaker Detection detailed card */
                                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                                          <svg
                                            className="w-4.5 h-4.5 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-9A2.25 2.25 0 002.25 5.25v9A2.25 2.25 0 004.5 16.5H9M9 12.75L11.25 15 15 9.75M18 9.75v8.25M21 12.75v5.25"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                                            Audio Signal
                                          </p>
                                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                                            Multiple Speaker Detection
                                          </h2>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                                            Contribution
                                          </p>
                                          <p className="text-sm font-bold text-gray-900">
                                            +{interviewProctoringData.categories.speakerDetection.score} pts
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                                          How this works
                                        </p>
                                        <p className="text-sm text-gray-700">
                                          We listen for more than one distinct voice while the candidate is answering. More
                                          extra voices and more questions affected mean a higher integrity risk.
                                        </p>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Number of Speakers
                                          </p>
                                          <p className="text-lg font-semibold text-gray-900">
                                            {interviewProctoringData.categories.speakerDetection.details?.uniqueSpeakers ??
                                              1}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Interventions
                                          </p>
                                          <p className="text-lg font-semibold text-rose-600">
                                            {interviewProctoringData.categories.speakerDetection.details?.totalEvents ?? 0}
                                          </p>
                                        </div>
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                                            Pattern
                                          </p>
                                          <p
                                            className={`text-sm font-semibold ${interviewProctoringData.categories.speakerDetection.details
                                              ?.isPatternSuspicious
                                              ? 'text-rose-600'
                                              : 'text-emerald-600'
                                              }`}
                                          >
                                            {interviewProctoringData.categories.speakerDetection.details
                                              ?.isPatternSuspicious
                                              ? 'Suspicious'
                                              : 'Normal'}
                                          </p>
                                        </div>
                                      </div>
                                      {interviewProctoringData.categories.speakerDetection.events?.length > 0 && (
                                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 space-y-2">
                                          <p className="text-xs font-semibold text-gray-700 uppercase tracking-[0.12em]">
                                            Detection Events
                                          </p>
                                          {interviewProctoringData.categories.speakerDetection.events.map((evt, idx) => (
                                            <div
                                              key={idx}
                                              className="p-2 bg-rose-50 border border-rose-200 rounded-lg text-sm flex items-start gap-1.5"
                                            >
                                              <span className="font-medium text-gray-900">
                                                Question {evt.questionIndex + 1}:
                                              </span>
                                              <span className="text-gray-700">
                                                {evt.speakerCount} speakers detected
                                                {evt.speakerLabels?.length > 0 &&
                                                  ` (${evt.speakerLabels.join(', ')})`}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {/* Calculated Criteria — Multiple Speaker Detection */}
                                      <div className="group inline-block relative cursor-help mt-4">
                                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                                          Calculated Criteria
                                        </span>
                                        <div
                                          role="tooltip"
                                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                                        >
                                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                                          {(() => {
                                            const details =
                                              interviewProctoringData.categories.speakerDetection.details || {};
                                            const events =
                                              interviewProctoringData.categories.speakerDetection.events || [];
                                            const questionsWithMultipleSpeakers =
                                              details.questionsWithMultipleSpeakers ??
                                              new Set(events.map(e => e.questionIndex)).size;
                                            const cat = interviewProctoringData.categories.speakerDetection;
                                            const questionsCapped = Math.min(questionsWithMultipleSpeakers, 3);
                                            const rawSpeakerScore =
                                              typeof cat?.rawScore === 'number'
                                                ? cat.rawScore
                                                : questionsCapped * 7.5;
                                            const speakerWeight =
                                              typeof cat?.weight === 'number' ? cat.weight : 0.6;
                                            const weightedScore = rawSpeakerScore * speakerWeight;
                                            const finalScore =
                                              typeof cat?.score === 'number'
                                                ? cat.score
                                                : Math.round(weightedScore);
                                            if (!questionsWithMultipleSpeakers) {
                                              return (
                                                <p className="text-xs text-gray-100 relative">
                                                  Only the candidate&apos;s voice was detected, so Multiple Speaker Detection
                                                  adds 0 pts.
                                                </p>
                                              );
                                            }
                                            return (
                                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                                <p className="font-semibold text-gray-50">
                                                  How we reached {finalScore} pts:
                                                </p>
                                                <p>
                                                  • Questions with multiple speakers: {questionsWithMultipleSpeakers} (capped
                                                  at 3) → {questionsCapped} × 7.5 ={' '}
                                                  <span className="font-semibold">{rawSpeakerScore} raw pts</span>
                                                </p>
                                                <p>
                                                  • Raw total × weight: {rawSpeakerScore} × {speakerWeight} ={' '}
                                                  <span className="font-semibold">
                                                    {weightedScore.toFixed(1)} pts
                                                  </span>{' '}
                                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                                  above.
                                                </p>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-lg flex flex-wrap items-center justify-end gap-2">
              {showHiddenAiReprocessButtons && (
                <button
                  type="button"
                  disabled={interviewAiReprocessLoading}
                  onClick={async () => {
                    const sid = selectedInterviewSession?.id || selectedInterviewSession?._id;
                    if (!sid) return;
                    setInterviewAiReprocessLoading(true);
                    try {
                      await axios.post(`/api/ai-interviews/session/${sid}/reprocess-ai-analysis`);
                      toast.success(
                        'Reprocessing started (recording → transcript if needed, proctoring, scoring). Reopen this candidate in 1–5 minutes or refresh data.'
                      );
                    } catch (e) {
                      toast.error(e.response?.data?.message || 'Could not start reprocessing');
                    } finally {
                      setInterviewAiReprocessLoading(false);
                    }
                  }}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${interviewAiReprocessLoading
                    ? 'bg-violet-100 text-violet-400 cursor-not-allowed'
                    : 'bg-violet-600 text-white hover:bg-violet-700'
                    }`}
                >
                  {interviewAiReprocessLoading ? (
                    <>
                      <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Starting…
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4" />
                      Re-run AI analysis
                    </>
                  )}
                </button>
              )}
              <button data-testid="modal-close-btn"
                onClick={closeInterviewReplayModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Assessment Score + Proctoring Details Modal (tabbed, mirrored from JobResults) */}
      {showAssessmentScoreModal && selectedAssessmentCandidate && (
        <div data-testid="modal-backdrop" className="fixed inset-0 bg-black/40 backdrop-blur-[2px] overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto border w-11/12 sm:w-full sm:max-w-5xl shadow-xl rounded-xl bg-white max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h3 className="text-2xl font-bold text-gray-900">Candidate Results</h3>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={async () => {
                      setAssessmentModalActiveTab('score');
                      if (!assessmentCandidateDetails && selectedAssessmentCandidate) {
                        await handleInlineViewAssessmentScore(selectedAssessmentCandidate);
                      }
                    }}
                    className={`px-3 py-1.5 text-sm font-semibold rounded-md border ${assessmentModalActiveTab === 'score'
                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                      : 'text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                  >
                    Assessment Score
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      setAssessmentModalActiveTab('proctoring');
                      if (!assessmentProctoringDetails && selectedAssessmentCandidate) {
                        await handleInlineViewAssessmentProctoring(selectedAssessmentCandidate);
                      }
                    }}
                    className={`px-3 py-1.5 text-sm font-semibold rounded-md border ${assessmentModalActiveTab === 'proctoring'
                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                      : 'text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                  >
                    Proctoring Details
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={closeAssessmentScoreModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {assessmentModalActiveTab === 'score' ? (
                <CandidateAssessmentScoreContent
                  details={assessmentCandidateDetails}
                  initialModuleIdx={assessmentModalInitialModuleIdx}
                />
              ) : assessmentModalActiveTab === 'proctoring' ? (
                <CandidateProctoringDetailsContent
                  session={assessmentProctoringDetails}
                  candidate={selectedAssessmentCandidate}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
      {/* Assessment Details Modal — compact, back to screening details */}
      {showAssessmentDetailsModal && (
        <div data-testid="modal-backdrop" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] overflow-y-auto p-4">
          <div className="relative w-full max-w-4xl rounded-xl border border-gray-200 bg-white shadow-xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex-shrink-0 flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50/80 px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  onClick={backToScreeningDetails}
                  className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-[#007BFF] hover:bg-[#007BFF]/10 transition-colors"
                  aria-label="Back to screening details"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2 min-w-0">
                  <AcademicCapIcon className="h-5 w-5 text-[#007BFF] flex-shrink-0" />
                  <h2 className="text-base font-semibold text-gray-900 truncate">Assessment details</h2>
                </div>
              </div>
              <button
                type="button"
                onClick={closeAssessmentDetails}
                className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {assessmentDetailsLoading ? (
                <div className="flex flex-col items-center justify-center py-10">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#007BFF] border-t-transparent" />
                  <p className="mt-2 text-sm text-gray-600">Loading…</p>
                </div>
              ) : assessmentDetails ? (
                <div className="space-y-4">
                  {/* Multi-module: list each module (in order) with its questions. */}
                  {(assessmentDetails.isMultiModule || assessmentDetails.isRubricBased) &&
                    Array.isArray(assessmentDetails.modules) &&
                    assessmentDetails.modules.length > 0 && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                        <h3 className="text-sm font-medium text-gray-900 px-3 py-2.5 bg-gray-50 border-b border-gray-200">
                          Modules ({assessmentDetails.modules.length})
                        </h3>
                        <div className="divide-y divide-gray-100">
                          {[...assessmentDetails.modules]
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((mod, index) => {
                              const test = (mod.libraryTestId && typeof mod.libraryTestId === 'object') ? mod.libraryTestId : {};
                              const qs = (Array.isArray(mod.questionBank) && mod.questionBank.length > 0)
                                ? mod.questionBank
                                : (Array.isArray(test.questions) && test.questions.length > 0)
                                  ? test.questions
                                  : (Array.isArray(assessmentDetails.content?.customQuestions)
                                    ? assessmentDetails.content.customQuestions.filter(
                                      (q) => String(q.moduleId) === String(mod._id) || String(q.moduleId) === String(test._id)
                                    )
                                    : []);
                              const moduleKey = test._id || mod._id || index;
                              const isExpanded = expandedAssessmentTests.has(moduleKey);
                              const qCount = (test.numberOfQuestions && test.numberOfQuestions > 0)
                                ? test.numberOfQuestions
                                : (mod.questionsToPick !== undefined && mod.questionsToPick !== null)
                                  ? mod.questionsToPick
                                  : qs.length;
                              return (
                                <div key={moduleKey} className="bg-white">
                                  <button
                                    type="button"
                                    onClick={() => toggleAssessmentTestExpansion(moduleKey)}
                                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-gray-50"
                                  >
                                    <span className="text-sm text-gray-900 truncate flex-1 min-w-0">
                                      #{index + 1} {mod.title || test.title || `Module ${index + 1}`}
                                    </span>
                                    <span className="text-xs text-gray-500 flex-shrink-0">
                                      {assessmentDetails.isRubricBased && mod.questionsToPick !== undefined && mod.questionsToPick !== null
                                        ? `Pick ${mod.questionsToPick} of ${qs.length} questions`
                                        : `${qs.length} questions`} • {test.estimatedTime ?? mod.duration ?? 0} min
                                    </span>
                                    {isExpanded ? (
                                      <ChevronUpIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    ) : (
                                      <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    )}
                                  </button>
                                  {isExpanded && qs.length > 0 && (
                                    <div className="px-3 pb-3 space-y-2">
                                      {qs.map((qi, qIndex) => {
                                        const q = qi.question || qi;
                                        if (!q) return null;
                                        return (
                                          <div
                                            key={q._id || qIndex}
                                            className="rounded-lg border border-gray-100 bg-gray-50 p-2.5"
                                          >
                                            <p className="text-sm font-medium text-gray-900">
                                              {qIndex + 1}. {q.title || q.content || 'Question'}
                                            </p>
                                            {Array.isArray(q.options) && q.options.length > 0 && (
                                              <ul className="mt-1.5 space-y-1">
                                                {q.options.map((opt, oi) => (
                                                  <li
                                                    key={oi}
                                                    className={`text-xs flex items-center gap-1.5 ${opt?.isCorrect
                                                        ? 'text-green-700 font-medium'
                                                        : 'text-gray-600'
                                                      }`}
                                                  >
                                                    <span className="w-4 h-4 inline-flex items-center justify-center rounded bg-white border border-gray-200 text-[10px]">
                                                      {String.fromCharCode(65 + oi)}
                                                    </span>
                                                    <span>{typeof opt === 'string' ? opt : opt?.text}</span>
                                                    {opt?.isCorrect && <span className="text-green-600">✓</span>}
                                                  </li>
                                                ))}
                                              </ul>
                                            )}
                                            {q.correctAnswer && (!Array.isArray(q.options) || q.options.length === 0) && (
                                              <div className="mt-2 text-xs text-gray-600 bg-green-50 px-2 py-1.5 rounded border border-green-200 font-medium inline-block">
                                                Correct: {q.correctAnswer === 'a' || q.correctAnswer === 'True' || q.correctAnswer === 'true' ? 'True' : q.correctAnswer === 'b' || q.correctAnswer === 'False' || q.correctAnswer === 'false' ? 'False' : q.correctAnswer}
                                              </div>
                                            )}
                                            <div className="text-[10px] text-gray-400 mt-2">
                                              Type: {q.type || 'Unknown'} • Points: {q.points || 1}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {isExpanded && qs.length === 0 && (
                                    <div className="px-3 pb-3 text-xs text-gray-500">
                                      No questions found for this module.
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                  {(!assessmentDetails.isMultiModule ||
                    (assessmentDetails.isRubricBased &&
                      ((assessmentDetails.content?.tests?.length > 0) ||
                        (assessmentDetails.content?.questions?.length > 0) ||
                        (assessmentDetails.content?.customQuestions?.length > 0)))) && assessmentDetails.content && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mt-4">
                        {assessmentDetails.content.tests &&
                          assessmentDetails.content.tests.length > 0 && (
                            <div>
                              <h3 className="text-sm font-medium text-gray-900 px-3 py-2.5 bg-gray-50 border-b border-gray-200">
                                Tests from Library ({assessmentDetails.content.tests.length})
                              </h3>
                              <div className="divide-y divide-gray-100">
                                {assessmentDetails.content.tests.map((testItem, index) => {
                                  const testId = testItem.test?._id || testItem.test?.id || index;
                                  const isExpanded = expandedAssessmentTests.has(testId);
                                  return (
                                    <div key={testId} className="bg-white">
                                      <button
                                        type="button"
                                        onClick={() => toggleAssessmentTestExpansion(testId)}
                                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-gray-50"
                                      >
                                        <span className="text-sm text-gray-900 truncate flex-1 min-w-0">
                                          #{index + 1} {testItem.test?.title}
                                        </span>
                                        <span className="text-xs text-gray-500 flex-shrink-0">
                                          {testItem.test?.numberOfQuestions} questions • {testItem.test?.estimatedTime} min
                                        </span>
                                        {isExpanded ? (
                                          <ChevronUpIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                        ) : (
                                          <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                        )}
                                      </button>
                                      {isExpanded &&
                                        testItem.test?.questions &&
                                        testItem.test.questions.length > 0 && (
                                          <div className="px-3 pb-3 pt-0 border-t border-gray-100 bg-gray-50/50">
                                            <h5 className="text-sm font-medium text-gray-900 mb-3">
                                              Questions in this test:
                                            </h5>
                                            <div className="space-y-3">
                                              {testItem.test.questions.map((questionItem, qIndex) => {
                                                const question =
                                                  questionItem.question || questionItem;
                                                if (!question) return null;
                                                return (
                                                  <div
                                                    key={question._id || qIndex}
                                                    className="pl-4 border-l-2 border-gray-200"
                                                  >
                                                    <div className="text-sm font-medium text-gray-900 mb-2">
                                                      Q{qIndex + 1}: {question.title}
                                                    </div>
                                                    {question.content &&
                                                      (() => {
                                                        const titleNorm = String(question.title || '')
                                                          .replace(/\s+/g, ' ')
                                                          .trim()
                                                          .toLowerCase();
                                                        const contentNorm = String(question.content || '')
                                                          .replace(/\s+/g, ' ')
                                                          .trim()
                                                          .toLowerCase();
                                                        const isRedundant =
                                                          !contentNorm ||
                                                          contentNorm === titleNorm ||
                                                          contentNorm.startsWith(titleNorm) ||
                                                          titleNorm.startsWith(contentNorm);
                                                        return !isRedundant;
                                                      })() && (
                                                        <div className="text-xs text-gray-600 mb-2">
                                                          {question.content}
                                                        </div>
                                                      )}
                                                    {question.options &&
                                                      question.options.length > 0 && (
                                                        <div className="mb-2">
                                                          <div className="text-xs font-medium text-gray-700 mb-1">
                                                            Options:
                                                          </div>
                                                          <div className="space-y-1">
                                                            {question.options.map(
                                                              (option, optIndex) => (
                                                                <div
                                                                  key={optIndex}
                                                                  className="flex items-center space-x-2"
                                                                >
                                                                  <span
                                                                    className={`text-xs px-2 py-1 rounded ${option && option.isCorrect
                                                                      ? 'bg-green-100 text-green-800 font-medium'
                                                                      : 'bg-gray-100 text-gray-600'
                                                                      }`}
                                                                  >
                                                                    {String.fromCharCode(
                                                                      65 + optIndex
                                                                    )}
                                                                    . {option && option.text}
                                                                  </span>
                                                                  {option && option.isCorrect && (
                                                                    <span className="text-xs text-green-600 font-medium">
                                                                      ✓ Correct
                                                                    </span>
                                                                  )}
                                                                </div>
                                                              )
                                                            )}
                                                          </div>
                                                        </div>
                                                      )}
                                                    {(!question.options ||
                                                      question.options.length === 0) &&
                                                      question.correctAnswer && (
                                                        <div className="mb-2">
                                                          <div className="text-xs font-medium text-gray-700">
                                                            Correct Answer:
                                                          </div>
                                                          <div className="text-xs text-gray-600 bg-green-50 px-2 py-1 rounded">
                                                            {question.correctAnswer}
                                                          </div>
                                                        </div>
                                                      )}
                                                    <div className="text-xs text-gray-500">
                                                      {question.type} • {questionItem.points || 1}{' '}
                                                      point
                                                      {(questionItem.points || 1) !== 1 ? 's' : ''}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        {assessmentDetails.content.questions &&
                          assessmentDetails.content.questions.length > 0 && (
                            <div className="border-t border-gray-200">
                              <h3 className="text-sm font-medium text-gray-900 px-3 py-2.5 bg-gray-50 border-b border-gray-200">
                                Questions from Library ({assessmentDetails.content.questions.length})
                              </h3>
                              <div className="divide-y divide-gray-100">
                                {assessmentDetails.content.questions.map((questionItem, index) => {
                                  const question = questionItem.question;
                                  if (!question) return null;
                                  const isExpanded = expandedAssessmentLibraryQuestions.has(index);
                                  const titleText = question.title || question.content || 'Question';
                                  return (
                                    <div key={question._id || index} className="bg-white">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setExpandedAssessmentLibraryQuestions((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(index)) next.delete(index);
                                            else next.add(index);
                                            return next;
                                          });
                                        }}
                                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-gray-50"
                                      >
                                        <span className="text-sm text-gray-900 truncate flex-1 min-w-0">
                                          #{index + 1} {titleText.length > 60 ? `${titleText.slice(0, 60)}…` : titleText}
                                        </span>
                                        <span className="text-xs text-gray-500 flex-shrink-0">{question.type}</span>
                                        {isExpanded ? (
                                          <ChevronUpIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                        ) : (
                                          <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                        )}
                                      </button>
                                      {isExpanded && (
                                        <div className="px-3 pb-3 pt-0 border-t border-gray-100 bg-gray-50/50">
                                          {question.content &&
                                            (() => {
                                              const titleNorm = String(question.title || '')
                                                .replace(/\s+/g, ' ')
                                                .trim()
                                                .toLowerCase();
                                              const contentNorm = String(question.content || '')
                                                .replace(/\s+/g, ' ')
                                                .trim()
                                                .toLowerCase();
                                              const isRedundant =
                                                !contentNorm ||
                                                contentNorm === titleNorm ||
                                                contentNorm.startsWith(titleNorm) ||
                                                titleNorm.startsWith(contentNorm);
                                              return !isRedundant;
                                            })() && (
                                              <div className="text-xs text-gray-600 mb-2">{question.content}</div>
                                            )}
                                          <div className="text-xs text-gray-500">{question.type}</div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        {!assessmentDetails.isRubricBased &&
                          assessmentDetails.content.customQuestions &&
                          assessmentDetails.content.customQuestions.length > 0 && (
                            <div className="border-t border-gray-200">
                              <h3 className="text-sm font-medium text-gray-900 px-3 py-2.5 bg-gray-50 border-b border-gray-200">
                                Custom Questions ({assessmentDetails.content.customQuestions.length})
                              </h3>
                              <div className="divide-y divide-gray-100">
                                {assessmentDetails.content.customQuestions.map((question, index) => {
                                  const isExpanded = expandedAssessmentCustomQuestions.has(index);
                                  const titleText = question.title || question.content || 'Question';
                                  return (
                                    <div key={question._id || index} className="bg-white">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setExpandedAssessmentCustomQuestions((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(index)) next.delete(index);
                                            else next.add(index);
                                            return next;
                                          });
                                        }}
                                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-gray-50"
                                      >
                                        <span className="text-sm text-gray-900 truncate flex-1 min-w-0">
                                          #{index + 1} {titleText.length > 60 ? `${titleText.slice(0, 60)}…` : titleText}
                                        </span>
                                        <span className="text-xs text-gray-500 flex-shrink-0">{question.type || 'Custom'}</span>
                                        {isExpanded ? (
                                          <ChevronUpIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                        ) : (
                                          <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                        )}
                                      </button>
                                      {isExpanded && (
                                        <div className="px-3 pb-3 pt-0 border-t border-gray-100 bg-gray-50/50">
                                          {question.content &&
                                            (() => {
                                              const titleNorm = String(question.title || '')
                                                .replace(/\s+/g, ' ')
                                                .trim()
                                                .toLowerCase();
                                              const contentNorm = String(question.content || '')
                                                .replace(/\s+/g, ' ')
                                                .trim()
                                                .toLowerCase();
                                              const isRedundant =
                                                !contentNorm ||
                                                contentNorm === titleNorm ||
                                                contentNorm.startsWith(titleNorm) ||
                                                titleNorm.startsWith(contentNorm);
                                              return !isRedundant;
                                            })() && (
                                              <div className="text-sm text-gray-600 mb-2">{question.content}</div>
                                            )}
                                          {(question.type === 'Multiple Choice (Single)' ||
                                            question.type === 'Multiple Choice (Multiple)' ||
                                            question.type === 'True/False' ||
                                            question.type === 'true-false' ||
                                            question.type === 'multiple-choice' ||
                                            question.type === 'Multiple Choice (Single Answer)' ||
                                            question.type === 'Multiple Choice (Multiple Answers)') &&
                                            question.options &&
                                            Array.isArray(question.options) &&
                                            question.options.length > 0 && (
                                              <div className="mb-3">
                                                <div className="text-sm font-medium text-gray-700 mb-2">Options:</div>
                                                <div className="space-y-2">
                                                  {question.options.map((option, optIndex) => (
                                                    <div key={optIndex} className="flex items-center space-x-2">
                                                      <span
                                                        className={`text-sm px-3 py-2 rounded border ${option && option.isCorrect
                                                          ? 'bg-green-100 text-green-800 border-green-300 font-medium'
                                                          : 'bg-gray-50 text-gray-700 border-gray-200'
                                                          }`}
                                                      >
                                                        {String.fromCharCode(65 + optIndex)}. {option && option.text ? option.text : ''}
                                                      </span>
                                                      {option && option.isCorrect && (
                                                        <span className="text-xs text-green-600 font-medium">✓ Correct</span>
                                                      )}
                                                    </div>
                                                  ))}
                                                </div>
                                                {question.options &&
                                                  question.options.filter((o) => o && o.isCorrect).length === 1 && (
                                                    <div className="mt-2 text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200 font-medium">
                                                      Correct: {String.fromCharCode(65 + question.options.findIndex((o) => o && o.isCorrect))}
                                                    </div>
                                                  )}
                                              </div>
                                            )}
                                          {question.type !== 'multiple-choice' &&
                                            question.type !== 'Multiple Choice (Single)' &&
                                            question.type !== 'Multiple Choice (Multiple)' &&
                                            question.type !== 'Multiple Choice (Single Answer)' &&
                                            question.type !== 'Multiple Choice (Multiple Answers)' &&
                                            question.correctAnswer && (
                                              <div className="mb-3">
                                                <div className="text-sm font-medium text-gray-700 mb-1">Correct Answer:</div>
                                                <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200 font-medium">
                                                  {question.correctAnswer === 'a' || question.correctAnswer === 'True'
                                                    ? 'True'
                                                    : question.correctAnswer === 'b' || question.correctAnswer === 'False'
                                                      ? 'False'
                                                      : question.correctAnswer}
                                                </div>
                                              </div>
                                            )}
                                          <div className="text-xs text-gray-500 mt-2">
                                            Type: {question.type || 'Unknown'} • Points: {question.points || 1}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                </div>
              ) : selectedProctoringCategory === 'transcriptAnalysis' ? (
                // Transcript Analysis detailed card (HTML adapted from copy file)
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                  <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                        <svg
                          className="w-4.5 h-4.5 text-white"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 5.5A2.5 2.5 0 016.5 3h11A2.5 2.5 0 0120 5.5v8A2.5 2.5 0 0117.5 16H9l-3.5 3 .5-3H6.5A2.5 2.5 0 014 13.5v-8z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                          AI-Assisted Signal
                        </p>
                        <h2 className="text-base font-bold text-gray-900 leading-tight">
                          Transcript Analysis Breakdown
                        </h2>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                          Contribution
                        </p>
                        <p className="text-sm font-bold text-gray-900">
                          +{(interviewProctoringData.categories.transcriptAnalysis || {}).score ?? 0} pts
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="md:col-span-2 rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                          How this works
                        </p>
                        <p className="text-sm text-gray-700">
                          We use an AI model to look at wording patterns in answers and flag segments that
                          appear machine-generated or overly generic. This is a supporting signal and should
                          be reviewed alongside other evidence.
                        </p>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                        <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                          Samples
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {(interviewProctoringData.categories.transcriptAnalysis || {}).details
                            ?.sampleCount ?? 0}
                        </p>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                        <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                          Suspicious
                        </p>
                        <p className="text-lg font-semibold text-rose-600">
                          {(interviewProctoringData.categories.transcriptAnalysis || {}).details
                            ?.suspiciousCount ?? 0}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                        <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                          Suspicious Rate
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {(interviewProctoringData.categories.transcriptAnalysis || {}).details
                            ?.suspiciousRate ?? 0}
                          %
                        </p>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                        <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                          Pattern
                        </p>
                        <p
                          className={`text-sm font-semibold ${(interviewProctoringData.categories.transcriptAnalysis || {}).details
                            ?.isPatternSuspicious
                            ? 'text-rose-600'
                            : 'text-emerald-600'
                            }`}
                        >
                          {(interviewProctoringData.categories.transcriptAnalysis || {}).details
                            ?.isPatternSuspicious
                            ? 'Suspicious'
                            : 'Normal'}
                        </p>
                      </div>
                    </div>
                    {(interviewProctoringData.categories.transcriptAnalysis || {}).perQuestion?.length >
                      0 && (
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 max-h-64 overflow-y-auto space-y-2">
                          <p className="text-xs font-semibold text-gray-700 uppercase tracking-[0.12em]">
                            Per-Question Analysis
                          </p>
                          {(interviewProctoringData.categories.transcriptAnalysis || {}).perQuestion.map(
                            (q, idx) => (
                              <div
                                key={idx}
                                className={`p-3 rounded-lg border ${q.isSuspicious ? 'bg-rose-50 border-rose-200' : 'bg-gray-50 border-gray-200'
                                  }`}
                              >
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-sm font-medium text-gray-900">
                                    Candidate Response {q.questionIndex + 1}
                                  </span>
                                  {q.isSuspicious && (
                                    <span className="text-xs font-medium text-rose-600">Flagged</span>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                  <div>
                                    <span className="text-gray-500">AI Likeness:</span>{' '}
                                    <span
                                      className={`font-medium ${(q.aiLikeness ?? 0) > 0.7 ? 'text-rose-600' : 'text-gray-900'
                                        }`}
                                    >
                                      {q.aiLikeness != null
                                        ? Math.round(q.aiLikeness * 100) + '%'
                                        : '—'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Genericness:</span>{' '}
                                    <span className="font-medium text-gray-900">
                                      {q.genericness != null
                                        ? Math.round(q.genericness * 100) + '%'
                                        : '—'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Structure:</span>{' '}
                                    <span className="font-medium text-gray-900">
                                      {q.structureDensity != null
                                        ? Math.round(q.structureDensity * 100) + '%'
                                        : '—'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Real Exp:</span>{' '}
                                    <span
                                      className={`font-medium ${(q.realExperience ?? 0) < 0.3 ? 'text-rose-600' : 'text-emerald-600'
                                        }`}
                                    >
                                      {q.realExperience != null
                                        ? Math.round(q.realExperience * 100) + '%'
                                        : '—'}
                                    </span>
                                  </div>
                                </div>
                                {q.notes && (
                                  <p className="text-xs text-gray-600 mt-2 italic">{q.notes}</p>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      )}
                    <div className="group inline-block relative cursor-help">
                      <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                        Calculated Criteria
                      </span>
                      <div
                        role="tooltip"
                        className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                      >
                        <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                        {(() => {
                          const details = (interviewProctoringData.categories.transcriptAnalysis || {})
                            .details || {};
                          const sampleCount = details.sampleCount ?? 0;
                          const suspiciousCount = details.suspiciousCount ?? 0;
                          const suspiciousRatePercent = details.suspiciousRate ?? 0;
                          const suspiciousRate = (suspiciousRatePercent || 0) / 100;
                          const baseFromCount = suspiciousCount * 1.5;
                          const curveFromRate = Math.pow(suspiciousRate || 0, 2) * 35;
                          const rawScore = baseFromCount + curveFromRate;
                          const roundedScore = Math.round(rawScore);
                          if (!sampleCount) {
                            return (
                              <p className="text-xs text-gray-100 relative">
                                AI-assisted score is 0 because there were no answers available to analyse.
                              </p>
                            );
                          }
                          if (!suspiciousCount) {
                            return (
                              <p className="text-xs text-gray-100 relative">
                                All {sampleCount} analysed answers looked human. With 0 suspicious answers,
                                the AI-assisted score stays at 0 pts.
                              </p>
                            );
                          }
                          return (
                            <div className="space-y-1.5 text-xs text-gray-100 relative">
                              <p className="font-semibold text-gray-50">
                                How we reached {roundedScore} pts:
                              </p>
                              <p>
                                • Suspicious answers: {suspiciousCount} × 1.5 pts ={' '}
                                <span className="font-semibold">{baseFromCount.toFixed(1)} pts</span>
                              </p>
                              <p>
                                • Suspicious rate curve: ({(suspiciousRate * 100).toFixed(0)}% ÷ 100)² × 35
                                ≈ <span className="font-semibold">{curveFromRate.toFixed(1)} pts</span>
                              </p>
                              <p>
                                • Total: {baseFromCount.toFixed(1)} + {curveFromRate.toFixed(1)} ≈{' '}
                                <span className="font-semibold">{rawScore.toFixed(1)} pts</span> → rounded
                                to <span className="font-semibold">{roundedScore} pts</span> shown above.
                              </p>
                              <p className="text-gray-300">
                                In short, more AI-like answers and a higher suspicious rate push this score
                                up.
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-10 text-center text-sm text-gray-600">Failed to load assessment details</div>
              )}
            </div>
          </div>
        </div>
      )}



      {/* Resume scoring details modal — read-only, back to screening details */}
      {showResumeScoringDetailsModal && (
        <div data-testid="modal-backdrop" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] overflow-y-auto p-4">
          <div className="relative w-full max-w-3xl rounded-xl border border-gray-200 bg-white shadow-xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex-shrink-0 flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50/80 px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  onClick={backToScreeningDetails}
                  className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-[#007BFF] hover:bg-[#007BFF]/10 transition-colors"
                  aria-label="Back to screening details"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2 min-w-0">
                  <DocumentTextIcon className="h-5 w-5 text-[#007BFF] flex-shrink-0" />
                  <h2 className="text-base font-semibold text-gray-900 truncate">Resume scoring details</h2>
                </div>
              </div>
              <button
                type="button"
                onClick={closeResumeScoringDetails}
                className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <p className="text-sm font-semibold text-gray-900">Criteria</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Read-only view of the resume scoring criteria configured for this screening.
                </p>
                <div className="mt-3">
                  <ResumeScoreConfig
                    screeningId={screeningId}
                    criteria={screening?.resumeScoringConfig?.criteria || []}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Interview details modal — compact, back to screening details */}
      {showInterviewDetailsModal && (
        <div data-testid="modal-backdrop" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] overflow-y-auto p-4">
          <div className="relative w-full max-w-4xl rounded-xl border border-gray-200 bg-white shadow-xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex-shrink-0 flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50/80 px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  onClick={backToScreeningDetails}
                  className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-[#007BFF] hover:bg-[#007BFF]/10 transition-colors"
                  aria-label="Back to screening details"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2 min-w-0">
                  <MicrophoneIcon className="h-5 w-5 text-[#007BFF] flex-shrink-0" />
                  <h2 className="text-base font-semibold text-gray-900 truncate">Interview details</h2>
                </div>
              </div>
              <button
                type="button"
                onClick={closeInterviewDetails}
                className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {interviewDetailsLoading ? (
                <div className="flex flex-col items-center justify-center py-10">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#007BFF] border-t-transparent" />
                  <p className="mt-2 text-sm text-gray-600">Loading…</p>
                </div>
              ) : interviewDetails ? (
                <div className="space-y-4">
                  {interviewDetails.questions && interviewDetails.questions.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <h3 className="text-sm font-medium text-gray-900 px-3 py-2.5 bg-gray-50 border-b border-gray-200">
                        Questions ({interviewDetails.questions.length})
                      </h3>
                      <div className="divide-y divide-gray-100">
                        {interviewDetails.questions.map((q, idx) => {
                          const isExpanded = expandedInterviewQuestions.has(idx);
                          const promptText = q.prompt || q.question || '';
                          return (
                            <div key={q._id || idx} className="bg-white">
                              <button
                                type="button"
                                onClick={() => {
                                  setExpandedInterviewQuestions((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(idx)) next.delete(idx);
                                    else next.add(idx);
                                    return next;
                                  });
                                }}
                                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-gray-50"
                              >
                                <span className="text-sm text-gray-900 truncate flex-1 min-w-0">
                                  #{idx + 1} {promptText.length > 60 ? `${promptText.slice(0, 60)}…` : promptText}
                                </span>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                  {q.type === 'yes_no' ? 'Yes/No' : 'Short answer'}
                                </span>
                                {isExpanded ? (
                                  <ChevronUpIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                ) : (
                                  <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                )}
                              </button>
                              {isExpanded && (
                                <div className="px-3 pb-3 pt-0 border-t border-gray-100 bg-gray-50/50">
                                  <p className="text-sm text-gray-900 mt-2">{promptText}</p>
                                  {q.expectedAnswer && (
                                    <p className="mt-1.5 text-xs text-gray-600 italic">Summary: {q.expectedAnswer}</p>
                                  )}
                                  {Array.isArray(q.expectedPoints) && q.expectedPoints.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Scoring Rubric Points:</p>
                                      {q.expectedPoints.map((point, pIdx) => (
                                        <div key={pIdx} className="flex gap-2 text-xs text-gray-700">
                                          <span className="text-indigo-500 font-bold">•</span>
                                          <span>{typeof point === 'string' ? point : point?.text || ''}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {interviewDetails?.advancedType === 'rubric-based' && Array.isArray(interviewDetails.rubrics) && interviewDetails.rubrics.length > 0 && (
                    <div className="space-y-4 mt-4">
                      <h3 className="text-sm font-medium text-gray-900 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                        Modules ({interviewDetails.rubrics.length})
                      </h3>
                      {interviewDetails.rubrics.map((module, mIdx) => (
                        <div key={module._id || mIdx} className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm">
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                            <div>
                              <h4 className="text-sm font-semibold text-gray-800">{module.name || 'Unnamed Module'}</h4>
                              <p className="text-xs text-gray-500 mt-0.5">{module.questionBank?.length || 0} questions in bank</p>
                            </div>
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-semibold">
                              Pick {module.questionsPerInterview}
                            </span>
                          </div>
                          <div className="p-4 space-y-3">
                            {module.questionBank?.map((q, qIdx) => (
                              <div key={q._id || qIdx} className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex gap-3">
                                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                                  {qIdx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-800">{q.prompt || q.question}</p>
                                  <div className="mt-1.5 flex gap-2 flex-wrap">
                                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">
                                      {q.type === 'yes_no' ? 'Yes/No' : 'Short answer'}
                                    </span>
                                    {q.critical && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded border border-red-200">Critical</span>}
                                    {q.mandatory && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded border border-blue-200">Mandatory</span>}
                                  </div>
                                  {q.expectedAnswer && (
                                    <p className="mt-2 text-xs text-gray-600 italic">
                                      <span className="font-semibold not-italic">Summary: </span>
                                      {q.expectedAnswer}
                                    </p>
                                  )}
                                  {Array.isArray(q.expectedPoints) && q.expectedPoints.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Scoring Rubric Points:</p>
                                      {q.expectedPoints.map((point, pIdx) => (
                                        <div key={pIdx} className="flex gap-2 text-xs text-gray-700">
                                          <span className="text-indigo-500 font-bold">•</span>
                                          <span>{typeof point === 'string' ? point : point?.text || ''}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!(interviewDetails?.advancedType === 'rubric-based') && (!interviewDetails.questions || interviewDetails.questions.length === 0) && (
                    <p className="py-10 text-center text-sm text-gray-500">No questions found for this interview.</p>
                  )}
                </div>
              ) : (
                <p className="py-10 text-center text-sm text-gray-500">Could not load interview details.</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Document Collection details modal — editable, back to screening details */}
      {showDocumentCollectionDetailsModal && (
        <div data-testid="modal-backdrop" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] overflow-y-auto p-4">
          <div className="relative w-full max-w-4xl rounded-xl border border-gray-200 bg-white shadow-xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex-shrink-0 flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50/80 px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowDocumentCollectionDetailsModal(false);
                    setShowViewDetailsModal(true);
                  }}
                  className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-[#007BFF] hover:bg-[#007BFF]/10 transition-colors"
                  aria-label="Back to screening details"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2 min-w-0">
                  <DocumentTextIcon className="h-5 w-5 text-[#007BFF] flex-shrink-0" />
                  <h2 className="text-base font-semibold text-gray-900 truncate">Document collection details</h2>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowDocumentCollectionDetailsModal(false)}
                className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 relative">
              {documentCollectionConfigUpdating && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-10 flex items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#007BFF] border-t-transparent" />
                </div>
              )}
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <p className="text-sm font-semibold text-gray-900">Requested Documents</p>
                <p className="text-xs text-gray-500 mt-0.5 mb-4">
                  Configure which documents are mandatory or optional. Changes are saved automatically.
                </p>
                <DocumentCollectionConfig
                  requestedDocuments={screening?.requestedDocuments || []}
                  onChange={handleUpdateDocumentCollectionConfig}
                  enableVerification={!!screening?.enableDocumentVerification}
                  onVerificationChange={async (newVal) => {
                    if (!screeningId) return;
                    try {
                      setDocumentCollectionConfigUpdating(true);
                      const res = await axios.put(`/api/screenings/${screeningId}`, {
                        enableDocumentVerification: newVal,
                      });
                      if (res.data?.screening) {
                        setScreening(prev => ({ ...prev, ...res.data.screening }));
                      }
                      toast.success(newVal ? 'Verification enabled' : 'Verification disabled', { style: { fontSize: '13px', padding: '6px 12px' } });
                    } catch (e) {
                      toast.error('Failed to save', { style: { fontSize: '13px', padding: '6px 12px' } });
                    } finally {
                      setDocumentCollectionConfigUpdating(false);
                    }
                  }}

                />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* View details modal — screening components and linked assessment / interview */}
      <AnimatedModal
        isOpen={showViewDetailsModal}
        onClose={() => setShowViewDetailsModal(false)}
        contentClassName="bg-white rounded-2xl shadow-xl sm:max-w-2xl w-full max-h-[90vh] overflow-hidden border border-gray-100"
        zIndex={50}
      >
        <div className="flex flex-col h-full max-h-[90vh]">
          <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50/80">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#007BFF]/10">
                <DocumentTextIcon className="h-5 w-5 text-[#007BFF]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Screening details</h2>
                <p className="text-xs text-gray-500">Components in this screening</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowViewDetailsModal(false)}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-3">
              {/* Resume scoring + criteria config */}

              {screeningMeta?.hasResumeScoring && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">

                  <div className="flex items-center justify-between gap-3 flex-wrap">

                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#007BFF]/10 flex items-center justify-center">
                        <DocumentTextIcon className="h-5 w-5 text-[#007BFF]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Resume Scoring</p>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {screeningMeta?.hasResumeScoring
                            ? 'Enabled. AI evaluates each candidate’s resume for this job.'
                            : 'Disabled. Candidates will not be asked to upload a resume and resume score will show as “—”.'}
                        </p>

                      </div>

                    </div>

                    {screeningMeta?.hasResumeScoring && screeningId && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowViewDetailsModal(false);
                          setShowResumeScoringDetailsModal(true);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#007BFF]/30 bg-white px-3 py-1.5 text-xs font-medium text-[#007BFF] hover:bg-[#007BFF]/5 shrink-0"
                        title="View details"
                      >
                        <EyeIcon className="h-4 w-4" />
                        View details
                      </button>
                    )}

                  </div>

                </div>
              )}



              {screeningMeta?.includeAssessment && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#007BFF]/10 flex items-center justify-center">
                        <AcademicCapIcon className="h-5 w-5 text-[#007BFF]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          Skill-based Assessment
                        </p>
                        {!screening?.assessmentId && (
                          <p className="text-xs text-gray-600 mt-0.5">Not attached yet.</p>
                        )}
                      </div>
                    </div>
                    {screening?.assessmentId ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowViewDetailsModal(false);
                          openAssessmentDetails(screening.assessmentId._id || screening.assessmentId);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#007BFF]/30 bg-white px-3 py-1.5 text-xs font-medium text-[#007BFF] hover:bg-[#007BFF]/5 shrink-0"
                      >
                        <EyeIcon className="h-4 w-4" />
                        View details
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setShowViewDetailsModal(false);
                          setShowCreateAssessmentModal(true);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#007BFF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0062CC] shrink-0"
                      >
                        <PlusCircleIcon className="h-4 w-4" />
                        Set up assessment
                      </button>
                    )}
                  </div>
                </div>
              )}
              {screeningMeta?.includeInterview && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#007BFF]/10 flex items-center justify-center">
                        <ChatBubbleLeftRightIcon className="h-5 w-5 text-[#007BFF]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          AI Interview
                        </p>
                        {!screening?.interviewId && (
                          <p className="text-xs text-gray-600 mt-0.5">Not attached yet.</p>
                        )}
                      </div>
                    </div>
                    {screening?.interviewId ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowViewDetailsModal(false);
                          openInterviewDetails(screening.interviewId._id || screening.interviewId);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#007BFF]/30 bg-white px-3 py-1.5 text-xs font-medium text-[#007BFF] hover:bg-[#007BFF]/5 shrink-0"
                      >
                        <EyeIcon className="h-4 w-4" />
                        View details
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setShowViewDetailsModal(false);
                          setShowCreateInterviewModal(true);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#007BFF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0062CC] shrink-0"
                      >
                        <PlusCircleIcon className="h-4 w-4" />
                        Set up interview
                      </button>
                    )}
                  </div>
                </div>
              )}
              {screeningMeta?.includeQualifyingAgent && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#007BFF]/10 flex items-center justify-center">
                        <PhoneIcon className="h-5 w-5 text-[#007BFF]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Qualifying Agent</p>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {qualifyingQuestionsSummary(screening)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowViewDetailsModal(false);
                        setShowQualifyingAgentDetailsModal(true);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[#007BFF]/30 bg-white px-3 py-1.5 text-xs font-medium text-[#007BFF] hover:bg-[#007BFF]/5 shrink-0"
                    >
                      <EyeIcon className="h-4 w-4" />
                      View details
                    </button>
                  </div>
                </div>
              )}
              {screeningMeta?.includeDocumentCollection && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#007BFF]/10 flex items-center justify-center">
                        <DocumentTextIcon className="h-5 w-5 text-[#007BFF]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          Document Collection
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {screening?.requestedDocuments?.length || 0} document(s) requested.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowViewDetailsModal(false);
                        setShowDocumentCollectionDetailsModal(true);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[#007BFF]/30 bg-white px-3 py-1.5 text-xs font-medium text-[#007BFF] hover:bg-[#007BFF]/5 shrink-0"
                    >
                      <EyeIcon className="h-4 w-4" />
                      View / Edit
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </AnimatedModal>
      {/* Candidate notifications panel (right-side sheet) */}
      <AnimatedModal
        isOpen={showNotificationsModal}
        onClose={() => setShowNotificationsModal(false)}
        ariaLabelledBy="candidate-notifications-title"
        contentClassName="bg-white rounded-2xl shadow-2xl w-full sm:max-w-md md:max-w-lg max-h-[85vh] overflow-hidden border border-gray-100"
        zIndex={60}
      >
        <div className="flex flex-col max-h-[85vh]">
          <div className="flex-shrink-0 flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-200 bg-white">
            <div className="min-w-0">
              <h2
                id="candidate-notifications-title"
                className="text-base font-semibold text-gray-900 truncate"
              >
                Notifications
              </h2>
              <p className="mt-0.5 text-xs text-gray-500">
                {unreadNotificationsCount} focused unread · {duplicateCandidates.length} duplicates
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={async () => {
                  if (!screeningId || visibleCandidateNotifications.length === 0) return;
                  try {
                    const res = await axios.post(
                      `/api/screenings/${screeningId}/notifications/mark-all-read`
                    );
                    setNotificationReadState(res.data?.readState || null);
                  } catch (e) {
                    toast.error(
                      e.response?.data?.message || 'Failed to mark notifications as read'
                    );
                  }
                }}
                disabled={visibleCandidateNotifications.length === 0 || unreadNotificationsCount === 0}
                className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${visibleCandidateNotifications.length === 0 || unreadNotificationsCount === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#007BFF] text-white hover:bg-[#0062CC]'
                  }`}
              >
                Mark all as read
              </button>
              <button
                type="button"
                onClick={() => setShowNotificationsModal(false)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:ring-offset-2"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="px-5 pt-4 pb-3 flex gap-2 border-b border-gray-200">
            {['focused', 'duplicates'].map((tab) => {
              const count = tab === 'focused' ? unreadNotificationsCount : duplicateCandidates.length;
              const disabled = tab === 'duplicates' && duplicateCandidates.length === 0;
              return (
                <button
                  key={tab}
                  type="button"
                  disabled={disabled}
                  onClick={() => setNotificationsTab(tab)}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${notificationsTab === tab
                    ? 'bg-[#007BFF] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {tab === 'focused' ? 'Focused' : 'Duplicates'}
                  <span className="inline-flex items-center justify-center rounded-full bg-white/20 px-1.5 text-[10px]">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {notificationsTab === 'duplicates' ? (
              duplicateCandidates.length === 0 ? (
                <p className="text-sm text-gray-500 py-10 text-center">No duplicate candidates yet.</p>
              ) : (
                <ul className="space-y-3">
                  {duplicateCandidates.map((candidate) => (
                    <li key={candidate._id} className="rounded-xl border border-gray-200 bg-white shadow-sm p-4">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {resolveCandidateDisplayName(candidate) || candidate.name || 'Candidate'}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {candidate.duplicateSummary?.count || 0} prior appearance{candidate.duplicateSummary?.count === 1 ? '' : 's'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setDuplicateModalCandidate(candidate)}
                          className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                        >
                          View history
                          <InformationCircleIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Latest status: {candidate.status || '—'}
                      </p>
                    </li>
                  ))}
                </ul>
              )
            ) : visibleCandidateNotifications.length === 0 ? (
              <p className="text-sm text-gray-500 py-10 text-center">
                No notifications yet.
              </p>
            ) : (
              <ul className="space-y-3">
                {visibleCandidateNotifications.map((item, index) => {
                  const isRead = item.isRead;
                  const completedAtText =
                    item.completedAt &&
                    new Date(item.completedAt).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    });
                  const scoreText = (v) => (v == null ? '—' : `${v}%`);
                  const showResumeScore = !!screeningMeta?.hasResumeScoring;
                  const showAssessmentScore =
                    !!screeningMeta?.includeAssessment && !!screeningMeta?.hasAssessment;
                  const showInterviewScore =
                    !!screeningMeta?.includeInterview && !!screeningMeta?.hasInterview;
                  return (
                    <li key={`${item.id || index}-${item.type}-${index}`}>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!screeningId) return;
                          if (isRead) return;
                          const notificationId = String(item.id);
                          // Keep the item visible during this open session (dot removed)
                          setNotificationSessionReadIds((prev) => {
                            const next = new Set(prev);
                            next.add(notificationId);
                            return next;
                          });
                          // Optimistic UI: mark only this item as read (per-id)
                          setNotificationReadState((prev) => {
                            const prevIds = Array.isArray(prev?.readNotificationIds)
                              ? prev.readNotificationIds
                              : [];
                            if (prevIds.includes(notificationId)) return prev;
                            return {
                              ...(prev || {}),
                              readNotificationIds: [...prevIds, notificationId],
                            };
                          });
                          try {
                            const res = await axios.post(
                              `/api/screenings/${screeningId}/notifications/mark-read`,
                              { notificationId }
                            );
                            setNotificationReadState(res.data?.readState || null);
                          } catch (e) {
                            // If persistence fails, refetch read-state to avoid drift.
                            fetchNotificationReadState();
                          }
                        }}
                        className={`w-full text-left flex items-start justify-between gap-4 rounded-xl border px-4 py-3.5 bg-white shadow-sm transition-colors ${isRead
                          ? 'border-gray-200 hover:bg-gray-50'
                          : 'border-gray-200 hover:border-[#007BFF]/40 hover:bg-[#007BFF]/[0.03]'
                          }`}
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          {(() => {
                            const isDocUpload = item.type === 'document_upload';
                            const isDocDeleted = item.type === 'document_deleted';
                            const isHMDecision = item.type === 'hiring_manager_decision';
                            const isShareLinkView = item.type === 'share_link_view';
                            const iconWrap =
                              isDocDeleted
                                ? 'bg-rose-50'
                                : isDocUpload
                                  ? 'bg-blue-50'
                                  : isShareLinkView
                                    ? 'bg-sky-50'
                                    : isHMDecision
                                      ? item.decisionStatus === 'selected'
                                        ? 'bg-green-50'
                                        : item.decisionStatus === 'rejected'
                                          ? 'bg-red-50'
                                          : item.decisionStatus === 'offered'
                                            ? 'bg-violet-50'
                                            : 'bg-yellow-50'
                                      : 'bg-emerald-50';
                            const iconColor =
                              isDocDeleted
                                ? 'text-rose-600'
                                : isDocUpload
                                  ? 'text-blue-600'
                                  : isShareLinkView
                                    ? 'text-sky-600'
                                    : isHMDecision
                                      ? item.decisionStatus === 'selected'
                                        ? 'text-green-600'
                                        : item.decisionStatus === 'rejected'
                                          ? 'text-red-600'
                                          : item.decisionStatus === 'offered'
                                            ? 'text-violet-600'
                                            : 'text-yellow-600'
                                      : 'text-emerald-600';
                            const Icon =
                              isDocDeleted
                                ? TrashIcon
                                : isDocUpload
                                  ? DocumentTextIcon
                                  : isShareLinkView
                                    ? EyeIcon
                                    : isHMDecision
                                      ? item.decisionStatus === 'selected'
                                        ? CheckCircleIcon
                                        : item.decisionStatus === 'rejected'
                                          ? XCircleIcon
                                          : item.decisionStatus === 'offered'
                                            ? ClipboardDocumentCheckIcon
                                            : ClockIcon
                                      : CheckCircleIcon;
                            return (
                              <div className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${iconWrap}`}>
                                <Icon className={`h-5 w-5 ${iconColor}`} />
                              </div>
                            );
                          })()}
                          <div className="min-w-0">
                            {item.type === 'completed' ? (
                              <>
                                <p className="text-sm text-gray-900">
                                  {item.candidateName}{' '}
                                  <span
                                    className={`text-sm font-semibold ${isRead ? 'text-gray-700' : 'text-gray-900'}`}
                                  >
                                    completed the screening
                                  </span>
                                </p>
                                {completedAtText && (
                                  <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                                    <span>Completed on {completedAtText}</span>
                                  </p>
                                )}
                                {(showResumeScore || showAssessmentScore || showInterviewScore) && (
                                  <div className="mt-2.5 text-xs text-gray-600">
                                    <div className="flex flex-wrap gap-4 sm:gap-6">
                                      {showResumeScore && (
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-gray-500">Resume:</span>
                                          <span className="font-medium text-gray-800">
                                            {scoreText(item.resumeScore)}
                                          </span>
                                        </div>
                                      )}
                                      {showAssessmentScore && (
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-gray-500">Assessment:</span>
                                          <span className="font-medium text-gray-800">
                                            {scoreText(item.assessmentScorePct)}
                                          </span>
                                        </div>
                                      )}
                                      {showInterviewScore && (
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-gray-500">AI Interview:</span>
                                          <span className="font-medium text-gray-800">
                                            {scoreText(item.interviewScore)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : item.type === 'hiring_manager_decision' ? (
                              <>
                                <p className={`text-sm font-semibold ${isRead ? 'text-gray-800' : 'text-gray-900'}`}>
                                  {item.message}
                                </p>
                                {completedAtText && (
                                  <p className="mt-1 text-xs text-gray-500">
                                    Decision made on {completedAtText}
                                  </p>
                                )}
                                {item.decisionNotes && (
                                  <p className="mt-1.5 text-xs text-gray-600 italic">
                                    "{item.decisionNotes}"
                                  </p>
                                )}
                              </>
                            ) : item.type === 'share_link_view' ? (
                              <>
                                <p className={`text-sm font-semibold ${isRead ? 'text-gray-800' : 'text-gray-900'}`}>
                                  {item.message}
                                </p>
                                {completedAtText && (
                                  <p className="mt-1 text-xs text-gray-500">
                                    Viewed on {completedAtText}
                                  </p>
                                )}
                              </>
                            ) : (
                              <>
                                <p className={`text-sm font-semibold ${isRead ? 'text-gray-800' : 'text-gray-900'}`}>
                                  {item.message || 'Document update'}
                                </p>
                                {completedAtText && (
                                  <p className="mt-1 text-xs text-gray-500">
                                    {item.type === 'document_deleted'
                                      ? `Deleted on ${completedAtText}`
                                      : `Submitted on ${completedAtText}`}
                                  </p>
                                )}
                                <p className="mt-2 text-xs text-gray-600">
                                  Candidate: <span className="font-medium text-gray-800">{item.candidateName}</span>
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 pt-2">
                          {!isRead && (
                            <span
                              className="inline-block h-2.5 w-2.5 rounded-full bg-[#007BFF]"
                              aria-label="Unread"
                              title="Unread"
                            />
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </AnimatedModal>
      {/* Resume + Score modal (shared with JobResults) */}
      <CandidateInsightModal
        isOpen={!!selectedCandidateForResumeBreakdown}
        screeningId={screeningId}
        onRescored={async (candidateId) => {
          // The modal reads from the `candidateInsightDetailsById` cache, so drop the stale
          // entry and refetch before the freshly written score can show up.
          setCandidateInsightDetailsById((prev) => {
            const next = { ...prev };
            delete next[candidateId];
            return next;
          });
          try {
            setCandidateInsightDetailsLoading(true);
            const res = await axios.get(`/api/screenings/${screeningId}/candidates/${candidateId}`);
            const detailedCandidate = res.data?.candidate || null;
            if (detailedCandidate) {
              const detailedDisplayName = resolveCandidateDisplayName(detailedCandidate);
              setCandidateInsightDetailsById((prev) => ({
                ...prev,
                [candidateId]: {
                  ...detailedCandidate,
                  canonicalDisplayName: detailedDisplayName,
                },
              }));
              setSelectedCandidateForResumeBreakdown((prev) => {
                if (!prev?._id || String(prev._id) !== String(candidateId)) return prev;
                return { ...prev, ...detailedCandidate };
              });
            }
          } catch (e) {
            toast.error('Rescored, but failed to reload the candidate — refresh to see the new score');
          } finally {
            setCandidateInsightDetailsLoading(false);
          }
          // Keep the candidates table score cell in sync with the modal.
          fetchCandidates({ forceRefresh: true, quiet: true });
        }}
        onClose={() => {
          setSelectedCandidateForResumeBreakdown(null);
          setCandidateInsightModalInitialTab('score');
          setCandidateInsightModalCandidateDetailsOnly(false);
          setCandidateInsightDetailsLoading(false);
        }}
        onSaveField={async (candidateId, updateData) => {
          const res = await axios.patch(
            `/api/screenings/${screeningId}/candidates/${candidateId}`,
            updateData
          );
          const updated = res.data?.candidate || null;
          if (updated) {
            setCandidateInsightDetailsById((prev) => ({
              ...prev,
              [candidateId]: {
                ...(prev[candidateId] || {}),
                ...updated,
              },
            }));
            setSelectedCandidateForResumeBreakdown((prev) => {
              if (!prev?._id || String(prev._id) !== String(candidateId)) return prev;
              return { ...prev, ...updated };
            });
          }
        }}
        candidate={(() => {
          if (!selectedCandidateForResumeBreakdown) return null;
          if (!selectedCandidateForResumeBreakdown?._id) return selectedCandidateForResumeBreakdown;
          const selectedCanonical =
            (selectedCandidateForResumeBreakdown.canonicalDisplayName || '').toString().trim() ||
            resolveCandidateDisplayName(selectedCandidateForResumeBreakdown);
          const detailed = candidateInsightDetailsById[selectedCandidateForResumeBreakdown._id];
          const base = detailed || selectedCandidateForResumeBreakdown;
          return {
            ...base,
            canonicalDisplayName:
              (base?.canonicalDisplayName || '').toString().trim() || selectedCanonical,
          };
        })()}
        detailsLoading={candidateInsightDetailsLoading}
        initialTab={candidateInsightModalInitialTab}
        candidateDetailsOnly={candidateInsightModalCandidateDetailsOnly}
      />
      {showSkippedNoEmailModal && (
        <div
          data-testid="modal-backdrop" className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-4"
          onClick={() => setShowSkippedNoEmailModal(false)}
        >
          <div
            className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-slate-50">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900">
                    {skippedNoEmailItems.length} resume{skippedNoEmailItems.length === 1 ? '' : 's'} will be skipped
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    We couldn’t find a valid email in {skippedNoEmailItems.length === 1 ? 'this resume' : 'these resumes'}. Candidates need an email to receive the invite, so they won’t be added to this screening.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSkippedNoEmailModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 bg-white">
              <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  Skipped resumes
                </p>
                <ul className="mt-2 max-h-60 overflow-auto space-y-1 text-sm text-slate-700">
                  {skippedNoEmailItems.map((item, i) => (
                    <li key={i} className="truncate">
                      <span className="font-medium text-slate-800">{item.label}</span>
                      <span className="text-slate-500"> — {item.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const header = 'Resume,Reason';
                    const escape = (s) => `"${String(s).replace(/"/g, '""')}"`;
                    const rows = skippedNoEmailItems.map((it) => `${escape(it.label)},${escape(it.reason)}`);
                    const csv = [header, ...rows].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'skipped_resumes.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Download list
                </button>
                <button
                  type="button"
                  onClick={() => setShowSkippedNoEmailModal(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Skip and continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <AutomationsModal
        isOpen={showAutomationsModal}
        onClose={() => setShowAutomationsModal(false)}
        jobId={jobId}
        screeningId={screeningId}
        onAppliedChanges={() => {
          fetchCandidates();
        }}
      />

      {decisionEmailModal && (
        <DecisionEmailModal
          screeningId={screeningId}
          candidateId={decisionEmailModal.candidateId}
          candidateName={decisionEmailModal.candidateName}
          decision={decisionEmailModal.decision}
          onClose={closeDecisionEmailPreview}
          onSent={decisionEmailModal.onSent}
        />
      )}

      {bkDecisionEmailModal && (
        <BKDecisionEmailModal
          screeningId={screeningId}
          candidateId={bkDecisionEmailModal.candidateId}
          candidateName={bkDecisionEmailModal.candidateName}
          onClose={closeBkDecisionEmailPreview}
          onSent={bkDecisionEmailModal.onSent}
        />
      )}

      {isSalaryModalOpen && selectedCandidateForSalaryModal && (
        <SalaryDetailsModal
          isOpen={isSalaryModalOpen}
          onClose={() => {
            setIsSalaryModalOpen(false);
            setSelectedCandidateForSalaryModal(null);
            setSelectedSalaryDataForModal(null);
          }}
          candidate={selectedCandidateForSalaryModal}
          salaryData={selectedSalaryDataForModal}
          screeningId={screeningId}
          screeningMeta={screeningMeta}
          onRefresh={fetchSalaryRequestsBulk}
          editable={salaryDetailsEditable}
        />
      )}
    </div>
  );
};
export default ScreeningDetailPage;
